// client/src/pages/SherlocReports.js (CLIENT Sherloc Reports + Looker embeds)
//
// ✅ STANDARDIZED to SUPERADMIN SherlocReports logic (behavior + data rules)
// ✅ Output style / renderer preserved EXACTLY (no visual/output format changes)
//
// ✅ FIX (this update): stops repeated 404/400 spam from "latest" calls by:
//    - Trying a small set of likely "latest" endpoints ONCE per advertiser
//    - Disabling future attempts for that advertiser (PERSISTED in localStorage) if backend doesn’t support it
//    - Falling back to localStorage cache only (your UI still works)
//
// ✅ NEW (THIS UPDATE — per DJ):
// 1) Client AI Summary + Platform Analysis can be LIVE-aware (same as superadmin)
//    - Pre-fetch Meta live (publisher_platform) BEFORE /api/ai/run/:id
//    - POST campaign/timeframe + meta_live snapshot
// 2) Fix EFFICIENCY (and similar) randomly rendering as giant H1
//    - Normalize section headers so they always render as H2 section headers
// 3) Optimize Platform Analysis formatting
//    - Automatic dividers between platform blocks + normalized section headers
//
// ✅ FIX (THIS UPDATE — DJ):
// 4) Audience Network / Messenger LIVE parsing now includes engagements correctly
//    - Parses inline_post_engagement / post_engagement
//    - Reads totalsByPlatform when row arrays are absent
//    - Prefers row-level data to avoid double counting
// 5) Client channel cards now match superadmin for LIVE Audience Network / Messenger
//    - Engagements + Engagement Rate will render when Meta returns them
//
// ✅ PERSISTENCE FIX (THIS UPDATE — DJ):
// 6) Advertiser identity, visibility and selection come from the shared
//    governed catalog behind the canonical image-based selector
// 7) Uses clientAuthToken fallback-safe auth header for report requests
//
// ✅ CONTEXT FIX (THIS UPDATE — DJ):
// 8) Campaign Summary + Platform Analysis only render when their AI payload matches the
//    current advertiser + campaign + timeframe context
// 9) Cached/latest AI text is ignored when its context does not match the current selection
// 10) Changing advertiser or campaign clears narrative text until a fresh Run Analysis is performed
//
// ✅ FULL TOTALS FIX (THIS UPDATE — DJ):
// 11) Displayed totals are always recomputed from resolved channel values
// 12) Per channel source priority is now:
//       LIVE > current run ingest > previous resolved fallback
// 13) Run Analysis reflects newly uploaded ingest correctly when LIVE is absent
//
// ✅ NEW (THIS UPDATE — DJ / GOOGLE ANALYTICS):
// 14) Google Analytics (GA4) live summary fetch with multi-endpoint fallback
// 15) GA4 measurement card keeps visual platform order while staying excluded from paid media math
// 16) GA4 summary is passed into Sherloc /api/ai/run using backend-supported keys:
//    - googleAnalytics
//    - google_analytics
//    - google_analytics_live
//    - ga_live
// 17) Google Analytics report embed support added for client advertiser links
//
// ✅ NEW (THIS UPDATE — DJ / MULTI-MODEL):
// 18) AI Engine selector added: Auto / Sherloc / Eden
// 19) Selected provider is passed to backend as aiProvider
// 20) Provider-used / model-used metadata displayed when backend returns it
//
// ------------------------------------------------------------

import { governedProviderSpend, formatProviderCurrency } from "../utils/governedProviderEvidence";
import { normalizeProviderSurfaceKey } from "../utils/providerSurfaceRegistry";
import yahooLogo from "../assets/yahoo.png";
import viantLogo from "../assets/viant.png";
import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import "./SherlocReports.css";
/*
 * The GA4 detail modules moved to src/ga4Analytics so this surface and the
 * /ga4-analytics page render the same code. Behaviour here is unchanged — the
 * same components, the same thresholding, the same missing-is-not-zero series.
 */
import {
  GA_DIMENSION_COLORS,
  GaTrafficOverTimeChart,
  buildGoogleAnalyticsAudienceDashboard,
  renderGoogleAnalyticsAudienceDashboard,
} from "../ga4Analytics/ga4Modules";
import "./CampaignAnalysisDesktopWidth.css";
import PoweredByLogo from "../components/PoweredByLogo";
import ReportEmbedFrame from "../components/ReportEmbedFrame";
import SherlocAnalystInsightsPanel, {
  extractAnalystPanelFromRunPayload,
} from "../components/SherlocAnalystInsightsPanel";
import { normalizeAnalyticsEmbedUrl } from "../components/analyticsReportUtils";
import reportLoadingIcon from "../assets/reporticonLAST.png";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import usePageAwareness from "../hooks/usePageAwareness";
import {
  selectApprovedPlanCampaign,
  resolveApprovedFlightTimeframe,
} from "../utils/optimusPacing";
import { formatFlightDateShort, formatFlightRange } from "../utils/flightDates";
import { enforceCampaignAdvertiserIsolation } from "../utils/campaignIsolation";
import { getCanonicalCampaignDisplayName } from "../utils/campaignDisplayName";
import { buildSherlocReadyPill } from "../utils/sherlocReadyStatusPresentation";
import {
  runSherlocAnalysisViaDurableOperation,
  getLatestPersistedRunAnalysis,
  buildRunAnalysisScopeKey,
  retryRunAnalysisOperation,
  isRunAnalysisOperationActive,
  isRunAnalysisOperationTerminal,
  isRunAnalysisRetryAvailable,
  resolveRunAnalysisDisposition,
  getRunAnalysisStageLabel,
  getRunAnalysisCycleLabel,
  getRunAnalysisSupportReference,
  describeRunAnalysisProvenance,
  getRunAnalysisElapsedMs,
  formatRunAnalysisElapsed,
  formatRunAnalysisHeartbeat,
  describeRunAnalysisTerminalFailure,
  SHERLOC_RUN_ANALYSIS_CONTINUITY_COPY,
  SHERLOC_RUN_ANALYSIS_ELAPSED_TICK_MS,
  SHERLOC_RUN_ANALYSIS_STATUSES,
} from "../utils/sherlocRunAnalysisOperation";
import {
  resolveCampaignAnalysisContextCampaignId,
  resolveCampaignAnalysisLiveCampaignId,
  resolveCampaignAnalysisReportingCampaignId,
  resolveAutoCampaignOptionLabel,
  resolveDefaultCampaignAnalysisCampaign,
  extractImportedReportingCampaigns,
  IMPORTED_REPORTING_CONTEXT_PATH,
} from "../utils/campaignAnalysisCampaignOptions";
import {
  buildPlatformAnalyticsScopePath,
  resolvePlatformAnalyticsHandoffCampaign,
} from "../platformAnalytics/platformAnalyticsCampaignScope";
import { buildApprovedPlanContext } from "../utils/creativeStudioIntelligence";
import {
  buildCampaignAnalysisGovernedCampaignsPath,
  buildCampaignAnalysisSelectorOptions,
  resolveSelectedGovernedCampaignId,
  selectGovernedCampaignsForSelection,
} from "../utils/campaignAnalysisGovernedCampaignOptions";
import {
  canReadProviderForCampaignAnalysisScope,
  resolveCampaignAnalysisAuthoritativeCampaignId,
  resolveCampaignAnalysisProviderScope,
} from "../utils/campaignAnalysisProviderScope";
import {
  CAMPAIGN_SELECTOR_AUTO_VALUE,
  buildCampaignSelectorRows,
  resolveGovernedSelectionUpgrade,
  shouldRenderAutoCampaignOption,
} from "../utils/campaignSelectorIdentity";
import {
  buildSherlocCampaignPlatformScope,
  isSherlocCampaignScopedPlatformKey,
} from "./sherlocCampaignPlatformScope";
import {
  buildIngestByChannel as buildSharedIngestByChannel,
  computeReportingMetrics,
  hasUsableMetrics as hasSharedUsableMetrics,
  resolveReportingTruth,
} from "../utils/reportingTruthResolver";
import {
  buildAiOptimizationKpiFields,
  fetchLatestAiAggregate,
  selectGovernedCountRatePair,
} from "../utils/aiOptimizationKpiParity";
import {
  buildCostPerQualifiedVideoViewTile,
  buildQualifiedVideoKpiTiles,
} from "../platformAnalytics/qualifiedVideoPresentation";
import { resolveOutcomeConversionsPresentation } from "../platformAnalytics/outcomeSemantics";
import {
  resolveSpendEfficiencyContract,
  resolveSpendEfficiencyMetric,
} from "../platformAnalytics/spendEfficiencyContract";
import { normalizeAdvertiserVertical } from "../config/verticalDashboardConfig";
import {
  getVerticalIntelligenceConfig,
  sanitizeVerticalIntelligenceCopy,
} from "../config/verticalIntelligenceConfig";
import { filterSherlocPerformancePlatformEntries } from "./sherlocReportPlatformPerformance";
import { formatSherlocCurrency } from "./sherlocReportMetricFormatting";
import {
  buildSherlocRequestOwnershipKey,
  isSherlocReportRequestCurrent,
} from "./sherlocReportRequestOwnership";
import { PLATFORM_PERFORMANCE_INGEST_PILL_COPY } from "./sherlocReportsCopy";
import {
  buildSherlocNarrativeGapNotice,
  isSherlocNarrativeCompleted,
  normalizeSherlocNarrativeCompletion,
  resolveSherlocNarrativeIssueMessage,
} from "./sherlocNarrativeCompletion";

// Social channel logos
import facebookLogo from "../assets/facebook.jpg";
import instagramLogo from "../assets/instagram.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import youtubeLogo from "../assets/youtube.jpg";
import xLogo from "../assets/x.jpg";
import truthsocialLogo from "../assets/truth-social-logo.jpg";
import rumbleLogo from "../assets/rumble-logo.jpg";
import googleLogo from "../assets/google.jpg";
import openaiLogo from "../assets/openAI.png";
import googleAnalyticsImage from "../assets/googleAnalytics.jpg";
import redditLogo from "../assets/reddit.jpg";
import amazonLogo from "../assets/amazon.jpg";

// Meta hero (used for Audience Network + Messenger cards)
import metaLogo from "../assets/meta.jpg";

// Programmatic / other channel logos
import audioAnalyticsImage from "../assets/audio.jpg";
import ingameAnalyticsImage from "../assets/ingame.jpg";
import ctvAnalyticsImage from "../assets/ctv.jpg";
import videoAnalyticsImage from "../assets/video.jpg";
import mobileAnalyticsImage from "../assets/mobile.jpg";
import displayAnalyticsImage from "../assets/display.jpg";
import doohAnalyticsImage from "../assets/dooh.gif";
import emailAnalyticsImage from "../assets/email.jpg";

// Cinema icon – PurpleFilmIcon
import purpleFilmIcon from "../assets/PurpleFilmIcon.jpg";

// Hero image for AI Summary / Overall hero
import aiOptimizationsImage from "../assets/aioptimizations.gif";
import aiOptimizationsOldImage from "../assets/aioptimizationsOld.gif";
import sherlocVoiceoImage from "../assets/sherloc-voiceo.gif";

// 🔊 Voice Summary image
import playVoiceSummaryImage from "../assets/PlayVoiceSummary.png";

// ✅ CRA-safe only (NO import.meta usage in this file)
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const SHERLOC_REPORTS_REFRESH_INTERVAL_MS = 60000;
const SHERLOCREPORTS_ASK_SELECTOR = ".cphere-voice-panel-toggle";
const SHERLOCREPORTS_EDEN_SELECTOR = ".cphere-voice-mic-button";
const noStoreFetch = (url, options = {}) => {
  const requestUrl = new URL(url, window.location.origin);
  requestUrl.searchParams.set("_", String(Date.now()));
  return fetch(requestUrl.toString(), {
    ...options,
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      ...(options.headers || {}),
    },
  });
};

const isAbortLikeSherlocError = (error) =>
  error?.name === "AbortError" || error?.code === 20 || /abort/i.test(String(error?.message || ""));

const normalizeSherlocReportSurfaceUrl = (rawUrl) => {
  const openUrl = String(rawUrl || "").trim();
  if (!openUrl) {
    return { openUrl: "", embedUrl: "", embedMode: "unavailable" };
  }

  try {
    const parsed = new URL(openUrl);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return { openUrl: "", embedUrl: "", embedMode: "unavailable" };
    }
  } catch (_error) {
    return { openUrl: "", embedUrl: "", embedMode: "unavailable" };
  }

  const normalized = normalizeAnalyticsEmbedUrl(openUrl);
  return {
    openUrl,
    embedUrl: normalized.embedUrl || "",
    embedMode: normalized.embedMode || "external",
  };
};
const formatCampaignTimeframeLabel = (timeframe) => {
  if (timeframe?.mode === "flight" && timeframe.date_start && timeframe.date_end) {
    return formatFlightRange(timeframe.date_start, timeframe.date_end);
  }
  return "Flight not available";
};
const parseSherlocDateMs = (value, boundary = "start") => {
  const raw = String(value || "").trim();
  if (!raw) return 0;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const suffix = boundary === "start" ? "T00:00:00" : "T23:59:59";
    const parsed = Date.parse(`${raw}${suffix}`);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatSherlocShortDate = (value) => {
  const formatted = formatFlightDateShort(value);
  return formatted === "—" ? "" : formatted;
};
const getApprovedWindowState = (timeframe, nowMs = Date.now()) => {
  if (timeframe?.mode !== "flight" || !timeframe?.date_start) {
    return {
      hasFutureApprovedWindow: false,
      approvedWindowStartLabel: "",
    };
  }

  const startMs = parseSherlocDateMs(timeframe.date_start, "start");
  return {
    hasFutureApprovedWindow: Boolean(startMs && startMs > nowMs),
    approvedWindowStartLabel: formatSherlocShortDate(timeframe.date_start),
  };
};

// ✅ Use canonical AdvertiserContext keys

// ✅ Legacy fallback used elsewhere in client

// ✅ Client-local cache keys (avoid colliding with superadmin)
const getLastRunStorageKey = (advertiserId) => `clSherlocLastRun:v3:${advertiserId}`;
const getCampaignSelectionKey = (advertiserId) => `clSherlocCampaignSelection:${advertiserId}`;

// ✅ Persist “latest unsupported” per advertiser (prevents refresh spam)
const getLatestUnsupportedKey = (advertiserId) => `clSherlocLatestUnsupported:${advertiserId}`;
const CL_AI_PROVIDER_KEY = "clSherlocAiProvider";
const SHERLOC_TRACE_FLAG_KEY = "sherlocDebugTrace";
const SHERLOC_REPORT_CACHE_VERSION = "v5";
const SHERLOC_REPORTS_BUILD_MARKER = "sherloc-render-fix-live-trace-20260422T000000Z";
const SHERLOC_TRACE_ALWAYS_LOG_EVENTS = new Set([
  "sherloc_reports_build_marker",
  "direct_run_response_received",
  "direct_run_response_committed",
  "visible_narrative_source_selected",
  "visible_narrative_final_state",
  "visible_narrative_overwrite_attempt",
  "visible_narrative_overwrite_rejected",
  "visible_narrative_cleared",
  "visible_narrative_cleared_reason",
  "waiting_placeholder_rendered",
  "waiting_placeholder_suppressed_for_successful_run",
  "visible_narrative_renderability_evaluated",
  "platform_analysis_recommendation_coverage",
  "live_platform_status_preserved",
]);

const storageKeyPart = (value) => encodeURIComponent(String(value ?? "").trim() || "none");

const getScopedReportStorageKey = ({ advertiserId, context = {}, aiProvider = "auto" } = {}) => {
  const normalizedContext = buildRunContext(context || {});
  return [
    `clSherlocReport:${SHERLOC_REPORT_CACHE_VERSION}`,
    storageKeyPart(advertiserId || normalizedContext.advertiserId),
    storageKeyPart(normalizedContext.campaignId || "auto"),
    storageKeyPart(normalizedContext.date_start || ""),
    storageKeyPart(normalizedContext.date_end || ""),
    storageKeyPart(normalizedContext.date_preset || ""),
    storageKeyPart(normalizeAiProviderForUi(aiProvider || "auto")),
  ].join(":");
};

const publishSherlocTrace = (surface, event, payload = {}) => {
  if (typeof window === "undefined") return;
  const detail = {
    surface,
    event,
    at: new Date().toISOString(),
    ...payload,
  };
  try {
    window.__SHERLOC_RUNTIME_TRACE__ = window.__SHERLOC_RUNTIME_TRACE__ || {};
    window.__SHERLOC_RUNTIME_TRACE__[surface] = detail;
    window.__SHERLOC_RUNTIME_TRACE_EVENTS__ = window.__SHERLOC_RUNTIME_TRACE_EVENTS__ || [];
    window.__SHERLOC_RUNTIME_TRACE_EVENTS__.push(detail);
    if (window.__SHERLOC_RUNTIME_TRACE_EVENTS__.length > 500) {
      window.__SHERLOC_RUNTIME_TRACE_EVENTS__.shift();
    }
  } catch {}
  try {
    if (
      localStorage.getItem(SHERLOC_TRACE_FLAG_KEY) === "1" ||
      SHERLOC_TRACE_ALWAYS_LOG_EVENTS.has(event)
    ) {
      sessionStorage.setItem(`sherlocTrace:${surface}`, JSON.stringify(detail));
      console.debug("[SherlocTrace]", detail);
    }
  } catch {}
};

const LIVE_PLATFORM_ROUTE_MAP = Object.freeze({
  amazon: "/amazon-analytics",
  yahoo_dsp: "/sherloc-reports#provider-yahoo_dsp",
  yahoo: "/sherloc-reports#provider-yahoo_dsp",
  viant: "/sherloc-reports#provider-viant",
  audio: "/audio-analytics",
  facebook: "/facebook-analytics",
  meta: "/meta-analytics",
  audience_network: "/social-analytics",
  messenger: "/social-analytics",
  social: "/social-analytics",
  instagram: "/instagram-analytics",
  tiktok: "/tiktok-analytics",
  x: "/x-analytics",
  twitter: "/x-analytics",
  youtube: "/youtube-analytics",
  google: "/google-analytics",
  /*
   * GA4 is measurement, /google-analytics is PAID Google Search. Pointing the
   * measurement card at the paid-search page sent the operator to a different
   * reporting product that looked plausible.
   */
  google_analytics: "/ga4-analytics",
  ga4: "/ga4-analytics",
  google_search: "/google-analytics",
  googlesearch: "/google-analytics",
  paid_search: "/google-analytics",
  search: "/google-analytics",
  truth_social: "/truthsocial-analytics",
  truthsocial: "/truthsocial-analytics",
  rumble: "/rumble-analytics",
  reddit: "/reddit-analytics",
  programmatic: "/programmatic-analytics",
  stackadapt: "/programmatic-analytics",
  community: "/community-analytics",
  metricool: "/community-analytics",
  ctv: "/ctv-analytics",
  connected_tv: "/ctv-analytics",
  display: "/display-analytics",
  dooh: "/dooh-analytics",
  digital_out_of_home: "/dooh-analytics",
  email: "/email-analytics",
  video: "/video-analytics",
  olv: "/video-analytics",
  online_video: "/video-analytics",
  mobile: "/mobile-analytics",
  in_game: "/ingame-analytics",
  ingame: "/ingame-analytics",
  cinema: "/cinema-analytics",
  theatrical: "/cinema-analytics",
});

const norm = (s) => String(s ?? "").trim().toLowerCase();

const normalizeLivePlatformRouteKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const resolveLivePlatformAnalyticsRoute = (...values) => {
  for (const value of values) {
    const key = normalizeProviderSurfaceKey(value) || normalizeLivePlatformRouteKey(value);
    if (LIVE_PLATFORM_ROUTE_MAP[key]) return LIVE_PLATFORM_ROUTE_MAP[key];
  }
  return "";
};

const toNumber = (v) => {
  if (v == null) return NaN;
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (!s) return NaN;
  const cleaned = s.replace(/[\s]/g, "").replace(/\$/g, "").replace(/,/g, "").replace(/%/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

const formatMoney = (v) => formatSherlocCurrency(v);

const formatNumber = (v) => {
  if (v == null || v === "") return "0";
  const num = typeof v === "number" ? v : toNumber(v);
  if (Number.isNaN(num)) return "0";
  return Number(num).toLocaleString("en-US");
};

const formatPercent = (v) => {
  const num = typeof v === "number" ? v : toNumber(v);
  if (v == null || Number.isNaN(num)) return "No Data";
  return `${Number(num).toFixed(2)}%`;
};

const hasDisplayMetricValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  return Number.isFinite(toNumber(value));
};

const formatOptionalPreciseMoney = (value) =>
  hasDisplayMetricValue(value) ? formatMoney(value) : "No Data";

const formatOptionalNumber = (value) =>
  hasDisplayMetricValue(value) ? formatNumber(value) : "No Data";

const formatDuration = (secondsLike) => {
  const total = toNumber(secondsLike);
  if (!Number.isFinite(total) || total < 0) return "No Data";
  const totalSeconds = Math.round(total);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${String(secs).padStart(2, "0")}s`;
};

const computeRatePercent = (numerator, denominator) => {
  const num = toNumber(numerator ?? 0);
  const den = toNumber(denominator ?? 0);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den <= 0) return null;
  const pct = (num / den) * 100;
  return Number.isFinite(pct) ? pct : null;
};

const pickFiniteMetric = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const getCommercialMetricContainer = (source = {}) => {
  if (source?.aggregateCommercialMetrics && typeof source.aggregateCommercialMetrics === "object") {
    return source.aggregateCommercialMetrics;
  }
  if (source?.commercialMetrics && typeof source.commercialMetrics === "object") {
    return source.commercialMetrics;
  }
  if (source?.clientBillingAttribution && typeof source.clientBillingAttribution === "object") {
    return source.clientBillingAttribution;
  }
  return {};
};

const resolveClientFacingSpend = (source = {}, fallbackSpend = 0) => {
  const pricing = source?.pricing && typeof source.pricing === "object" ? source.pricing : {};
  const pricingMetrics =
    source?.pricingMetrics && typeof source.pricingMetrics === "object" ? source.pricingMetrics : {};
  const commercial = getCommercialMetricContainer(source);
  const explicit = pickFiniteMetric(
    source.clientFacingSpend,
    source.client_facing_spend,
    source.billedSpend,
    source.billed_spend,
    source.clientBilledSpend,
    source.client_billed_spend,
    source.effectiveSpend,
    source.effective_spend,
    source.billedCost,
    source.billed_cost,
    pricing.billedSpend,
    pricing.billed_spend,
    pricing.billedCost,
    pricing.billed_cost,
    pricingMetrics.billedSpend,
    pricingMetrics.billed_spend,
    pricingMetrics.billedCost,
    pricingMetrics.billed_cost,
    commercial.clientFacingSpend,
    commercial.client_facing_spend,
    commercial.billedSpend,
    commercial.billed_spend,
    commercial.billedCost,
    commercial.billed_cost,
    commercial.effectiveSpend,
    commercial.effective_spend
  );
  return explicit != null ? explicit : fallbackSpend;
};

const resolveClientFacingCpm = (source = {}, impressions = 0, clientFacingSpend = 0, fallbackCpm = null) => {
  const pricing = source?.pricing && typeof source.pricing === "object" ? source.pricing : {};
  const pricingMetrics =
    source?.pricingMetrics && typeof source.pricingMetrics === "object" ? source.pricingMetrics : {};
  const commercial = getCommercialMetricContainer(source);
  const explicit = pickFiniteMetric(
    source.clientFacingCpm,
    source.client_facing_cpm,
    source.billedCpm,
    source.billed_cpm,
    source.clientBilledCpm,
    source.client_billed_cpm,
    source.effectiveCpm,
    source.effective_cpm,
    pricing.billedCpm,
    pricing.billed_cpm,
    pricingMetrics.billedCpm,
    pricingMetrics.billed_cpm,
    commercial.clientFacingCpm,
    commercial.client_facing_cpm,
    commercial.billedCpm,
    commercial.billed_cpm,
    commercial.effectiveCpm,
    commercial.effective_cpm
  );
  if (explicit != null) return explicit;
  if (
    Number.isFinite(impressions) &&
    impressions > 0 &&
    Number.isFinite(clientFacingSpend) &&
    clientFacingSpend > 0
  ) {
    return (clientFacingSpend / impressions) * 1000;
  }
  return fallbackCpm;
};

const resolveClientFacingCpc = (source = {}, clicks = 0, clientFacingSpend = 0, fallbackCpc = null) => {
  const pricing = source?.pricing && typeof source.pricing === "object" ? source.pricing : {};
  const pricingMetrics =
    source?.pricingMetrics && typeof source.pricingMetrics === "object" ? source.pricingMetrics : {};
  const commercial = getCommercialMetricContainer(source);
  const explicit = pickFiniteMetric(
    source.clientFacingCpc,
    source.client_facing_cpc,
    source.billedCpc,
    source.billed_cpc,
    source.clientBilledCpc,
    source.client_billed_cpc,
    source.effectiveCpc,
    source.effective_cpc,
    pricing.billedCpc,
    pricing.billed_cpc,
    pricingMetrics.billedCpc,
    pricingMetrics.billed_cpc,
    commercial.clientFacingCpc,
    commercial.client_facing_cpc,
    commercial.billedCpc,
    commercial.billed_cpc,
    commercial.effectiveCpc,
    commercial.effective_cpc
  );
  if (explicit != null) return explicit;
  if (
    Number.isFinite(clicks) &&
    clicks > 0 &&
    Number.isFinite(clientFacingSpend) &&
    clientFacingSpend > 0
  ) {
    return clientFacingSpend / clicks;
  }
  return fallbackCpc;
};

const normalizeAiProviderForUi = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return "auto";
  if (
    s === "openai" ||
    s === "gpt" ||
    s === "chatgpt" ||
    s.includes("openai") ||
    s.includes("chatgpt") ||
    s.includes("gpt-") ||
    s.startsWith("gpt")
  ) {
    return "chatgpt";
  }
  if (
    s === "eden" ||
    s === "cphere_eden" ||
    s === "cphere-eden" ||
    s === "anthropic" ||
    s === "claude" ||
    s.includes("eden") ||
    s.includes("anthropic") ||
    s.includes("claude")
  ) {
    return "eden";
  }
  if (s === "chatgpt" || s === "eden" || s === "auto") return s;
  return "auto";
};

const prettyAiProvider = (v) => {
  const s = normalizeAiProviderForUi(v);
  if (s === "chatgpt") return "Sherloc";
  if (s === "eden") return "Eden";
  return "Auto";
};

const getBrandedAiModelLabel = (v) => {
  const s = normalizeAiProviderForUi(v);
  if (s === "chatgpt") return "1.1";
  if (s === "eden") return "2.1";
  return "";
};

const AI_PROVIDER_LOGOS = {
  chatgpt: {
    src: aiOptimizationsOldImage,
    alt: "Sherloc logo",
  },
  eden: {
    src: sherlocVoiceoImage,
    alt: "Eden logo",
  },
};

const getAiProviderPresentation = (provider) => {
  const raw = String(provider || "").trim();
  const normalized = normalizeAiProviderForUi(provider);
  const logo = AI_PROVIDER_LOGOS[normalized] || null;
  const hasKnownProvider =
    !raw ||
    ["auto", "chatgpt", "eden", "claude", "openai", "gpt", "anthropic"].includes(
      raw.toLowerCase()
    );
  return {
    normalized,
    label: hasKnownProvider ? prettyAiProvider(normalized) : raw,
    logoSrc: logo?.src || null,
    logoAlt: logo?.alt || "",
  };
};

const getAiModelDisplay = (model, providerHint = "") => {
  const normalized = normalizeAiProviderForUi(model || providerHint);
  if (normalized === "chatgpt" || normalized === "eden") {
    return getBrandedAiModelLabel(normalized);
  }
  return "";
};

const SherlocHeroFrame = ({
  src,
  alt,
  title = "",
  outerClassName = "",
  mediaClassName = "",
  imageClassName = "",
  ariaHidden = false,
}) => {
  if (!src) return null;

  const outerClasses = ["sherlocreports-hero-frame", outerClassName]
    .filter(Boolean)
    .join(" ");
  const mediaClasses = ["sherlocreports-hero-frame-media", mediaClassName]
    .filter(Boolean)
    .join(" ");
  const imageClasses = ["sherlocreports-hero-frame-image", imageClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={outerClasses} aria-hidden={ariaHidden ? "true" : undefined}>
      <span className={mediaClasses}>
        <img
          src={src}
          alt={ariaHidden ? "" : alt}
          aria-hidden={ariaHidden ? "true" : undefined}
          title={title || undefined}
          className={imageClasses}
        />
      </span>
    </span>
  );
};

const AiProviderInline = ({ provider, className = "" }) => {
  const { label, logoSrc, logoAlt } = getAiProviderPresentation(provider);
  const wrapperClassName = ["sherlocreports-ai-engine-inline", className]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={wrapperClassName}>
      {logoSrc ? (
        <SherlocHeroFrame
          src={logoSrc}
          alt={logoAlt}
          title={logoAlt}
          outerClassName="sherlocreports-ai-engine-logo-hero"
          mediaClassName="sherlocreports-hero-frame-media-cover sherlocreports-ai-engine-logo-media"
          imageClassName="sherlocreports-ai-engine-logo sherlocreports-hero-frame-image-cover sherlocreports-ai-engine-logo-image"
          ariaHidden={true}
        />
      ) : null}
      <span className="sherlocreports-ai-engine-label">{label}</span>
    </span>
  );
};

// -----------------------------
// ✅ payload hardening
// -----------------------------
const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);
const isAggregateLike = (v) => {
  if (!isPlainObject(v)) return false;
  const hasTotals =
    isPlainObject(v.totals) || isPlainObject(v.total) || isPlainObject(v.summaryTotals);
  const hasBy =
    isPlainObject(v.byChannel) || isPlainObject(v.by_channel) || isPlainObject(v.channels);
  return !!(hasTotals && hasBy);
};

const pickFirst = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    if (typeof v === "string" && !v.trim()) continue;
    return v;
  }
  return null;
};

const getSherlocReportSourcePriority = (source) => {
  const s = String(source || "").trim().toLowerCase();
  if (s === "run_analysis_locked_snapshot" || s === "run_analysis_snapshot_lock") {
    return 4;
  }
  if (
    s === "canonical_performance_summary" ||
    s === "performance_summary" ||
    s.includes("canonical_performance")
  ) {
    return 3;
  }
  if (
    s === "run_sherloc" ||
    s === "run_sherloc_preflight" ||
    s === "ai_run" ||
    s === "latest_aggregate_refresh" ||
    s.includes("ai_aggregate")
  ) {
    return 2;
  }
  return 1;
};

const parseSherlocReportTimestamp = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const getSherlocReportDataTimestamp = (...items) => {
  for (const item of items) {
    if (!isPlainObject(item)) continue;
    const candidate = pickFirst(
      item.dataFreshness?.timestamp,
      item.dataFreshness?.sourceReportedAt,
      item.dataFreshness?.observedAt,
      item.dataFreshness?.ingestedAt,
      item.dataFreshness?.sourceUpdatedAt,
      item.dataFreshness?.sourceTimestamp,
      item.sourceReportedAt,
      item.observedAt,
      item.ingestedAt,
      item.sourceUpdatedAt,
      item.sourceTimestamp,
      item.source?.timestamp,
      item.sourceContract?.sourceUpdatedAt,
      item.ts,
      item.timestamp
    );
    const parsed = parseSherlocReportTimestamp(candidate);
    if (parsed != null) return parsed;
  }
  return null;
};

const getSherlocReportContextKey = (context) =>
  JSON.stringify(buildRunContext(context || {}));

const findAggregateDeep = (root) => {
  if (!isPlainObject(root)) return null;

  const direct = pickFirst(
    root.aggregate,
    root.aggregates,
    root.aggregateSnapshot,
    root.snapshot,
    root.result?.aggregate,
    root.result?.snapshot,
    root.data?.aggregate,
    root.data?.snapshot,
    root.run?.aggregate,
    root.run?.snapshot
  );
  if (isAggregateLike(direct)) return direct;

  for (const k of Object.keys(root)) {
    const v = root[k];
    if (isAggregateLike(v)) return v;
    if (isPlainObject(v)) {
      for (const k2 of Object.keys(v)) {
        const v2 = v[k2];
        if (isAggregateLike(v2)) return v2;
      }
    }
  }

  return null;
};

const normalizeSummary = (payload) => {
  const aiSummaryObj = isPlainObject(payload?.aiSummary) ? payload.aiSummary : null;

  if (
    aiSummaryObj &&
    (aiSummaryObj.title ||
      (Array.isArray(aiSummaryObj.bullets) && aiSummaryObj.bullets.length))
  ) {
    return {
      title: String(aiSummaryObj.title || "").trim(),
      bullets: Array.isArray(aiSummaryObj.bullets)
        ? aiSummaryObj.bullets.map((b) => String(b))
        : [],
    };
  }

  const title = String(
    pickFirst(
      payload?.summaryTitle,
      payload?.run?.summaryTitle,
      payload?.ai_summary_title,
      payload?.aiSummaryTitle,
      payload?.title,
      payload?.summary_title,
      payload?.run?.summary_title
    ) || ""
  ).trim();

  const bullets =
    payload?.aiSummary?.bullets ||
    payload?.summaryBullets ||
    payload?.ai_summary_bullets ||
    payload?.run?.summaryBullets ||
    payload?.run?.aiSummary?.bullets ||
    payload?.bullets ||
    payload?.summary_bullets ||
    payload?.run?.summary_bullets ||
    [];

  const bulletsArr = Array.isArray(bullets) ? bullets.map((b) => String(b)) : [];

  const summaryText = String(
    pickFirst(
      payload?.summary,
      payload?.ai_summary,
      payload?.run?.summary,
      payload?.run?.output_text,
      payload?.run?.output,
      payload?.run?.summary_text,
      payload?.summary_text
    ) || ""
  ).trim();

  if (summaryText && !title && bulletsArr.length === 0) {
    return { title: summaryText, bullets: [] };
  }

  if (title || bulletsArr.length) return { title, bullets: bulletsArr };

  return { title: "", bullets: [] };
};

const escapeRegExp = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeRecommendationEntry = (value) => {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.flatMap(normalizeRecommendationEntry);
  }

  if (typeof value === "object") {
    const label = String(
      pickFirst(
        value.platform,
        value.platform_label,
        value.platformLabel,
        value.channel,
        value.channel_key,
        value.channelKey,
        value.title,
        value.heading,
        value.label
      ) || ""
    ).trim();
    const body = String(
      pickFirst(
        value.body,
        value.text,
        value.recommendation,
        value.analysis,
        value.summary,
        value.insight,
        value.narrative,
        value.content
      ) || ""
    ).trim();
    if (label && body && !new RegExp(`^${escapeRegExp(label)}\\s*:`, "i").test(body)) {
      return [`${label}: ${body}`];
    }
    if (body) return [body];
    if (label) return [label];
    return [];
  }

  const text = String(value || "").trim();
  if (!text) return [];
  return text
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
};

const normalizeRecommendations = (payload) => {
  const r =
    payload?.aiRecommendations ??
    payload?.result?.aiRecommendations ??
    payload?.data?.aiRecommendations ??
    payload?.recommendations ??
    payload?.result?.recommendations ??
    payload?.data?.recommendations ??
    payload?.ai_recommendations ??
    payload?.result?.ai_recommendations ??
    payload?.data?.ai_recommendations ??
    payload?.run?.aiRecommendations ??
    payload?.run?.recommendations ??
    payload?.run?.ai_recommendations ??
    payload?.outputLines ??
    payload?.run?.outputLines ??
    payload?.run?.output_blocks ??
    payload?.run?.outputBlocks ??
    payload?.platform_analysis ??
    payload?.run?.platform_analysis ??
    payload?.platformAnalysis ??
    payload?.run?.platformAnalysis ??
    payload?.analysis ??
    payload?.run?.analysis ??
    null;

  if (isPlainObject(r)) {
    return normalizeRecommendationEntry(
      r.recommendations ??
        r.items ??
        r.lines ??
        r.blocks ??
        r.platforms ??
        r.platform_analysis ??
        r.platformAnalysis ??
        r.text ??
        r.body
    );
  }

  return normalizeRecommendationEntry(r);
};

const normalizeAiMeta = (payload) => {
  const requested = pickFirst(
    payload?.aiProviderRequested,
    payload?.ai_provider_requested,
    payload?.run?.aiProviderRequested,
    payload?.run?.ai_provider_requested,
    payload?.provider_requested,
    payload?.providerRequested
  );

  const used = pickFirst(
    payload?.aiProviderUsed,
    payload?.ai_provider_used,
    payload?.provider_used,
    payload?.providerUsed,
    payload?.run?.aiProviderUsed,
    payload?.run?.ai_provider_used,
    payload?.run?.provider_used,
    payload?.run?.providerUsed
  );

  const model = pickFirst(
    payload?.aiModelUsed,
    payload?.ai_model_used,
    payload?.model_used,
    payload?.modelUsed,
    payload?.run?.aiModelUsed,
    payload?.run?.ai_model_used,
    payload?.run?.model_used,
    payload?.run?.modelUsed
  );

  const fallbackRaw = pickFirst(
    payload?.aiFallbackUsed,
    payload?.ai_fallback_used,
    payload?.fallback_used,
    payload?.fallbackUsed,
    payload?.run?.aiFallbackUsed,
    payload?.run?.ai_fallback_used,
    payload?.run?.fallback_used,
    payload?.run?.fallbackUsed
  );
  const fallbackStatus = String(
    pickFirst(
      payload?.diagnostics?.finalRunClassification,
      payload?.diagnostics?.runStatus,
      payload?.diagnostics?.finalRunStatus,
      payload?.run?.finalRunClassification,
      payload?.run?.status,
      payload?.status
    ) || ""
  )
    .trim()
    .toLowerCase();

  const fallbackUsed =
    typeof fallbackRaw === "boolean"
      ? fallbackRaw
      : String(fallbackRaw || "").trim().toLowerCase() === "true" ||
        fallbackStatus === "completed_with_fallback" ||
        fallbackStatus === "success_grounded_fallback";

  return {
    requested: requested ? normalizeAiProviderForUi(requested) : null,
    used: used ? normalizeAiProviderForUi(used) : null,
    model: model ? String(model) : "",
    fallbackUsed,
  };
};

const getMetricsFromReportPayload = ({ aggregate = null, resolved = null, metrics = null } = {}) => {
  if (isPlainObject(metrics)) return metrics;
  const usesResolvedTotals = Boolean(
    resolved?.totals && Object.keys(resolved.totals || {}).length
  );
  const totals = (usesResolvedTotals ? resolved.totals : aggregate?.totals) || null;
  // Channel evidence rides along so blended rates fold only metric-eligible
  // channels, never the whole impression pool.
  const totalsByChannel =
    (usesResolvedTotals ? resolved?.byChannel : aggregate?.byChannel) || null;
  return computeReportingMetrics(totals, totalsByChannel);
};

const roundForNarrativeSignature = (value, precision = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const factor = 10 ** precision;
  return Math.round(parsed * factor) / factor;
};

const HISTORICAL_NARRATIVE_UNTRUSTED_MESSAGE =
  "This historical summary predates the latest reporting integrity safeguards and cannot be displayed as trusted prose. Please rerun analysis to generate an updated summary.";
const AI_NARRATIVE_FAILED_WITH_METRICS_MESSAGE =
  "Campaign metrics loaded, but Sherloc narrative generation did not complete. KPI cards and platform performance remain available from the latest resolved reporting truth.";

const buildNarrativeMetricSignature = (payload = {}) => {
  const metrics = getMetricsFromReportPayload(payload);
  if (!metrics) return null;

  const values = {
    spend: roundForNarrativeSignature(metrics.spend, 2),
    impressions: roundForNarrativeSignature(metrics.impressions, 0),
    clicks: roundForNarrativeSignature(metrics.clicks, 0),
    ctr: roundForNarrativeSignature(metrics.ctr, 2),
    cpc: roundForNarrativeSignature(metrics.cpc, 2),
    cpm: roundForNarrativeSignature(metrics.cpm, 2),
    engagements: roundForNarrativeSignature(metrics.engagements, 0),
    er: roundForNarrativeSignature(metrics.er, 2),
    videoViews: roundForNarrativeSignature(metrics.videoViews, 0),
    vvr: roundForNarrativeSignature(metrics.vvr, 2),
    conversions: roundForNarrativeSignature(metrics.conversions, 0),
    conversionValue: roundForNarrativeSignature(metrics.conversionValue, 2),
  };

  return {
    key: JSON.stringify(values),
    values,
  };
};

const hasMaterialNarrativeMetricDiff = (currentSignature, narrativeSignature) => {
  if (!currentSignature) return false;
  if (!narrativeSignature) return true;

  const current = currentSignature.values || {};
  const previous = narrativeSignature.values || {};
  const differs = (field, absoluteThreshold, relativeThreshold = 0.01) => {
    const a = Number(current[field] || 0);
    const b = Number(previous[field] || 0);
    const delta = Math.abs(a - b);
    if (delta > absoluteThreshold) return true;
    const baseline = Math.max(Math.abs(a), Math.abs(b), 1);
    return delta / baseline > relativeThreshold;
  };

  return (
    differs("spend", 1, 0.01) ||
    differs("impressions", 25, 0.01) ||
    differs("clicks", 1, 0.01) ||
    differs("ctr", 0.05, 0.02) ||
    differs("cpc", 0.05, 0.02) ||
    differs("cpm", 0.05, 0.02) ||
    differs("engagements", 2, 0.01) ||
    differs("er", 0.05, 0.02) ||
    differs("videoViews", 5, 0.01) ||
    differs("vvr", 0.05, 0.02) ||
    differs("conversions", 1, 0.01) ||
    differs("conversionValue", 1, 0.01)
  );
};

const buildNarrativeMetricDelta = (currentSignature, narrativeSignature) => {
  const current = currentSignature?.values || {};
  const previous = narrativeSignature?.values || {};
  const fields = [
    "spend",
    "impressions",
    "clicks",
    "ctr",
    "cpc",
    "cpm",
    "engagements",
    "er",
    "videoViews",
    "vvr",
    "conversions",
    "conversionValue",
  ];

  return fields.reduce((acc, field) => {
    const expected = Number(current[field] || 0);
    const actual = Number(previous[field] || 0);
    const delta = actual - expected;
    if (!expected && !actual) return acc;
    acc[field] = { expected, actual, delta };
    return acc;
  }, {});
};

const normalizeSherlocResolvedSnapshot = (resolved = null) => ({
  byChannel: resolved?.byChannel || resolved?.by_channel || {},
  totals: resolved?.totals || {},
});

const getSherlocSnapshotTotals = ({ aggregate = null, resolved = null } = {}) => {
  const normalizedResolved = normalizeSherlocResolvedSnapshot(resolved);
  if (Object.keys(normalizedResolved.totals || {}).length) return normalizedResolved.totals;
  if (isPlainObject(aggregate?.totals)) return aggregate.totals;
  return {};
};

const isCanonicalSherlocAggregate = (aggregate = null) =>
  Boolean(
    aggregate?.contract === "canonical_reporting_v1" ||
      aggregate?.canonical_reporting_contract === "canonical_reporting_v1" ||
      aggregate?.sourceContract?.contract === "canonical_reporting_v1"
  );

const getSherlocSnapshotReportingByChannel = (snapshot = null) => {
  if (
    isPlainObject(snapshot?.resolved?.byChannel) &&
    Object.keys(snapshot.resolved.byChannel).length > 0
  ) {
    return snapshot.resolved.byChannel;
  }

  return isPlainObject(snapshot?.aggregate?.byChannel) ? snapshot.aggregate.byChannel : {};
};

const buildSherlocRunSnapshotDescriptor = ({
  source = "unknown",
  timestamp = null,
  payload = null,
  aggregate = null,
  resolved = null,
  sources = null,
} = {}) => {
  const normalizedResolved = normalizeSherlocResolvedSnapshot(resolved);
  const totals = getSherlocSnapshotTotals({ aggregate, resolved: normalizedResolved });
  const metrics = computeReportingMetrics(totals);
  const signature = buildNarrativeMetricSignature({ metrics });
  const parsedTimestamp =
    parseSherlocReportTimestamp(timestamp) ||
    getSherlocReportDataTimestamp(
      payload,
      aggregate,
      normalizedResolved
    );

  return {
    source: source || "unknown",
    timestamp: parsedTimestamp || null,
    aggregate: aggregate || null,
    resolved: normalizedResolved,
    sources: sources || null,
    metrics,
    metricSignature: signature,
    hasMetrics: hasSharedUsableMetrics(metrics) || hasSharedUsableMetrics(totals),
  };
};

const hasSherlocSnapshotMetricDowngrade = (currentSnapshot, candidateSnapshot) => {
  if (!currentSnapshot?.hasMetrics || !candidateSnapshot?.hasMetrics) return false;
  const current = currentSnapshot.metricSignature?.values || {};
  const candidate = candidateSnapshot.metricSignature?.values || {};
  const lowerThanCurrent = (field, absoluteThreshold = 0) => {
    const currentValue = Number(current[field] || 0);
    const candidateValue = Number(candidate[field] || 0);
    return currentValue > 0 && candidateValue + absoluteThreshold < currentValue;
  };

  return (
    lowerThanCurrent("spend", 1) ||
    lowerThanCurrent("impressions", 25) ||
    lowerThanCurrent("clicks", 1) ||
    lowerThanCurrent("engagements", 2) ||
    lowerThanCurrent("videoViews", 5) ||
    lowerThanCurrent("conversions", 1)
  );
};

const getStoredNarrativeIntegrity = (payload = {}) => {
  if (!payload || typeof payload !== "object") return null;
  return (
    payload.narrativeIntegrity ||
    payload.narrative_integrity ||
    payload.run?.narrativeIntegrity ||
    payload.run?.narrative_integrity ||
    payload.latest?.narrativeIntegrity ||
    payload.latest?.narrative_integrity ||
    null
  );
};

const hasPersistedNarrativeText = (summary, recommendations) => {
  return (
    Boolean(String(summary?.title || "").trim()) ||
    (Array.isArray(summary?.bullets) && summary.bullets.some((line) => String(line || "").trim())) ||
    (Array.isArray(recommendations) &&
      recommendations.some((line) => String(line || "").trim()))
  );
};

const buildUntrustedHistoricalNarrative = (message = HISTORICAL_NARRATIVE_UNTRUSTED_MESSAGE) => ({
  summary: {
    title: "Historical Summary Requires Refresh",
    bullets: [message],
  },
  recommendations: [
    normalizeSherlocTextForRender(
      `CROSS PLATFORM ANALYSIS:\n- ${message}\n- KPI cards and historical metrics remain available when canonical aggregate data is present.\n- Rerun analysis to create an updated trusted narrative.`
    ),
  ],
});

const validatePersistedNarrativePlayback = ({
  payload = {},
  aggregate = null,
  resolved = null,
  summary = null,
  recommendations = [],
  source = "saved_report_playback",
} = {}) => {
  if (!hasPersistedNarrativeText(summary, recommendations)) {
    return { trusted: true, reason: "no_narrative", signature: null, source };
  }

  const integrity = getStoredNarrativeIntegrity(payload) || {};
  const canonicalSignature = buildNarrativeMetricSignature({ aggregate, resolved });
  const storedSignature =
    integrity.metricSignature ||
    integrity.metric_signature ||
    payload.metricSignature ||
    payload.metric_signature ||
    null;

  if (integrity.playbackTrusted === false) {
    return {
      trusted: false,
      reason: integrity.reason || "backend_marked_untrusted",
      message: HISTORICAL_NARRATIVE_UNTRUSTED_MESSAGE,
      signature: canonicalSignature,
      source,
    };
  }

  if (!canonicalSignature) {
    return {
      trusted: false,
      reason: "missing_canonical_signature",
      message: HISTORICAL_NARRATIVE_UNTRUSTED_MESSAGE,
      signature: null,
      source,
    };
  }

  if (!storedSignature) {
    return {
      trusted: false,
      reason: "legacy_missing_metric_signature",
      message: HISTORICAL_NARRATIVE_UNTRUSTED_MESSAGE,
      signature: canonicalSignature,
      source,
    };
  }

  if (hasMaterialNarrativeMetricDiff(canonicalSignature, storedSignature)) {
    return {
      trusted: false,
      reason: "metric_signature_mismatch",
      message: HISTORICAL_NARRATIVE_UNTRUSTED_MESSAGE,
      signature: canonicalSignature,
      source,
    };
  }

  return { trusted: true, reason: "validated", signature: canonicalSignature, source };
};

// -----------------------------
// ✅ AI context helpers
// -----------------------------
const buildRunContext = ({ advertiserId, campaignId, date_start, date_end, date_preset }) => ({
  advertiserId: advertiserId ? String(advertiserId) : "",
  campaignId: campaignId && String(campaignId) !== "auto" ? String(campaignId) : "auto",
  date_start: date_start ? String(date_start) : "",
  date_end: date_end ? String(date_end) : "",
  date_preset: date_preset ? String(date_preset) : "",
});

const extractRunContextFromPayload = (payload = {}, fallback = {}) => {
  const run =
    payload?.run ||
    payload?.result?.run ||
    payload?.data?.run ||
    payload?.aiRun ||
    payload?.runRecord ||
    {};
  const agg =
    payload?.aggregate ||
    payload?.result?.aggregate ||
    payload?.data?.aggregate ||
    findAggregateDeep(payload) ||
    {};
  const aggregateTimeframe =
    agg?.timeframe ||
    agg?.sourceContract?.timeframe ||
    agg?.source_contract?.timeframe ||
    payload?.canonicalSummary?.timeframe ||
    payload?.canonicalReporting?.timeframe ||
    payload?.canonicalAggregate?.timeframe ||
    {};
  const timeframe =
    payload?.timeframe ||
    payload?.report_context ||
    payload?.reportContext ||
    run?.timeframe ||
    aggregateTimeframe ||
    {};

  const advertiserId = pickFirst(
    payload?.advertiserId,
    payload?.advertiser_id,
    payload?.report_context?.advertiserId,
    payload?.report_context?.advertiser_id,
    payload?.reportContext?.advertiserId,
    payload?.reportContext?.advertiser_id,
    run?.advertiserId,
    run?.advertiser_id,
    agg?.advertiserId,
    agg?.advertiser_id,
    fallback?.advertiserId
  );

  const campaignIdRaw = pickFirst(
    payload?.campaignId,
    payload?.campaign_id,
    payload?.report_context?.campaignId,
    payload?.report_context?.campaign_id,
    payload?.reportContext?.campaignId,
    payload?.reportContext?.campaign_id,
    run?.campaignId,
    run?.campaign_id,
    agg?.campaignId,
    agg?.campaign_id,
    fallback?.campaignId
  );
  const date_preset = pickFirst(
    payload?.date_preset,
    payload?.datePreset,
    payload?.report_context?.date_preset,
    payload?.report_context?.datePreset,
    payload?.reportContext?.date_preset,
    payload?.reportContext?.datePreset,
    timeframe?.date_preset,
    timeframe?.datePreset,
    timeframe?.preset,
    run?.date_preset,
    run?.datePreset,
    run?.timeframe?.date_preset,
    run?.timeframe?.datePreset,
    run?.timeframe?.preset,
    agg?.date_preset,
    agg?.datePreset,
    fallback?.date_preset
  );

  const date_start = pickFirst(
    payload?.date_start,
    payload?.dateStart,
    payload?.report_context?.date_start,
    payload?.report_context?.dateStart,
    payload?.reportContext?.date_start,
    payload?.reportContext?.dateStart,
    payload?.timeframe_start,
    payload?.timeframeStart,
    timeframe?.date_start,
    timeframe?.dateStart,
    timeframe?.start,
    timeframe?.from,
    timeframe?.since,
    run?.date_start,
    run?.dateStart,
    run?.timeframe_start,
    run?.timeframeStart,
    run?.timeframe?.date_start,
    run?.timeframe?.dateStart,
    run?.timeframe?.from,
    run?.timeframe?.since,
    agg?.date_start,
    agg?.dateStart,
    agg?.sourceContract?.timeframe?.start,
    agg?.source_contract?.timeframe?.start,
    fallback?.date_start
  );

  const date_end = pickFirst(
    payload?.date_end,
    payload?.dateEnd,
    payload?.report_context?.date_end,
    payload?.report_context?.dateEnd,
    payload?.reportContext?.date_end,
    payload?.reportContext?.dateEnd,
    payload?.timeframe_end,
    payload?.timeframeEnd,
    timeframe?.date_end,
    timeframe?.dateEnd,
    timeframe?.end,
    timeframe?.to,
    timeframe?.until,
    run?.date_end,
    run?.dateEnd,
    run?.timeframe_end,
    run?.timeframeEnd,
    run?.timeframe?.date_end,
    run?.timeframe?.dateEnd,
    run?.timeframe?.to,
    run?.timeframe?.until,
    agg?.date_end,
    agg?.dateEnd,
    agg?.sourceContract?.timeframe?.end,
    agg?.source_contract?.timeframe?.end,
    fallback?.date_end
  );

  return buildRunContext({
    advertiserId,
    campaignId: campaignIdRaw,
    date_start,
    date_end,
    date_preset,
  });
};

const contextsMatch = (a, b) => {
  if (!a || !b) return false;
  return (
    String(a.advertiserId || "") === String(b.advertiserId || "") &&
    String(a.campaignId || "auto") === String(b.campaignId || "auto") &&
    String(a.date_start || "") === String(b.date_start || "") &&
    String(a.date_end || "") === String(b.date_end || "") &&
    String(a.date_preset || "") === String(b.date_preset || "")
  );
};

const hasRenderableSummary = (summary) =>
  !!String(summary?.title || "").trim() ||
  (Array.isArray(summary?.bullets) &&
    summary.bullets.some((line) => String(line || "").trim()));

const hasRenderableNarrative = (summary, recommendations) => {
  const hasSummary = hasRenderableSummary(summary);
  const hasRecs = Array.isArray(recommendations) && recommendations.length > 0;
  return hasSummary || hasRecs;
};

const buildCampaignSummaryTextForRender = (summary) => {
  const title = String(summary?.title || "").trim();
  const bullets = Array.isArray(summary?.bullets)
    ? summary.bullets.map((line) => String(line || "").trim()).filter(Boolean)
    : [];

  if (!title && bullets.length === 0) return "";

  const resolvedTitle = title || "Campaign Performance Snapshot";
  if (!bullets.length) return resolvedTitle;

  return `${resolvedTitle}\n\n_____________________________\n\n${bullets
    .map((line) => `- ${line}`)
    .join("\n")}`;
};

const buildPlatformLabelListForSummary = (byChannel = {}) => {
  const labels = Object.entries(byChannel || {})
    .filter(([, metrics]) => hasUsableMetrics(metrics || {}))
    .map(([key]) => CHANNEL_LABELS[canonicalizeChannelKey(key)] || key)
    .filter(Boolean);
  const unique = Array.from(new Set(labels));
  if (!unique.length) return "active platforms";
  if (unique.length === 1) return unique[0];
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`;
  return `${unique.slice(0, -1).join(", ")}, and ${unique[unique.length - 1]}`;
};

const buildCanonicalCampaignSummaryForRender = ({
  summary,
  metrics,
  byChannel,
  timeframeLabel,
}) => {
  const title = String(summary?.title || "").trim() || "Campaign Performance Snapshot";
  if (!metrics || !hasSharedUsableMetrics(metrics)) {
    return buildCampaignSummaryTextForRender(summary);
  }

  const platformLabelList = buildPlatformLabelListForSummary(byChannel);
  const bullets = [
    `Scale: ${formatNumber(metrics.impressions)} impressions, ${formatNumber(
      metrics.clicks
    )} clicks, and ${formatMoney(metrics.spend)} in spend delivered across ${platformLabelList}.`,
    `Efficiency: overall CTR was ${formatPercent(metrics.ctr || 0)} with ${formatMoney(
      metrics.cpc || 0
    )} CPC and ${formatMoney(metrics.cpm || 0)} CPM.`,
    metrics.conversions > 0
      ? `Outcome: ${formatNumber(metrics.conversions)} conversions were recorded${
          metrics.conversionValue > 0
            ? ` with ${formatMoney(metrics.conversionValue)} in conversion value`
            : ""
        }.`
      : metrics.engagements > 0 || metrics.videoViews > 0
      ? `Outcome: ${formatNumber(metrics.engagements)} engagements and ${formatNumber(
          metrics.videoViews
        )} video views were recorded.`
      : "Outcome: Delivery data is present; platform-level performance remains available for review.",
    timeframeLabel
      ? `Timeframe: ${timeframeLabel}.`
      : "Timeframe: current resolved reporting window.",
    `Cross Platform Analysis: evaluate performance as a single integrated mix across ${platformLabelList}. Reallocate budget toward the strongest channel-appropriate efficiency pockets while using engagement quality, completion quality, and conversion contribution to validate downstream impact.`,
  ];

  return `${title}\n\n_____________________________\n\n${bullets
    .map((line) => `- ${line}`)
    .join("\n")}`;
};

const getRunErrorMessage = (run) => {
  if (!run) return "";
  const status = String(run.status || "").toLowerCase();
  const msg = String(run.error_message || run.errorMessage || run.error || "").trim();
  if (status === "error" && msg) return msg;
  if (msg && !String(run.summary_title || "").trim() && !String(run.recommendations || "").trim())
    return msg;
  return "";
};

const isFailedRunRecord = (run) => {
  if (!run || typeof run !== "object") return false;
  return String(run.status || "").toLowerCase() === "error";
};

// Hydration trigger: a Run Analysis pass that ATTACHES to an analysis the
// backend already holds instead of generating a new one. It shares the whole
// render pipeline with a fresh run — deliberately, so the report an operator
// sees after a reload is assembled by exactly the same code that assembled it
// the moment it was generated.
// The prefix resolveAutoCampaignOptionLabel applies to a detected campaign
// name. Sherloc Reports shows the campaign by its own name — the DETECTED pill
// and the option's aria-label carry the "this was automatic" meaning instead.

const PERSISTED_HYDRATION_TRIGGER = "persisted_analysis_hydration";

const getSherlocNarrativeSourcePriority = (source) => {
  const s = String(source || "").trim().toLowerCase();
  if (s === "direct_run_response" || s === "run_sherloc") return 100;
  if (s === "latest_run" || s === "latest_bootstrap") return 80;
  if (s === "saved_playback" || s === "server_saved_playback") return 50;
  if (s === "local_cache" || s === "local_cache_restore") return 40;
  return 10;
};

const getSherlocRunId = (run, payload = {}) =>
  pickFirst(
    run?.id,
    run?.runId,
    run?.run_id,
    run?.aiRunId,
    run?.ai_run_id,
    payload?.runId,
    payload?.run_id,
    payload?.id,
    payload?.latest?.id
  );

const getSherlocRunNumber = (runId) => {
  const parsed = Number(String(runId || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const buildVisibleNarrativeSelection = ({
  source,
  run = null,
  payload = null,
  aggregate = null,
  resolved = null,
  context = null,
  summary = null,
  recommendations = [],
  trusted = true,
  blockedReason = "",
} = {}) => {
  const runId = getSherlocRunId(run, payload || {});
  const normalizedContext = buildRunContext(context || {});
  return {
    source,
    priority: getSherlocNarrativeSourcePriority(source),
    runId: runId || null,
    runNumber: getSherlocRunNumber(runId),
    timestamp: getSherlocReportDataTimestamp(payload, run, aggregate, resolved),
    context: normalizedContext,
    contextKey: getSherlocReportContextKey(normalizedContext),
    trusted: Boolean(trusted),
    blockedReason: blockedReason || "",
    hasRenderable: hasRenderableNarrative(summary, recommendations),
  };
};

const shouldApplyVisibleNarrativeSelection = ({ current = null, candidate = null } = {}) => {
  if (!candidate?.hasRenderable) {
    return { apply: false, reason: "candidate_has_no_renderable_narrative" };
  }
  if (!candidate.trusted) {
    return { apply: false, reason: candidate.blockedReason || "candidate_playback_blocked" };
  }
  if (!current || current.contextKey !== candidate.contextKey || !current.hasRenderable) {
    return { apply: true, reason: "no_current_visible_narrative" };
  }
  if (current.priority > candidate.priority) {
    return {
      apply: false,
      reason: `current_${current.source}_has_higher_precedence`,
    };
  }
  if (
    current.runNumber != null &&
    candidate.runNumber != null &&
    candidate.runNumber < current.runNumber
  ) {
    return { apply: false, reason: "candidate_run_id_older_than_visible_run" };
  }
  if (
    current.priority === candidate.priority &&
    candidate.timestamp < current.timestamp &&
    (candidate.runNumber == null || current.runNumber == null)
  ) {
    return { apply: false, reason: "candidate_timestamp_older_than_visible_run" };
  }
  return { apply: true, reason: "candidate_selected" };
};

const hasPersistedReportMetrics = (payload = {}) => {
  if (!payload || typeof payload !== "object") return false;
  return (
    hasSharedUsableMetrics(payload?.resolved?.totals) ||
    hasSharedUsableMetrics(payload?.aggregate?.totals) ||
    hasSharedUsableMetrics(payload?.aggregate?.overall_for_ai) ||
    hasSharedUsableMetrics(payload?.aggregate?.overallForAi)
  );
};

const getClientFacingRunErrorMessage = (message) => {
  const normalized = String(message || "").trim().toLowerCase();
  if (!normalized) return "";

  if (
    normalized.includes("no kpi snapshots found") &&
    normalized.includes("no live meta data was available")
  ) {
    return "Sherloc could not complete this report for the selected timeframe because not enough campaign data is available yet. Please refresh or try a different analysis window.";
  }

  return String(message || "").trim();
};

// -----------------------------
// Channels / visuals
// -----------------------------
const CHANNEL_LABELS = {
  overall: "Overall",
  facebook: "Facebook",
  instagram: "Instagram",
  audience_network: "Audience Network",
  messenger: "Messenger",
  tiktok: "TikTok",
  x: "X",
  youtube: "YouTube",
  google_search: "Google Search",
  chatgpt_search: "ChatGPT Search",
  google_analytics: "Google Analytics",
  rumble: "Rumble",
  truth_social: "Truth Social",
  dooh: "DOOH",
  ctv: "CTV",
  video: "Video",
  audio: "Audio",
  display: "Display",
  mobile: "Mobile",
  ingame: "In-Game",
  email: "Email",
  cinema: "Cinema",
  programmatic: "Programmatic",
  yahoo_dsp: "Yahoo!",
  viant: "Viant",
};

const CHANNEL_ORDER = [
  "facebook",
  "instagram",
  "audience_network",
  "messenger",
  "tiktok",
  "x",
  "youtube",
  "google_search",
  "chatgpt_search",
  "rumble",
  "truth_social",
  "amazon",
  "yahoo_dsp",
  "programmatic",
  "viant",
  "dooh",
  "ctv",
  "video",
  "audio",
  "display",
  "mobile",
  "ingame",
  "email",
  "google_analytics",
  "cinema",
];

/*
 * The canonical Platform Analytics destination for THIS portal.
 *
 * The campaign-level action used to have no destination of its own. It picked
 * the first channel in the report inventory that happened to be a social one
 * and opened that platform's embedded dashboard — so "the campaign report" was
 * whichever platform sorted first, and for Never Stop Chasing that was TikTok.
 * Array order is not a destination. This is.
 */
const PLATFORM_ANALYTICS_ROUTE = "/platform-analytics";

const SHERLOC_DISPLAY_METRIC_CHANNELS = new Set([
  "display",
  "mobile",
  "ingame",
  "email",
  "programmatic",
]);

const SHERLOC_VIDEO_METRIC_CHANNELS = new Set(["ctv", "video"]);

const SHERLOC_SOCIAL_METRIC_CHANNELS = new Set([
  "facebook",
  "instagram",
  "audience_network",
  "messenger",
  "tiktok",
  "x",
  "rumble",
  "truth_social",
  "reddit",
  "amazon",
]);

const CHANNEL_FIELD_KEYS = {
  yahoo_dsp: "yahoo_dsp",
  viant: "viant",
  facebook: "facebook",
  instagram: "instagram",
  tiktok: "tiktok",
  x: "x",
  youtube: "youtube",
  google_search: "googleSearch",
  chatgpt_search: "chatgptSearch",
  google_analytics: "googleAnalytics",
  rumble: "rumble",
  truth_social: "truthSocial",
  dooh: "dooh",
  ctv: "ctv",
  video: "video",
  audio: "audio",
  display: "display",
  mobile: "mobile",
  ingame: "ingame",
  email: "email",
  cinema: "cinema",
};

const CHANNEL_ICONS = {
  yahoo_dsp: yahooLogo,
  viant: viantLogo,
  overall: aiOptimizationsImage,
  facebook: facebookLogo,
  instagram: instagramLogo,
  audience_network: metaLogo,
  messenger: metaLogo,
  tiktok: tiktokLogo,
  youtube: youtubeLogo,
  x: xLogo,
  truth_social: truthsocialLogo,
  rumble: rumbleLogo,
  google_search: googleLogo,
  chatgpt_search: openaiLogo,
  google_analytics: googleAnalyticsImage,
  dooh: doohAnalyticsImage,
  ctv: ctvAnalyticsImage,
  video: videoAnalyticsImage,
  audio: audioAnalyticsImage,
  display: displayAnalyticsImage,
  mobile: mobileAnalyticsImage,
  ingame: ingameAnalyticsImage,
  email: emailAnalyticsImage,
  cinema: purpleFilmIcon,
};

const LIVE_PLATFORM_ORDER = [
  "facebook",
  "instagram",
  "tiktok",
  "x",
  "youtube",
  "google_search",
  "chatgpt_search",
  "rumble",
  "truth_social",
  "reddit",
  "amazon",
  "yahoo_dsp",
  "programmatic",
  "viant",
  "dooh",
  "ctv",
  "video",
  "audio",
  "display",
  "mobile",
  "ingame",
  "email",
  "cinema",
];

const LIVE_PLATFORM_META = {
  facebook: { label: "Facebook", icon: facebookLogo },
  instagram: { label: "Instagram", icon: instagramLogo },
  tiktok: { label: "TikTok", icon: tiktokLogo },
  youtube: { label: "YouTube", icon: youtubeLogo },
  google_search: { label: "Google Search", icon: googleLogo },
  rumble: { label: "Rumble", icon: rumbleLogo },
  truth_social: { label: "Truth Social", icon: truthsocialLogo },
  reddit: { label: "Reddit", icon: redditLogo },
  amazon: { label: "Amazon", icon: amazonLogo },
  yahoo_dsp: { label: "Yahoo!", icon: yahooLogo },
  viant: { label: "Viant", icon: viantLogo },
  x: { label: "X", icon: xLogo },
  dooh: { label: "DOOH", icon: doohAnalyticsImage },
  ctv: { label: "CTV", icon: ctvAnalyticsImage },
  video: { label: "Video", icon: videoAnalyticsImage },
  audio: { label: "Audio", icon: audioAnalyticsImage },
  display: { label: "Display", icon: displayAnalyticsImage },
  mobile: { label: "Mobile", icon: mobileAnalyticsImage },
  ingame: { label: "In-Game", icon: ingameAnalyticsImage },
  email: { label: "Email", icon: emailAnalyticsImage },
  cinema: { label: "Cinema", icon: purpleFilmIcon },
  programmatic: { label: "Programmatic", icon: null },
};

const normalizeLivePlatformKey = (rawKey) => {
  const providerKey = normalizeProviderSurfaceKey(rawKey);
  if (providerKey) return providerKey;
  const raw = String(rawKey || "").trim().toLowerCase();
  if (!raw) return "";

  if (raw.includes("amazon")) return "amazon";
  if (raw.includes("reddit")) return "reddit";
  if ((raw.includes("stackadapt") || raw.includes("programmatic")) && raw.includes("ctv")) {
    return "ctv";
  }
  if (
    (raw.includes("stackadapt") || raw.includes("programmatic")) &&
    raw.includes("display")
  ) {
    return "display";
  }
  if (raw.includes("stackadapt") || raw.includes("programmatic")) return "programmatic";

  const canonical = canonicalizeChannelKey(rawKey);
  if (!canonical || canonical === "overall") return "";

  if (canonical === "google_analytics") return "";
  if (canonical === "audience_network") return "";
  if (canonical === "messenger") return "";

  return LIVE_PLATFORM_META[canonical] ? canonical : "";
};

const getSherlocDeliveryMetricValue = (channel = {}, ...keys) => {
  const nestedMetrics = isPlainObject(channel?.metrics) ? channel.metrics : {};
  const nestedTotals = isPlainObject(channel?.totals) ? channel.totals : {};
  const nestedAggregateTotals = isPlainObject(channel?.aggregateTotals)
    ? channel.aggregateTotals
    : {};
  return pickFirst(
    ...keys.flatMap((key) => [
      channel?.[key],
      nestedMetrics?.[key],
      nestedTotals?.[key],
      nestedAggregateTotals?.[key],
    ])
  );
};

const buildSherlocDeliveryEvidence = (channel = {}) => {
  if (!channel || typeof channel !== "object") {
    return {
      hasDelivery: false,
      metrics: {},
      reason: "no_current_delivery_metrics",
    };
  }

  const metricValue = (...keys) => {
    const value = getSherlocDeliveryMetricValue(channel, ...keys);
    const parsed = toNumber(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const metrics = {
    spend: metricValue(
      "clientFacingSpend",
      "client_facing_spend",
      "billedSpend",
      "billed_spend",
      "spend",
      "spend_usd"
    ),
    impressions: metricValue("impressions"),
    clicks: metricValue("clicks"),
    engagements: metricValue("engagements", "actions", "action_count", "actionCount"),
    conversions: metricValue("conversions", "conversion_count", "conversionCount"),
    videoViews: metricValue("videoViews", "video_views", "views"),
    videoStarts: metricValue("videoStarts", "video_starts", "video_started", "starts"),
    videoCompletions: metricValue(
      "videoCompletions",
      "video_completions",
      "video_completed",
      "completions"
    ),
  };
  const evidenceMetric = Object.entries(metrics).find(
    ([, value]) => Number.isFinite(value) && value > 0
  );

  return {
    hasDelivery: Boolean(evidenceMetric),
    metrics,
    reason: evidenceMetric
      ? `current_${evidenceMetric[0]}_delivery`
      : "no_current_delivery_metrics",
  };
};

const getLivePlatformsForUi = ({ liveByChannel = null, reportingByChannel = null } = {}) => {
  const sourceMaps = [
    liveByChannel && typeof liveByChannel === "object" ? liveByChannel : null,
    reportingByChannel && typeof reportingByChannel === "object" ? reportingByChannel : null,
  ].filter(Boolean);
  const seen = new Map();
  if (!sourceMaps.length) return [];

  sourceMaps.forEach((sourceMap) => {
    Object.entries(sourceMap).forEach(([rawKey, value]) => {
      const deliveryEvidence = buildSherlocDeliveryEvidence(value);
      if (!deliveryEvidence.hasDelivery) return;
      const normalized = normalizeLivePlatformKey(rawKey);
      if (!normalized || seen.has(normalized)) return;
      const meta = LIVE_PLATFORM_META[normalized];
      if (!meta) return;
      seen.set(normalized, {
        key: normalized,
        ...meta,
        status: "LIVE",
        deliveryStatus: "LIVE",
        deliveryEvidence,
        statusReason: deliveryEvidence.reason,
      });
    });
  });

  return LIVE_PLATFORM_ORDER.filter((key) => seen.has(key)).map((key) => seen.get(key));
};

const getApprovedPlanPlatformsForUi = ({ campaignPlatformScope = null } = {}) => {
  const seen = new Map();
  if (!campaignPlatformScope?.resolved) return [];

  campaignPlatformScope.keys.forEach((key) => {
    const normalized = normalizeLivePlatformKey(key);
    if (!normalized || seen.has(normalized)) return;
    const meta = LIVE_PLATFORM_META[normalized];
    if (!meta) return;
    seen.set(normalized, {
      key: normalized,
      ...meta,
      status: "READY",
      deliveryStatus: "READY",
      approvalStatus: "APPROVED",
      statusReason: "approved_no_current_delivery",
    });
  });

  return LIVE_PLATFORM_ORDER.filter((key) => seen.has(key)).map((key) => seen.get(key));
};

const mergePlatformSignalsForUi = ({
  approvedPlatforms = [],
  livePlatforms = [],
  campaignPlatformScope = null,
} = {}) => {
  // Campaign membership is the only admission gate. With no resolved campaign platform
  // scope nothing is campaign-associated, so admit nothing rather than falling back to
  // every connected/reportable platform.
  if (!campaignPlatformScope?.resolved) return [];

  const merged = new Map();

  approvedPlatforms.forEach((platform) => {
    if (!platform?.key || merged.has(platform.key)) return;
    if (!isSherlocCampaignScopedPlatformKey(campaignPlatformScope, platform.key)) return;
    merged.set(platform.key, {
      ...platform,
      approvalStatus: platform.approvalStatus || "APPROVED",
      status: platform.status === "LIVE" ? "LIVE" : "READY",
      deliveryStatus: platform.status === "LIVE" ? "LIVE" : "READY",
      statusReason:
        platform.status === "LIVE"
          ? platform.statusReason || "current_delivery_metrics"
          : platform.statusReason || "approved_no_current_delivery",
    });
  });

  livePlatforms.forEach((platform) => {
    if (!platform?.key) return;
    // A live provider signal enriches an admitted platform; it never admits one.
    if (!isSherlocCampaignScopedPlatformKey(campaignPlatformScope, platform.key)) return;
    merged.set(platform.key, {
      ...merged.get(platform.key),
      ...platform,
      status: "LIVE",
      deliveryStatus: "LIVE",
      approvalStatus: merged.get(platform.key)?.approvalStatus || platform.approvalStatus || "APPROVED",
      statusReason: platform.statusReason || "current_delivery_metrics",
    });
  });

  return LIVE_PLATFORM_ORDER.filter((key) => merged.has(key)).map((key) => merged.get(key));
};

const buildPlatformStatusTrace = (platforms = []) =>
  (Array.isArray(platforms) ? platforms : []).reduce((acc, platform) => {
    if (!platform?.key) return acc;
    acc[platform.key] = platform.status || "";
    return acc;
  }, {});

const mergePlatformSignalsPreservingLive = ({
  currentPlatforms = [],
  preservedPlatforms = [],
} = {}) => {
  const merged = new Map();

  (Array.isArray(currentPlatforms) ? currentPlatforms : []).forEach((platform) => {
    if (!platform?.key) return;
    merged.set(platform.key, platform);
  });

  (Array.isArray(preservedPlatforms) ? preservedPlatforms : []).forEach((platform) => {
    if (!platform?.key || platform.status !== "LIVE") return;
    const current = merged.get(platform.key);
    merged.set(platform.key, {
      ...(current || platform),
      ...platform,
      status: "LIVE",
    });
  });

  return LIVE_PLATFORM_ORDER.filter((key) => merged.has(key)).map((key) => merged.get(key));
};

const buildPendingGoogleAnalyticsStatus = (message = "Checking GA4…") => ({
  state: "loading",
  message,
  contextConnected: false,
  classification: "pending",
  connectionState: "checking",
  connected: false,
  mapped: false,
  reportingReady: false,
});

const canonicalizeChannelKey = (rawKey) => {
  const providerKey = normalizeProviderSurfaceKey(rawKey);
  if (providerKey) return providerKey;
  const s = String(rawKey ?? "").trim();
  if (!s) return "";
  const low = s.toLowerCase();
  const compact = low
    .replace(/[()]/g, " ")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const underscored = compact.replace(/\s+/g, "_");
  const explicitAliases = {
    meta_facebook: "facebook",
    meta_instagram: "instagram",
    google_youtube: "youtube",
    stackadapt_ctv: "ctv",
    stackadapt_display: "display",
    programmatic_display: "display",
  };
  if (explicitAliases[underscored]) return explicitAliases[underscored];

  const has = (w) => compact.includes(w);

  if (has("overall")) return "overall";
  if (compact === "fb" || compact === "meta fb" || has("meta facebook")) return "facebook";
  if (has("facebook")) return "facebook";
  if (compact === "ig" || compact === "meta ig" || has("meta instagram")) return "instagram";
  if (has("instagram")) return "instagram";
  if (
    has("audience network") ||
    has("audience_network") ||
    (has("audience") && has("network"))
  )
    return "audience_network";
  if (has("messenger")) return "messenger";
  if (has("tiktok") || has("tik tok")) return "tiktok";
  if (compact === "x" || has("twitter") || has("x com")) return "x";
  if (has("youtube") || has("you tube")) return "youtube";

  if (
    has("google analytics") ||
    has("google analytic") ||
    compact === "ga4" ||
    compact === "ga" ||
    has("website analytics") ||
    has("site analytics") ||
    has("web analytics")
  ) {
    return "google_analytics";
  }

  if (
    compact === "google" ||
    has("google search") ||
    has("paid search") ||
    has("ppc") ||
    (has("google") && has("search")) ||
    (has("google") && has("paid"))
  ) {
    return "google_search";
  }

  if (has("rumble")) return "rumble";
  if (has("truth social") || has("truthsocial") || (has("truth") && has("social")))
    return "truth_social";
  if (
    has("dooh") ||
    has("digital out of home") ||
    has("digital out-of-home") ||
    has("ooh")
  )
    return "dooh";
  if (has("ctv") || has("connected tv") || has("ott") || has("fast")) return "ctv";
  if (has("stackadapt") || has("programmatic")) return "programmatic";
  if (has("olv") || has("online video") || has("pre roll") || has("preroll") || has("video"))
    return "video";
  if (
    has("audio") ||
    has("digital audio") ||
    has("podcast") ||
    has("spotify") ||
    has("pandora")
  )
    return "audio";
  if (has("display") || has("banner")) return "display";
  if (has("mobile") || has("in app") || has("in-app")) return "mobile";
  if (has("in game") || has("ingame") || has("gaming")) return "ingame";
  if (has("email") || has("e mail") || has("crm")) return "email";
  if (has("cinema") || has("theatrical") || has("theater") || has("movie")) return "cinema";

  return compact.replace(/\s+/g, "_");
};

const mergeChannelAgg = (base = {}, incoming = {}) => {
  const out = { ...(base || {}) };

  const sumFields = [
    "spend",
    "spend_usd",
    "clientFacingSpend",
    "client_facing_spend",
    "billedSpend",
    "billed_spend",
    "clientBilledSpend",
    "client_billed_spend",
    "reportSpend",
    "report_spend",
    "providerSpend",
    "provider_spend",
    "impressions",
    "clicks",
    "engagements",
    "video_views",
    "videoViews",
    "conversions",
    "video_started",
    "videoStarts",
    "video_start",
    "video_starts",
    "video_completed",
    "videoCompletes",
    "video_complete",
    "completed_views",
    "conversion_value",
    "conversionValue",
    "conversion_value_usd",
    "conversionValueUsd",
    "ticket_sales",
    "ticketSales",
    "ticket_sales_usd",
    "ticketSalesUsd",
    "users",
    "active_users",
    "activeUsers",
    "new_users",
    "newUsers",
    "sessions",
    "engaged_sessions",
    "engagedSessions",
    "event_count",
    "eventCount",
    "page_views",
    "pageViews",
    "screen_page_views",
    "screenPageViews",
    "key_events",
    "keyEvents",
    "avg_session_duration",
    "averageSessionDuration",
  ];

  const maxFields = [
    "total_booked_theaters",
    "totalBookedTheaters",
    "total_circuits",
    "totalCircuits",
    "total_cities",
    "totalCities",
    "total_states",
    "totalStates",
  ];

  for (const k of Object.keys(incoming || {})) {
    const v = incoming[k];

    if (maxFields.includes(k)) {
      const a = toNumber(out[k] ?? -Infinity);
      const b = toNumber(v ?? -Infinity);
      if (Number.isFinite(a) || Number.isFinite(b)) {
        out[k] = Math.max(
          Number.isFinite(a) ? a : -Infinity,
          Number.isFinite(b) ? b : -Infinity
        );
      }
      continue;
    }

    if (sumFields.includes(k)) {
      const a = toNumber(out[k] ?? 0);
      const b = toNumber(v ?? 0);
      const aOk = Number.isFinite(a);
      const bOk = Number.isFinite(b);
      if (aOk || bOk) out[k] = (aOk ? a : 0) + (bOk ? b : 0);
      continue;
    }

    out[k] = v;
  }

  return out;
};

const hasUsableMetrics = (m) => {
  if (!m) return false;
  const nestedMetrics = isPlainObject(m?.metrics) ? m.metrics : {};
  const metricValue = (...keys) =>
    pickFirst(
      ...keys.flatMap((key) => [m?.[key], nestedMetrics?.[key]])
    );
  const impressions = toNumber(metricValue("impressions") ?? 0);
  const spend = toNumber(
    metricValue(
      "clientFacingSpend",
      "client_facing_spend",
      "billedSpend",
      "billed_spend",
      "spend",
      "spend_usd"
    ) ?? 0
  );
  const clicks = toNumber(metricValue("clicks") ?? 0);
  const video = toNumber(metricValue("video_views", "videoViews") ?? 0);
  const conv = toNumber(metricValue("conversions") ?? 0);
  const engagements = toNumber(metricValue("engagements") ?? 0);
  const users = toNumber(metricValue("users", "active_users", "activeUsers") ?? 0);
  const sessions = toNumber(metricValue("sessions") ?? 0);
  const pageViews = toNumber(
    metricValue("page_views", "pageViews", "screen_page_views", "screenPageViews") ?? 0
  );
  const events = toNumber(metricValue("event_count", "eventCount", "key_events", "keyEvents") ?? 0);

  return (
    (Number.isFinite(impressions) && impressions > 0) ||
    (Number.isFinite(spend) && spend > 0) ||
    (Number.isFinite(clicks) && clicks > 0) ||
    (Number.isFinite(video) && video > 0) ||
    (Number.isFinite(conv) && conv > 0) ||
    (Number.isFinite(engagements) && engagements > 0) ||
    (Number.isFinite(users) && users > 0) ||
    (Number.isFinite(sessions) && sessions > 0) ||
    (Number.isFinite(pageViews) && pageViews > 0) ||
    (Number.isFinite(events) && events > 0)
  );
};

const hasMeaningfulDeliveryMetrics = (metrics) => {
  if (!metrics || typeof metrics !== "object") return false;
  const impressions = toNumber(metrics.impressions ?? 0);
  const rawSpend = toNumber(metrics.spend ?? metrics.spend_usd ?? 0);
  const clientFacingSpend = toNumber(
    metrics.clientFacingSpend ??
      metrics.client_facing_spend ??
      metrics.billedSpend ??
      metrics.billed_spend ??
      metrics.clientBilledSpend ??
      metrics.client_billed_spend ??
      metrics.effectiveSpend ??
      metrics.effective_spend ??
      0
  );

  return (
    (Number.isFinite(impressions) && impressions > 0) ||
    (Number.isFinite(rawSpend) && rawSpend > 0) ||
    (Number.isFinite(clientFacingSpend) && clientFacingSpend > 0)
  );
};

const isLiveSherlocChannel = (channel = {}) => {
  const source = String(
    channel?.data_source ||
      channel?.dataSource ||
      channel?.sourceTier ||
      channel?.source ||
      ""
  )
    .trim()
    .toLowerCase();
  const sourceContract = String(
    channel?.canonicalReportingContract ||
      channel?.canonical_reporting_contract ||
      channel?.sourceContract?.contract ||
      channel?.source_contract?.contract ||
      ""
  )
    .trim()
    .toLowerCase();

  return (
    channel?.isLive === true ||
    channel?.hasCanonicalMetrics === true ||
    sourceContract === "canonical_reporting_v1" ||
    source === "canonical_reporting_v1" ||
    source === "canonical_performance_summary" ||
    source.includes("live") ||
    source === "meta_live" ||
    source === "google_analytics_live"
  );
};

// The ONE canonical campaign platform set for Campaign Analysis. Live Platforms, its
// count pill, Platform Performance and platform-analysis eligibility all read this.
const normalizeCampaignPlatformScopeKey = (key) =>
  normalizeLivePlatformKey(key) || canonicalizeChannelKey(key);

const buildSherlocCampaignPlatformScopeForCampaigns = (campaigns = []) =>
  buildSherlocCampaignPlatformScope({
    approvedPlatformKeys: buildApprovedPlanContext(campaigns)?.approvedPlatformKeys || [],
    normalizeKey: normalizeCampaignPlatformScopeKey,
  });

const isSherlocClientChannelEligible = ({
  key,
  channel,
  approvedChannelKeys,
  liveByChannel = {},
}) => {
  if (!key) return false;
  if (isLiveSherlocChannel(channel)) return true;
  if (isLiveSherlocChannel(liveByChannel?.[key])) return true;
  if (approvedChannelKeys?.has?.(key)) return true;
  return hasMeaningfulDeliveryMetrics(channel);
};

const filterSherlocClientEligibleByChannel = ({
  byChannel = {},
  approvedChannelKeys,
  liveByChannel = {},
}) => {
  const filtered = {};
  Object.entries(byChannel || {}).forEach(([rawKey, channel]) => {
    const key = canonicalizeChannelKey(rawKey);
    if (!key) return;
    if (
      isSherlocClientChannelEligible({
        key,
        channel,
        approvedChannelKeys,
        liveByChannel,
      })
    ) {
      filtered[key] = channel;
    }
  });
  return filtered;
};

const SHERLOC_CANONICAL_METRIC_FIELDS = new Set([
  "spend",
  "spend_usd",
  "clientFacingSpend",
  "client_facing_spend",
  "billedSpend",
  "billed_spend",
  "reportSpend",
  "report_spend",
  "impressions",
  "clicks",
  "ctr",
  "click_through_rate",
  "clickThroughRate",
  "cpc",
  "clientFacingCpc",
  "client_facing_cpc",
  "cpm",
  "clientFacingCpm",
  "client_facing_cpm",
  "engagements",
  "er",
  "engagementRate",
  "engagement_rate",
  "vvr",
  "video_views",
  "videoViews",
  "videoStarts",
  "video_starts",
  "video_start",
  "video_started",
  "videoCompletions",
  "videoCompletes",
  "video_completions",
  "video_complete",
  "video_completed",
  "completed_views",
  "completionRate",
  "completion_rate",
  "videoCompletionRate",
  "video_completion_rate",
  "videoViewRate",
  "video_view_rate",
  "conversions",
  "conversion_value_usd",
  "conversionValueUsd",
  "conversionValue",
  "conversion_value",
  "users",
  "newUsers",
  "new_users",
  "sessions",
  "engagedSessions",
  "engaged_sessions",
  "eventCount",
  "event_count",
  "averageSessionDuration",
  "avgSessionDuration",
  "average_session_duration",
  "avg_session_duration",
  "ticketSales",
  "ticket_sales",
  "totalBookedTheaters",
  "total_booked_theaters",
]);

const SHERLOC_CANONICAL_METADATA_FIELDS = new Set([
  "currency", "spendCurrency", "provider", "providerKey", "provenance", "reportingEvidence", "basisProvenance", "financialAuthority", "optimizationEligible", "reportingVerification", "accountVerification", "capabilities", "aggregationRole", "spendAvailable",
  "sourceLabel",
  "source_label",
  "spendBasis",
  "spend_basis",
  "pricingModel",
  "pricing_model",
  "billingMetric",
  "billing_metric",
  "providerSpend",
  "provider_spend",
  "clientFacingSpend",
  "client_facing_spend",
  "billedSpend",
  "billed_spend",
  "clientFacingCpm",
  "client_facing_cpm",
  "providerCpm",
  "provider_cpm",
  "billedCpm",
  "billed_cpm",
  "selectionReason",
  "selection_reason",
]);

const canonicalizeSherlocMetricChannelKey = (rawKey) => {
  const providerKey = normalizeProviderSurfaceKey(rawKey);
  if (providerKey) return providerKey;
  const raw = String(rawKey ?? "").trim();
  if (!raw) return "";
  const compact = raw
    .toLowerCase()
    .replace(/[()]/g, " ")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const underscored = compact.replace(/\s+/g, "_");
  const explicitAliases = {
    fb: "facebook",
    meta_facebook: "facebook",
    ig: "instagram",
    meta_instagram: "instagram",
    google_youtube: "youtube",
    connected_tv: "ctv",
    stackadapt_ctv: "ctv",
    stackadapt_display: "display",
    programmatic_display: "display",
  };
  if (explicitAliases[underscored]) return explicitAliases[underscored];

  const has = (word) => compact.includes(word);
  const isProgrammatic = has("stackadapt") || has("programmatic");

  if (isProgrammatic && (has("ctv") || has("connected tv") || has("ott"))) return "ctv";
  if (isProgrammatic && (has("display") || has("banner"))) return "display";
  if (has("meta") && has("facebook")) return "facebook";
  if (has("meta") && has("instagram")) return "instagram";

  return canonicalizeChannelKey(rawKey);
};

const hasSherlocCanonicalMetricPayload = (metrics = {}) =>
  isPlainObject(metrics) &&
  Object.entries(metrics).some(
    ([key, value]) =>
      SHERLOC_CANONICAL_METRIC_FIELDS.has(key) &&
      value !== null &&
      value !== undefined &&
      value !== ""
  );

const hasSherlocCanonicalMetricMap = (map = {}) =>
  isPlainObject(map) &&
  Object.values(map).some((entry) => {
    if (!isPlainObject(entry)) return false;
    const nestedMetrics = extractSherlocCanonicalPlatformMetrics(entry);
    const sourceContract = String(
      entry?.contract ||
        entry?.canonicalReportingContract ||
        entry?.canonical_reporting_contract ||
        entry?.sourceContract?.contract ||
        entry?.source_contract?.contract ||
        entry?.source ||
        entry?.data_source ||
        ""
    )
      .trim()
      .toLowerCase();
    return (
      entry.hasCanonicalMetrics === true ||
      sourceContract === "canonical_reporting_v1" ||
      sourceContract === "canonical_performance_summary" ||
      hasSherlocCanonicalMetricPayload(nestedMetrics)
    );
  });

const getSherlocCanonicalByChannel = (payload = {}) =>
  (isPlainObject(payload?.byChannel) && payload.byChannel) ||
  (isPlainObject(payload?.by_channel) && payload.by_channel) ||
  (isPlainObject(payload?.channels) && payload.channels) ||
  {};

const getSherlocPlatformPerformanceMaps = (payload = {}) => {
  const maps = [
    isPlainObject(payload?.platformPerformanceByChannel) ? payload.platformPerformanceByChannel : null,
    isPlainObject(payload?.platform_performance_by_channel)
      ? payload.platform_performance_by_channel
      : null,
    isPlainObject(payload?.platformPerformance) ? payload.platformPerformance : null,
    isPlainObject(payload?.platform_performance) ? payload.platform_performance : null,
    isPlainObject(payload?.report?.platformPerformance) ? payload.report.platformPerformance : null,
    isPlainObject(payload?.report?.platform_performance) ? payload.report.platform_performance : null,
  ].filter(Boolean);

  const cardMap = {};
  const cards = Array.isArray(payload?.platformPerformanceCards)
    ? payload.platformPerformanceCards
    : Array.isArray(payload?.platform_performance_cards)
    ? payload.platform_performance_cards
    : [];

  cards.forEach((card) => {
    if (!isPlainObject(card)) return;
    const key = canonicalizeSherlocMetricChannelKey(
      card.key || card.platformKey || card.platform_key || card.platform || card.channel || card.name
    );
    if (!key) return;
    cardMap[key] = {
      ...(cardMap[key] || {}),
      ...card,
      ...(isPlainObject(card.metrics) ? card.metrics : {}),
    };
  });

  if (Object.keys(cardMap).length) maps.push(cardMap);
  return maps;
};

const getSherlocCanonicalPlatformMaps = (payload = {}) =>
  [
    isPlainObject(payload?.platforms) ? payload.platforms : null,
    isPlainObject(payload?.channelBreakdown) ? payload.channelBreakdown : null,
    isPlainObject(payload?.channel_breakdown) ? payload.channel_breakdown : null,
    isPlainObject(payload?.platformBreakdown) ? payload.platformBreakdown : null,
    isPlainObject(payload?.platform_breakdown) ? payload.platform_breakdown : null,
  ].filter(Boolean);

const collectSherlocCanonicalPayloads = (root = null) => {
  const payloads = [];
  const seen = new Set();
  const visit = (value) => {
    if (!isPlainObject(value) || seen.has(value)) return;
    seen.add(value);

    const sourceContract = String(
      value?.contract ||
        value?.canonical_reporting_contract ||
        value?.sourceContract?.contract ||
        value?.source_contract?.contract ||
        value?.source ||
        value?.data_source ||
        ""
    )
      .trim()
      .toLowerCase();
    const hasCanonicalShape =
      Object.keys(getSherlocCanonicalByChannel(value)).length > 0 ||
      getSherlocCanonicalPlatformMaps(value).length > 0 ||
      getSherlocPlatformPerformanceMaps(value).length > 0;
    const hasCanonicalMetricEntries =
      hasSherlocCanonicalMetricMap(getSherlocCanonicalByChannel(value)) ||
      getSherlocCanonicalPlatformMaps(value).some((map) =>
        hasSherlocCanonicalMetricMap(map)
      ) ||
      getSherlocPlatformPerformanceMaps(value).some((map) =>
        hasSherlocCanonicalMetricMap(map)
      );

    if (
      hasCanonicalShape &&
      (sourceContract === "canonical_reporting_v1" ||
        sourceContract === "canonical_performance_summary" ||
        hasCanonicalMetricEntries)
    ) {
      payloads.push(value);
    }

    [
      value.canonicalReporting,
      value.canonical_reporting,
      value.canonicalMetrics,
      value.canonicalMetrics?.summary,
      value.canonical_metrics,
      value.canonical_metrics?.summary,
      value.canonicalSummary,
      value.canonical_summary,
      value.canonicalAggregate,
      value.canonical_aggregate,
      value.summary,
      value.aggregate,
      value.result?.canonicalReporting,
      value.result?.canonicalMetrics,
      value.result?.canonicalMetrics?.summary,
      value.result?.canonicalSummary,
      value.result?.canonicalAggregate,
      value.result?.summary,
      value.result?.aggregate,
      value.data?.canonicalReporting,
      value.data?.canonicalMetrics,
      value.data?.canonicalMetrics?.summary,
      value.data?.canonicalSummary,
      value.data?.canonicalAggregate,
      value.data?.summary,
      value.data?.aggregate,
    ].forEach(visit);
  };

  visit(root);
  return payloads;
};

/**
 * The campaign's outcome-semantics declaration, wherever the canonical payload
 * carries it.
 *
 * The verdict is resolved server-side against the live tracker configuration
 * and rides on the canonical summary, but this page rebuilds `aggregate` and
 * `resolved` into its own shapes and drops everything it does not explicitly
 * copy. So the declaration never reached the tile, and the outcome label fell
 * back to the generic name on every campaign — including ones whose trackers
 * genuinely earn Ad-Attributed Site Visits.
 */
const extractSherlocOutcomeSemantics = (payload) => {
  if (!isPlainObject(payload)) return null;
  const candidates = [
    payload.outcomeSemantics,
    payload.outcome_semantics,
    payload.canonicalSummary?.outcomeSemantics,
    payload.canonical_summary?.outcomeSemantics,
    payload.canonicalReporting?.outcomeSemantics,
    payload.canonicalAggregate?.outcomeSemantics,
    payload.aggregate?.outcomeSemantics,
    payload.resolved?.outcomeSemantics,
    payload.result?.outcomeSemantics,
    payload.result?.canonicalSummary?.outcomeSemantics,
    payload.data?.outcomeSemantics,
    payload.data?.canonicalSummary?.outcomeSemantics,
  ];
  for (const candidate of candidates) {
    if (isPlainObject(candidate) && String(candidate.classification || "").trim()) {
      return candidate;
    }
  }
  return null;
};

const normalizeSherlocCanonicalMetrics = (metrics = {}, metadata = {}) => {
  const normalized = isPlainObject(metrics) ? { ...metrics } : {};
  Object.entries(metadata || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && normalized[key] === undefined) {
      normalized[key] = value;
    }
  });

  const hasCanonicalMetrics =
    metadata?.hasCanonicalMetrics === true || hasSherlocCanonicalMetricPayload(normalized);
  if (hasCanonicalMetrics) {
    normalized.hasCanonicalMetrics = true;
    normalized.canonicalReportingContract = "canonical_reporting_v1";
    normalized.data_source = normalized.data_source || normalized.dataSource || "canonical_reporting_v1";
  }
  return normalized;
};

const extractSherlocCanonicalPlatformMetrics = (platform = {}) => {
  if (!isPlainObject(platform)) return {};
  const metrics = {};
  [
    platform.metrics,
    platform.totals,
    platform.aggregateTotals,
    platform.aggregate_totals,
    platform,
  ].forEach((source) => {
    if (!isPlainObject(source)) return;
    Object.entries(source).forEach(([key, value]) => {
      if (
        ["metrics", "totals", "aggregateTotals", "aggregate_totals"].includes(key)
      ) {
        return;
      }
      if (
        SHERLOC_CANONICAL_METRIC_FIELDS.has(key) ||
        SHERLOC_CANONICAL_METADATA_FIELDS.has(key)
      ) {
        metrics[key] = value;
      }
    });
  });

  return normalizeSherlocCanonicalMetrics(metrics, {
    sourceLabel: platform?.sourceLabel || platform?.source_label || metrics?.sourceLabel || null,
    spendBasis: platform?.spendBasis || platform?.spend_basis || metrics?.spendBasis || null,
    pricingModel: platform?.pricingModel || platform?.pricing_model || metrics?.pricingModel || null,
    billingMetric:
      platform?.billingMetric || platform?.billing_metric || metrics?.billingMetric || null,
    providerSpend: platform?.providerSpend ?? platform?.provider_spend ?? metrics?.providerSpend,
    clientFacingSpend:
      platform?.clientFacingSpend ??
      platform?.client_facing_spend ??
      metrics?.clientFacingSpend,
    hasCanonicalMetrics: platform?.hasCanonicalMetrics === true,
  });
};

const mergeSherlocCanonicalChannel = (out, rawKey, metrics = {}) => {
  const key = canonicalizeSherlocMetricChannelKey(rawKey);
  if (!key || !isPlainObject(metrics)) return;
  const normalizedMetrics = normalizeSherlocCanonicalMetrics(
    extractSherlocCanonicalPlatformMetrics(metrics)
  );
  out[key] = {
    ...(out[key] || {}),
    ...normalizedMetrics,
    resolvedCanonicalKey: key,
    rawCanonicalKey: rawKey,
    rawChannelObject: metrics,
    hasCanonicalMetrics:
      out[key]?.hasCanonicalMetrics === true || normalizedMetrics.hasCanonicalMetrics === true,
  };
};

const buildSherlocCanonicalChannelMap = (aggregate = null) => {
  const out = {};
  collectSherlocCanonicalPayloads(aggregate).forEach((payload) => {
    getSherlocPlatformPerformanceMaps(payload).forEach((platformMap) => {
      Object.entries(platformMap || {}).forEach(([rawKey, platform]) => {
        mergeSherlocCanonicalChannel(out, rawKey, platform || {});
      });
    });

    getSherlocCanonicalPlatformMaps(payload).forEach((platformMap) => {
      Object.entries(platformMap || {}).forEach(([rawKey, platform]) => {
        mergeSherlocCanonicalChannel(out, rawKey, extractSherlocCanonicalPlatformMetrics(platform));
      });
    });

    Object.entries(getSherlocCanonicalByChannel(payload)).forEach(([rawKey, channel]) => {
      mergeSherlocCanonicalChannel(out, rawKey, channel || {});
    });
  });

  return out;
};

const getSherlocAggregateTotalCandidates = (root = null) => {
  if (!isPlainObject(root)) return [];
  const candidates = [];
  const add = (value) => {
    if (isPlainObject(value)) candidates.push(value);
  };

  [
    root.canonicalMetrics?.summary?.aggregateTotals,
    root.canonicalMetrics?.summary?.totals,
    root.canonicalMetrics?.aggregateTotals,
    root.canonicalMetrics?.totals,
    root.canonical_metrics?.summary?.aggregateTotals,
    root.canonical_metrics?.summary?.totals,
    root.canonicalReporting?.aggregateTotals,
    root.canonicalReporting?.totals,
    root.canonical_reporting?.aggregateTotals,
    root.canonical_reporting?.totals,
    root.canonicalSummary?.aggregateTotals,
    root.canonicalSummary?.totals,
    root.canonical_summary?.aggregateTotals,
    root.canonical_summary?.totals,
    root.summary?.aggregateTotals,
    root.summary?.totals,
    root.aggregateTotals,
    root.aggregate_totals,
    root.currentKpis,
    root.current_kpis,
    root.kpiSnapshot,
    root.kpi_snapshot,
    root.kpis,
    root.totals,
    root.total,
    root.summaryTotals,
    root.resolved?.totals,
    root.aggregate?.aggregateTotals,
    root.aggregate?.totals,
    root.result?.canonicalMetrics?.summary?.aggregateTotals,
    root.result?.canonicalReporting?.aggregateTotals,
    root.result?.summary?.aggregateTotals,
    root.result?.aggregateTotals,
    root.result?.totals,
    root.data?.canonicalMetrics?.summary?.aggregateTotals,
    root.data?.canonicalReporting?.aggregateTotals,
    root.data?.summary?.aggregateTotals,
    root.data?.aggregateTotals,
    root.data?.totals,
  ].forEach(add);

  collectSherlocCanonicalPayloads(root).forEach((payload) => {
    [payload.aggregateTotals, payload.aggregate_totals, payload.totals, payload.summaryTotals].forEach(add);
  });

  return candidates;
};

/*
 * The per-channel evidence must travel with the totals it summarises.
 *
 * `computeReportingMetrics` takes byChannel as its second argument and folds
 * every blended rate through the canonical eligibility engine when it has one.
 * This call site passed only the totals — and it is the FIRST branch of the
 * aggregate resolver, so on any campaign with a canonical payload it is the
 * branch that renders. The engine had nothing to govern with and every rate on
 * the strip fell through to whole-pool division.
 *
 * On Never Stop Chasing that published CPC $5.13 — all spend over all clicks,
 * including the $7,536.45 of CTV inventory that cannot produce one — where
 * Campaign Insights published the governed $2.66 from the same window. The
 * channel map is resolved from the SAME response the totals came from, so the
 * two halves can never describe different scopes.
 */
const resolveCanonicalAggregateMetrics = (...responses) => {
  for (const response of responses) {
    const responseByChannel = buildSherlocCanonicalChannelMap(response);
    const governedByChannel =
      responseByChannel && Object.keys(responseByChannel).length ? responseByChannel : null;
    for (const totals of getSherlocAggregateTotalCandidates(response)) {
      const normalizedTotals = extractSherlocCanonicalPlatformMetrics(totals);
      const metrics = computeReportingMetrics(normalizedTotals, governedByChannel, { preserveMissingMetrics: true });
      if (metrics && (hasSharedUsableMetrics(metrics) || hasSherlocCanonicalMetricPayload(normalizedTotals))) {
        return {
          ...metrics,
          hasCanonicalMetrics: true,
          canonicalReportingContract: "canonical_reporting_v1",
        };
      }
    }
  }
  return null;
};

const resolveCanonicalPlatformMetrics = (response, platformKey) => {
  const key = canonicalizeSherlocMetricChannelKey(platformKey);
  if (!key) return null;
  const map = buildSherlocCanonicalChannelMap(response);
  return map[key] || null;
};

const SHERLOC_PAID_MEDIA_AGGREGATE_EXCLUDED_KEYS = new Set([
  "overall",
  "google_analytics",
  "cinema",
]);

const SHERLOC_HIDDEN_META_PLACEMENT_CARD_KEYS = new Set([
  "audience_network",
  "messenger",
]);

const isSherlocPaidPerformanceChannelKey = (rawKey) => {
  const normalizedKey = canonicalizeChannelKey(rawKey);
  if (!normalizedKey) return false;
  if (normalizedKey === "overall") return false;
  if (normalizedKey === "google_analytics") return false;
  if (SHERLOC_HIDDEN_META_PLACEMENT_CARD_KEYS.has(normalizedKey)) return false;
  return true;
};

const rollupSherlocUiPlatformChannelMap = (channelMap = {}) => {
  if (!isPlainObject(channelMap)) return {};

  return Object.entries(channelMap).reduce((acc, [rawKey, channel]) => {
    const rawCanonicalKey = canonicalizeSherlocMetricChannelKey(rawKey);
    const uiKey = SHERLOC_HIDDEN_META_PLACEMENT_CARD_KEYS.has(rawCanonicalKey)
      ? "facebook"
      : rawCanonicalKey;
    if (!uiKey || uiKey === "overall") return acc;

    const metrics = extractSherlocCanonicalPlatformMetrics(channel || {});
    const source =
      hasSherlocCanonicalMetricPayload(metrics) || hasSharedUsableMetrics(metrics)
        ? metrics
        : isPlainObject(channel)
          ? channel
          : {};
    const merged = acc[uiKey] ? mergeChannelAgg(acc[uiKey], source) : { ...source };
    const rollupSourceKeys = Array.from(
      new Set([
        ...(Array.isArray(acc[uiKey]?.rawUiRollupSourceKeys)
          ? acc[uiKey].rawUiRollupSourceKeys
          : []),
        rawCanonicalKey || rawKey,
      ].filter(Boolean))
    );

    acc[uiKey] = normalizeSherlocCanonicalMetrics(
      {
        ...merged,
        resolvedCanonicalKey: uiKey,
        rawCanonicalKey: uiKey,
        rawUiRollupSourceKeys: rollupSourceKeys,
        rawChannelObject:
          rollupSourceKeys.length > 1
            ? {
                ...(isPlainObject(acc[uiKey]?.rawChannelObject)
                  ? acc[uiKey].rawChannelObject
                  : {}),
                [rawCanonicalKey || rawKey]: channel,
              }
            : channel?.rawChannelObject || channel,
        platformMetricSource:
          rollupSourceKeys.length > 1
            ? "sherloc_ui_platform_rollup"
            : merged.platformMetricSource || channel?.platformMetricSource,
      },
      {
        hasCanonicalMetrics:
          merged.hasCanonicalMetrics === true ||
          channel?.hasCanonicalMetrics === true ||
          hasSherlocCanonicalMetricPayload(merged),
      }
    );

    return acc;
  }, {});
};

const hasSherlocPaidPlatformMetricEvidence = (channelMap = {}) => {
  if (!isPlainObject(channelMap)) return false;
  return Object.entries(channelMap).some(([rawKey, channel]) => {
    const key = canonicalizeSherlocMetricChannelKey(rawKey);
    if (!key || SHERLOC_PAID_MEDIA_AGGREGATE_EXCLUDED_KEYS.has(key)) return false;
    const metrics = extractSherlocCanonicalPlatformMetrics(channel || {});
    const deliveryEvidence = buildSherlocDeliveryEvidence(metrics);
    return (
      deliveryEvidence.hasDelivery ||
      hasSherlocCanonicalMetricPayload(metrics) ||
      hasSharedUsableMetrics(metrics)
    );
  });
};

const getSherlocMetricNumber = (source = {}, ...keys) => {
  const raw = pickFirst(...keys.map((key) => source?.[key]));
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = toNumber(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

const deriveSherlocPaidPlatformDisplayMetrics = (metrics = {}) => {
  const displayedSpend = getSherlocMetricNumber(
    metrics,
    "clientFacingSpend",
    "client_facing_spend",
    "billedSpend",
    "billed_spend",
    "clientBilledSpend",
    "client_billed_spend",
    "reportSpend",
    "report_spend",
    "spend",
    "spend_usd"
  );
  const impressions = getSherlocMetricNumber(metrics, "impressions", "reportImpressions", "report_impressions");
  const clicks = getSherlocMetricNumber(metrics, "clicks", "reportClicks", "report_clicks");
  const engagements = getSherlocMetricNumber(metrics, "engagements", "actions");
  const conversions = getSherlocMetricNumber(metrics, "conversions");
  const videoViews = getSherlocMetricNumber(metrics, "video_views", "videoViews", "views");
  const videoStarted = getSherlocMetricNumber(
    metrics,
    "videoStarted",
    "videoStarts",
    "video_starts",
    "video_start",
    "video_started"
  );
  const videoCompleted = getSherlocMetricNumber(
    metrics,
    "videoCompleted",
    "videoCompletions",
    "videoCompletes",
    "video_completions",
    "video_complete",
    "video_completed",
    "completed_views"
  );
  const conversionValue = getSherlocMetricNumber(metrics, "conversionValue", "conversion_value", "conversion_value_usd");
  const displayedMetrics = {
    ...metrics,
    spend: displayedSpend,
    impressions,
    clicks,
    engagements,
    conversions,
    conversionValue,
    videoViews,
    videoStarted,
    videoCompleted,
    ctr: clicks > 0 && impressions > 0 ? (clicks / impressions) * 100 : null,
    cpc: displayedSpend > 0 && clicks > 0 ? displayedSpend / clicks : null,
    cpm: displayedSpend > 0 && impressions > 0 ? (displayedSpend / impressions) * 1000 : null,
    er: engagements > 0 && impressions > 0 ? (engagements / impressions) * 100 : null,
    vvr: videoViews > 0 && impressions > 0 ? (videoViews / impressions) * 100 : null,
    videoCompletionRate:
      videoCompleted > 0 && videoStarted > 0 ? (videoCompleted / videoStarted) * 100 : null,
    cpa: displayedSpend > 0 && conversions > 0 ? displayedSpend / conversions : null,
    cvr: conversions > 0 && clicks > 0 ? (conversions / clicks) * 100 : null,
    spendBasis: metrics?.spendBasis || metrics?.spend_basis || "client_facing",
    reportingWindow:
      metrics?.reportingWindow ||
      metrics?.reporting_window ||
      metrics?.window ||
      metrics?.timeframe ||
      "canonical_current",
  };

  if (process.env.NODE_ENV === "development") {
    [
      ["cpc", displayedMetrics.cpc],
      ["cpm", displayedMetrics.cpm],
      ["ctr", displayedMetrics.ctr],
    ].forEach(([field, expected]) => {
      const provided = getSherlocMetricNumber(metrics, field);
      if (!Number.isFinite(provided) || !Number.isFinite(expected)) return;
      const tolerance = field === "ctr" ? 0.05 : 0.01;
      if (Math.abs(provided - expected) > tolerance) {
        console.warn("[SherlocReports] Platform KPI display mismatch", {
          field,
          provided,
          expected,
          spendBasis: displayedMetrics.spendBasis,
          reportingWindow: displayedMetrics.reportingWindow,
        });
      }
    });
  }

  return displayedMetrics;
};

const addSherlocAggregateMetric = (totals, present, key, value) => {
  if (!Number.isFinite(value)) return;
  totals[key] = (Number.isFinite(totals[key]) ? totals[key] : 0) + value;
  present[key] = true;
};

const deriveSherlocAggregateMetricsFromPlatformChannels = (...responses) => {
  const selectedChannels = {};

  responses.forEach((response) => {
    const map = buildSherlocCanonicalChannelMap(response);
    Object.entries(map || {}).forEach(([rawKey, channel]) => {
      const key = canonicalizeSherlocMetricChannelKey(rawKey);
      if (!key || SHERLOC_PAID_MEDIA_AGGREGATE_EXCLUDED_KEYS.has(key)) return;
      if (selectedChannels[key]) return;

      const metrics = extractSherlocCanonicalPlatformMetrics(channel);
      if (!hasSherlocCanonicalMetricPayload(metrics) && !hasSharedUsableMetrics(metrics)) return;
      selectedChannels[key] = metrics;
    });
  });

  const totals = {};
  const present = {};

  Object.values(selectedChannels).forEach((channel) => {
    const spend = getSherlocMetricNumber(
      channel,
      "clientFacingSpend",
      "client_facing_spend",
      "billedSpend",
      "billed_spend",
      "reportSpend",
      "report_spend",
      "spend",
      "spend_usd"
    );
    if (Number.isFinite(spend)) {
      addSherlocAggregateMetric(totals, present, "spend", spend);
      addSherlocAggregateMetric(totals, present, "clientFacingSpend", spend);
    }
    addSherlocAggregateMetric(
      totals,
      present,
      "impressions",
      getSherlocMetricNumber(channel, "impressions", "reportImpressions", "report_impressions")
    );
    addSherlocAggregateMetric(
      totals,
      present,
      "clicks",
      getSherlocMetricNumber(channel, "clicks", "reportClicks", "report_clicks")
    );
    addSherlocAggregateMetric(
      totals,
      present,
      "engagements",
      getSherlocMetricNumber(channel, "engagements", "actions")
    );
    addSherlocAggregateMetric(
      totals,
      present,
      "video_views",
      getSherlocMetricNumber(channel, "video_views", "videoViews", "views")
    );
    addSherlocAggregateMetric(
      totals,
      present,
      "video_started",
      getSherlocMetricNumber(
        channel,
        "video_started",
        "videoStarts",
        "video_start",
        "video_starts"
      )
    );
    addSherlocAggregateMetric(
      totals,
      present,
      "video_completed",
      getSherlocMetricNumber(
        channel,
        "video_completed",
        "videoCompletions",
        "videoCompletes",
        "video_complete",
        "completed_views",
        "completedViews",
        "completions"
      )
    );
    addSherlocAggregateMetric(
      totals,
      present,
      "conversions",
      getSherlocMetricNumber(channel, "conversions")
    );
    addSherlocAggregateMetric(
      totals,
      present,
      "conversion_value",
      getSherlocMetricNumber(
        channel,
        "conversion_value_usd",
        "conversionValueUsd",
        "conversion_value",
        "conversionValue"
      )
    );
  });

  /*
   * The channels these totals were summed from ride along, for the same reason
   * they do on the canonical branch above: the sum is only the numerator pool,
   * and the eligibility engine needs the per-channel rows to decide which of
   * them may enter a blended rate's denominator — and to fold the qualified
   * video total, which no sum of totals can produce.
   */
  const metrics = computeReportingMetrics(
    totals,
    Object.keys(selectedChannels).length ? selectedChannels : null
  );
  if (!metrics || !hasSharedUsableMetrics(metrics)) return null;

  const derived = {
    ...metrics,
    spend: present.spend ? metrics.spend : null,
    clientFacingSpend: present.spend ? metrics.clientFacingSpend : null,
    rawSpend: present.spend ? metrics.rawSpend : null,
    impressions: present.impressions ? metrics.impressions : null,
    clicks: present.clicks ? metrics.clicks : null,
    engagements: present.engagements ? metrics.engagements : null,
    videoViews: present.video_views ? metrics.videoViews : null,
    conversions: present.conversions ? metrics.conversions : null,
    conversionValue: present.conversion_value ? metrics.conversionValue : null,
    ctr: present.clicks && present.impressions ? metrics.ctr : null,
    cpc: present.spend && present.clicks ? metrics.cpc : null,
    cpm: present.spend && present.impressions ? metrics.cpm : null,
    er: present.engagements && present.impressions ? metrics.er : null,
    vvr: present.video_views && present.impressions ? metrics.vvr : null,
    hasCanonicalMetrics: true,
    canonicalReportingContract: "canonical_reporting_v1",
    derivedFromPlatformMetrics: true,
    derivedPlatformKeys: Object.keys(selectedChannels),
  };

  return hasSharedUsableMetrics(derived) ? derived : null;
};

const SHERLOC_PLATFORM_METRIC_KEY_ALIASES = {
  yahoo_dsp: ["yahoo_dsp", "yahoo", "yahoodsp"],
  viant: ["viant", "viant_dsp", "viantdsp"],
  facebook: ["facebook", "fb", "meta_facebook"],
  instagram: ["instagram", "ig", "meta_instagram"],
  youtube: ["youtube", "google_youtube"],
  ctv: ["ctv", "connected_tv", "stackadapt_ctv"],
  display: ["display", "programmatic_display", "stackadapt_display"],
  google_analytics: ["google_analytics", "ga4", "google_analytics_4"],
};

const getSherlocPlatformMetricCandidateKeys = (platformKey) => {
  const canonicalKey = canonicalizeSherlocMetricChannelKey(platformKey);
  if (!canonicalKey) return [];
  const keys = new Set([String(platformKey || "").trim(), canonicalKey]);
  (SHERLOC_PLATFORM_METRIC_KEY_ALIASES[canonicalKey] || []).forEach((key) => {
    keys.add(key);
    const normalized = canonicalizeSherlocMetricChannelKey(key);
    if (normalized) keys.add(normalized);
  });
  return Array.from(keys).filter(Boolean);
};

const buildSherlocPlatformCardMap = (cards = []) => {
  if (!Array.isArray(cards)) return {};
  return cards.reduce((acc, card) => {
    if (!isPlainObject(card)) return acc;
    const rawKey =
      card.key || card.platformKey || card.platform_key || card.platform || card.channel || card.name;
    const key = canonicalizeSherlocMetricChannelKey(rawKey);
    if (!key) return acc;
    acc[key] = {
      ...(acc[key] || {}),
      ...card,
      ...(isPlainObject(card.metrics) ? card.metrics : {}),
    };
    return acc;
  }, {});
};

const addSherlocPlatformMetricMap = (maps, label, path, map) => {
  if (!isPlainObject(map) || !Object.keys(map).length) return;
  maps.push({ label, path, map });
};

const collectSherlocPlatformMetricMaps = (source = {}) => {
  const root = source?.value || source;
  if (!isPlainObject(root)) return [];
  const label = source?.label || "source";
  const maps = [];

  addSherlocPlatformMetricMap(
    maps,
    label,
    `${label}.platformPerformanceCards`,
    buildSherlocPlatformCardMap(root.platformPerformanceCards || root.platform_performance_cards)
  );
  addSherlocPlatformMetricMap(maps, label, `${label}.platformPerformanceByChannel`, root.platformPerformanceByChannel);
  addSherlocPlatformMetricMap(
    maps,
    label,
    `${label}.platform_performance_by_channel`,
    root.platform_performance_by_channel
  );
  addSherlocPlatformMetricMap(maps, label, `${label}.canonicalMetrics.summary.byChannel`, root.canonicalMetrics?.summary?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.canonicalMetrics.byChannel`, root.canonicalMetrics?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.canonicalSummary.byChannel`, root.canonicalSummary?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.canonicalAggregate.byChannel`, root.canonicalAggregate?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.currentKpis.byChannel`, root.currentKpis?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.aggregate.byChannel`, root.aggregate?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.summary.byChannel`, root.summary?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.data.canonicalMetrics.summary.byChannel`, root.data?.canonicalMetrics?.summary?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.data.canonicalSummary.byChannel`, root.data?.canonicalSummary?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.data.aggregate.byChannel`, root.data?.aggregate?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.result.canonicalMetrics.summary.byChannel`, root.result?.canonicalMetrics?.summary?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.result.canonicalSummary.byChannel`, root.result?.canonicalSummary?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.result.aggregate.byChannel`, root.result?.aggregate?.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.byChannel`, root.byChannel);
  addSherlocPlatformMetricMap(maps, label, `${label}.channels`, root.channels);
  addSherlocPlatformMetricMap(maps, label, `${label}.platforms`, root.platforms);
  addSherlocPlatformMetricMap(maps, label, `${label}.canonicalChannelMap`, buildSherlocCanonicalChannelMap(root));

  return maps;
};

const findSherlocPlatformMetricMatch = (map, candidateKeys) => {
  if (!isPlainObject(map)) return null;
  for (const key of candidateKeys) {
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      return { rawKey: key, channel: map[key] };
    }
  }

  for (const [rawKey, channel] of Object.entries(map)) {
    const normalized = canonicalizeSherlocMetricChannelKey(rawKey);
    if (candidateKeys.includes(normalized)) {
      return { rawKey, channel };
    }
  }

  return null;
};

const resolvePlatformMetricsForCard = (platformKey, sources = {}) => {
  const canonicalKey = canonicalizeSherlocMetricChannelKey(platformKey);
  const candidateKeys = getSherlocPlatformMetricCandidateKeys(platformKey);
  const debug = {
    platformKey,
    canonicalKey,
    candidateKeys,
    checked: [],
    rawByChannelObjectKeys: [],
    matchedPath: null,
    matchedRawKey: null,
    matchedChannelObject: null,
  };

  const sourceGroups = [
    { label: "current", values: sources.current || [] },
    { label: "fallback", values: sources.fallback || [] },
  ];

  for (const group of sourceGroups) {
    for (const source of group.values) {
      const maps = collectSherlocPlatformMetricMaps(source);
      for (const candidateMap of maps) {
        const rawKeys = Object.keys(candidateMap.map || {});
        debug.checked.push({
          group: group.label,
          source: candidateMap.label,
          path: candidateMap.path,
          keys: rawKeys,
        });
        if (candidateMap.path.endsWith(".byChannel")) {
          debug.rawByChannelObjectKeys.push({
            group: group.label,
            path: candidateMap.path,
            keys: rawKeys,
          });
        }

        const match = findSherlocPlatformMetricMatch(candidateMap.map, candidateKeys);
        if (!match || !isPlainObject(match.channel)) continue;

        const extracted = extractSherlocCanonicalPlatformMetrics(match.channel);
        if (!hasSherlocCanonicalMetricPayload(extracted) && !hasSharedUsableMetrics(extracted)) {
          continue;
        }

        const metrics = {
          ...extracted,
          resolvedCanonicalKey: canonicalKey,
          rawCanonicalKey: match.rawKey,
          rawChannelObject: match.channel,
          platformMetricSource: candidateMap.path,
          platformMetricSourceGroup: group.label,
        };
        debug.matchedPath = candidateMap.path;
        debug.matchedRawKey = match.rawKey;
        debug.matchedChannelObject = match.channel;
        return { metrics, debug };
      }
    }
  }

  return { metrics: null, debug };
};

const buildSherlocCardInventoryKeys = ({
  aggregate = null,
  approvedChannelKeys = new Set(),
  liveByChannel = {},
  platformInventory = [],
  resolvedAdvertiser = null,
  googleAnalyticsChannel = null,
  campaignPlatformScope = null,
} = {}) => {
  const keys = new Set();
  // Media inventory is admitted by campaign membership only. Approved keys, live signal,
  // aggregate scope and reporting availability can each name a platform, but none of them
  // may put one on the page unless the selected campaign includes it. GA4 is measurement,
  // not campaign media, and is admitted separately below.
  const add = (rawKey) => {
    const key = canonicalizeChannelKey(rawKey);
    if (!key || key === "overall") return;
    if (!isSherlocCampaignScopedPlatformKey(campaignPlatformScope, key)) return;
    keys.add(key);
  };

  approvedChannelKeys?.forEach?.(add);

  (Array.isArray(platformInventory) ? platformInventory : []).forEach((platform) => {
    add(platform?.key || platform?.platform || platform?.label || platform?.name);
  });

  Object.entries(liveByChannel || {}).forEach(([rawKey, channel]) => {
    if (isLiveSherlocChannel(channel) || hasSharedUsableMetrics(channel)) add(rawKey);
  });

  (aggregate?.platformScope?.keys || []).forEach(add);
  Object.keys(aggregate?.platforms || {}).forEach(add);
  Object.keys(aggregate?.byChannel || {}).forEach(add);

  if (googleAnalyticsChannel || getGoogleAnalyticsAdvertiserUrl(resolvedAdvertiser)) {
    keys.add("google_analytics");
  }

  return keys;
};

const isSherlocPerformanceCardVisible = ({
  key,
  channel,
  inventoryKeys,
  approvedChannelKeys,
  liveByChannel = {},
  campaignPlatformScope = null,
}) => {
  const normalizedKey = canonicalizeChannelKey(key);
  if (!normalizedKey || normalizedKey === "overall") return false;
  if (normalizedKey === "google_analytics") return false;

  // Campaign membership decides which platforms are ELIGIBLE to render. Everything below
  // decides the STATE of an eligible platform — READY / LIVE / INGEST / NOT ACTIVE.
  if (!isSherlocCampaignScopedPlatformKey(campaignPlatformScope, normalizedKey)) return false;

  if (normalizedKey === "cinema") {
    return (
      isLiveSherlocChannel(channel) ||
      isLiveSherlocChannel(liveByChannel?.[normalizedKey]) ||
      hasMeaningfulDeliveryMetrics(channel)
    );
  }

  if (inventoryKeys?.has?.(normalizedKey)) return true;

  return isSherlocClientChannelEligible({
    key: normalizedKey,
    channel,
    approvedChannelKeys,
    liveByChannel,
  });
};

const computeResolvedTotals = (resolvedByChannel) => {
  let totals = {};
  let cinemaOnly = {};

  for (const [key, ch] of Object.entries(resolvedByChannel || {})) {
    if (!ch) continue;

    if (key === "cinema") {
      const cinemaSlice = {
        ticket_sales:
          ch.ticket_sales ?? ch.ticketSales ?? ch.ticket_sales_usd ?? ch.ticketSalesUsd,
        ticketSales: ch.ticketSales,
        ticket_sales_usd: ch.ticket_sales_usd,
        ticketSalesUsd: ch.ticketSalesUsd,

        total_booked_theaters: ch.total_booked_theaters ?? ch.totalBookedTheaters,
        totalBookedTheaters: ch.totalBookedTheaters,
        total_circuits: ch.total_circuits ?? ch.totalCircuits,
        totalCircuits: ch.totalCircuits,
        total_cities: ch.total_cities ?? ch.totalCities,
        totalCities: ch.totalCities,
        total_states: ch.total_states ?? ch.totalStates,
        totalStates: ch.totalStates,

        conversion_value:
          ch.conversion_value ??
          ch.conversionValue ??
          ch.conversion_value_usd ??
          ch.conversionValueUsd,
        conversionValue: ch.conversionValue,
        conversion_value_usd: ch.conversion_value_usd,
        conversionValueUsd: ch.conversionValueUsd,
      };
      cinemaOnly = mergeChannelAgg(cinemaOnly, cinemaSlice);
      continue;
    }

    totals = mergeChannelAgg(totals, ch);
  }

  totals = mergeChannelAgg(totals, cinemaOnly);
  return totals;
};

// ------------------------------------------------------------
// ✅ Meta LIVE aggregation helpers
// ------------------------------------------------------------
const toNumSafe = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n) ? n : 0;
};

const sumActions = (actionsArr, actionTypes = []) => {
  if (!Array.isArray(actionsArr) || actionTypes.length === 0) return 0;
  const wanted = new Set(actionTypes.map((t) => String(t).toLowerCase()));
  let total = 0;

  for (const a of actionsArr) {
    const t = String(a?.action_type ?? a?.actionType ?? "").toLowerCase();
    if (!t || !wanted.has(t)) continue;
    total += toNumSafe(a?.value);
  }
  return total;
};

const buildMetaPublisherPlatformAgg = (payload, options = {}) => {
  const selectedCampaignId =
    options?.campaignId &&
    String(options.campaignId).trim() &&
    String(options.campaignId) !== "auto"
      ? String(options.campaignId).trim()
      : null;

  const rowsRaw =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(payload?.data?.data) && payload.data.data) ||
    (Array.isArray(payload?.insights?.data) && payload.insights.data) ||
    (Array.isArray(payload?.insightsArray) && payload.insightsArray) ||
    [];

  const rows = (() => {
    if (!selectedCampaignId) return rowsRaw;

    const hasCampaignField = rowsRaw.some(
      (r) => r?.campaign_id != null || r?.campaignId != null || r?.campaign?.id != null
    );
    if (!hasCampaignField) return rowsRaw;

    return rowsRaw.filter((r) => {
      const cid = r?.campaign_id ?? r?.campaignId ?? r?.campaign?.id ?? null;
      if (cid == null) return false;
      return String(cid) === selectedCampaignId;
    });
  })();

  const map = {};

  const normPlat = (p) => {
    const s = String(p ?? "").trim().toLowerCase();
    if (!s) return "";
    if (s === "audience_network" || (s.includes("audience") && s.includes("network")))
      return "audience_network";
    if (s === "messenger") return "messenger";
    if (s === "facebook") return "facebook";
    if (s === "instagram") return "instagram";
    return s.replace(/\s+/g, "_");
  };

  if (rows.length > 0) {
    rows.forEach((r) => {
      const plat = normPlat(r?.publisher_platform ?? r?.publisherPlatform ?? r?.platform);
      if (!plat) return;

      const spend = toNumSafe(r?.spend ?? r?.spend_usd);
      const impressions = toNumSafe(r?.impressions);
      const clicks = toNumSafe(r?.clicks);

      const conversions = Math.max(
        toNumSafe(r?.conversions),
        toNumSafe(r?.total_conversions),
        sumActions(r?.actions, [
          "offsite_conversion",
          "omni_purchase",
          "purchase",
          "lead",
          "complete_registration",
        ])
      );

      const engagements = Math.max(
        toNumSafe(r?.inline_post_engagement),
        toNumSafe(r?.post_engagement),
        toNumSafe(r?.engagements),
        sumActions(r?.actions, [
          "post_engagement",
          "page_engagement",
          "post_reaction",
          "comment",
          "post_save",
          "share",
        ])
      );

      const videoViews = Math.max(
        toNumSafe(r?.video_views),
        toNumSafe(r?.videoView),
        sumActions(r?.video_3_sec_watched_actions, ["video_view"]),
        sumActions(r?.video_play_actions, ["video_view"]),
        sumActions(r?.actions, ["video_view"])
      );

      const videoStarted = Math.max(
        toNumSafe(r?.video_started),
        toNumSafe(r?.video_starts),
        sumActions(r?.video_play_actions, ["video_play"]),
        sumActions(r?.actions, ["video_play"])
      );

      const videoCompleted = Math.max(
        toNumSafe(r?.video_completed),
        toNumSafe(r?.completed_views),
        sumActions(r?.video_p100_watched_actions, ["video_view"]),
        sumActions(r?.actions, ["video_complete"])
      );

      const incoming = {
        spend,
        impressions,
        clicks,
        conversions,
        engagements,
        video_views: videoViews,
        video_started: videoStarted,
        video_completed: videoCompleted,
        data_source: "meta_live",
      };

      map[plat] = map[plat] ? mergeChannelAgg(map[plat], incoming) : incoming;
    });

    return map;
  }

  const tbp =
    (isPlainObject(payload?.totalsByPlatform) && payload.totalsByPlatform) ||
    (isPlainObject(payload?.totals_by_platform) && payload.totals_by_platform) ||
    null;

  if (tbp) {
    for (const [rawPlat, obj] of Object.entries(tbp)) {
      const plat = normPlat(rawPlat);
      if (!plat || plat === "other") continue;

      const incoming = {
        spend: toNumSafe(obj?.spend ?? obj?.spend_usd),
        impressions: toNumSafe(obj?.impressions),
        clicks: toNumSafe(obj?.clicks),
        conversions: toNumSafe(obj?.conversions),
        engagements: toNumSafe(obj?.engagements),
        video_views: toNumSafe(obj?.video_views),
        video_started: toNumSafe(obj?.video_starts ?? obj?.video_started),
        video_completed: toNumSafe(obj?.video_completes ?? obj?.video_completed),
        data_source: "meta_live",
      };

      map[plat] = map[plat] ? mergeChannelAgg(map[plat], incoming) : incoming;
    }
  }

  return map;
};

const buildStackAdaptLiveByChannel = (payload) => {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  const map = {};

  rows.forEach((row) => {
    const key = canonicalizeChannelKey(
      row?.channel || row?.source_channel || row?.campaign_type || row?.platform || ""
    );
    if (!key || key === "overall" || key === "google_analytics") return;

    const incoming = {
      spend: toNumSafe(row?.spend ?? row?.cost ?? row?.spend_usd),
      impressions: toNumSafe(row?.impressions),
      clicks: toNumSafe(row?.clicks),
      conversions: toNumSafe(row?.conversions),
      engagements: Math.max(
        toNumSafe(row?.engagements),
        toNumSafe(row?.page_starts),
        toNumSafe(row?.audio_starts),
        toNumSafe(row?.clicks)
      ),
      video_views: Math.max(
        toNumSafe(row?.video_views),
        toNumSafe(row?.vcomp_0),
        toNumSafe(row?.video_started)
      ),
      video_started: Math.max(
        toNumSafe(row?.video_started),
        toNumSafe(row?.vcomp_0),
        toNumSafe(row?.video_views)
      ),
      video_completed: Math.max(
        toNumSafe(row?.video_completed),
        toNumSafe(row?.vcomp_95)
      ),
      data_source: "stackadapt_live",
      provider: "stackadapt",
    };

    if (!hasSharedUsableMetrics(incoming)) return;
    map[key] = map[key] ? mergeChannelAgg(map[key], incoming) : incoming;
  });

  return map;
};

const buildMetaCampaignListFromInsights = (payload) => {
  const rows =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(payload?.data?.data) && payload.data.data) ||
    [];

  const out = [];

  rows.forEach((r) => {
    const id = r?.campaign_id ?? r?.campaignId ?? r?.id ?? r?.campaign?.id;
    const name = r?.campaign_name ?? r?.campaignName ?? r?.name ?? r?.campaign?.name;

    const spend = toNumber(r?.spend ?? r?.spend_usd ?? 0);
    const impressions = toNumber(r?.impressions ?? 0);

    if (!id && !name) return;

    out.push({
      id: id ? String(id) : "",
      name: name ? String(name) : id ? `Campaign ${String(id).slice(-6)}` : "Campaign",
      spend: Number.isFinite(spend) ? spend : 0,
      impressions: Number.isFinite(impressions) ? impressions : 0,
      status: "",
    });
  });

  const seen = new Set();
  const deduped = [];
  for (const c of out) {
    const key = c.id ? `id:${c.id}` : `name:${norm(c.name)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(c);
  }

  deduped.sort((a, b) => {
    const aActive = (a.spend > 0 ? 1 : 0) + (a.impressions > 0 ? 1 : 0);
    const bActive = (b.spend > 0 ? 1 : 0) + (b.impressions > 0 ? 1 : 0);
    if (bActive !== aActive) return bActive - aActive;
    return String(a.name).localeCompare(String(b.name));
  });

  return deduped;
};

// ------------------------------------------------------------
// ✅ Google Analytics normalization / fetching
// ------------------------------------------------------------
const pickFirstObject = (...vals) => {
  for (const v of vals) {
    if (isPlainObject(v)) return v;
  }
  return null;
};



const GA_AUDIENCE_AVAILABLE_COPY =
  "Google Analytics measures site activity and post-click engagement across users, sessions, engaged sessions, event activity, and audience breakdowns.";
/* Device breakdowns present, age/gender absent. The reason matters: GA4 sets
   metadata.subjectToThresholding when it is WITHHOLDING those rows to protect
   privacy, which is a durable state, not a queue. Saying "may take time to
   populate" told operators to wait for something that does not arrive on its
   own — see backend #161, where the same mis-classification was fixed at the
   source. The rows return when eligible traffic clears Google's threshold. */
const GA_DEVICE_BREAKDOWN_AVAILABLE_COPY =
  "Google Analytics measures site activity and post-click engagement across users, sessions, engaged sessions, and event activity. Device breakdowns are available for this reporting window. Google is withholding age and gender rows for privacy: the demographic query is subject to Google's thresholding, and those rows appear once eligible traffic clears its threshold.";
const GA_AGGREGATE_ONLY_COPY =
  "Google Analytics measures site activity and post-click engagement across users, sessions, engaged sessions, and event activity.";
const STACKADAPT_INSIGHT_FALLBACK_TITLES = {
  audience: "Performance by Audience",
  demographics: "Performance by Demographics Segment",
  device: "Performance by Device",
  geo: "Performance by Geo",
  supplySource: "Performance by Supply Source",
  category: "Performance by Category",
};
const CLIENT_AUDIENCE_INSIGHTS_UNAVAILABLE_COPY =
  "Audience insight rows are not available for this campaign window yet.";

const toClientSafeAudienceInsightCopy = (value) => {
  const text = String(value || "").trim();
  if (!text) return CLIENT_AUDIENCE_INSIGHTS_UNAVAILABLE_COPY;
  const safeText = text
    .replace(/\bStackAdapt\s+audience\s+insights\b/gi, "Audience Insights")
    .replace(/\bStackAdapt\s+audience\s+insight\b/gi, "Audience Insight")
    .replace(/\bStackAdapt\s+/gi, "")
    .replace(/\bStackAdapt\b/gi, "Programmatic")
    .replace(/campaign\/window/gi, "campaign window")
    .trim();
  return safeText.replace(/^audience/i, "Audience");
};

const normalizeGoogleAnalyticsDimensionRows = (
  input,
  { dimensionKeys = [], defaultMetric = "users" } = {}
) => {
  const sourceRows = Array.isArray(input)
    ? input
    : Array.isArray(input?.rows)
    ? input.rows
    : Array.isArray(input?.items)
    ? input.items
    : Array.isArray(input?.data)
    ? input.data
    : isPlainObject(input)
    ? Object.entries(input).map(([label, value]) =>
        isPlainObject(value) ? { label, ...value } : { label, [defaultMetric]: value }
      )
    : [];

  return sourceRows
    .map((row) => {
      if (!isPlainObject(row)) return null;
      const label = String(
        pickFirst(...dimensionKeys.map((key) => row?.[key]), row?.label, row?.name, row?.dimension)
          ?? ""
      ).trim();
      if (!label) return null;

      const users = toNumSafe(
        pickFirst(row.users, row.totalUsers, row.activeUsers, row.active_users, row.value)
      );
      const sessions = toNumSafe(pickFirst(row.sessions, row.sessionCount));
      const engagedSessions = toNumSafe(
        pickFirst(row.engagedSessions, row.engaged_sessions)
      );
      const eventCount = toNumSafe(pickFirst(row.eventCount, row.event_count, row.events));
      const avgSessionDuration = toNumSafe(
        pickFirst(
          row.avgSessionDuration,
          row.averageSessionDuration,
          row.avg_session_duration,
          row.average_session_duration
        )
      );
      const providedEngagementRate = toNumber(
        pickFirst(row.engagementRate, row.engagement_rate)
      );
      const engagementRate =
        Number.isFinite(providedEngagementRate) && providedEngagementRate > 1
          ? providedEngagementRate
          : Number.isFinite(providedEngagementRate) && providedEngagementRate >= 0
          ? providedEngagementRate * 100
          : computeRatePercent(engagedSessions, sessions);
      const eventsPerUser = users > 0 && eventCount > 0 ? eventCount / users : null;
      const sessionsPerUser = users > 0 && sessions > 0 ? sessions / users : null;

      const hasMetric = [
        users,
        sessions,
        engagedSessions,
        eventCount,
        avgSessionDuration,
        engagementRate,
      ].some((value) => Number.isFinite(value) && value > 0);

      if (!hasMetric) return null;

      return {
        label,
        users,
        sessions,
        engagedSessions,
        engagementRate,
        eventCount,
        avgSessionDuration,
        eventsPerUser,
        sessionsPerUser,
      };
    })
    .filter(Boolean);
};

const pickGoogleAnalyticsCollection = (root, data, summary, ...keys) => {
  const ga4 = isPlainObject(root?.ga4) ? root.ga4 : null;
  const dataGa4 = isPlainObject(data?.ga4) ? data.ga4 : null;
  const overview = isPlainObject(root?.overview) ? root.overview : null;
  const dataOverview = isPlainObject(data?.overview) ? data.overview : null;
  const demographics = pickFirstObject(
    root?.demographics,
    data?.demographics,
    summary?.demographics,
    ga4?.demographics,
    dataGa4?.demographics,
    overview?.demographics,
    dataOverview?.demographics
  );

  for (const key of keys) {
    const value = pickFirst(
      root?.[key],
      data?.[key],
      summary?.[key],
      ga4?.[key],
      dataGa4?.[key],
      overview?.[key],
      dataOverview?.[key],
      demographics?.[key]
    );
    if (value) return value;
  }

  return null;
};

const pickGoogleAnalyticsDemographicsStatus = (root, data, summary) => {
  const ga4 = isPlainObject(root?.ga4) ? root.ga4 : null;
  const dataGa4 = isPlainObject(data?.ga4) ? data.ga4 : null;
  const overview = isPlainObject(root?.overview) ? root.overview : null;
  const dataOverview = isPlainObject(data?.overview) ? data.overview : null;
  const demographics = pickFirstObject(
    root?.demographics,
    data?.demographics,
    summary?.demographics,
    ga4?.demographics,
    dataGa4?.demographics,
    overview?.demographics,
    dataOverview?.demographics
  );

  return pickFirstObject(
    root?.gaDemographicsStatus,
    data?.gaDemographicsStatus,
    summary?.gaDemographicsStatus,
    root?.demographicStatus,
    data?.demographicStatus,
    summary?.demographicStatus,
    demographics
  );
};

const extractGoogleAnalyticsSummaryRoot = (payload) => {
  if (!isPlainObject(payload)) return null;

  const root = payload;
  const data = isPlainObject(root?.data) ? root.data : null;

  const directSummary = pickFirstObject(
    root?.summary,
    data?.summary,
    root?.ga4,
    data?.ga4,
    root?.overview,
    data?.overview,
    root?.totals,
    data?.totals
  );

  const nestedSummary = pickFirstObject(
    root?.summary?.overview,
    data?.summary?.overview,
    root?.overview?.overview,
    data?.overview?.overview,
    root?.ga4?.overview,
    data?.ga4?.overview,
    root?.data?.summary?.overview,
    root?.data?.overview?.overview
  );

  const supportingCollections = {
    topSources: pickFirst(
      root?.summary?.topSources,
      data?.summary?.topSources,
      root?.overview?.topSources,
      data?.overview?.topSources,
      root?.ga4?.topSources,
      data?.ga4?.topSources,
      root?.topSources,
      data?.topSources
    ),
    topLandingPages: pickFirst(
      root?.summary?.topLandingPages,
      data?.summary?.topLandingPages,
      root?.overview?.topLandingPages,
      data?.overview?.topLandingPages,
      root?.ga4?.topLandingPages,
      data?.ga4?.topLandingPages,
      root?.topLandingPages,
      data?.topLandingPages
    ),
    topCities: pickFirst(
      root?.summary?.topCities,
      data?.summary?.topCities,
      root?.overview?.topCities,
      data?.overview?.topCities,
      root?.ga4?.topCities,
      data?.ga4?.topCities,
      root?.topCities,
      data?.topCities
    ),
    deviceMix: pickFirst(
      root?.summary?.deviceMix,
      data?.summary?.deviceMix,
      root?.overview?.deviceMix,
      data?.overview?.deviceMix,
      root?.ga4?.deviceMix,
      data?.ga4?.deviceMix,
      root?.deviceMix,
      data?.deviceMix
    ),
    ageBreakdown: pickGoogleAnalyticsCollection(
      root,
      data,
      directSummary,
      "ageBreakdown",
      "age_breakdown",
      "ageGroups",
      "age_groups",
      "byAge",
      "by_age",
      "audienceByAge",
      "usersByAge"
    ),
    genderMix: pickGoogleAnalyticsCollection(
      root,
      data,
      directSummary,
      "genderMix",
      "gender_mix",
      "genderBreakdown",
      "gender_breakdown",
      "byGender",
      "by_gender",
      "usersByGender"
    ),
    demographicsStatus: pickGoogleAnalyticsDemographicsStatus(root, data, directSummary),
  };

  return {
    metricRoot: nestedSummary || directSummary || root,
    supportingCollections,
    rawRoot: root,
  };
};

const buildGoogleAnalyticsChannel = (payload) => {
  if (!payload) return null;

  const extracted = extractGoogleAnalyticsSummaryRoot(payload);
  if (!extracted) return null;

  const summary = isPlainObject(extracted.metricRoot) ? extracted.metricRoot : {};
  const root = extracted.rawRoot || {};

  const users = pickFirst(
    summary?.users,
    summary?.totalUsers,
    summary?.activeUsers,
    summary?.active_users,
    root?.users,
    root?.totalUsers,
    root?.activeUsers,
    root?.active_users
  );

  const newUsers = pickFirst(
    summary?.newUsers,
    summary?.new_users,
    root?.newUsers,
    root?.new_users
  );

  const sessions = pickFirst(summary?.sessions, root?.sessions);

  const engagedSessions = pickFirst(
    summary?.engagedSessions,
    summary?.engaged_sessions,
    root?.engagedSessions,
    root?.engaged_sessions
  );

  const pageViews = pickFirst(
    summary?.pageViews,
    summary?.page_views,
    summary?.screenPageViews,
    summary?.screen_page_views,
    root?.pageViews,
    root?.page_views,
    root?.screenPageViews,
    root?.screen_page_views
  );

  const eventCount = pickFirst(
    summary?.eventCount,
    summary?.event_count,
    root?.eventCount,
    root?.event_count
  );

  const keyEvents = pickFirst(
    summary?.keyEvents,
    summary?.key_events,
    summary?.conversions,
    root?.keyEvents,
    root?.key_events,
    root?.conversions
  );

  const avgSessionDuration = pickFirst(
    summary?.averageSessionDuration,
    summary?.avg_session_duration,
    summary?.avgSessionDuration,
    root?.averageSessionDuration,
    root?.avg_session_duration,
    root?.avgSessionDuration
  );

  const engagementRateInput = pickFirst(
    summary?.engagementRate,
    summary?.engagement_rate,
    root?.engagementRate,
    root?.engagement_rate
  );

  const sumRows = (rows, ...keys) =>
    (Array.isArray(rows) ? rows : []).reduce((total, row) => {
      const value = pickFirst(...keys.map((key) => row?.[key]));
      const numeric = toNumber(value);
      return total + (Number.isFinite(numeric) ? numeric : 0);
    }, 0);

  const topSourceRows = extracted.supportingCollections?.topSources;
  const fallbackSessions = sumRows(topSourceRows, "sessions");
  const fallbackEngagedSessions = sumRows(
    topSourceRows,
    "engagedSessions",
    "engaged_sessions"
  );
  const fallbackEvents = sumRows(topSourceRows, "eventCount", "event_count");

  const normalized = {
    users: toNumSafe(users),
    new_users: toNumSafe(newUsers),
    sessions: Math.max(toNumSafe(sessions), fallbackSessions),
    engaged_sessions: Math.max(toNumSafe(engagedSessions), fallbackEngagedSessions),
    page_views: toNumSafe(pageViews),
    event_count: Math.max(toNumSafe(eventCount), fallbackEvents),
    key_events: toNumSafe(keyEvents),
    avg_session_duration: toNumSafe(avgSessionDuration),
    data_source: "google_analytics_live",

    top_sources: Array.isArray(extracted.supportingCollections?.topSources)
      ? extracted.supportingCollections.topSources
      : [],
    top_landing_pages: Array.isArray(extracted.supportingCollections?.topLandingPages)
      ? extracted.supportingCollections.topLandingPages
      : [],
    top_cities: Array.isArray(extracted.supportingCollections?.topCities)
      ? extracted.supportingCollections.topCities
      : [],
    device_mix: Array.isArray(extracted.supportingCollections?.deviceMix)
      ? extracted.supportingCollections.deviceMix
      : [],
    age_breakdown: normalizeGoogleAnalyticsDimensionRows(
      extracted.supportingCollections?.ageBreakdown,
      {
        dimensionKeys: ["ageRange", "age_range", "ageGroup", "age_group", "userAgeBracket", "age"],
      }
    ),
    gender_breakdown: normalizeGoogleAnalyticsDimensionRows(
      extracted.supportingCollections?.genderMix,
      {
        dimensionKeys: ["gender", "userGender"],
      }
    ),
    device_breakdown: normalizeGoogleAnalyticsDimensionRows(
      extracted.supportingCollections?.deviceMix,
      {
        dimensionKeys: ["deviceCategory", "device_category", "device", "platform"],
      }
    ),
    ga_demographics_status: extracted.supportingCollections?.demographicsStatus || null,
  };

  const computedEngagementRate = computeRatePercent(
    normalized.engaged_sessions,
    normalized.sessions
  );
  const providedEngagementRate = toNumber(engagementRateInput);

  normalized.engagement_rate =
    Number.isFinite(providedEngagementRate) && providedEngagementRate > 1
      ? providedEngagementRate
      : Number.isFinite(providedEngagementRate) &&
        providedEngagementRate >= 0 &&
        providedEngagementRate <= 1
      ? providedEngagementRate * 100
      : computedEngagementRate;

  if (!hasUsableMetrics(normalized)) return null;
  return normalized;
};

const buildGoogleAnalyticsChannelFromCanonicalAggregate = (aggregatePayload) => {
  if (!isPlainObject(aggregatePayload)) return null;
  const siteAnalytics = pickFirstObject(
    aggregatePayload?.siteAnalytics,
    aggregatePayload?.site_analytics,
    aggregatePayload?.summary?.siteAnalytics,
    aggregatePayload?.summary?.site_analytics,
    aggregatePayload?.canonicalReporting?.siteAnalytics,
    aggregatePayload?.canonical_reporting?.siteAnalytics,
    aggregatePayload?.data?.siteAnalytics,
    aggregatePayload?.data?.site_analytics
  );

  return siteAnalytics ? buildGoogleAnalyticsChannel(siteAnalytics) : null;
};


const getGoogleAnalyticsDetailSummary = (dashboard) => {
  if (dashboard?.hasAgeBreakdown || dashboard?.hasGenderBreakdown) {
    return GA_AUDIENCE_AVAILABLE_COPY;
  }
  if (dashboard?.hasDeviceBreakdown) {
    return GA_DEVICE_BREAKDOWN_AVAILABLE_COPY;
  }
  return GA_AGGREGATE_ONLY_COPY;
};

const getGoogleAnalyticsPropertyId = (payload) => {
  if (!isPlainObject(payload)) return null;

  const root = payload;
  const data = isPlainObject(root?.data) ? root.data : null;

  return (
    pickFirst(
      root?.propertyId,
      data?.propertyId,
      root?.summary?.propertyId,
      data?.summary?.propertyId,
      root?.ga4?.propertyId,
      data?.ga4?.propertyId,
      root?.overview?.propertyId,
      data?.overview?.propertyId
    ) || null
  );
};

const normalizeGoogleAnalyticsPayload = (payload) => {
  if (!isPlainObject(payload)) return null;

  const root = payload;
  const data = isPlainObject(root?.data) ? root.data : null;
  const extracted = extractGoogleAnalyticsSummaryRoot(payload);
  const channel = buildGoogleAnalyticsChannel(payload);
  const propertyId = getGoogleAnalyticsPropertyId(payload);
  const backendDiagnostics =
    pickFirstObject(
      root?.ga4Diagnostics,
      data?.ga4Diagnostics,
      root?.ga4Status,
      data?.ga4Status,
      root?.summary?.ga4Diagnostics,
      data?.summary?.ga4Diagnostics
    ) || {};

  const classification =
    backendDiagnostics?.classification ||
    (channel
      ? "connected_live"
      : extracted
      ? propertyId
        ? "connected_empty"
        : "property_missing"
      : "unsupported_payload");

  const hasData =
    typeof backendDiagnostics?.hasData === "boolean" ? backendDiagnostics.hasData : !!channel;

  return {
    raw: payload,
    channel: channel || null,
    diagnostics: {
      classification,
      propertyId: backendDiagnostics?.propertyId || propertyId || null,
      connected:
        typeof backendDiagnostics?.connected === "boolean"
          ? backendDiagnostics.connected
          : Boolean(propertyId),
      supportedPayload:
        typeof backendDiagnostics?.supportedPayload === "boolean"
          ? backendDiagnostics.supportedPayload
          : !!extracted,
      hasData,
      reason:
        backendDiagnostics?.reason ||
        (classification === "connected_live"
          ? "GA4 payload normalized successfully."
          : classification === "connected_empty"
          ? "GA4 payload is valid but contains no activity for the selected context."
          : classification === "property_missing"
          ? "GA4 property mapping is missing for this advertiser."
          : "GA4 payload shape is not currently supported."),
    },
  };
};

const normalizeGoogleAnalyticsConnectionStatus = (payload) => {
  if (!isPlainObject(payload)) return null;

  const root = payload;
  const data = isPlainObject(root?.data) ? root.data : null;
  const status =
    pickFirstObject(root?.status, data?.status, root?.googleAnalyticsStatus, data?.googleAnalyticsStatus) ||
    (isPlainObject(root) && "connected" in root ? root : null);

  if (!isPlainObject(status)) return null;

  const lifecycle = isPlainObject(status?.lifecycle) ? status.lifecycle : {};
  const connected =
    typeof status?.connected === "boolean"
      ? status.connected
      : Boolean(
          lifecycle?.verified ||
            String(status?.oauthStatus || "").toLowerCase() === "ok" ||
            String(status?.oauthStatus || "").toLowerCase() === "expiring"
        );
  const mapped =
    typeof status?.mapped === "boolean"
      ? status.mapped
      : Boolean(lifecycle?.mapped || status?.propertyId);
  const reportingReady =
    typeof status?.reportingReady === "boolean"
      ? status.reportingReady
      : Boolean(lifecycle?.reportingAvailable);

  return {
    raw: payload,
    connected,
    mapped,
    reportingReady,
    oauthStatus: String(status?.oauthStatus || "").toLowerCase(),
    propertyId: status?.propertyId || null,
    propertySource: status?.propertySource || null,
    mappingSource: status?.mappingSource || null,
    credentialSource: status?.credentialSource || null,
    reason: status?.reason || null,
    lifecycle,
  };
};

const GA_STATUS_PRIORITY = {
  connected_live: 5,
  connected_empty: 4,
  property_missing: 3,
  config_missing: 3,
  auth_unavailable: 2,
  unsupported_payload: 1,
  unavailable: 0,
};

const preferGoogleAnalyticsPayload = (current, candidate) => {
  if (!candidate) return current || null;
  if (!current) return candidate;

  const currentRank = GA_STATUS_PRIORITY[current?.diagnostics?.classification] ?? -1;
  const candidateRank = GA_STATUS_PRIORITY[candidate?.diagnostics?.classification] ?? -1;

  return candidateRank > currentRank ? candidate : current;
};

const getGoogleAnalyticsStatusDescriptor = (
  gaPayload,
  gaConnectionStatus,
  { forAi = false, focusedCampaignId, hasFutureApprovedWindow = false, approvedWindowStartLabel = "" } = {}
) => {
  const suffix = forAi ? " for AI" : "";
  const campaignSuffix = focusedCampaignId ? " (campaign context applied)" : "";
  const classification = gaPayload?.diagnostics?.classification || "unavailable";
  const providerConnected = Boolean(gaConnectionStatus?.connected);
  const providerMapped = Boolean(gaConnectionStatus?.mapped);
  const providerReportingReady = Boolean(gaConnectionStatus?.reportingReady);
  const providerAuthIssue = gaConnectionStatus?.oauthStatus === "expired";

  if (!gaPayload && !gaConnectionStatus) {
    return {
      state: "warn",
      message: `GA4 unavailable${suffix} (no response).`,
      channel: null,
      contextConnected: false,
      classification,
      connectionState: "unavailable",
      connected: false,
      mapped: false,
      reportingReady: false,
    };
  }

  if (classification === "connected_live") {
    return {
      state: "ok",
      message: hasFutureApprovedWindow
        ? `GA4 tracking live${suffix}${campaignSuffix} ahead of the approved window.`
        : `GA4 refreshed${suffix}${campaignSuffix}.`,
      detail:
        hasFutureApprovedWindow && approvedWindowStartLabel
          ? `Live now. Approved window starts ${approvedWindowStartLabel}.`
          : hasFutureApprovedWindow
          ? "Live now. Approved window has not started yet."
          : "",
      channel: gaPayload?.channel || null,
      contextConnected: true,
      classification,
      connectionState: hasFutureApprovedWindow ? "preflight_live" : "live",
      connected: true,
      mapped: true,
      reportingReady: true,
    };
  }

  if (providerConnected && providerAuthIssue) {
    return {
      state: "warn",
      message: `GA4 connected${suffix}, but the stored authorization needs attention.`,
      channel: null,
      contextConnected: false,
      classification,
      connectionState: "auth",
      connected: true,
      mapped: providerMapped,
      reportingReady: providerReportingReady,
    };
  }

  if (providerConnected && !providerMapped) {
    return {
      state: "warn",
      message: `GA4 connected${suffix}, but property mapping still needs to be completed.`,
      channel: null,
      contextConnected: false,
      classification:
        classification === "config_missing" || classification === "property_missing"
          ? classification
          : "property_missing",
      connectionState: "partial",
      connected: true,
      mapped: false,
      reportingReady: false,
    };
  }

  if (providerConnected) {
    return {
      state: "warn",
      message:
        classification === "connected_empty"
          ? `GA4 connection verified${suffix}${campaignSuffix}.`
          : `GA4 connection verified${suffix}${campaignSuffix}.`,
      detail:
        classification === "connected_empty"
          ? "Tracking is ready. No activity has been returned yet for the selected approved window."
          : "Connection is verified and ready for campaign-window analysis.",
      channel: null,
      contextConnected: false,
      classification,
      connectionState: "ready",
      connected: true,
      mapped: providerMapped,
      reportingReady: providerReportingReady,
    };
  }

  if (classification === "auth_unavailable") {
    return {
      state: "warn",
      message: `GA4 unavailable${suffix} (auth/integration issue).`,
      channel: null,
      contextConnected: false,
      classification,
      connectionState: "auth",
      connected: false,
      mapped: false,
      reportingReady: false,
    };
  }

  if (classification === "config_missing" || classification === "property_missing") {
    return {
      state: "warn",
      message: `GA4 setup incomplete${suffix}.`,
      channel: null,
      contextConnected: false,
      classification,
      connectionState: "setup",
      connected: false,
      mapped: false,
      reportingReady: false,
    };
  }

  return {
    state: "warn",
    message: `GA4 unavailable${suffix} (unsupported payload shape).`,
    channel: null,
    contextConnected: false,
    classification,
    connectionState: "unavailable",
    connected: false,
    mapped: false,
    reportingReady: false,
  };
};

const getGoogleAnalyticsPillPresentation = (gaStatus) => {
  const connectionState = String(gaStatus?.connectionState || "").trim().toLowerCase();

  if (gaStatus?.state === "loading" || connectionState === "checking") {
    return {
      label: "CHECKING",
      className: "sherlocreports-source-pill sherlocreports-source-pill-ready",
    };
  }

  if (gaStatus?.contextConnected) {
    return {
      label: connectionState === "preflight_live" ? "LIVE SIGNAL" : "LIVE",
      className: "sherlocreports-source-pill sherlocreports-source-pill-live",
    };
  }

  if (connectionState === "ready") {
    return {
      label: "READY",
      className: "sherlocreports-source-pill sherlocreports-source-pill-ready",
    };
  }

  if (connectionState === "partial") {
    return {
      label: "PARTIAL",
      className: "sherlocreports-source-pill sherlocreports-source-pill-partial",
    };
  }

  if (connectionState === "setup") {
    return {
      label: "SETUP",
      className: "sherlocreports-source-pill sherlocreports-source-pill-setup",
    };
  }

  if (connectionState === "auth") {
    return {
      label: "AUTH",
      className: "sherlocreports-source-pill sherlocreports-source-pill-setup",
    };
  }

  return {
    label: "UNAVAILABLE",
    className: "sherlocreports-source-pill sherlocreports-source-pill-setup",
  };
};

const isGoogleAnalyticsPillReadyLabel = (label) =>
  ["LIVE", "LIVE SIGNAL", "READY"].includes(String(label || "").trim().toUpperCase());

const isGoogleAnalyticsMeasurementReadyStatus = (gaStatus, pillLabel = "") => {
  const connectionState = String(gaStatus?.connectionState || "").trim().toLowerCase();
  return Boolean(
    isGoogleAnalyticsPillReadyLabel(pillLabel) ||
      gaStatus?.contextConnected ||
      ["live", "preflight_live", "ready"].includes(connectionState) ||
      (gaStatus?.connected && (gaStatus?.mapped || gaStatus?.reportingReady))
  );
};

const isGoogleAnalyticsStatusVisuallyLive = (gaStatus) => {
  const connectionState = String(gaStatus?.connectionState || "").trim().toLowerCase();
  return Boolean(
    gaStatus?.contextConnected &&
      (connectionState === "live" ||
        connectionState === "preflight_live" ||
        gaStatus?.state === "ok")
  );
};

const getGoogleAnalyticsAdvertiserUrl = (adv) => {
  if (!adv || typeof adv !== "object") return "";

  const direct = pickFirst(
    adv?.googleAnalytics,
    adv?.google_analytics,
    adv?.ga4,
    adv?.ga4Report,
    adv?.ga_report,
    adv?.websiteAnalytics,
    adv?.website_analytics,
    adv?.webAnalytics,
    adv?.web_analytics,
    adv?.analytics,
    adv?.siteAnalytics
  );

  if (direct) return String(direct);

  const nested = pickFirst(
    adv?.lookerLinks?.googleAnalytics,
    adv?.looker_links?.googleAnalytics,
    adv?.lookerLinks?.google_analytics,
    adv?.looker_links?.google_analytics,
    adv?.reportLinks?.googleAnalytics,
    adv?.report_links?.googleAnalytics,
    adv?.reportLinks?.google_analytics,
    adv?.report_links?.google_analytics
  );

  return nested ? String(nested) : "";
};

// ------------------------------------------------------------
// ✅ Output normalization
// ------------------------------------------------------------
const normalizeSherlocTextForRender = (inputText) => {
  const raw = normalizeGaEngagementRateText(
    dedupeRepeatedPlatformLabels(String(inputText || ""))
  );
  if (!raw.trim()) return raw;

  const SECTION_KEYS = new Set([
    "SCALE",
    "EFFICIENCY",
    "OUTCOME",
    "TIMEFRAME",
    "CROSS PLATFORM ANALYSIS",
    "CROSS_PLATFORM_ANALYSIS",
    "OUTPUT",
  ]);

  const BARE_PLATFORM_HEADER_MAP = new Map([
    ["yahoo", "Yahoo!:"],
    ["yahoo!", "Yahoo!:"],
    ["yahoo dsp", "Yahoo!:"],
    ["yahoo! dsp", "Yahoo!:"],
    ["viant", "Viant:"],
    ["viant dsp", "Viant:"],
    ["facebook", "Facebook:"],
    ["instagram", "Instagram:"],
    ["audience network", "Audience Network:"],
    ["audience_network", "Audience Network:"],
    ["audiencenetwork", "Audience Network:"],
    ["messenger", "Messenger:"],
    ["tiktok", "TikTok:"],
    ["tik tok", "TikTok:"],
    ["youtube", "YouTube:"],
    ["google search", "Google Search:"],
    ["google_search", "Google Search:"],
    ["google analytics", "Google Analytics:"],
    ["google_analytics", "Google Analytics:"],
    ["ga4", "Google Analytics:"],
    ["ga", "Google Analytics:"],
    ["website analytics", "Google Analytics:"],
    ["x", "X:"],
    ["twitter", "X:"],
    ["truth social", "Truth Social:"],
    ["truth_social", "Truth Social:"],
    ["rumble", "Rumble:"],
    ["dooh", "DOOH:"],
    ["ctv", "CTV:"],
    ["video", "Video:"],
    ["audio", "Audio:"],
    ["display", "Display:"],
    ["mobile", "Mobile:"],
    ["in-game", "In-Game:"],
    ["ingame", "In-Game:"],
    ["email", "Email:"],
    ["cinema", "Cinema:"],
  ]);

  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const out = [];

  const looksDivider = (l) => {
    const s = String(l || "").trim();
    return /^_{6,}$/.test(s) || /^-{3,}$/.test(s) || /^={3,}$/.test(s);
  };

  const isSectionNoColon = (s) => {
    const t = String(s || "").trim();
    if (!t) return false;
    return SECTION_KEYS.has(t.toUpperCase());
  };

  const normalizeSectionLine = (s) => {
    const t = String(s || "").trim();
    const upper = t.toUpperCase();
    if (upper === "CROSS_PLATFORM_ANALYSIS" || upper === "CROSS PLATFORM ANALYSIS")
      return "Cross Platform Analysis:";
    return `${upper.charAt(0)}${upper.slice(1).toLowerCase()}:`;
  };

  const normalizeBarePlatformHeaderIfNeeded = (s) => {
    const t = String(s || "").trim();
    if (!t) return null;
    if (/:$/.test(t)) return null;

    const cleaned = t
      .replace(/[•\-–—_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    if (BARE_PLATFORM_HEADER_MAP.has(cleaned)) return BARE_PLATFORM_HEADER_MAP.get(cleaned);
    return null;
  };

  const isPlatformHeaderOnlyLine = (s) => {
    const m = String(s || "").trim().match(/^(.+?):\s*$/);
    if (!m) return null;
    const label = (m[1] || "").trim();
    const k = canonicalizeChannelKey(label);
    if (!k) return null;
    if (!CHANNEL_LABELS[k]) return null;
    return { key: k, pretty: CHANNEL_LABELS[k], raw: label };
  };

  const isPlatformHeader = (s) => {
    const m = String(s || "").trim().match(/^(.+?):\s*$/);
    if (!m) return false;
    const label = (m[1] || "").trim();
    const k = canonicalizeChannelKey(label);
    return !!CHANNEL_LABELS[k];
  };

  const shouldCollapseWithNext = (nextLine) => {
    const n = String(nextLine ?? "").trim();
    if (!n) return false;
    if (looksDivider(n)) return false;
    if (isSectionNoColon(n)) return false;
    if (/^(.+?):\s*$/.test(n)) return false;
    return true;
  };

  let lastWasDivider = false;

  for (let i = 0; i < lines.length; i++) {
    const t = String(lines[i] ?? "");

    const forcedHeader = normalizeBarePlatformHeaderIfNeeded(t);
    if (forcedHeader) {
      const headerOnly = isPlatformHeaderOnlyLine(forcedHeader);
      if (headerOnly && i + 1 < lines.length && shouldCollapseWithNext(lines[i + 1])) {
        const next = String(lines[i + 1] ?? "").trim();
        out.push(`${CHANNEL_LABELS[headerOnly.key] || headerOnly.raw}: ${next}`);
        i += 1;
        lastWasDivider = false;
        continue;
      }

      out.push(forcedHeader);
      lastWasDivider = false;
      continue;
    }

    if (isSectionNoColon(t)) {
      out.push(normalizeSectionLine(t));
      lastWasDivider = false;
      continue;
    }

    const headerOnly = isPlatformHeaderOnlyLine(t);
    if (headerOnly && i + 1 < lines.length && shouldCollapseWithNext(lines[i + 1])) {
      const next = String(lines[i + 1] ?? "").trim();
      out.push(`${CHANNEL_LABELS[headerOnly.key] || headerOnly.raw}: ${next}`);
      i += 1;
      lastWasDivider = false;
      continue;
    }

    if (isPlatformHeader(t)) {
      if (out.length > 0 && !lastWasDivider) {
        out.push("");
        out.push("_____________________________");
        out.push("");
      }
      out.push(t.trim());
      lastWasDivider = true;
      continue;
    }

    out.push(t);
    lastWasDivider = false;
  }

  return out.join("\n");
};

function SherlocRichOutputWithHeroes({
  text,
  emptyMessage = "No output yet.",
  showChannelHeroes = true,
  forceFirstLineAsH1 = false,
  suppressOverallHero = false,
}) {
  const raw = (text || "").toString();
  if (!raw.trim()) {
    return (
      <div className="sherlocreports-rich-output">
        <div className="sherlocreports-rich-empty">{emptyMessage}</div>
      </div>
    );
  }

  const lines = raw.replace(/\r\n/g, "\n").split("\n");

  const isDivider = (l) => {
    const s = l.trim();
    if (!s) return false;
    if (/^_{6,}$/.test(s)) return true;
    if (/^-{3,}$/.test(s)) return true;
    if (/^={3,}$/.test(s)) return true;
    return false;
  };

  const stripBulletPrefix = (s) => s.replace(/^(-\s+|•\s+|🟣\s*|✅\s*|➡️\s*)/, "").trim();

  const isBullet = (l) => {
    const s = l.trim();
    return (
      s.startsWith("- ") ||
      s.startsWith("• ") ||
      s.startsWith("🟣") ||
      s.startsWith("✅") ||
      s.startsWith("➡️")
    );
  };

  const looksLikeH1 = (s) => {
    const t = s.trim();
    if (!t) return false;
    if (t.length < 6) return false;
    const letters = t.replace(/[^A-Za-z]/g, "");
    if (!letters) return false;
    const upperRatio = letters.replace(/[^A-Z]/g, "").length / letters.length;
    return upperRatio >= 0.85 && t.length <= 90;
  };

  const parseH2ChannelHeader = (s) => {
    const m = s.trim().match(/^(.+?):\s*$/);
    if (!m) return null;
    const label = (m[1] || "").trim();
    if (!label) return null;

    const key = canonicalizeChannelKey(label);
    if (!key) return null;

    const pretty = CHANNEL_LABELS[key] || label;
    const icon = CHANNEL_ICONS[key] || null;

    const isKnown = !!CHANNEL_LABELS[key];
    const isSectionHeader =
      /^(scale|efficiency|outcome|timeframe|cross_platform_analysis|cross platform analysis|output)$/i.test(
        String(label).trim().toLowerCase()
      );

    if (!isKnown && !isSectionHeader && label.length > 26) return null;

    return { key, pretty, icon, rawLabel: label };
  };

  const parseKV = (s) => {
    const m = s.trim().match(/^([^:]{2,40}):\s+(.*)$/);
    if (!m) return null;
    const k = (m[1] || "").trim();
    const v = (m[2] || "").trim();
    if (!k || !v) return null;
    if (k.length > 40) return null;

    const channelKey = canonicalizeChannelKey(k);
    const channelIcon = CHANNEL_ICONS[channelKey] || null;

    return { k, v, channelKey, channelIcon };
  };

  const shouldShowIconFor = (channelKey) => {
    if (!showChannelHeroes) return false;
    if (suppressOverallHero && channelKey === "overall") return false;
    return true;
  };

  const renderKVText = (kv) => (
    <div className="sherlocreports-rich-kvtext">
      <span className="sherlocreports-rich-k">{kv.k}:</span>{" "}
      <span className="sherlocreports-rich-v">{kv.v}</span>
    </div>
  );

  return (
    <div className="sherlocreports-rich-output">
      <div className="sherlocreports-rich-block">
        {lines.map((line, idx) => {
          const s = (line ?? "").toString();

          if (!s.trim()) return <div key={idx} className="sherlocreports-rich-spacer" />;
          if (isDivider(s)) return <div key={idx} className="sherlocreports-rich-divider" />;

          if (forceFirstLineAsH1 && idx === 0) {
            return (
              <div key={idx} className="sherlocreports-rich-h1">
                {s.trim()}
              </div>
            );
          }

          const h2 = parseH2ChannelHeader(s);
          if (h2) {
            const isSectionHeader =
              !CHANNEL_LABELS[h2.key] &&
              /^(scale|efficiency|outcome|timeframe|cross_platform_analysis|cross platform analysis|output)$/i.test(
                String(h2.rawLabel).trim().toLowerCase()
              );

            return (
              <div
                key={idx}
                className={
                  isSectionHeader
                    ? "sherlocreports-rich-h2-row sherlocreports-rich-h2-section"
                    : "sherlocreports-rich-h2-row"
                }
              >
                {shouldShowIconFor(h2.key) && h2.icon && (
                  <SherlocHeroFrame
                    src={h2.icon}
                    alt={`${h2.pretty} logo`}
                    outerClassName="sherlocreports-card-icon-wrapper sherlocreports-report-hero-frame"
                    mediaClassName="sherlocreports-hero-frame-media-cover"
                    imageClassName="sherlocreports-card-icon sherlocreports-hero-frame-image-cover"
                  />
                )}
                <div className="sherlocreports-rich-h2">{h2.pretty}</div>
              </div>
            );
          }

          if (looksLikeH1(s)) {
            return (
              <div key={idx} className="sherlocreports-rich-h1">
                {s.trim()}
              </div>
            );
          }

          if (isBullet(s)) {
            const txt = stripBulletPrefix(s.trim());
            const kvInsideBullet = parseKV(txt);

            return (
              <div key={idx} className="sherlocreports-rich-bullet">
                <div className="sherlocreports-rich-bullet-dot">•</div>
                <div className="sherlocreports-rich-text">
                  {kvInsideBullet ? (
                    <div className="sherlocreports-rich-line">{renderKVText(kvInsideBullet)}</div>
                  ) : (
                    <div className="sherlocreports-rich-line">{txt}</div>
                  )}
                </div>
              </div>
            );
          }

          const kv = parseKV(s);
          if (kv) {
            return (
              <div key={idx} className="sherlocreports-rich-h2-row">
                {shouldShowIconFor(kv.channelKey) && kv.channelIcon && (
                  <SherlocHeroFrame
                    src={kv.channelIcon}
                    alt={`${kv.k} logo`}
                    outerClassName="sherlocreports-card-icon-wrapper sherlocreports-report-hero-frame"
                    mediaClassName="sherlocreports-hero-frame-media-cover"
                    imageClassName="sherlocreports-card-icon sherlocreports-hero-frame-image-cover"
                  />
                )}
                {renderKVText(kv)}
              </div>
            );
          }

          return (
            <div key={idx} className="sherlocreports-rich-line">
              {s}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// ✅ Platform Analysis UPSERT
// ------------------------------------------------------------
const buildPlacementNarrativeBlock = (channelKey, ch) => {
  if (!ch || !hasUsableMetrics(ch)) return null;

  const label = CHANNEL_LABELS[channelKey] || channelKey.replace(/_/g, " ");
  const impressions = toNumber(ch.impressions ?? 0);
  const clicks = toNumber(ch.clicks ?? 0);
  const rawSpend = toNumber(ch.spend ?? ch.spend_usd ?? 0);
  const spend = resolveClientFacingSpend(ch, Number.isFinite(rawSpend) ? rawSpend : 0);

  const providedCtr = toNumber(ch.ctr ?? ch.click_through_rate ?? ch.clickThroughRate);
  const ctr =
    Number.isFinite(providedCtr) && providedCtr >= 0
      ? providedCtr
      : computeRatePercent(clicks, impressions);
  const fallbackCpc =
    clicks > 0 && spend > 0 && Number.isFinite(spend / clicks) ? spend / clicks : null;
  const fallbackCpm =
    impressions > 0 && spend > 0 && Number.isFinite((spend / impressions) * 1000)
      ? (spend / impressions) * 1000
      : null;
  const providedCpc = toNumber(ch.cpc ?? ch.clientFacingCpc ?? ch.client_facing_cpc);
  const providedCpm = toNumber(ch.cpm ?? ch.clientFacingCpm ?? ch.client_facing_cpm);
  const cpc =
    Number.isFinite(providedCpc) && providedCpc >= 0
      ? providedCpc
      : resolveClientFacingCpc(ch, clicks, spend, fallbackCpc);
  const cpm =
    Number.isFinite(providedCpm) && providedCpm >= 0
      ? providedCpm
      : resolveClientFacingCpm(ch, impressions, spend, fallbackCpm);

  const statsPhrase = `Spend ${formatMoney(spend)} | Impressions ${formatNumber(
    impressions
  )} | Clicks ${formatNumber(clicks)} | CTR ${
    ctr != null ? formatPercent(ctr) : "No Data"
  } | CPC ${cpc != null ? formatMoney(cpc) : "No Data"} | CPM ${
    cpm != null ? formatMoney(cpm) : "No Data"
  }`;

  let action;
  if (spend > 0 && impressions > 0 && clicks === 0) {
    action =
      "Delivery is present but click intent is not converting. Review placement-level creative fit and tighten audience/optimization goal to improve qualified clicks.";
  } else if (spend > 0 && impressions > 0 && clicks > 0) {
    action =
      "This placement is contributing measurable volume. Compare CTR/CPC vs Facebook/Instagram and shift budget toward the strongest efficiency signals.";
  } else if (spend === 0 && impressions > 0) {
    action =
      "Impressions are registering without meaningful spend. Confirm billing/budget delivery and placement eligibility.";
  } else if (spend > 0 && impressions === 0) {
    action =
      "Spend is registering without impressions. Inspect reporting window, delivery status, and placement breakdown integrity.";
  } else {
    action = "No meaningful delivery yet. Confirm placement eligibility and campaign status.";
  }

  return `${label}: ${statsPhrase}. ${action}`;
};

const getChannelForNarrative = (resolvedByChannel = {}, channelKey) => {
  const targetKey = canonicalizeChannelKey(channelKey);
  if (!targetKey || !resolvedByChannel || typeof resolvedByChannel !== "object") return null;

  for (const [rawKey, value] of Object.entries(resolvedByChannel)) {
    if (canonicalizeChannelKey(rawKey) === targetKey) return value || null;
  }

  return null;
};

const buildCtvPlatformNarrativeLine = (ctvMetrics) => {
  if (!ctvMetrics || !hasUsableMetrics(ctvMetrics)) return null;

  const impressions = toNumber(ctvMetrics.impressions ?? 0);
  const rawSpend = toNumber(ctvMetrics.spend ?? ctvMetrics.spend_usd ?? 0);
  const spend = resolveClientFacingSpend(
    ctvMetrics,
    Number.isFinite(rawSpend) ? rawSpend : 0
  );
  const videoViews = toNumber(ctvMetrics.video_views ?? ctvMetrics.videoViews ?? 0);
  const videoStarted = toNumber(
    ctvMetrics.video_started ??
      ctvMetrics.videoStarts ??
      ctvMetrics.video_start ??
      ctvMetrics.video_starts ??
      0
  );
  const videoCompleted = toNumber(
    ctvMetrics.video_completed ??
      ctvMetrics.videoCompletions ??
      ctvMetrics.video_completions ??
      ctvMetrics.videoCompletes ??
      ctvMetrics.video_complete ??
      ctvMetrics.completedViews ??
      ctvMetrics.completed_views ??
      ctvMetrics.completeViews ??
      ctvMetrics.views100 ??
      ctvMetrics.videoViews100 ??
      ctvMetrics.quartile_100 ??
      ctvMetrics.q100 ??
      0
  );

  const fallbackCpm =
    impressions > 0 && spend > 0 && Number.isFinite((spend / impressions) * 1000)
      ? (spend / impressions) * 1000
      : null;
  const providedCpm = toNumber(
    ctvMetrics.cpm ?? ctvMetrics.clientFacingCpm ?? ctvMetrics.client_facing_cpm
  );
  const cpm =
    Number.isFinite(providedCpm) && providedCpm >= 0
      ? providedCpm
      : resolveClientFacingCpm(ctvMetrics, impressions, spend, fallbackCpm);
  const providedVideoViewRate = toNumber(
    ctvMetrics.vvr ?? ctvMetrics.video_view_rate ?? ctvMetrics.videoViewRate
  );
  const videoViewRate =
    Number.isFinite(providedVideoViewRate) && providedVideoViewRate >= 0
      ? providedVideoViewRate
      : computeRatePercent(videoViews, impressions);
  const providedCompletionRate = toNumber(
    ctvMetrics.video_completion_rate ??
      ctvMetrics.videoCompletionRate ??
      ctvMetrics.completionRate
  );
  const completionRate =
    Number.isFinite(providedCompletionRate) && providedCompletionRate >= 0
      ? providedCompletionRate
      : computeRatePercent(videoCompleted, videoStarted);

  const metricBits = [];
  if (impressions > 0) metricBits.push(`${formatNumber(impressions)} impressions`);
  if (spend > 0) metricBits.push(`${formatMoney(spend)} spend`);
  if (cpm != null) metricBits.push(`${formatMoney(cpm)} CPM`);
  if (videoViewRate != null && videoViews > 0) {
    metricBits.push(`${formatPercent(videoViewRate)} video view rate`);
  }
  if (completionRate != null && videoCompleted > 0) {
    metricBits.push(`${formatPercent(completionRate)} completion rate`);
  }

  const metricPhrase = metricBits.length
    ? `CTV is delivering ${metricBits.join(", ")}.`
    : "CTV is contributing upper-funnel delivery in the current report window.";

  return `${metricPhrase} Evaluate CTV as an awareness and premium video-reach layer, with optimization based on CPM efficiency, reach scale, view-through quality, completion behavior, and cross-platform assisted impact.`;
};

const buildCtvPlatformNarrativeFallbackLine = () =>
  "CTV is an upper-funnel awareness and premium video-reach layer. Evaluate it using CPM efficiency, reach scale, view-through quality, completion behavior, and cross-platform assisted impact.";

const NARRATIVE_PLATFORM_LABEL_RULES = [
  { key: "yahoo_dsp", label: "Yahoo!", aliases: ["yahoo dsp", "yahoo"] },
  { key: "viant", label: "Viant", aliases: ["viant dsp", "viant"] },
  { key: "facebook", label: "Facebook", aliases: ["facebook"] },
  { key: "instagram", label: "Instagram", aliases: ["instagram"] },
  { key: "youtube", label: "YouTube", aliases: ["youtube"] },
  { key: "display", label: "Display", aliases: ["display", "stackadapt", "programmatic"] },
  { key: "ctv", label: "CTV", aliases: ["ctv", "connected tv", "connected television"] },
  {
    key: "google_analytics",
    label: "Google Analytics",
    aliases: ["google analytics", "ga4", "ga"],
  },
];
const NARRATIVE_PLATFORM_ALIAS_LOOKUP = new Map(
  NARRATIVE_PLATFORM_LABEL_RULES.flatMap((rule) =>
    rule.aliases.map((alias) => [
      alias
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim(),
      rule.key,
    ])
  )
);
const NARRATIVE_PLATFORM_ALIAS_PATTERN = NARRATIVE_PLATFORM_LABEL_RULES.flatMap((rule) =>
  rule.aliases
).sort((left, right) => right.length - left.length);
const NARRATIVE_PLATFORM_TOKEN_RE = new RegExp(
  `\\b(?:${NARRATIVE_PLATFORM_ALIAS_PATTERN.map((alias) =>
    alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  ).join("|")})\\b`,
  "gi"
);
const NARRATIVE_PLATFORM_SEQUENCE_RE = new RegExp(
  `${NARRATIVE_PLATFORM_TOKEN_RE.source}(?:[\\s/|,-]+${NARRATIVE_PLATFORM_TOKEN_RE.source})+`,
  "gi"
);

const normalizeNarrativePlatformAlias = (value) =>
  NARRATIVE_PLATFORM_ALIAS_LOOKUP.get(
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
  ) || "";

const dedupeRepeatedPlatformLabels = (text) => {
  const input = String(text || "");
  if (!input) return "";

  return input.replace(NARRATIVE_PLATFORM_SEQUENCE_RE, (match) => {
    const tokens = match.match(NARRATIVE_PLATFORM_TOKEN_RE) || [];
    if (tokens.length < 2) return match;

    const canonicalKeys = tokens.map(normalizeNarrativePlatformAlias).filter(Boolean);
    if (canonicalKeys.length !== tokens.length) return match;

    const firstKey = canonicalKeys[0];
    if (!canonicalKeys.every((key) => key === firstKey)) return match;

    return (
      NARRATIVE_PLATFORM_LABEL_RULES.find((rule) => rule.key === firstKey)?.label ||
      tokens[0]
    );
  });
};

const normalizeAnalyticsPercentValue = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return n;
  return n >= 0 && n <= 1 ? n * 100 : n;
};

const normalizeGaEngagementRateText = (text) =>
  String(text || "").replace(
    /(\b(?:ga|google analytics)\b(?:[^.\n)]{0,80}?[:(]\s*|\s+))(0?\.\d+)(\s+engagement rate\b)/gi,
    (_match, prefix, rawValue, suffix) => {
      const n = Number(rawValue);
      if (!Number.isFinite(n) || n < 0 || n > 1) return `${prefix}${rawValue}${suffix}`;
      const pct = n * 100;
      const formatted = Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.00$/, "");
      return `${prefix}${formatted}%${suffix}`;
    }
  );

const getClientSafePlatformLabel = (channelKey, rawLabel) => {
  const key = canonicalizeChannelKey(channelKey || rawLabel);
  const label = String(rawLabel || CHANNEL_LABELS[key] || key || "")
    .replace(/\bStackAdapt\s*\/\s*Display\b/gi, "Display")
    .replace(/\bStackAdapt\s*\/\s*/gi, "")
    .replace(/\bStackAdapt\b/gi, "Display")
    .trim();

  if (key === "google_analytics") return "Google Analytics";
  if (key === "stackadapt" || key === "programmatic") return "Display";
  return label || CHANNEL_LABELS[key] || key;
};

const sanitizeClientFacingPlatformNarrativeText = (line) =>
  dedupeRepeatedPlatformLabels(
    String(line || "")
      .replace(/\bStackAdapt\s*\/\s*Display\b/gi, "Display")
      .replace(/\bStackAdapt\s*\/\s*/gi, "")
      .replace(/\bStackAdapt\b/gi, "Display")
      .trim()
  );

const buildGoogleAnalyticsPlatformNarrativeLine = (ch) => {
  const sessions = toNumber(ch?.sessions ?? ch?.ga_sessions ?? 0);
  const users = toNumber(ch?.users ?? ch?.total_users ?? 0);
  const newUsers = toNumber(ch?.new_users ?? ch?.newUsers ?? 0);
  const engagedSessions = toNumber(ch?.engaged_sessions ?? ch?.engagedSessions ?? 0);
  const engagementRate = normalizeAnalyticsPercentValue(
    ch?.engagement_rate_percent ?? ch?.engagement_rate ?? ch?.engagementRate ?? 0
  );
  const eventCount = toNumber(ch?.event_count ?? ch?.eventCount ?? ch?.events ?? 0);
  const conversions = toNumber(ch?.conversions ?? ch?.key_events ?? 0);
  const sentences = [];

  if (sessions > 0 && users > 0) {
    sentences.push(
      `Site traffic shows meaningful interest in the current reporting window, with ${formatNumber(sessions)} sessions and ${formatNumber(users)} users reaching the site.`
    );
  } else if (sessions > 0) {
    sentences.push(`${formatNumber(sessions)} sessions reached the site in the current reporting window.`);
  } else if (users > 0) {
    sentences.push(`${formatNumber(users)} users reached the site in the current reporting window.`);
  } else {
    sentences.push("On-site activity is showing up in the current reporting window.");
  }

  if (newUsers > 0) {
    const newUserShare = users > 0 ? newUsers / users : null;
    if (newUserShare !== null && newUserShare >= 0.7) {
      sentences.push(
        `The high share of ${formatNumber(newUsers)} new users suggests the campaign is continuing to bring fresh audiences onto the site.`
      );
    } else if (newUserShare !== null && newUserShare >= 0.35) {
      sentences.push(
        `${formatNumber(newUsers)} new users suggest the campaign is still expanding reach while also generating some return visitation.`
      );
    } else {
      sentences.push(
        `${formatNumber(newUsers)} new users show audience expansion is still present, although repeat visitation is carrying more of the traffic mix right now.`
      );
    }
  }

  if (engagedSessions > 0 && engagementRate > 0) {
    sentences.push(
      `With ${formatNumber(engagedSessions)} engaged sessions and a ${formatPercent(engagementRate)} engagement rate, a meaningful share of visitors is moving beyond arrival and showing qualified on-site behavior.`
    );
  } else if (engagedSessions > 0) {
    sentences.push(
      `${formatNumber(engagedSessions)} engaged sessions suggest a meaningful portion of visitors are continuing deeper into the site after arrival.`
    );
  } else if (engagementRate > 0) {
    sentences.push(
      `The ${formatPercent(engagementRate)} engagement rate gives a useful traffic-quality signal to monitor as visitors move through the site.`
    );
  }

  if (eventCount > 0 && conversions > 0) {
    sentences.push(
      `${formatNumber(eventCount)} tracked events and ${formatNumber(conversions)} key events/conversions show deeper on-site action and give a clear next checkpoint for landing-page and conversion-path optimization.`
    );
  } else if (conversions > 0) {
    sentences.push(
      `${formatNumber(conversions)} key events/conversions show that traffic is producing downstream action, which is the next signal to watch as landing-page paths are refined.`
    );
  } else if (eventCount > 0) {
    sentences.push(
      `${formatNumber(eventCount)} tracked events provide a useful next signal to watch as landing-page paths and conversion flow are refined.`
    );
  } else if (sentences.length < 4) {
    sentences.push(
      "Watch how engagement quality changes as landing-page paths and the broader conversion journey are refined."
    );
  }

  return sentences.join(" ");
};

const buildGenericPlatformNarrativeLine = (channelKey, ch) => {
  const key = canonicalizeChannelKey(channelKey);
  if (!key || !ch || !hasUsableMetrics(ch)) return null;
  if (key === "ctv") return buildCtvPlatformNarrativeLine(ch);
  if (key === "google_analytics") return buildGoogleAnalyticsPlatformNarrativeLine(ch);

  const label = getClientSafePlatformLabel(key);
  const impressions = toNumber(ch.impressions ?? 0);
  const clicks = toNumber(ch.clicks ?? 0);
  const rawSpend = toNumber(ch.spend ?? ch.spend_usd ?? 0);
  const spend = resolveClientFacingSpend(ch, Number.isFinite(rawSpend) ? rawSpend : 0);
  const conversions = toNumber(ch.conversions ?? 0);
  const engagements = toNumber(ch.engagements ?? 0);
  const videoViews = toNumber(ch.video_views ?? ch.videoViews ?? 0);
  const providedCtr = toNumber(ch.ctr ?? ch.click_through_rate ?? ch.clickThroughRate);
  const ctr =
    Number.isFinite(providedCtr) && providedCtr >= 0
      ? providedCtr
      : computeRatePercent(clicks, impressions);
  const fallbackCpc =
    clicks > 0 && spend > 0 && Number.isFinite(spend / clicks) ? spend / clicks : null;
  const fallbackCpm =
    impressions > 0 && spend > 0 && Number.isFinite((spend / impressions) * 1000)
      ? (spend / impressions) * 1000
      : null;
  const providedCpc = toNumber(ch.cpc ?? ch.clientFacingCpc ?? ch.client_facing_cpc);
  const providedCpm = toNumber(ch.cpm ?? ch.clientFacingCpm ?? ch.client_facing_cpm);
  const cpc =
    Number.isFinite(providedCpc) && providedCpc >= 0
      ? providedCpc
      : resolveClientFacingCpc(ch, clicks, spend, fallbackCpc);
  const cpm =
    Number.isFinite(providedCpm) && providedCpm >= 0
      ? providedCpm
      : resolveClientFacingCpm(ch, impressions, spend, fallbackCpm);

  const metricBits = [];
  if (impressions > 0) metricBits.push(`${formatNumber(impressions)} impressions`);
  if (spend > 0) metricBits.push(`${formatMoney(spend)} spend`);
  if (clicks > 0) metricBits.push(`${formatNumber(clicks)} clicks`);
  if (ctr != null && clicks > 0) metricBits.push(`${formatPercent(ctr)} CTR`);
  if (cpm != null) metricBits.push(`${formatMoney(cpm)} CPM`);
  if (cpc != null && clicks > 0) metricBits.push(`${formatMoney(cpc)} CPC`);
  if (engagements > 0) metricBits.push(`${formatNumber(engagements)} engagements`);
  if (videoViews > 0) metricBits.push(`${formatNumber(videoViews)} video views`);
  if (conversions > 0) metricBits.push(`${formatNumber(conversions)} conversions`);

  const metricPhrase = metricBits.length
    ? `${label} is delivering ${metricBits.join(", ")}.`
    : `${label} is contributing delivery in the current reporting window.`;

  if (key === "display") {
    return `${metricPhrase} Evaluate Display as a programmatic reach and conversion-support layer, using CPM efficiency, CTR/CPC, retargeting quality, and downstream conversion contribution to determine whether paid media budget should hold, shift, or scale.`;
  }

  return `${metricPhrase} Evaluate ${label} against its channel role in the current media mix, balancing delivery scale, efficiency, engagement quality, and downstream contribution before reallocating budget.`;
};

const splitRecommendationLines = (recommendations = []) =>
  (Array.isArray(recommendations) ? recommendations : [])
    .flatMap((line) =>
      String(line || "")
        .replace(/\r\n/g, "\n")
        .split("\n")
    )
    .map((line) => String(line || "").trim())
    .filter(Boolean);

const getRecommendationPlatformKeys = (recommendations = []) => {
  const keys = new Set();
  splitRecommendationLines(recommendations).forEach((line) => {
    const text = String(line || "").replace(/^[-•]\s*/, "").trim();
    const channelMatch = text.match(/^([^:]{2,40})\s*:/);
    const key = channelMatch ? canonicalizeChannelKey(channelMatch[1]) : "";
    if (key && CHANNEL_LABELS[key]) keys.add(key);
  });
  return Array.from(keys);
};

const applyChannelAwarePlatformAnalysis = (recommendations = [], resolvedByChannel = {}, advertiserVertical = "general") => {
  if (!Array.isArray(recommendations)) return [];
  const verticalConfig = getVerticalIntelligenceConfig(advertiserVertical);

  const eligibleKeys = new Set(Object.keys(resolvedByChannel || {}).map(canonicalizeChannelKey));
  const representedKeys = new Set();

  const cleaned = splitRecommendationLines(recommendations)
    .map((line) => {
      const text = String(line || "").trim();
      const channelMatch = text
        .replace(/^[-•]\s*/, "")
        .match(/^([^:]{2,40})\s*:/);
      const channelKey = channelMatch ? canonicalizeChannelKey(channelMatch[1]) : "";
      if (channelKey && CHANNEL_LABELS[channelKey] && !eligibleKeys.has(channelKey)) {
        return "";
      }

      if (channelKey && CHANNEL_LABELS[channelKey]) representedKeys.add(channelKey);

      if (channelKey === "google_analytics") {
        const gaLine = buildGoogleAnalyticsPlatformNarrativeLine(
          getChannelForNarrative(resolvedByChannel, "google_analytics")
        );
        return `Google Analytics: ${gaLine}`;
      }

      if (channelKey !== "ctv") {
        return sanitizeVerticalIntelligenceCopy(
          sanitizeClientFacingPlatformNarrativeText(line),
          advertiserVertical
        );
      }
      const ctvLine =
        buildCtvPlatformNarrativeLine(getChannelForNarrative(resolvedByChannel, "ctv")) ||
        buildCtvPlatformNarrativeFallbackLine();
      return sanitizeVerticalIntelligenceCopy(`CTV: ${ctvLine}`, advertiserVertical);
    })
    .filter((line) => String(line || "").trim());

  const orderedEligibleKeys = [
    ...CHANNEL_ORDER.filter((key) => eligibleKeys.has(key)),
    ...Array.from(eligibleKeys).filter((key) => !CHANNEL_ORDER.includes(key)),
  ];

  orderedEligibleKeys.forEach((key) => {
    if (!key || representedKeys.has(key)) return;
    const metrics = getChannelForNarrative(resolvedByChannel, key);
    if (!metrics || !hasUsableMetrics(metrics)) return;
    const narrativeLine = buildGenericPlatformNarrativeLine(key, metrics);
    if (!narrativeLine) return;
    cleaned.push(
      sanitizeVerticalIntelligenceCopy(
        `${getClientSafePlatformLabel(key)}: ${narrativeLine} ${verticalConfig.narrativeGuidance}`,
        advertiserVertical
      )
    );
    representedKeys.add(key);
  });

  return cleaned;
};

const upsertPlacementLinesIntoPlatformAnalysis = (text, resolvedByChannel = {}) => {
  const base = String(text || "").trim();
  if (!base) return base;

  const audLine = buildPlacementNarrativeBlock("audience_network", resolvedByChannel.audience_network);
  const msgLine = buildPlacementNarrativeBlock("messenger", resolvedByChannel.messenger);

  if (!audLine && !msgLine) return base;

  const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const lines = base.replace(/\r\n/g, "\n").split("\n");

  const looksDivider = (l) => {
    const s = String(l || "").trim();
    return /^_{6,}$/.test(s) || /^-{3,}$/.test(s) || /^={3,}$/.test(s);
  };

  const stripBulletPrefix = (s) =>
    String(s || "").replace(/^(-\s+|•\s+|🟣\s*|✅\s*|➡️\s*)/, "").trim();

  const looksStatsy = (s) => {
    const t = String(s || "").trim();
    if (!t) return false;
    return (
      /\b(spend|impressions|clicks|ctr|cpc|cpm|engagement|engagements|video|views|conversions)\b/i.test(
        t
      ) ||
      /\|\s*impressions\s*\d/i.test(t) ||
      /\$\d/.test(t)
    );
  };

  const removePlatformBlock = (platformLabel) => {
    const lbl = String(platformLabel || "").trim();
    if (!lbl) return;

    const kvLineRe = new RegExp(`^\\s*(?:[-•]\\s+)?${escapeRe(lbl)}\\s*:\\s+\\S+`, "i");
    const headerOnlyRe = new RegExp(`^\\s*(?:[-•]\\s+)?${escapeRe(lbl)}\\s*:\\s*$`, "i");

    for (let i = 0; i < lines.length; i++) {
      const cur = String(lines[i] || "");

      if (kvLineRe.test(cur.trim())) {
        lines.splice(i, 1);
        i -= 1;
        continue;
      }

      if (headerOnlyRe.test(cur.trim())) {
        lines.splice(i, 1);

        if (i < lines.length) {
          const next = String(lines[i] || "");
          if (looksStatsy(next) && !looksDivider(next)) {
            lines.splice(i, 1);
          }
        }

        i -= 1;
      }
    }
  };

  removePlatformBlock("Audience Network");
  removePlatformBlock("Messenger");

  const isKVChannelLine = (line) => {
    const t = stripBulletPrefix(line);
    const m = String(t || "").trim().match(/^([^:]{2,40}):\s+.+$/);
    if (!m) return null;
    const key = canonicalizeChannelKey(m[1]);
    return CHANNEL_LABELS[key] ? key : null;
  };

  const isHeaderOnlyChannelLine = (line) => {
    const t = stripBulletPrefix(line);
    const m = String(t || "").trim().match(/^([^:]{2,40}):\s*$/);
    if (!m) return null;
    const key = canonicalizeChannelKey(m[1]);
    return CHANNEL_LABELS[key] ? key : null;
  };

  let igIdx = -1;
  let fbIdx = -1;

  for (let i = 0; i < lines.length; i++) {
    const kKV = isKVChannelLine(lines[i]);
    const kH = isHeaderOnlyChannelLine(lines[i]);
    const k = kKV || kH;

    if (k === "instagram") igIdx = i;
    if (k === "facebook") fbIdx = i;
  }

  let insertAt = igIdx >= 0 ? igIdx + 1 : fbIdx >= 0 ? fbIdx + 1 : lines.length;

  if (insertAt < lines.length && !String(lines[insertAt] || "").trim()) {
    insertAt += 1;
  }

  while (insertAt < lines.length && looksDivider(lines[insertAt])) {
    lines.splice(insertAt, 1);
  }
  while (insertAt - 1 >= 0 && looksDivider(lines[insertAt - 1])) {
    lines.splice(insertAt - 1, 1);
    insertAt -= 1;
  }

  const insertion = [];
  if (audLine) insertion.push(audLine);
  if (msgLine) insertion.push(msgLine);

  const nextLines = [...lines.slice(0, insertAt), ...insertion, ...lines.slice(insertAt)];
  return nextLines.join("\n");
};

const SherlocReports = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    document.body.classList.add("sherloc-reports-route");
    document.documentElement.classList.add("sherloc-reports-route");

    return () => {
      document.body.classList.remove("sherloc-reports-route");
      document.documentElement.classList.remove("sherloc-reports-route");
    };
  }, []);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  // Durable Run Analysis lifecycle: the operator card and the running label
  // derive from the durable operation row, never from component-local fetch
  // state (2026-08-16 NSC incident: a false RUNNING label with no operation
  // identity, elapsed time, heartbeat, or terminal disposition).
  const [runAnalysisOperation, setRunAnalysisOperation] = useState(null);
  const [, setRunAnalysisElapsedTick] = useState(0);
  useEffect(() => {
    if (!isRunAnalysisOperationActive(runAnalysisOperation)) return undefined;
    const timer = setInterval(
      () => setRunAnalysisElapsedTick((tick) => tick + 1),
      SHERLOC_RUN_ANALYSIS_ELAPSED_TICK_MS
    );
    return () => clearInterval(timer);
  }, [runAnalysisOperation]);
  const [lastRun, setLastRun] = useState(null);
  const [aggregate, setAggregate] = useState(null);

  const [aiSummary, setAiSummary] = useState({ title: "", bullets: [] });
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [analystPanel, setAnalystPanel] = useState(null);
  // The campaign's earned outcome-semantics verdict, kept beside the metrics
  // it names so the label and the count always describe the same payload.
  const [outcomeSemantics, setOutcomeSemantics] = useState(null);
  const [aiRunContext, setAiRunContext] = useState(null);
  const [aiMeta, setAiMeta] = useState({
    requested: null,
    used: null,
    model: "",
    fallbackUsed: false,
  });
  const [aiNarrativeMetricSignature, setAiNarrativeMetricSignature] = useState(null);
  const [narrativeAlignmentFailure, setNarrativeAlignmentFailure] = useState(null);
  const [runSnapshotLock, setRunSnapshotLock] = useState(null);
  const [runStatusPreservation, setRunStatusPreservation] = useState(null);
  const [selectedAiProvider, setSelectedAiProvider] = useState(() => {
    try {
      return normalizeAiProviderForUi(localStorage.getItem(CL_AI_PROVIDER_KEY) || "auto");
    } catch {
      return "auto";
    }
  });

  const [reportViewerState, setReportViewerState] = useState(null);
  const [viewerStatus, setViewerStatus] = useState("idle");
  const [viewerError, setViewerError] = useState("");
  const [stackAdaptInsightState, setStackAdaptInsightState] = useState({});
  /* Daily GA4 observations for Traffic Over Time. Kept separate from the
     aggregate GA summary: they answer different questions and the aggregate
     path stays authoritative for the KPI cards above the chart. */
  const [gaDailyMetric, setGaDailyMetric] = useState("sessions");
  const [gaDailyState, setGaDailyState] = useState({
    status: "idle",
    series: null,
    error: "",
    scopeKey: "",
  });


  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceError, setVoiceError] = useState("");

  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState("");
  const [metaCampaigns, setMetaCampaigns] = useState([]);
  const [approvedPlanCampaigns, setApprovedPlanCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("auto");
  const [campaignSelectionHydrated, setCampaignSelectionHydrated] = useState(false);
  const [approvedPlanCampaignsHydrated, setApprovedPlanCampaignsHydrated] = useState(false);

  const [sources, setSources] = useState({ live: { byChannel: {} }, ingest: { byChannel: {} } });
  const [sourcesContext, setSourcesContext] = useState(null);
  const [resolved, setResolved] = useState({ byChannel: {}, totals: {} });
  const [resolvedContext, setResolvedContext] = useState(null);
  const [resolvedOrigin, setResolvedOrigin] = useState(null);
  const [appliedReportDataSource, setAppliedReportDataSource] = useState({
    priority: 0,
    timestamp: 0,
    source: "empty",
    context: null,
  });
  const [kpiHydrationGate, setKpiHydrationGate] = useState({
    contextKey: "",
    status: "idle",
  });

  const [liveStatus, setLiveStatus] = useState({
    state: "idle",
    message: "",
    contextConnected: false,
  });
  const [gaStatus, setGaStatus] = useState(buildPendingGoogleAnalyticsStatus());
  const [statusRefreshTick, setStatusRefreshTick] = useState(0);

  const latestAttemptedRef = useRef(new Map());
  const latestInFlightRef = useRef(new Map());
  const narrativeAlignmentAttemptedRef = useRef(new Set());
  const runSherlocRef = useRef(null);
  // The advertiser+campaign scope the in-flight hydration read belongs to.
  // Compared against the server's own scope stamp so a late answer for a
  // campaign the operator has already left cannot render over the current one.
  const persistedAnalysisScopeRef = useRef("");
  // The full run context (advertiser + campaign + timeframe) already hydrated,
  // so a settled context is read exactly once instead of on every re-render.
  const persistedAnalysisHydratedContextRef = useRef("");
  const mountedRef = useRef(false);
  // resetAdvertiserScopedState is declared below; the governed catalog needs a
  // stable handle to it at hook-wiring time.
  const resetAdvertiserScopedStateRef = useRef(null);
  const runAbortRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const campaignsFetchedForAdvertiserRef = useRef("");
  const metaInsightsCacheRef = useRef(new Map());
  const metaInsightsInFlightRef = useRef(new Map());
  const gaSummaryCacheRef = useRef(new Map());
  const gaSummaryInFlightRef = useRef(new Map());
  const stackAdaptLiveCacheRef = useRef(new Map());
  const stackAdaptLiveInFlightRef = useRef(new Map());
  const stackAdaptInsightCacheRef = useRef(new Map());
  const stackAdaptInsightInFlightRef = useRef(new Map());
  const liveStatusRequestSeqRef = useRef(0);
  const contextScopedAggregateRef = useRef(null);
  const contextScopedResolvedRef = useRef({ byChannel: {}, totals: {} });
  const runSnapshotLockRef = useRef(null);
  const narrativeIntegrityRerunAttemptedRef = useRef(new Set());
  const reportDataSourceRef = useRef({
    priority: 0,
    timestamp: 0,
    source: "empty",
    context: null,
  });
  const metaCampaignsRef = useRef([]);
  const activeAdvertiserIdRef = useRef("");
  const activeRunContextRef = useRef(buildRunContext({}));
  const previousAdvertiserIdRef = useRef("");
  const visibleNarrativeSelectionRef = useRef(null);
  const confirmedLiveStatusRef = useRef({
    contextKey: "",
    ga: null,
    platforms: [],
  });
  const confirmedGoogleAnalyticsChannelRef = useRef({
    contextKey: "",
    channel: null,
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (runAbortRef.current) runAbortRef.current.abort();
      publishSherlocTrace("client", "page_request_cleanup", {
        abortedRequests: ["advertisers", "run_analysis"],
      });
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__SHERLOC_REPORTS_BUILD = SHERLOC_REPORTS_BUILD_MARKER;
    window.__SHERLOC_REPORTS_BUILD_SURFACE = "client";
    publishSherlocTrace("client", "sherloc_reports_build_marker", {
      build: SHERLOC_REPORTS_BUILD_MARKER,
      requiredEvents: Array.from(SHERLOC_TRACE_ALWAYS_LOG_EVENTS),
    });
  }, []);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem("clientAuthToken") || localStorage.getItem("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const buildEmptyAiSummary = () => ({ title: "", bullets: [] });
  const buildFallbackAiSummaryForPartialNarrative = (reason, totals = null) => {
    const t = totals && typeof totals === "object" ? totals : {};
    const impressions = Number(t.impressions || 0);
    const clicks = Number(t.clicks || 0);
    const spend = Number(t.spend || 0);
    const engagements = Number(t.engagements || 0);
    const videoViews = Number(t.video_views || t.videoViews || 0);
    const metricLine =
      impressions > 0 || clicks > 0 || spend > 0
        ? `Scale: ${Math.round(impressions).toLocaleString()} impressions, ${Math.round(
            clicks
          ).toLocaleString()} clicks, and $${spend.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })} in spend are available for this run.`
        : "Scale: KPI cards and platform performance remain available from resolved delivery data.";
    return {
      title: "Campaign Performance Snapshot",
      bullets: [
        metricLine,
        engagements || videoViews
          ? `Outcome: ${Math.round(engagements).toLocaleString()} engagements and ${Math.round(
              videoViews
            ).toLocaleString()} video views are present in the current campaign data.`
          : "Outcome: Delivery signals are present; use Platform Analysis for channel-level context.",
        `Narrative Status: Campaign Summary fallback was generated because Sherloc returned platform analysis without a complete summary${
          reason ? ` (${reason})` : ""
        }.`,
      ],
    };
  };
  const buildEmptyAiMeta = () => ({
    requested: null,
    used: null,
    model: "",
    fallbackUsed: false,
  });
  const buildEmptySources = () => ({ live: { byChannel: {} }, ingest: { byChannel: {} } });
  const buildEmptyResolved = () => ({ byChannel: {}, totals: {} });
  const clearVisibleNarrativeSelection = useCallback(
    ({
      source = "unknown",
      reason = "unspecified",
      context = null,
      clearLastRun = false,
      detail = {},
    } = {}) => {
      const normalizedContext = context ? buildRunContext(context) : null;
      const contextKey = normalizedContext ? getSherlocReportContextKey(normalizedContext) : "";
      const previousSelection = visibleNarrativeSelectionRef.current;
      if (previousSelection) {
        publishSherlocTrace("client", "visible_narrative_overwrite_attempt", {
          source,
          reason,
          currentContextKey: contextKey,
          currentContext: normalizedContext,
          visibleRunId: previousSelection.runId,
          visibleSource: previousSelection.source,
          visibleSelection: previousSelection,
          ...detail,
        });
      }
      visibleNarrativeSelectionRef.current = null;
      if (clearLastRun) setLastRun(null);
      setAiSummary({ title: "", bullets: [] });
      setAiRecommendations([]);
      setAnalystPanel(null);
      setAiRunContext(null);
      setAiNarrativeMetricSignature(null);
      setAiMeta({
        requested: null,
        used: null,
        model: "",
        fallbackUsed: false,
      });
      setVoiceError("");
      const clearedPayload = {
        source,
        reason,
        advertiserId: normalizedContext?.advertiserId || "",
        campaignId: normalizedContext?.campaignId || "",
        timeframeKey: contextKey,
        visibleRunId: previousSelection?.runId || null,
        visibleSource: previousSelection?.source || null,
        summaryPresent: false,
        recommendationsPresent: false,
        currentContextKey: contextKey,
        candidateContextKey: "",
        ...detail,
      };
      publishSherlocTrace("client", "visible_narrative_cleared", clearedPayload);
      publishSherlocTrace("client", "visible_narrative_cleared_reason", clearedPayload);
    },
    []
  );
  const buildCurrentDisplayNarrativeSignature = useCallback((fallbackTotals = null) => {
    const resolvedTotals =
      contextScopedResolvedRef.current?.totals &&
      Object.keys(contextScopedResolvedRef.current.totals || {}).length
        ? contextScopedResolvedRef.current.totals
        : null;
    const aggregateTotals =
      contextScopedAggregateRef.current?.totals &&
      Object.keys(contextScopedAggregateRef.current.totals || {}).length
        ? contextScopedAggregateRef.current.totals
        : null;
    const totals = resolvedTotals || aggregateTotals || fallbackTotals || null;
    return buildNarrativeMetricSignature({ metrics: computeReportingMetrics(totals) });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CL_AI_PROVIDER_KEY, selectedAiProvider);
    } catch (_e) {
      // ignore
    }
  }, [selectedAiProvider]);

  /**
   * ✅ Governed advertiser authority
   * Campaign Analysis renders the same canonical image-based selector as
   * Campaign Optimizer. The governed catalog is the only source of advertiser
   * identity, visibility and authorization on this page — it keeps no
   * advertiser list of its own and issues no advertiser catalog request.
   * Campaign selection stays a separate concept below.
   */
  const clearAdvertiserScopedPresentationState = useCallback(() => {
    resetAdvertiserScopedStateRef.current?.({ resetCampaignSelection: true });
  }, []);

  const {
    selectedAdvertiserId,
    selectedAdvertiserName,
    visibleAdvertisers,
    currentAdvertiser,
    loading: advertiserCatalogLoading,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedPresentationState,
  });

  // Only a governed-confirmed advertiser scopes this page. An unconfirmed id
  // resolves to "", which fails closed to the empty analysis state.
  const resolvedAdvertiserId = currentAdvertiser?.id
    ? String(currentAdvertiser.id)
    : "";

  const governedAdvertiserName =
    currentAdvertiser?.name || selectedAdvertiserName || "";

  // Downstream reporting reads GA links, flight windows and vertical off the
  // full advertiser record, which the governed catalog carries as `raw`.
  const resolvedAdvertiser = useMemo(
    () =>
      currentAdvertiser
        ? currentAdvertiser.raw || currentAdvertiser
        : null,
    [currentAdvertiser]
  );

  const resolvedAdvertiserVertical = useMemo(
    () =>
      normalizeAdvertiserVertical(
        currentAdvertiser?.vertical || resolvedAdvertiser?.vertical
      ),
    [currentAdvertiser, resolvedAdvertiser]
  );

  const autoDetectedMetaCampaign = useMemo(
    // Canonical default selection (shared with superadmin): the merged
    // canonical/reporting-backed option outranks stale legacy live rows, which
    // stay manually selectable but never win "Auto".
    () => resolveDefaultCampaignAnalysisCampaign(metaCampaigns),
    [metaCampaigns]
  );

  const selectedMetaCampaignForContext = useMemo(() => {
    if (!selectedCampaignId || selectedCampaignId === "auto") return null;
    return metaCampaigns.find((campaign) => String(campaign.id) === String(selectedCampaignId)) || null;
  }, [metaCampaigns, selectedCampaignId]);

  const selectedMetaCampaignName = selectedMetaCampaignForContext?.name || null;

  /*
   * The governed identity of the current selection, and the advertiser's
   * governed rows narrowed to it.
   *
   * Everything campaign-scoped on this page below reads `governedCampaignsForSelection`
   * rather than the advertiser's whole approved list. With one campaign per
   * advertiser the two were the same list; with two they are not, and the
   * heuristics that pick "the" approved campaign out of an advertiser's rows
   * score campaigns without ever consulting the selection. That is how
   * Campaign 50 would have been shown Campaign 44's flight window and
   * Campaign 44's Live Platforms while correctly reporting itself as
   * Campaign 50 everywhere else.
   */
  const selectedGovernedCampaignId = useMemo(
    () =>
      resolveSelectedGovernedCampaignId({
        selectedCampaignId,
        selectedCampaign: selectedMetaCampaignForContext,
      }),
    [selectedCampaignId, selectedMetaCampaignForContext]
  );

  const governedCampaignsForSelection = useMemo(
    () =>
      selectGovernedCampaignsForSelection({
        campaigns: approvedPlanCampaigns,
        selectedCampaignId,
        selectedCampaign: selectedMetaCampaignForContext,
      }),
    [approvedPlanCampaigns, selectedCampaignId, selectedMetaCampaignForContext]
  );

  const advertiserApprovedPlanCampaign = useMemo(
    () =>
      selectApprovedPlanCampaign({
        campaigns: governedCampaignsForSelection,
        selectedCampaignId: selectedGovernedCampaignId,
        allowSingleApprovedFallback: true,
      }),
    [governedCampaignsForSelection, selectedGovernedCampaignId]
  );

  const advertiserApprovedPlanCampaignName = useMemo(
    () => getCanonicalCampaignDisplayName(advertiserApprovedPlanCampaign) || null,
    [advertiserApprovedPlanCampaign]
  );

  const selectedLiveMetaCampaignId = useMemo(() => {
    return resolveCampaignAnalysisLiveCampaignId({
      selectedCampaignId,
      selectedCampaign: selectedMetaCampaignForContext,
    });
  }, [selectedCampaignId, selectedMetaCampaignForContext]);

  /*
   * What the paid-media providers may be asked, given the current selection.
   * `unresolved` means a campaign IS selected and the provider cannot address
   * it — the read is not issued, and it is emphatically not widened to the
   * advertiser. The governed campaign id below stays authoritative either way.
   */
  const campaignProviderScope = useMemo(
    () =>
      resolveCampaignAnalysisProviderScope({
        selectedCampaignId,
        providerCampaignId: selectedLiveMetaCampaignId,
        governedCampaignId: selectedGovernedCampaignId,
      }),
    [selectedCampaignId, selectedGovernedCampaignId, selectedLiveMetaCampaignId]
  );

  const providerReadsPermitted = useMemo(
    () => canReadProviderForCampaignAnalysisScope(campaignProviderScope),
    [campaignProviderScope]
  );

  const authoritativeCampaignId = useMemo(
    () => resolveCampaignAnalysisAuthoritativeCampaignId(campaignProviderScope),
    [campaignProviderScope]
  );

  /*
   * Every paid-media provider read on this page goes through here.
   *
   * When the selected campaign is one the provider cannot address, the read is
   * SKIPPED and resolves null — the same "no payload" value these fetchers
   * already return on failure, which every caller below already handles as
   * absent evidence. It is deliberately not an advertiser-wide read: the
   * provider genuinely has nothing to say about THIS campaign, and saying the
   * advertiser's numbers instead would be a different campaign's delivery
   * wearing this campaign's label.
   *
   * GA4 is intentionally NOT gated. Its governed reporting identity for this
   * advertiser is `advertiser_only_not_campaign_attributed` — it is a
   * measurement source that declares its own advertiser scope and is labelled
   * as such on the surface, not a media provider claiming campaign delivery.
   */
  const readProviderForSelectedCampaign = useCallback(
    (run) => (providerReadsPermitted ? run() : Promise.resolve(null)),
    [providerReadsPermitted]
  );

  const selectedCampaignContextCampaignId = useMemo(() => {
    return resolveCampaignAnalysisContextCampaignId({
      selectedCampaignId,
      selectedCampaign: selectedMetaCampaignForContext,
    });
  }, [selectedCampaignId, selectedMetaCampaignForContext]);

  const selectedReportingCampaignId = useMemo(() => {
    return resolveCampaignAnalysisReportingCampaignId({
      selectedCampaignId,
      selectedCampaign: selectedMetaCampaignForContext,
    });
  }, [selectedCampaignId, selectedMetaCampaignForContext]);

  const selectedCampaignPublicCampaignId = useMemo(() => {
    return selectedCampaignContextCampaignId && selectedCampaignContextCampaignId !== "auto"
      ? selectedCampaignContextCampaignId
      : null;
  }, [selectedCampaignContextCampaignId]);

  const selectedCampaignNameForContext = useMemo(() => {
    if (selectedMetaCampaignName) return selectedMetaCampaignName;
    if (advertiserApprovedPlanCampaignName) return advertiserApprovedPlanCampaignName;
    return autoDetectedMetaCampaign?.name || null;
  }, [
    advertiserApprovedPlanCampaignName,
    autoDetectedMetaCampaign,
    selectedMetaCampaignName,
  ]);

  const defaultTimeframe = useMemo(() =>
    resolveApprovedFlightTimeframe({
      campaigns: governedCampaignsForSelection,
      advertiser: resolvedAdvertiser,
      selectedCampaignId: selectedGovernedCampaignId,
      selectedCampaignName: selectedCampaignNameForContext,
      allowAdvertiserBestFit: false,
      allowSingleApprovedFallback: true,
    }),
  [
    governedCampaignsForSelection,
    resolvedAdvertiser,
    selectedCampaignNameForContext,
    selectedGovernedCampaignId,
  ]);

  /*
   * Fetch the governed daily series only while the GA4 detail is actually open.
   * Scoped by advertiser + window + metric so switching any of them refetches
   * and a stale response can never paint over a newer scope.
   */
  const gaDailyScopeKey = useMemo(() => {
    const viewerChannel = reportViewerState?.inlineReport?.channelKey || "";
    if (viewerChannel !== "google_analytics") return "";
    if (!resolvedAdvertiserId) return "";
    return [
      resolvedAdvertiserId,
      defaultTimeframe?.date_start || "",
      defaultTimeframe?.date_end || "",
      gaDailyMetric,
    ].join("::");
  }, [
    defaultTimeframe,
    gaDailyMetric,
    reportViewerState,
    resolvedAdvertiserId,
  ]);

  useEffect(() => {
    if (!gaDailyScopeKey) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    setGaDailyState((previous) => ({ ...previous, status: "loading", error: "" }));

    const params = new URLSearchParams();
    params.set("metric", gaDailyMetric);
    if (defaultTimeframe?.date_start && defaultTimeframe?.date_end) {
      params.set("startDate", String(defaultTimeframe.date_start));
      params.set("endDate", String(defaultTimeframe.date_end));
    } else {
      params.set("datePreset", "last_30d");
    }

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/google-analytics/daily-sessions/${encodeURIComponent(
            String(resolvedAdvertiserId)
          )}?${params.toString()}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            signal: controller.signal,
            cache: "no-store",
          }
        );
        clearTimeout(timeout);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || data?.error) {
          setGaDailyState({
            status: "error",
            series: null,
            scopeKey: gaDailyScopeKey,
            error:
              "Daily Google Analytics observations are unavailable for this window.",
          });
          return;
        }
        setGaDailyState({
          status: "ready",
          series: data?.series || null,
          error: "",
          scopeKey: gaDailyScopeKey,
        });
      } catch (err) {
        clearTimeout(timeout);
        if (cancelled || err?.name === "AbortError") return;
        setGaDailyState({
          status: "error",
          series: null,
          scopeKey: gaDailyScopeKey,
          error: "Daily Google Analytics observations could not be loaded.",
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [
    defaultTimeframe,
    gaDailyMetric,
    gaDailyScopeKey,
    getAuthHeader,
    resolvedAdvertiserId,
  ]);

  const defaultTimeframeLabel = useMemo(
    () => formatCampaignTimeframeLabel(defaultTimeframe),
    [defaultTimeframe]
  );
  const approvedWindowState = useMemo(
    () => getApprovedWindowState(defaultTimeframe),
    [defaultTimeframe]
  );

  const currentRunContext = useMemo(() => {
    return buildRunContext({
      advertiserId: resolvedAdvertiserId,
      campaignId: selectedCampaignContextCampaignId,
      date_start: defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_start || null : null,
      date_end: defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_end || null : null,
      date_preset: defaultTimeframe?.mode === "preset" ? defaultTimeframe?.date_preset || "" : "",
    });
  }, [defaultTimeframe, resolvedAdvertiserId, selectedCampaignContextCampaignId]);
  const currentRunContextKey = useMemo(
    () => getSherlocReportContextKey(currentRunContext),
    [currentRunContext]
  );

  useEffect(() => {
    activeAdvertiserIdRef.current = String(resolvedAdvertiserId || "");
  }, [resolvedAdvertiserId]);

  useEffect(() => {
    activeRunContextRef.current = currentRunContext;
  }, [currentRunContext]);

  const isActiveAdvertiserId = useCallback((advertiserId) => {
    return String(advertiserId || "") === String(activeAdvertiserIdRef.current || "");
  }, []);

  const isActiveRunContext = useCallback((advertiserId, context) => {
    if (!isActiveAdvertiserId(advertiserId)) return false;
    if (!context) return true;
    return isSherlocReportRequestCurrent(
      buildRunContext(context),
      activeRunContextRef.current
    );
  }, [isActiveAdvertiserId]);

  const applyRunSnapshotLock = useCallback((lock) => {
    const normalizedLock = lock
      ? {
          ...lock,
          context: buildRunContext(lock.context || currentRunContext),
          lockedAt: lock.lockedAt || new Date().toISOString(),
        }
      : null;
    runSnapshotLockRef.current = normalizedLock;
    setRunSnapshotLock(normalizedLock);
  }, [currentRunContext]);

  const clearRunSnapshotLock = useCallback(() => {
    runSnapshotLockRef.current = null;
    setRunSnapshotLock(null);
  }, []);

  const resetReportDataSourceGuard = useCallback(() => {
    const emptySource = {
      priority: 0,
      timestamp: 0,
      source: "empty",
      context: null,
    };
    reportDataSourceRef.current = emptySource;
    setAppliedReportDataSource(emptySource);
  }, []);

  const canApplyReportDataSource = useCallback(({ source, timestamp, context }) => {
    const candidatePriority = getSherlocReportSourcePriority(source);
    const candidateTimestamp = parseSherlocReportTimestamp(timestamp) || 0;
    const current = reportDataSourceRef.current || {};
    const activeLock = runSnapshotLockRef.current;
    const activeLockMatches =
      activeLock?.context && contextsMatch(activeLock.context, currentRunContext);
    const isLockSource =
      String(source || "").trim().toLowerCase() === "run_analysis_locked_snapshot";
    if (activeLockMatches && !isLockSource) {
      return false;
    }
    const currentContextMatches =
      current.context && contextsMatch(current.context, currentRunContext);

    if (currentContextMatches) {
      if (candidatePriority < (current.priority || 0)) return false;
      if (
        candidatePriority === (current.priority || 0) &&
        candidateTimestamp < (current.timestamp || 0)
      ) {
        return false;
      }
    }

    return true;
  }, [currentRunContext]);

  const markReportDataSourceApplied = useCallback(({ source, timestamp, context }) => {
    const nextSource = {
      priority: getSherlocReportSourcePriority(source),
      timestamp: parseSherlocReportTimestamp(timestamp) || 0,
      source: source || "unknown",
      context: buildRunContext(context || currentRunContext),
    };
    reportDataSourceRef.current = nextSource;
    setAppliedReportDataSource(nextSource);
  }, [currentRunContext]);

  const markKpiHydrationPending = useCallback((context) => {
    setKpiHydrationGate({
      contextKey: getSherlocReportContextKey(context || currentRunContext),
      status: "pending",
    });
  }, [currentRunContext]);

  const markKpiHydrationSettled = useCallback((context) => {
    const contextKey = getSherlocReportContextKey(context || currentRunContext);
    setKpiHydrationGate((current) => {
      if (current.contextKey && current.contextKey !== contextKey) return current;
      return { contextKey, status: "settled" };
    });
  }, [currentRunContext]);

  const publishReportDataSkip = useCallback(({ source, timestamp, context, traceSource }) => {
    publishSherlocTrace("client", "report_data_update_skipped", {
      traceSource,
      incoming: {
        source,
        priority: getSherlocReportSourcePriority(source),
        timestamp: parseSherlocReportTimestamp(timestamp) || null,
        context: buildRunContext(context || {}),
      },
      current: reportDataSourceRef.current,
    });
  }, []);

  const resetAdvertiserScopedState = useCallback(({ resetCampaignSelection = false } = {}) => {
    liveStatusRequestSeqRef.current += 1;
    resetReportDataSourceGuard();
    clearRunSnapshotLock();
    setRunStatusPreservation(null);
    setLastRun(null);
    setAggregate(null);
    setError("");
    setReportViewerState(null);
    setViewerStatus("idle");
    setViewerError("");
    clearVisibleNarrativeSelection({
      source: "advertiser_scoped_reset",
      reason: "advertiser_context_changed",
      clearLastRun: true,
    });
    setNarrativeAlignmentFailure(null);
    setVoiceLoading(false);
    setMetaCampaigns([]);
    setApprovedPlanCampaigns([]);
    setCampaignsError("");
    if (resetCampaignSelection) setSelectedCampaignId("auto");
    setSources(buildEmptySources());
    setSourcesContext(null);
    setResolved(buildEmptyResolved());
    setResolvedContext(null);
    setResolvedOrigin(null);
    setLiveStatus({ state: "idle", message: "", contextConnected: false });
    setGaStatus(buildPendingGoogleAnalyticsStatus());
    confirmedLiveStatusRef.current = {
      contextKey: "",
      ga: null,
      platforms: [],
    };
    confirmedGoogleAnalyticsChannelRef.current = {
      contextKey: "",
      channel: null,
    };

    campaignsFetchedForAdvertiserRef.current = "";
    metaInsightsCacheRef.current = new Map();
    metaInsightsInFlightRef.current = new Map();
    gaSummaryCacheRef.current = new Map();
    gaSummaryInFlightRef.current = new Map();
    stackAdaptLiveCacheRef.current = new Map();
    stackAdaptLiveInFlightRef.current = new Map();
    stackAdaptInsightCacheRef.current = new Map();
    stackAdaptInsightInFlightRef.current = new Map();
    setStackAdaptInsightState({});
    narrativeAlignmentAttemptedRef.current = new Set();
    narrativeIntegrityRerunAttemptedRef.current = new Set();

    for (const [, controller] of latestInFlightRef.current.entries()) {
      try {
        controller.abort();
      } catch (_e) {
        // ignore
      }
    }
    latestInFlightRef.current = new Map();

    try {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current = null;
      }
    } catch (_e) {
      // ignore
    }
  }, [clearRunSnapshotLock, clearVisibleNarrativeSelection, resetReportDataSourceGuard]);

  useEffect(() => {
    resetAdvertiserScopedStateRef.current = resetAdvertiserScopedState;
  }, [resetAdvertiserScopedState]);

  const aggregateRunContext = useMemo(() => {
    return extractRunContextFromPayload(
      {
        aggregate,
        run: lastRun,
      },
      {}
    );
  }, [aggregate, lastRun]);

  const isAggregateContextCurrent = useMemo(() => {
    return contextsMatch(aggregateRunContext, currentRunContext);
  }, [aggregateRunContext, currentRunContext]);
  const isRollingPresetContext = useMemo(
    () => Boolean(currentRunContext?.date_preset),
    [currentRunContext]
  );
  const canHydratePersistedAnalysis = !isRollingPresetContext;
  const canAttemptPersistedReportRestore =
    canHydratePersistedAnalysis && campaignSelectionHydrated && approvedPlanCampaignsHydrated;

  useEffect(() => {
    if (!resolvedAdvertiserId || isRollingPresetContext) {
      setKpiHydrationGate({ contextKey: currentRunContextKey, status: "settled" });
      return;
    }
    setKpiHydrationGate({ contextKey: currentRunContextKey, status: "pending" });
  }, [currentRunContextKey, isRollingPresetContext, resolvedAdvertiserId]);

  const contextScopedAggregate = isAggregateContextCurrent ? aggregate : null;
  const contextScopedLastRun = isAggregateContextCurrent ? lastRun : null;
  const isResolvedContextCurrent = useMemo(() => {
    return contextsMatch(resolvedContext, currentRunContext);
  }, [resolvedContext, currentRunContext]);
  const isSourcesContextCurrent = useMemo(() => {
    return contextsMatch(sourcesContext, currentRunContext);
  }, [sourcesContext, currentRunContext]);
  const contextScopedResolved = useMemo(
    () => (isResolvedContextCurrent ? resolved : { byChannel: {}, totals: {} }),
    [isResolvedContextCurrent, resolved]
  );
  const contextScopedSources = useMemo(
    () =>
      isSourcesContextCurrent
        ? sources
        : { live: { byChannel: {} }, ingest: { byChannel: {} } },
    [isSourcesContextCurrent, sources]
  );
  /**
   * How many channels a candidate basis can actually speak for.
   *
   * Used to keep the report on ONE epoch. See activeRunSnapshotLock below.
   */
  const countSignalChannels = useCallback((...candidates) => {
    const channels = new Set();
    for (const candidate of candidates) {
      const byChannel = candidate?.byChannel || candidate?.platforms || null;
      if (!isPlainObject(byChannel)) continue;
      for (const [key, metrics] of Object.entries(byChannel)) {
        if (key && hasSharedUsableMetrics(metrics)) channels.add(key);
      }
    }
    return channels.size;
  }, []);

  /**
   * The run snapshot lock, but only once it can carry the WHOLE report.
   *
   * Freezing the evidence basis at run start is right: KPI cards must not
   * drift under a narrative that was computed from a different moment. What
   * went wrong is that the lock took over the display the instant a run
   * started, even when the snapshot it captured was still partial — so the
   * KPI strip dropped to whichever channels had resolved so far (a single
   * platform's spend, impressions and clicks, with the campaign's other
   * channels simply absent) while Platform Analysis and the narrative
   * correctly went on showing the last completed report.
   *
   * That is two report epochs on screen at once, with nothing disclosing it,
   * and the strip silently reads as a campaign total while showing one
   * platform. A partial snapshot is not a report.
   *
   * So an incomplete lock is ignored for DISPLAY — the last good report stays
   * whole across every section, and the operator card is where a run in
   * progress is announced. The lock still governs the AI evidence basis and
   * still commits atomically when the run completes; only its power to
   * narrow what is already on screen is withdrawn.
   */
  const activeRunSnapshotLock = useMemo(() => {
    const lock =
      runSnapshotLock?.context && contextsMatch(runSnapshotLock.context, currentRunContext)
        ? runSnapshotLock
        : null;
    if (!lock) return null;

    const lockedChannels = countSignalChannels(lock.resolved, lock.aggregate);
    const displayedChannels = countSignalChannels(contextScopedResolved, contextScopedAggregate);

    // Strictly fewer channels than what is already rendered means this
    // snapshot cannot stand in for the report yet.
    if (lockedChannels < displayedChannels) {
      publishSherlocTrace("client", "run_snapshot_lock_display_withheld", {
        reason: "incomplete_snapshot_would_narrow_report",
        lockedChannels,
        displayedChannels,
        contextKey: getSherlocReportContextKey(currentRunContext),
      });
      return null;
    }
    return lock;
  }, [
    contextScopedAggregate,
    contextScopedResolved,
    countSignalChannels,
    currentRunContext,
    runSnapshotLock,
  ]);
  const displayScopedAggregate = useMemo(
    () => activeRunSnapshotLock?.aggregate || contextScopedAggregate,
    [activeRunSnapshotLock, contextScopedAggregate]
  );
  const displayScopedResolved = useMemo(
    () => activeRunSnapshotLock?.resolved || contextScopedResolved || { byChannel: {}, totals: {} },
    [activeRunSnapshotLock, contextScopedResolved]
  );
  const displayScopedSources = useMemo(
    () =>
      activeRunSnapshotLock?.sources ||
      contextScopedSources || { live: { byChannel: {} }, ingest: { byChannel: {} } },
    [activeRunSnapshotLock, contextScopedSources]
  );

  useEffect(() => {
    const nextAdvertiserId = String(resolvedAdvertiserId || "");
    if (previousAdvertiserIdRef.current === nextAdvertiserId) return;
    previousAdvertiserIdRef.current = nextAdvertiserId;
    resetAdvertiserScopedState({ resetCampaignSelection: true });
  }, [resolvedAdvertiserId, resetAdvertiserScopedState]);

  useEffect(() => {
    contextScopedAggregateRef.current = displayScopedAggregate;
  }, [displayScopedAggregate]);

  useEffect(() => {
    contextScopedResolvedRef.current = displayScopedResolved;
  }, [displayScopedResolved]);

  useEffect(() => {
    metaCampaignsRef.current = metaCampaigns;
  }, [metaCampaigns]);

  const shouldRenderNarrative = useMemo(() => {
    return (
      contextsMatch(aiRunContext, currentRunContext) &&
      hasRenderableNarrative(aiSummary, aiRecommendations)
    );
  }, [aiRunContext, currentRunContext, aiSummary, aiRecommendations]);
  const hasCurrentContextRenderableNarrative = shouldRenderNarrative;
  const hasSuccessfulVisibleNarrative = Boolean(
    visibleNarrativeSelectionRef.current?.trusted &&
      visibleNarrativeSelectionRef.current?.hasRenderable &&
      visibleNarrativeSelectionRef.current?.contextKey === currentRunContextKey &&
      hasCurrentContextRenderableNarrative
  );
  const hasSuccessfulDirectRunNarrative = Boolean(
    hasSuccessfulVisibleNarrative &&
      visibleNarrativeSelectionRef.current?.source === "direct_run_response"
  );

  const commitVisibleNarrativeSelection = useCallback(
    ({
      source,
      run = null,
      payload = null,
      aggregate = null,
      resolved = null,
      context = null,
      summary = null,
      recommendations = [],
      trusted = true,
      blockedReason = "",
      apply,
    } = {}) => {
      const candidate = buildVisibleNarrativeSelection({
        source,
        run,
        payload,
        aggregate,
        resolved,
        context,
        summary,
        recommendations,
        trusted,
        blockedReason,
      });
      const current = visibleNarrativeSelectionRef.current;
      const decision = shouldApplyVisibleNarrativeSelection({ current, candidate });
      if (current?.hasRenderable && current.contextKey === candidate.contextKey) {
        publishSherlocTrace("client", "visible_narrative_overwrite_attempt", {
          source,
          candidate,
          current,
          reason: decision.reason,
        });
      }
      publishSherlocTrace("client", decision.apply ? "visible_narrative_source_selected" : "visible_narrative_source_rejected", {
        source,
        reason: decision.reason,
        selectedVisibleRunId: decision.apply ? candidate.runId : current?.runId || null,
        selectedVisibleSource: decision.apply ? candidate.source : current?.source || null,
        blockedPlaybackRunId: trusted ? null : candidate.runId,
        newerSuccessfulRunId:
          !decision.apply && current?.trusted && current?.runNumber > (candidate.runNumber ?? -Infinity)
            ? current.runId
            : null,
        candidate,
        current,
      });
      if (!decision.apply) {
        publishSherlocTrace("client", "visible_narrative_overwrite_rejected", {
          source,
          reason: decision.reason,
          rejectedRunId: candidate.runId,
          rejectedSource: candidate.source,
          selectedVisibleRunId: current?.runId || null,
          selectedVisibleSource: current?.source || null,
          blockedPlaybackRunId: trusted ? null : candidate.runId,
          newerSuccessfulRunId:
            current?.trusted && current?.runNumber > (candidate.runNumber ?? -Infinity)
              ? current.runId
              : null,
          candidate,
          current,
        });
        return false;
      }
      visibleNarrativeSelectionRef.current = candidate;
      if (typeof apply === "function") apply();
      if (source === "direct_run_response") {
        publishSherlocTrace("client", "direct_run_response_committed", {
          advertiserId: candidate.context?.advertiserId || "",
          campaignId: candidate.context?.campaignId || "",
          timeframeKey: candidate.contextKey,
          visibleRunId: candidate.runId,
          visibleSource: candidate.source,
          currentContextKey: current?.contextKey || "",
          candidateContextKey: candidate.contextKey,
          contextKey: candidate.contextKey,
          summaryPresent: hasRenderableSummary(summary),
          recommendationsPresent:
            Array.isArray(recommendations) &&
            recommendations.some((line) => String(line || "").trim()),
          reason: decision.reason,
        });
      }
      publishSherlocTrace("client", "visible_narrative_final_state", {
        advertiserId: candidate.context?.advertiserId || "",
        campaignId: candidate.context?.campaignId || "",
        timeframeKey: candidate.contextKey,
        visibleRunId: candidate.runId,
        visibleSource: candidate.source,
        currentContextKey: current?.contextKey || "",
        candidateContextKey: candidate.contextKey,
        contextKey: candidate.contextKey,
        summaryTitlePresent: Boolean(String(summary?.title || "").trim()),
        summaryBulletCount: Array.isArray(summary?.bullets)
          ? summary.bullets.filter((line) => String(line || "").trim()).length
          : 0,
        recommendationsCount: Array.isArray(recommendations) ? recommendations.length : 0,
        summaryPresent: hasRenderableSummary(summary),
        recommendationsPresent:
          Array.isArray(recommendations) &&
          recommendations.some((line) => String(line || "").trim()),
        reason: decision.reason,
      });
      return true;
    },
    []
  );

  useEffect(() => {
    if (!resolvedAdvertiserId || !canAttemptPersistedReportRestore) return;

    try {
      const scopedKey = getScopedReportStorageKey({
        advertiserId: resolvedAdvertiserId,
        context: currentRunContext,
        aiProvider: selectedAiProvider,
      });
      const legacyKey = getLastRunStorageKey(resolvedAdvertiserId);
      const scopedRaw = localStorage.getItem(scopedKey);
      const legacyRaw = scopedRaw ? null : localStorage.getItem(legacyKey);
      const key = scopedRaw ? scopedKey : legacyKey;
      const raw = scopedRaw || legacyRaw;
      publishSherlocTrace("client", "report_restore_lookup", {
        scopedKey,
        legacyKey,
        usedKey: raw ? key : null,
        hasScopedPayload: Boolean(scopedRaw),
        hasLegacyPayload: Boolean(legacyRaw),
        currentRunContext,
        selectedAiProvider,
      });
      if (raw) {
        const parsed = JSON.parse(raw);
        const saved = isPlainObject(parsed) ? parsed : {};

        const cachedSummary = isPlainObject(saved.aiSummary)
          ? {
              title: String(saved.aiSummary.title || ""),
              bullets: Array.isArray(saved.aiSummary.bullets)
                ? saved.aiSummary.bullets.map((b) => String(b || "")).filter(Boolean)
                : [],
            }
          : { title: "", bullets: [] };
        const cachedRecs = Array.isArray(saved.aiRecommendations)
          ? saved.aiRecommendations.map((rec) => String(rec || "")).filter(Boolean)
          : [];
        const cachedContext = isPlainObject(saved.aiRunContext) ? saved.aiRunContext : null;
        const cachedNarrativeMetricSignature = buildNarrativeMetricSignature({
          aggregate: isPlainObject(saved.aggregate) ? saved.aggregate : null,
          resolved: isPlainObject(saved.resolved) ? saved.resolved : null,
        });
        const playbackValidation = validatePersistedNarrativePlayback({
          payload: saved,
          aggregate: isPlainObject(saved.aggregate) ? saved.aggregate : null,
          resolved: isPlainObject(saved.resolved) ? saved.resolved : null,
          summary: cachedSummary,
          recommendations: cachedRecs,
          source: "client_local_cache_restore",
        });
        const untrustedNarrative = playbackValidation.trusted
          ? null
          : buildUntrustedHistoricalNarrative(playbackValidation.message);
        let safeCachedSummary = playbackValidation.trusted
          ? cachedSummary
          : untrustedNarrative.summary;
        const safeCachedRecs = playbackValidation.trusted
          ? cachedRecs
          : untrustedNarrative.recommendations;
        if (playbackValidation.trusted && !hasRenderableSummary(safeCachedSummary) && safeCachedRecs.length) {
          safeCachedSummary = buildFallbackAiSummaryForPartialNarrative(
            "local_cache_restore_partial_narrative",
            saved?.resolved?.totals || saved?.aggregate?.totals || null
          );
          publishSherlocTrace("client", "frontend_summary_repaired", {
            source: "local_cache_restore",
            key,
            context: currentRunContext,
            reason: "cached_recommendations_present_summary_missing",
            summaryTitle: safeCachedSummary.title,
            summaryBulletCount: safeCachedSummary.bullets.length,
          });
        }
        const cachedPayloadContext = extractRunContextFromPayload(
          {
            run: isPlainObject(saved.run) ? saved.run : null,
            aggregate: isPlainObject(saved.aggregate) ? saved.aggregate : null,
          },
          { advertiserId: resolvedAdvertiserId }
        );
        const cachedProvider = saved.aiProviderKey
          ? normalizeAiProviderForUi(saved.aiProviderKey)
          : normalizeAiProviderForUi(selectedAiProvider);
        const providerMatches = cachedProvider === normalizeAiProviderForUi(selectedAiProvider);
        const hasMatchingContext =
          canAttemptPersistedReportRestore &&
          providerMatches &&
          contextsMatch(cachedPayloadContext, currentRunContext) &&
          cachedContext &&
          contextsMatch(cachedContext, currentRunContext);
        const cacheTimestamp = getSherlocReportDataTimestamp(
          saved,
          saved.run,
          saved.aggregate,
          saved.resolved
        );
        const canApplyCachedReportData =
          hasMatchingContext &&
          canApplyReportDataSource({
            source: "local_cache_restore",
            timestamp: cacheTimestamp,
            context: cachedContext,
          });
        const shouldSuppressFailedCachedRun =
          hasMatchingContext &&
          isFailedRunRecord(saved.run) &&
          hasPersistedReportMetrics(saved);
        const safeCachedRun =
          hasMatchingContext && isPlainObject(saved.run) && !shouldSuppressFailedCachedRun
            ? saved.run
            : null;

        if (shouldSuppressFailedCachedRun) {
          publishSherlocTrace("client", "failed_run_restore_suppressed", {
            source: "local_cache_restore",
            key,
            context: cachedContext,
            currentRunContext,
            hasMetrics: true,
          });
        }

        setAggregate(null);

        if (canApplyCachedReportData) {
          publishSherlocTrace("client", "local_cache_report_metrics_suppressed", {
            key,
            source: "local_cache_restore",
            timestamp: cacheTimestamp,
            cachedContext,
            currentRunContext,
          });
        } else {
          if (hasMatchingContext) {
            publishReportDataSkip({
              source: "local_cache_restore",
              timestamp: cacheTimestamp,
              context: cachedContext,
              traceSource: "local_cache_restore",
            });
          }
        }

        const cachedAiMeta = isPlainObject(saved.aiMeta)
          ? {
              requested: saved.aiMeta.requested ?? null,
              used: saved.aiMeta.used ?? null,
              model: String(saved.aiMeta.model || ""),
              fallbackUsed: Boolean(saved.aiMeta.fallbackUsed),
            }
          : {
              requested: null,
              used: null,
              model: "",
              fallbackUsed: false,
            };

        if (hasMatchingContext && cachedContext && contextsMatch(cachedContext, currentRunContext)) {
          if (!playbackValidation.trusted) {
            console.warn("[SherlocReports] blocked persisted narrative playback", {
              source: "client_local_cache_restore",
              reason: playbackValidation.reason,
              key,
            });
          }
          commitVisibleNarrativeSelection({
            source: "local_cache",
            run: safeCachedRun || saved.run || null,
            payload: saved,
            aggregate: isPlainObject(saved.aggregate) ? saved.aggregate : null,
            resolved: isPlainObject(saved.resolved) ? saved.resolved : null,
            context: cachedContext,
            summary: safeCachedSummary,
            recommendations: safeCachedRecs,
            trusted: playbackValidation.trusted,
            blockedReason: playbackValidation.reason,
            apply: () => {
              setLastRun(safeCachedRun);
              setAiSummary(safeCachedSummary);
              setAiRecommendations(safeCachedRecs);
              setAnalystPanel(null);
              setAiRunContext(cachedContext);
              setAiMeta(cachedAiMeta);
              setAiNarrativeMetricSignature(cachedNarrativeMetricSignature);
            },
          });
        } else {
          clearVisibleNarrativeSelection({
            source: "local_cache_restore",
            reason: "cached_context_or_provider_mismatch",
            context: currentRunContext,
            detail: {
              cachedContext,
              cachedPayloadContext,
              providerMatches,
            },
          });
        }

        setSources({ live: { byChannel: {} }, ingest: { byChannel: {} } });
        setSourcesContext(null);

        setResolved({ byChannel: {}, totals: {} });
        setResolvedContext(null);
        setResolvedOrigin(null);
        publishSherlocTrace("client", "local_cache_restore", {
          matched: Boolean(hasMatchingContext),
          key,
          scopedKey,
          legacyKey,
          hasSavedPayload: Boolean(raw),
          currentRunContext,
          cachedContext,
          cachedPayloadContext,
          cachedProvider,
          providerMatches,
          selectedCampaignId,
          selectedAiProvider,
          narrativePlaybackTrusted: playbackValidation.trusted,
          narrativePlaybackReason: playbackValidation.reason,
        });
      } else {
        setAggregate(null);
        clearVisibleNarrativeSelection({
          source: "local_cache_restore",
          reason: "no_saved_payload",
          context: currentRunContext,
          clearLastRun: true,
        });
        setSources(buildEmptySources());
        setSourcesContext(null);
        setResolved(buildEmptyResolved());
        setResolvedContext(null);
        setResolvedOrigin(null);
        publishSherlocTrace("client", "local_cache_restore", {
          matched: false,
          key: scopedKey,
          legacyKey,
          hasSavedPayload: false,
          currentRunContext,
          selectedCampaignId,
          selectedAiProvider,
        });
      }
    } catch (err) {
      console.error("SherlocReports (client): failed to load cached run:", err);
    }
  }, [
    canApplyReportDataSource,
    canAttemptPersistedReportRestore,
    clearVisibleNarrativeSelection,
    commitVisibleNarrativeSelection,
    currentRunContext,
    markReportDataSourceApplied,
    publishReportDataSkip,
    resolvedAdvertiserId,
    selectedAiProvider,
    selectedCampaignId,
  ]);

  useEffect(() => {
    if (contextsMatch(aiRunContext, currentRunContext)) return;

    const currentContextKey = getSherlocReportContextKey(currentRunContext);
    const visibleSelection = visibleNarrativeSelectionRef.current;
    const hasCurrentRenderableNarrative = hasRenderableNarrative(aiSummary, aiRecommendations);

    if (
      visibleSelection?.trusted &&
      visibleSelection?.hasRenderable &&
      visibleSelection.contextKey === currentContextKey &&
      hasCurrentRenderableNarrative
    ) {
      publishSherlocTrace("client", "visible_narrative_overwrite_attempt", {
        source: "context_mismatch_guard",
        reason: "raw_context_mismatch_after_visible_selection",
        currentRunContext,
        aiRunContext,
        visibleSelection,
      });
      publishSherlocTrace("client", "visible_narrative_overwrite_rejected", {
        source: "context_mismatch_guard",
        reason: "canonical_context_key_matches_visible_selection",
        selectedVisibleRunId: visibleSelection.runId,
        selectedVisibleSource: visibleSelection.source,
        currentRunContext,
        aiRunContext,
      });
      setAiRunContext(currentRunContext);
      setVoiceError("");
      publishSherlocTrace("client", "visible_narrative_final_state", {
        visibleRunId: visibleSelection.runId,
        visibleSource: visibleSelection.source,
        contextKey: visibleSelection.contextKey,
        repairedRunContext: true,
        summaryTitlePresent: Boolean(String(aiSummary?.title || "").trim()),
        summaryBulletCount: Array.isArray(aiSummary?.bullets)
          ? aiSummary.bullets.filter((line) => String(line || "").trim()).length
          : 0,
        recommendationsCount: Array.isArray(aiRecommendations) ? aiRecommendations.length : 0,
      });
      return;
    }

    if (!visibleSelection && !hasCurrentRenderableNarrative && !aiRunContext) return;

    if (visibleSelection || hasCurrentRenderableNarrative) {
      publishSherlocTrace("client", "visible_narrative_overwrite_attempt", {
        source: "context_mismatch_clear",
        reason: "active_context_changed",
        currentRunContext,
        aiRunContext,
        visibleSelection,
      });
    }
    clearVisibleNarrativeSelection({
      source: "context_mismatch_clear",
      reason: "active_context_changed",
      context: currentRunContext,
      detail: {
        aiRunContext,
        currentContextKey,
      },
    });
  }, [
    currentRunContext,
    aiRunContext,
    aiSummary,
    aiRecommendations,
    clearVisibleNarrativeSelection,
  ]);

  const buildMetaInsightsKey = ({
    advertiserId,
    level = "campaign",
    breakdowns = "",
    campaignId,
    campaign_id,
    date_start,
    date_end,
    datePreset,
    date_preset,
  }) => {
    const cid = campaignId || campaign_id || "";
    const ds = date_start || "";
    const de = date_end || "";
    const preset = datePreset || date_preset || "";
    return JSON.stringify({
      a: String(advertiserId || ""),
      lvl: String(level || ""),
      bd: String(breakdowns || ""),
      cid: String(cid || ""),
      ds: String(ds || ""),
      de: String(de || ""),
      preset: String(preset || ""),
    });
  };

  const buildGaSummaryKey = ({
    advertiserId,
    campaignId,
    campaign_id,
    date_start,
    date_end,
    datePreset,
    date_preset,
  }) => {
    const cid = campaignId || campaign_id || "";
    const ds = date_start || "";
    const de = date_end || "";
    const preset = datePreset || date_preset || "";
    return JSON.stringify({
      a: String(advertiserId || ""),
      cid: String(cid || ""),
      ds: String(ds || ""),
      de: String(de || ""),
      preset: String(preset || ""),
    });
  };

  const buildStackAdaptLiveKey = ({
    advertiserId,
    date_start,
    date_end,
    datePreset,
    date_preset,
  }) => {
    const ds = date_start || "";
    const de = date_end || "";
    const preset = datePreset || date_preset || "";
    return JSON.stringify({
      a: String(advertiserId || ""),
      ds: String(ds || ""),
      de: String(de || ""),
      preset: String(preset || ""),
    });
  };

  const buildStackAdaptInsightKey = ({
    advertiserId,
    channelKey,
    campaignId,
    date_start,
    date_end,
    datePreset,
    date_preset,
  }) => {
    const ds = date_start || "";
    const de = date_end || "";
    const preset = datePreset || date_preset || "";
    return JSON.stringify({
      a: String(advertiserId || ""),
      ch: String(channelKey || ""),
      cid: String(campaignId || ""),
      ds: String(ds || ""),
      de: String(de || ""),
      preset: String(preset || ""),
    });
  };

  const fetchMetaInsights = useCallback(async ({
    advertiserId,
    date_start,
    date_end,
    breakdowns,
    campaign_id,
    campaignId,
    level = "campaign",
  }) => {
    if (!advertiserId) return null;

    const params = new URLSearchParams();
    params.set("advertiserId", String(advertiserId));
    params.set("level", level);

    if (breakdowns) params.set("breakdowns", breakdowns);

    const cid = campaignId || campaign_id;
    if (cid) {
      params.set("campaignId", String(cid));
      params.set("campaign_id", String(cid));
    }

    if (date_start && date_end) {
      params.set("date_start", String(date_start));
      params.set("date_end", String(date_end));
    } else if (
      defaultTimeframe?.mode === "flight" &&
      defaultTimeframe.date_start &&
      defaultTimeframe.date_end
    ) {
      params.set("date_start", String(defaultTimeframe.date_start));
      params.set("date_end", String(defaultTimeframe.date_end));
    } else {
      params.set("datePreset", "last_30d");
      params.set("date_preset", "last_30d");
    }

    const cacheKey = buildMetaInsightsKey({
      advertiserId,
      level,
      breakdowns,
      campaignId: cid,
      date_start: params.get("date_start"),
      date_end: params.get("date_end"),
      datePreset: params.get("datePreset"),
      date_preset: params.get("date_preset"),
    });

    if (metaInsightsCacheRef.current.has(cacheKey)) {
      return metaInsightsCacheRef.current.get(cacheKey);
    }

    const inflight = metaInsightsInFlightRef.current.get(cacheKey);
    if (inflight) return inflight;

    const doFetch = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      try {
        const res = await fetch(`${API_BASE_URL}/api/meta/insights?${params.toString()}`, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          signal: controller.signal,
          cache: "no-store",
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.error) {
          console.warn(
            "[SherlocReports][Meta Live] insights failed:",
            res.status,
            data?.error || data
          );
          return null;
        }

        metaInsightsCacheRef.current.set(cacheKey, data);
        return data;
      } catch (err) {
        console.warn(
          "[SherlocReports][Meta Live] insights error:",
          err?.name || err,
          err?.message || ""
        );
        return null;
      } finally {
        clearTimeout(timeout);
        metaInsightsInFlightRef.current.delete(cacheKey);
      }
    })();

    metaInsightsInFlightRef.current.set(cacheKey, doFetch);
    return doFetch;
  }, [defaultTimeframe, getAuthHeader]);

  const fetchStackAdaptLiveReporting = useCallback(async ({
    advertiserId,
    date_start,
    date_end,
    datePreset,
    date_preset,
  }) => {
    if (!advertiserId) return null;

    const params = new URLSearchParams();
    const startDate =
      date_start ||
      (defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_start || null : null);
    const endDate =
      date_end ||
      (defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_end || null : null);
    const preset =
      datePreset ||
      date_preset ||
      (defaultTimeframe?.mode === "preset" ? defaultTimeframe?.date_preset || "last_30d" : "");

    if (startDate) params.set("startDate", String(startDate));
    if (endDate) params.set("endDate", String(endDate));
    if (!startDate && !endDate && preset) {
      params.set("datePreset", String(preset));
      params.set("date_preset", String(preset));
    }

    const cacheKey = buildStackAdaptLiveKey({
      advertiserId,
      date_start: params.get("startDate"),
      date_end: params.get("endDate"),
      datePreset: params.get("datePreset"),
      date_preset: params.get("date_preset"),
    });

    if (stackAdaptLiveCacheRef.current.has(cacheKey)) {
      return stackAdaptLiveCacheRef.current.get(cacheKey);
    }

    const inflight = stackAdaptLiveInFlightRef.current.get(cacheKey);
    if (inflight) return inflight;

    const doFetch = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      try {
        const res = await fetch(
          `${API_BASE_URL}/api/stackadapt/${encodeURIComponent(String(advertiserId))}/reporting?${
            params.toString()
          }`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            signal: controller.signal,
            cache: "no-store",
          }
        );

        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.error || data?.ok === false) {
          console.warn(
            "[SherlocReports][StackAdapt Live] reporting failed:",
            res.status,
            data?.error || data?.reason || data
          );
          return null;
        }

        stackAdaptLiveCacheRef.current.set(cacheKey, data);
        return data;
      } catch (err) {
        console.warn(
          "[SherlocReports][StackAdapt Live] reporting error:",
          err?.name || err,
          err?.message || ""
        );
        return null;
      } finally {
        clearTimeout(timeout);
        stackAdaptLiveInFlightRef.current.delete(cacheKey);
      }
    })();

    stackAdaptLiveInFlightRef.current.set(cacheKey, doFetch);
    return doFetch;
  }, [defaultTimeframe, getAuthHeader]);

  const fetchStackAdaptInsightBreakdowns = useCallback(async ({
    advertiserId,
    channelKey,
    date_start,
    date_end,
    datePreset,
    date_preset,
    campaignId,
  }) => {
    if (!advertiserId) return null;

    const params = new URLSearchParams();
    const startDate =
      date_start ||
      (defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_start || null : null);
    const endDate =
      date_end ||
      (defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_end || null : null);
    const preset =
      datePreset ||
      date_preset ||
      (defaultTimeframe?.mode === "preset" ? defaultTimeframe?.date_preset || "last_30d" : "");

    if (startDate) params.set("startDate", String(startDate));
    if (endDate) params.set("endDate", String(endDate));
    if (!startDate && !endDate && preset) {
      params.set("datePreset", String(preset));
      params.set("date_preset", String(preset));
    }
    if (campaignId) {
      params.set("campaignId", String(campaignId));
      params.set("campaign_id", String(campaignId));
    }

    const cacheKey = buildStackAdaptInsightKey({
      advertiserId,
      channelKey,
      campaignId,
      date_start: params.get("startDate"),
      date_end: params.get("endDate"),
      datePreset: params.get("datePreset"),
      date_preset: params.get("date_preset"),
    });

    if (stackAdaptInsightCacheRef.current.has(cacheKey)) {
      return stackAdaptInsightCacheRef.current.get(cacheKey);
    }

    const inflight = stackAdaptInsightInFlightRef.current.get(cacheKey);
    if (inflight) return inflight;

    const doFetch = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);

      try {
        const res = await fetch(
          `${API_BASE_URL}/api/stackadapt/${encodeURIComponent(String(advertiserId))}/insights?${
            params.toString()
          }`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            signal: controller.signal,
            cache: "no-store",
          }
        );

        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.error) {
          console.warn(
            "[SherlocReports][StackAdapt Insights] insight fetch failed:",
            res.status,
            data?.error || data?.reason || data
          );
          return data?.code ? data : null;
        }

        stackAdaptInsightCacheRef.current.set(cacheKey, data);
        return data;
      } catch (err) {
        console.warn(
          "[SherlocReports][StackAdapt Insights] insight fetch error:",
          err?.name || err,
          err?.message || ""
        );
        return null;
      } finally {
        clearTimeout(timeout);
        stackAdaptInsightInFlightRef.current.delete(cacheKey);
      }
    })();

    stackAdaptInsightInFlightRef.current.set(cacheKey, doFetch);
    return doFetch;
  }, [defaultTimeframe, getAuthHeader]);

  const fetchGoogleAnalyticsSummary = useCallback(async ({
    advertiserId,
    date_start,
    date_end,
    campaign_id,
    campaignId,
  }) => {
    if (!advertiserId) return null;

    const params = new URLSearchParams();
    params.set("advertiserId", String(advertiserId));

    const cid = campaignId || campaign_id;
    if (cid) {
      params.set("campaignId", String(cid));
      params.set("campaign_id", String(cid));
    }

    if (date_start && date_end) {
      params.set("date_start", String(date_start));
      params.set("date_end", String(date_end));
    } else if (
      defaultTimeframe?.mode === "flight" &&
      defaultTimeframe.date_start &&
      defaultTimeframe.date_end
    ) {
      params.set("date_start", String(defaultTimeframe.date_start));
      params.set("date_end", String(defaultTimeframe.date_end));
    } else {
      params.set("datePreset", "last_30d");
      params.set("date_preset", "last_30d");
    }

    const cacheKey = buildGaSummaryKey({
      advertiserId,
      campaignId: cid,
      date_start: params.get("date_start"),
      date_end: params.get("date_end"),
      datePreset: params.get("datePreset"),
      date_preset: params.get("date_preset"),
    });

    if (gaSummaryCacheRef.current.has(cacheKey)) {
      return gaSummaryCacheRef.current.get(cacheKey);
    }

    const inflight = gaSummaryInFlightRef.current.get(cacheKey);
    if (inflight) return inflight;

    const endpointCandidates = [
      `${API_BASE_URL}/api/google-analytics/summary?${params.toString()}`,
      `${API_BASE_URL}/api/google-analytics/report/summary?${params.toString()}`,
      `${API_BASE_URL}/api/google-analytics/overview?${params.toString()}`,
      `${API_BASE_URL}/api/ga4/summary?${params.toString()}`,
      `${API_BASE_URL}/api/ga4/report/summary?${params.toString()}`,
      `${API_BASE_URL}/api/google-analytics/sherloc/${encodeURIComponent(
        String(advertiserId)
      )}?startDate=${encodeURIComponent(
        date_start || "30daysAgo"
      )}&endDate=${encodeURIComponent(date_end || "today")}`,
    ];

    const doFetch = (async () => {
      let bestCandidate = null;

      for (const url of endpointCandidates) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);

        try {
          const res = await fetch(url, {
            method: "GET",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            signal: controller.signal,
            cache: "no-store",
          });

          clearTimeout(timeout);

          if (res.status === 404) continue;

          const data = await res.json().catch(() => ({}));
          if (!res.ok || data?.error) {
            const errorText = String(data?.error || data?.message || "").toLowerCase();
            if (res.status === 401 || res.status === 403) {
              bestCandidate = preferGoogleAnalyticsPayload(bestCandidate, {
                raw: data,
                channel: null,
                diagnostics: {
                  classification: "auth_unavailable",
                  propertyId: null,
                  connected: false,
                  supportedPayload: false,
                  hasData: false,
                  reason: "GA4 authentication or integration access failed.",
                },
              });
            } else if (errorText.includes("no google analytics integration found")) {
              bestCandidate = preferGoogleAnalyticsPayload(bestCandidate, {
                raw: data,
                channel: null,
                diagnostics: {
                  classification: "config_missing",
                  propertyId: null,
                  connected: false,
                  supportedPayload: true,
                  hasData: false,
                  reason: "No Google Analytics integration is configured for this advertiser.",
                },
              });
            } else if (errorText.includes("property id is missing")) {
              bestCandidate = preferGoogleAnalyticsPayload(bestCandidate, {
                raw: data,
                channel: null,
                diagnostics: {
                  classification: "property_missing",
                  propertyId: null,
                  connected: false,
                  supportedPayload: true,
                  hasData: false,
                  reason: "GA4 property mapping is missing for this advertiser.",
                },
              });
            }
            continue;
          }

          const out = normalizeGoogleAnalyticsPayload(data);
          if (!out) continue;

          if (out.diagnostics?.classification === "unsupported_payload") {
            console.warn("[SherlocReports][GA4] Unsupported payload shape from summary endpoint:", {
              url,
              keys: Object.keys(data || {}),
            });
          }

          if (out.diagnostics?.classification === "connected_live" && out.channel) {
            gaSummaryCacheRef.current.set(cacheKey, out);
            return out;
          }

          bestCandidate = preferGoogleAnalyticsPayload(bestCandidate, out);
        } catch (_err) {
          clearTimeout(timeout);
        }
      }

      if (bestCandidate) {
        gaSummaryCacheRef.current.set(cacheKey, bestCandidate);
        return bestCandidate;
      }

      return null;
    })();

    gaSummaryInFlightRef.current.set(cacheKey, doFetch);

    try {
      return await doFetch;
    } finally {
      gaSummaryInFlightRef.current.delete(cacheKey);
    }
  }, [defaultTimeframe, getAuthHeader]);

  const fetchGoogleAnalyticsConnectionStatus = useCallback(
    async ({ advertiserId, date_start = null, date_end = null }) => {
    if (!advertiserId) return null;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const query = new URLSearchParams();
      if (date_start) query.set("date_start", String(date_start));
      if (date_end) query.set("date_end", String(date_end));
      const queryString = query.toString();
      const res = await fetch(
        `${API_BASE_URL}/api/ga4/status/${encodeURIComponent(String(advertiserId))}${
          queryString ? `?${queryString}` : ""
        }`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          signal: controller.signal,
          cache: "no-store",
        }
      );

      clearTimeout(timeout);

      if (res.status === 404) return null;

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error || data?.success === false) return null;

      return normalizeGoogleAnalyticsConnectionStatus(data);
    } catch (_err) {
      clearTimeout(timeout);
      return null;
    }
    },
    [getAuthHeader]
  );

  const fetchMetaCampaigns = async ({ advertiserId }) => {
    if (!advertiserId) return [];

    const fetchImportedReportingCampaignRows = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        const res = await fetch(
          `${API_BASE_URL}${IMPORTED_REPORTING_CONTEXT_PATH}?advertiserId=${encodeURIComponent(
            String(advertiserId)
          )}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            signal: controller.signal,
            cache: "no-store",
          }
        );
        clearTimeout(timeout);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return extractImportedReportingCampaigns(data);
      } catch (_e) {
        return [];
      }
    };

    /*
     * The canonical advertiser -> campaign relationship, fetched here rather
     * than read from page state so the selector's inventory is decided by one
     * request and REFRESH genuinely re-reconciles it. `cache: "no-store"` and
     * the fetch living inside this function are what stop a campaign approved
     * after the page hydrated from being invisible until a reload.
     */
    const fetchGovernedCampaignRows = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        const res = await fetch(
          `${API_BASE_URL}${buildCampaignAnalysisGovernedCampaignsPath(advertiserId)}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            signal: controller.signal,
            cache: "no-store",
          }
        );
        clearTimeout(timeout);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        const rows = Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data)
          ? data
          : [];
        return enforceCampaignAdvertiserIsolation({
          rows,
          expectedAdvertiserId: advertiserId,
          responseAdvertiserId: data?.advertiserId ?? null,
          requireRowAdvertiser: true,
          source: "governed_campaign_discovery",
          onViolation: (violation) => {
            console.warn("[SherlocReports] campaign isolation violation:", violation);
            publishSherlocTrace("client", "campaign_isolation_violation", violation);
          },
        }).rows;
      } catch (_e) {
        return [];
      }
    };

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      const res = await fetch(
        `${API_BASE_URL}/api/meta/campaigns?advertiserId=${encodeURIComponent(
          String(advertiserId)
        )}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          signal: controller.signal,
          cache: "no-store",
        }
      );

      clearTimeout(timeout);

      const data = await res.json().catch(() => ({}));

      if (res.ok && !data?.error) {
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const [importedReportingCampaigns, mediaCampaigns] = await Promise.all([
          fetchImportedReportingCampaignRows(),
          fetchGovernedCampaignRows(),
        ]);
        /*
         * Three inputs, one inventory. The governed list is what makes a
         * campaign EXIST here; live and imported rows say what it has
         * delivered. Ordering is the shared deterministic contract, not the
         * old active-first/localeCompare sort — which put the answer at the
         * mercy of the viewer's locale and of whether a platform happened to
         * call a campaign ACTIVE.
         */
        const list = buildCampaignAnalysisSelectorOptions({
          liveCampaigns: rows,
          importedReportingCampaigns,
          mediaCampaigns,
        })
          .map((c) => ({
            ...c,
            updated_time: c?.updated_time ?? c?.updatedTime ?? "",
            spend: c?.spend ?? 0,
            impressions: c?.impressions ?? 0,
            clicks: c?.clicks ?? 0,
          }))
          .filter((c) => c.id || c.name);

        const isolation = enforceCampaignAdvertiserIsolation({
          rows: list,
          expectedAdvertiserId: advertiserId,
          responseAdvertiserId: data?.advertiserId ?? null,
          source: "meta_campaigns",
          onViolation: (violation) => {
            console.warn("[SherlocReports] campaign isolation violation:", violation);
            publishSherlocTrace("client", "campaign_isolation_violation", violation);
          },
        });
        return isolation.rows;
      }
    } catch (_e) {
      // ignore and fallback
    }

    /*
     * Every fallback below carries the governed inventory too. A Meta outage,
     * a 404 on an advertiser with no ad account, or an empty insights payload
     * are all statements about PROVIDER data — none of them is a statement
     * about which campaigns the advertiser has, so none may empty the selector.
     */
    const payload = await fetchMetaInsights({ advertiserId, level: "campaign" });
    if (!payload) {
      const [importedReportingCampaigns, mediaCampaigns] = await Promise.all([
        fetchImportedReportingCampaignRows(),
        fetchGovernedCampaignRows(),
      ]);
      return buildCampaignAnalysisSelectorOptions({
        importedReportingCampaigns,
        mediaCampaigns,
      }).map((campaign) => ({
        ...campaign,
        spend: campaign.spend ?? 0,
        impressions: campaign.impressions ?? 0,
        clicks: campaign.clicks ?? 0,
      }));
    }
    const fallbackIsolation = enforceCampaignAdvertiserIsolation({
      rows: buildMetaCampaignListFromInsights(payload),
      expectedAdvertiserId: advertiserId,
      responseAdvertiserId: payload?.advertiserId ?? null,
      source: "meta_insights_fallback",
      onViolation: (violation) => {
        console.warn("[SherlocReports] campaign isolation violation:", violation);
        publishSherlocTrace("client", "campaign_isolation_violation", violation);
      },
    });
    const [fallbackImportedRows, fallbackMediaCampaigns] = await Promise.all([
      fetchImportedReportingCampaignRows(),
      fetchGovernedCampaignRows(),
    ]);
    return buildCampaignAnalysisSelectorOptions({
      liveCampaigns: fallbackIsolation.rows,
      importedReportingCampaigns: fallbackImportedRows,
      mediaCampaigns: fallbackMediaCampaigns,
    }).map((campaign) => ({
      ...campaign,
      spend: campaign.spend ?? 0,
      impressions: campaign.impressions ?? 0,
      clicks: campaign.clicks ?? 0,
    }));
  };

  const refreshMetaCampaigns = async ({ advertiserId, force = false }) => {
    if (!advertiserId) return;

    const key = String(advertiserId);
    if (!force && campaignsFetchedForAdvertiserRef.current === key) return;
    campaignsFetchedForAdvertiserRef.current = key;

    setCampaignsLoading(true);
    setCampaignsError("");

    try {
      const list = await fetchMetaCampaigns({ advertiserId });
      if (!isActiveAdvertiserId(key)) return;
      setMetaCampaigns(list);

      if (
        selectedCampaignId !== "auto" &&
        !list.some((c) => c.id && c.id === selectedCampaignId)
      ) {
        setSelectedCampaignId("auto");
        try {
          const ck = getCampaignSelectionKey(advertiserId);
          localStorage.setItem(ck, "auto");
        } catch (_e) {
          // ignore
        }
      }
    } catch (e) {
      console.warn("[SherlocReports] refreshMetaCampaigns error:", e);
      if (!isActiveAdvertiserId(key)) return;
      setMetaCampaigns([]);
      setCampaignsError("Could not load Meta campaigns.");
    } finally {
      if (!isActiveAdvertiserId(key)) return;
      setCampaignsLoading(false);
    }
  };

  useEffect(() => {
    if (!resolvedAdvertiserId) return;
    refreshMetaCampaigns({ advertiserId: resolvedAdvertiserId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAdvertiserId]);

  useEffect(() => {
    if (!resolvedAdvertiserId) {
      setApprovedPlanCampaigns([]);
      setApprovedPlanCampaignsHydrated(false);
      return;
    }

    setApprovedPlanCampaignsHydrated(false);
    let cancelled = false;
    const requestAdvertiserId = String(resolvedAdvertiserId);

    const loadApprovedPlanCampaigns = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}${buildCampaignAnalysisGovernedCampaignsPath(requestAdvertiserId)}`,
          {
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
          }
        );
        if (!res.ok) throw new Error(`Failed to load approved campaigns (${res.status})`);
        const data = await res.json().catch(() => ({}));
        const rows = Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data)
          ? data
          : [];
        if (!cancelled && isActiveAdvertiserId(requestAdvertiserId)) {
          const isolation = enforceCampaignAdvertiserIsolation({
            rows,
            expectedAdvertiserId: requestAdvertiserId,
            responseAdvertiserId: data?.advertiserId ?? null,
            requireRowAdvertiser: true,
            source: "media_campaigns",
            onViolation: (violation) => {
              console.warn("[SherlocReports] campaign isolation violation:", violation);
              publishSherlocTrace("client", "campaign_isolation_violation", violation);
            },
          });
          setApprovedPlanCampaigns(isolation.rows);
          setApprovedPlanCampaignsHydrated(true);
        }
      } catch {
        if (!cancelled && isActiveAdvertiserId(requestAdvertiserId)) {
          setApprovedPlanCampaigns([]);
          setApprovedPlanCampaignsHydrated(true);
        }
      }
    };

    loadApprovedPlanCampaigns();
    return () => {
      cancelled = true;
    };
  }, [resolvedAdvertiserId, getAuthHeader, isActiveAdvertiserId]);

  useEffect(() => {
    if (!resolvedAdvertiserId) {
      setCampaignSelectionHydrated(false);
      return;
    }

    setCampaignSelectionHydrated(false);
    try {
      const ck = getCampaignSelectionKey(resolvedAdvertiserId);
      const savedSel = localStorage.getItem(ck);
      setSelectedCampaignId(savedSel || "auto");
      publishSherlocTrace("client", "campaign_selection_restore", {
        advertiserId: resolvedAdvertiserId,
        key: ck,
        hasSavedSelection: Boolean(savedSel),
        selectedCampaignId: savedSel || "auto",
      });
    } catch (_e) {
      setSelectedCampaignId("auto");
    } finally {
      setCampaignSelectionHydrated(true);
    }
  }, [resolvedAdvertiserId]);

  const activeCampaignLabel = useMemo(() => {
    if (selectedCampaignId && selectedCampaignId !== "auto") {
      const picked = metaCampaigns.find((c) => String(c.id) === String(selectedCampaignId));
      if (picked) return `Focused: ${picked.name}`;
      return "Focused: Campaign";
    }

    if (advertiserApprovedPlanCampaignName) {
      return "DETECTED";
    }

    if (autoDetectedMetaCampaign?.name) {
      return "DETECTED";
    }

    return "AUTO DETECT";
  }, [
    advertiserApprovedPlanCampaignName,
    autoDetectedMetaCampaign,
    metaCampaigns,
    selectedCampaignId,
  ]);

  /**
   * What the campaign selector SHOWS for the automatic entry.
   *
   * The shared resolveAutoCampaignOptionLabel prefixes "Auto — " so the entry
   * cannot be mistaken for the real option beside it; Optimus still needs
   * that and is deliberately left alone. On this page the automatic selection
   * is already disclosed — the DETECTED pill sits directly above the control,
   * and the option carries its own aria-label — so the prefix only made the
   * campaign read as something other than its name.
   *
   * Only the campaign NAME loses the prefix. When no campaign is detected the
   * shared helper returns a STATE ("Auto (Most Active)", "Auto (No Meta
   * campaigns)") rather than a name, and stripping those would leave the
   * option blank, so they are passed through untouched.
   */
  const autoCampaignOptionLabel = useMemo(() => {
    if (campaignsLoading) return "Loading Meta campaigns…";
    const label = resolveAutoCampaignOptionLabel({
      autoDetectedCampaignName:
        advertiserApprovedPlanCampaignName || autoDetectedMetaCampaign?.name || "",
      hasCampaigns: metaCampaigns.length > 0,
    });
    /*
     * The prefix stays on.
     *
     * It was stripped so the selector would "show the campaign, not the
     * mechanism" — sound while this row was the ONLY row, because then it WAS
     * the campaign. Governed discovery ended that: a stripped label put the
     * mode beside the campaign it names, wearing the same words. This row now
     * survives only when it stands for no campaign on offer, and in that case
     * naming the mechanism is exactly right.
     */
    return label;
  }, [
    advertiserApprovedPlanCampaignName,
    autoDetectedMetaCampaign,
    campaignsLoading,
    metaCampaigns.length,
  ]);

  /*
   * The selector's rows, normalized to one row per immutable identity. This is
   * what the <select> renders — never the raw merge, and never a label-deduped
   * copy of it.
   */
  const campaignSelectorRows = useMemo(
    () => buildCampaignSelectorRows({ options: metaCampaigns }),
    [metaCampaigns]
  );

  const governedSelectionUpgrade = useMemo(
    () =>
      resolveGovernedSelectionUpgrade({
        selectedCampaignId,
        rows: campaignSelectorRows,
        autoDetectedCampaign: autoDetectedMetaCampaign,
        approvedPlanCampaign: advertiserApprovedPlanCampaign,
      }),
    [
      advertiserApprovedPlanCampaign,
      autoDetectedMetaCampaign,
      campaignSelectorRows,
      selectedCampaignId,
    ]
  );

  const renderAutoCampaignOption = useMemo(
    () =>
      shouldRenderAutoCampaignOption({
        selectedCampaignId,
        governedSelectionUpgrade,
      }),
    [governedSelectionUpgrade, selectedCampaignId]
  );

  /*
   * Resolve the mode into the campaign it stands for.
   *
   * Auto is a question ("which campaign?"), and once the governed answer is on
   * screen the question should stop being a selectable row. Persisting the
   * campaign's own id is what makes the synthetic row disappear because it was
   * ANSWERED rather than hidden — and it is what stops every downstream read
   * from being keyed on "auto", a value no campaign-scoped API can resolve.
   * The operator's intent is unchanged: auto already meant this campaign.
   */
  useEffect(() => {
    if (!resolvedAdvertiserId) return;
    if (campaignsLoading || !campaignSelectionHydrated) return;
    if (selectedCampaignId !== CAMPAIGN_SELECTOR_AUTO_VALUE) return;
    if (!governedSelectionUpgrade) return;

    setSelectedCampaignId(governedSelectionUpgrade);
    try {
      localStorage.setItem(
        getCampaignSelectionKey(resolvedAdvertiserId),
        governedSelectionUpgrade
      );
    } catch (_e) {
      // a selector that cannot persist still resolves correctly this session
    }
    publishSherlocTrace("client", "campaign_selection_governed_upgrade", {
      advertiserId: resolvedAdvertiserId,
      from: CAMPAIGN_SELECTOR_AUTO_VALUE,
      to: governedSelectionUpgrade,
    });
  }, [
    resolvedAdvertiserId,
    campaignSelectionHydrated,
    campaignsLoading,
    governedSelectionUpgrade,
    selectedCampaignId,
  ]);

  const autoCampaignOptionAriaLabel = useMemo(
    () =>
      campaignsLoading
        ? autoCampaignOptionLabel
        : `${autoCampaignOptionLabel} (automatically detected campaign)`,
    [autoCampaignOptionLabel, campaignsLoading]
  );

  const activeRunStatusPreservation = useMemo(
    () =>
      runStatusPreservation?.context &&
      contextsMatch(runStatusPreservation.context, currentRunContext)
        ? runStatusPreservation
        : null,
    [currentRunContext, runStatusPreservation]
  );

  const rawGaPillPresentation = getGoogleAnalyticsPillPresentation(gaStatus);
  useEffect(() => {
    const current = confirmedLiveStatusRef.current || {};
    const contextMatches = current.contextKey === currentRunContextKey;
    const next = contextMatches
      ? { ...current }
      : { contextKey: currentRunContextKey, ga: null, platforms: [] };

    if (isGoogleAnalyticsPillReadyLabel(rawGaPillPresentation.label)) {
      next.ga = rawGaPillPresentation;
    }

    confirmedLiveStatusRef.current = next;
  }, [currentRunContextKey, rawGaPillPresentation]);

  const gaPillPresentation = (() => {
    const preserved = confirmedLiveStatusRef.current;
    if (
      preserved?.contextKey === currentRunContextKey &&
      isGoogleAnalyticsPillReadyLabel(preserved?.ga?.label) &&
      !isGoogleAnalyticsMeasurementReadyStatus(gaStatus, rawGaPillPresentation.label)
    ) {
      return preserved.ga;
    }
    return isGoogleAnalyticsPillReadyLabel(activeRunStatusPreservation?.ga?.label) &&
      !isGoogleAnalyticsMeasurementReadyStatus(gaStatus, rawGaPillPresentation.label)
      ? activeRunStatusPreservation.ga
      : rawGaPillPresentation;
  })();
  const gaConnectionPill = gaPillPresentation.label;
  const gaConnectionPillClass = gaPillPresentation.className;

  const visibleGoogleAnalyticsChannel = useMemo(() => {
    const canonicalSiteAnalyticsChannel =
      buildGoogleAnalyticsChannelFromCanonicalAggregate(displayScopedAggregate);
    const channel = [
      canonicalSiteAnalyticsChannel,
      gaStatus?.channel,
      displayScopedSources?.live?.byChannel?.google_analytics,
      displayScopedResolved?.byChannel?.google_analytics,
      displayScopedAggregate?.byChannel?.google_analytics,
    ].find((candidate) => hasUsableMetrics(candidate));

    return channel
      ? {
          ...channel,
          data_source: channel.data_source || "google_analytics_live",
        }
      : null;
  }, [displayScopedAggregate, displayScopedResolved, displayScopedSources, gaStatus]);
  useEffect(() => {
    if (visibleGoogleAnalyticsChannel) {
      confirmedGoogleAnalyticsChannelRef.current = {
        contextKey: currentRunContextKey,
        channel: visibleGoogleAnalyticsChannel,
      };
      return;
    }

    if (!running || confirmedGoogleAnalyticsChannelRef.current?.contextKey !== currentRunContextKey) {
      confirmedGoogleAnalyticsChannelRef.current = {
        contextKey: currentRunContextKey,
        channel: null,
      };
    }
  }, [currentRunContextKey, running, visibleGoogleAnalyticsChannel]);

  // Canonical selected-campaign platform identity. Every platform surface on this page
  // reads this one set — Live Platforms, its count pill, and Platform Performance.
  const campaignPlatformScope = useMemo(
    () => buildSherlocCampaignPlatformScopeForCampaigns(governedCampaignsForSelection),
    [governedCampaignsForSelection]
  );
  const approvedSherlocChannelKeys = campaignPlatformScope.keys;

  const rebuildResolvedView = useCallback(({
    aggregateInput,
    metaLiveByPlat,
    gaLiveChannel,
    stackAdaptLiveByChannel,
    previousResolvedByChannel = {},
    traceSource = "unknown",
    applyState = true,
  }) => {
    const applyContext = buildRunContext(currentRunContext);

    if (isCanonicalSherlocAggregate(aggregateInput)) {
      const nextSources = buildEmptySources();
      const nextResolved = buildEmptyResolved();
      const canonicalSource = "canonical_performance_summary";
      const candidateTimestamp = getSherlocReportDataTimestamp(
        aggregateInput,
        aggregateInput?.dataFreshness || null,
        { source: canonicalSource }
      );
      const shouldApply =
        applyState &&
        canApplyReportDataSource({
          source: canonicalSource,
          timestamp: candidateTimestamp,
          context: applyContext,
        });

      if (shouldApply) {
        markReportDataSourceApplied({
          source: canonicalSource,
          timestamp: candidateTimestamp,
          context: applyContext,
        });
        if (isPlainObject(aggregateInput) && Object.keys(aggregateInput).length > 0) {
          setAggregate(aggregateInput);
        }
        setSources(nextSources);
        setSourcesContext(applyContext);
        setResolved(nextResolved);
        setResolvedContext(applyContext);
        setResolvedOrigin({
          source: canonicalSource,
          context: applyContext,
        });
      } else if (applyState) {
        publishReportDataSkip({
          source: canonicalSource,
          timestamp: candidateTimestamp,
          context: applyContext,
          traceSource,
        });
      }

      publishSherlocTrace("client", "resolved_rebuilt_canonical_bypass", {
        traceSource,
        currentRunContext,
        applied: shouldApply,
        applyState,
        totals: aggregateInput?.totals || null,
        byChannelKeys: Object.keys(aggregateInput?.byChannel || {}),
      });

      return { nextSources, nextResolved, applied: shouldApply };
    }

    const ingestByChannel = buildSharedIngestByChannel(aggregateInput);
    const liveByChannel = { ...(metaLiveByPlat || {}) };

    Object.keys(liveByChannel).forEach((k) => {
      liveByChannel[k] = { ...(liveByChannel[k] || {}), data_source: "meta_live" };
    });

    if (gaLiveChannel && hasSharedUsableMetrics(gaLiveChannel)) {
      liveByChannel.google_analytics = {
        ...(liveByChannel.google_analytics || {}),
        ...gaLiveChannel,
        data_source: "google_analytics_live",
      };
    }

    const platformLiveByChannel = { ...liveByChannel };
    Object.entries(stackAdaptLiveByChannel || {}).forEach(([key, value]) => {
      if (!key || !hasSharedUsableMetrics(value)) return;
      platformLiveByChannel[key] = platformLiveByChannel[key]
        ? mergeChannelAgg(platformLiveByChannel[key], value || {})
        : { ...(value || {}) };
    });
    const eligibleIngestByChannel = filterSherlocClientEligibleByChannel({
      byChannel: ingestByChannel,
      approvedChannelKeys: approvedSherlocChannelKeys,
      liveByChannel: platformLiveByChannel,
    });
    const { resolvedByChannel } = resolveReportingTruth({
      liveByChannel: platformLiveByChannel,
      ingestByChannel: eligibleIngestByChannel,
      previousResolvedByChannel,
    });
    const eligibleResolvedByChannel = filterSherlocClientEligibleByChannel({
      byChannel: resolvedByChannel,
      approvedChannelKeys: approvedSherlocChannelKeys,
      liveByChannel: platformLiveByChannel,
    });
    const eligibleResolvedTotals = computeResolvedTotals(eligibleResolvedByChannel);
    ["ctv", "display"].forEach((debugKey) => {
      const channel = eligibleResolvedByChannel?.[debugKey];
      if (!channel) return;
      console.debug("[SherlocReports][platform-card-source]", {
        surface: "client",
        channel: debugKey,
        sourceLabel: channel?.sourceLabel || channel?.data_source || null,
        spendBasis: channel?.spendBasis || channel?.spend_basis || null,
        selectionReason: channel?.selectionReason || null,
        spendCandidates: {
          reportSpend: channel?.reportSpend ?? channel?.report_spend ?? null,
          clientFacingSpend: channel?.clientFacingSpend ?? channel?.client_facing_spend ?? null,
          billedSpend: channel?.billedSpend ?? channel?.billed_spend ?? null,
          snapshotSpend: channel?.snapshotSpend ?? channel?.snapshot_spend ?? null,
          mediaSpend: channel?.mediaSpend ?? channel?.providerSpend ?? channel?.spend ?? null,
        },
        impressionCandidates: {
          reportImpressions: channel?.reportImpressions ?? channel?.report_impressions ?? null,
          snapshotImpressions: channel?.snapshotImpressions ?? channel?.snapshot_impressions ?? null,
          clientFacingImpressions:
            channel?.clientFacingImpressions ?? channel?.client_facing_impressions ?? null,
          liveImpressions: channel?.impressions ?? null,
        },
      });
    });

    const nextSources = {
      live: { byChannel: platformLiveByChannel },
      ingest: { byChannel: eligibleIngestByChannel },
    };

    const nextResolved = {
      byChannel: eligibleResolvedByChannel,
      totals: eligibleResolvedTotals,
    };

    const candidateTimestamp = getSherlocReportDataTimestamp(
      aggregateInput,
      nextResolved,
      { source: traceSource }
    );
    const shouldApply =
      applyState &&
      canApplyReportDataSource({
        source: traceSource,
        timestamp: candidateTimestamp,
        context: applyContext,
      });

    if (shouldApply) {
      markReportDataSourceApplied({
        source: traceSource,
        timestamp: candidateTimestamp,
        context: applyContext,
      });
      if (isPlainObject(aggregateInput) && Object.keys(aggregateInput).length > 0) {
        setAggregate(aggregateInput);
      }
      setSources(nextSources);
      setSourcesContext(applyContext);
      setResolved(nextResolved);
      setResolvedContext(applyContext);
      setResolvedOrigin({
        source: traceSource,
        context: applyContext,
      });
    } else if (applyState) {
      publishReportDataSkip({
        source: traceSource,
        timestamp: candidateTimestamp,
        context: applyContext,
        traceSource,
      });
    }

    publishSherlocTrace("client", "resolved_rebuilt", {
      traceSource,
      currentRunContext,
      applied: shouldApply,
      applyState,
      hasLiveChannels: Object.keys(platformLiveByChannel || {}).length > 0,
      hasIngestChannels: Object.keys(ingestByChannel || {}).length > 0,
      eligibleIngestChannels: Object.keys(eligibleIngestByChannel || {}),
      previousResolvedChannels: Object.keys(previousResolvedByChannel || {}).length,
      resolvedChannels: Object.keys(resolvedByChannel || {}),
      eligibleResolvedChannels: Object.keys(eligibleResolvedByChannel || {}),
      excludedInactiveChannels: Object.keys(resolvedByChannel || {}).filter(
        (key) => !eligibleResolvedByChannel[key]
      ),
      totals: nextResolved?.totals || null,
    });

    return { nextSources, nextResolved, applied: shouldApply };
  }, [
    approvedSherlocChannelKeys,
    canApplyReportDataSource,
    currentRunContext,
    markReportDataSourceApplied,
    publishReportDataSkip,
  ]);

  const refreshLatestAggregateSnapshot = useCallback(async (
    advertiserId,
    { signal, traceSource = "latest_aggregate_refresh", applyState = true } = {}
  ) => {
    const advId = String(advertiserId || "").trim();
    // Rolling-preset contexts (no resolvable flight window) still hydrate KPI
    // truth from the canonical performance summary: fetchLatestAiAggregate
    // omits start/end dates for preset contexts, so the request stays
    // advertiser/campaign-scoped and the backend resolves its own canonical
    // window (active campaigns, else the imported-reporting fallback). Without
    // this, advertisers whose only delivery is an imported reporting campaign
    // (flight not available on the focused live campaign) render $0 forever.
    if (!advId) return null;

    const requestContext = buildRunContext({
      ...currentRunContext,
      advertiserId: advId,
    });

    if (traceSource === "page_entry") {
      markKpiHydrationPending(requestContext);
    }

    try {
      const fetchedSnapshot = await fetchLatestAiAggregate({
        advertiserId: advId,
        apiBaseUrl: API_BASE_URL,
        getAuthHeader,
        fetchImpl: noStoreFetch,
        signal,
        timeframe: requestContext,
        campaignId: selectedReportingCampaignId,
        campaignName: selectedCampaignNameForContext,
      });

      if (!fetchedSnapshot?.aggregate && !fetchedSnapshot?.resolved) return null;
      if (!isActiveRunContext(advId, requestContext)) return null;

      const extractedContext = extractRunContextFromPayload(
        {
          ...(fetchedSnapshot.payload || {}),
          aggregate: fetchedSnapshot.aggregate || null,
          resolved: fetchedSnapshot.resolved || null,
        },
        { advertiserId: advId }
      );
      const canonicalSummary =
        fetchedSnapshot.payload?.canonicalSummary ||
        fetchedSnapshot.payload?.canonicalReporting ||
        fetchedSnapshot.aggregate ||
        null;
      const canonicalCampaignIds = Array.isArray(canonicalSummary?.campaignScope?.activeCampaignIds)
        ? canonicalSummary.campaignScope.activeCampaignIds.map(String)
        : [];
      const canonicalIsCampaignScoped =
        canonicalSummary?.scope === "campaign" &&
        Number(canonicalSummary?.campaignScope?.activeCount || canonicalCampaignIds.length) === 1;
      const canonicalMatchesFocusedCampaign = selectedReportingCampaignId
        ? canonicalIsCampaignScoped && canonicalCampaignIds.includes(String(selectedReportingCampaignId))
        : canonicalIsCampaignScoped;
      const hasMatchingContext = fetchedSnapshot.source === "canonical_performance_summary"
        ? requestContext.campaignId === "auto" || canonicalMatchesFocusedCampaign
        : contextsMatch(extractedContext, requestContext);

      publishSherlocTrace("client", "latest_aggregate_refresh", {
        traceSource,
        source: fetchedSnapshot.source || null,
        matched: hasMatchingContext,
        applyState,
        currentRunContext: requestContext,
        extractedContext,
        hasAggregate: Boolean(fetchedSnapshot.aggregate),
        hasResolved: Boolean(fetchedSnapshot.resolved),
      });

      if (!hasMatchingContext) return null;

      const latestSnapshot = fetchedSnapshot.aggregate
        ? {
            ...fetchedSnapshot,
            aggregate: {
              ...fetchedSnapshot.aggregate,
              reportContext: requestContext,
            },
          }
        : fetchedSnapshot;

      const hasResolvedSnapshot =
        Object.keys(latestSnapshot.resolved?.totals || {}).length > 0 ||
        Object.keys(latestSnapshot.resolved?.byChannel || {}).length > 0;
      const snapshotSource = latestSnapshot.source || traceSource;
      const snapshotTimestamp = getSherlocReportDataTimestamp(
        latestSnapshot.payload,
        latestSnapshot.aggregate,
        latestSnapshot.resolved
      );
      const snapshotIsCanonical = isCanonicalSherlocAggregate(latestSnapshot.aggregate);
      const canonicalSnapshotSource =
        snapshotSource === "canonical_performance_summary"
          ? snapshotSource
          : "canonical_performance_summary";

      if (snapshotIsCanonical) {
        if (
          applyState &&
          canApplyReportDataSource({
            source: canonicalSnapshotSource,
            timestamp: snapshotTimestamp,
            context: requestContext,
          })
        ) {
          markReportDataSourceApplied({
            source: canonicalSnapshotSource,
            timestamp: snapshotTimestamp,
            context: requestContext,
          });
          if (latestSnapshot.aggregate) {
            setAggregate(latestSnapshot.aggregate);
          }
          setSources(buildEmptySources());
          setSourcesContext(requestContext);
          setResolved(buildEmptyResolved());
          setResolvedContext(requestContext);
          setResolvedOrigin({
            source: canonicalSnapshotSource,
            context: requestContext,
          });
        } else if (applyState) {
          publishReportDataSkip({
            source: canonicalSnapshotSource,
            timestamp: snapshotTimestamp,
            context: requestContext,
            traceSource,
          });
        }
      } else if (hasResolvedSnapshot) {
        const nextResolved = {
          byChannel: latestSnapshot.resolved.byChannel || {},
          totals: latestSnapshot.resolved.totals || {},
        };
        if (
          applyState &&
          canApplyReportDataSource({
            source: snapshotSource,
            timestamp: snapshotTimestamp,
            context: requestContext,
          })
        ) {
          markReportDataSourceApplied({
            source: snapshotSource,
            timestamp: snapshotTimestamp,
            context: requestContext,
          });
          if (latestSnapshot.aggregate) {
            setAggregate(latestSnapshot.aggregate);
          }
          setResolved(nextResolved);
          setResolvedContext(requestContext);
          setSourcesContext(requestContext);
          setResolvedOrigin({
            source: snapshotSource,
            context: requestContext,
          });
        } else if (applyState) {
          publishReportDataSkip({
            source: snapshotSource,
            timestamp: snapshotTimestamp,
            context: requestContext,
            traceSource,
          });
        }
      } else if (applyState && latestSnapshot.aggregate) {
        rebuildResolvedView({
          aggregateInput: latestSnapshot.aggregate,
          previousResolvedByChannel: contextScopedResolvedRef.current?.byChannel || {},
          traceSource: snapshotSource,
        });
      }

      return latestSnapshot;
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      publishSherlocTrace("client", "latest_aggregate_refresh_error", {
        traceSource,
        advertiserId: advId,
        message: err?.message || String(err),
      });
      return null;
    } finally {
      if (traceSource === "page_entry" && isActiveRunContext(advId, requestContext)) {
        markKpiHydrationSettled(requestContext);
      }
    }
  }, [
    canApplyReportDataSource,
    currentRunContext,
    getAuthHeader,
    isActiveRunContext,
    markKpiHydrationPending,
    markKpiHydrationSettled,
    markReportDataSourceApplied,
    publishReportDataSkip,
    rebuildResolvedView,
    selectedReportingCampaignId,
    selectedCampaignNameForContext,
  ]);

  const refreshLatestAggregateSnapshotRef = useRef(refreshLatestAggregateSnapshot);
  useEffect(() => {
    refreshLatestAggregateSnapshotRef.current = refreshLatestAggregateSnapshot;
  }, [refreshLatestAggregateSnapshot]);

  const tryFetchLatestFromServerOnce = async (advertiserId) => {
    if (!advertiserId) return;
    if (isRollingPresetContext) {
      publishSherlocTrace("client", "latest_bootstrap_skipped", {
        reason: "rolling_preset_context",
        currentRunContext,
      });
      return;
    }

    const advId = String(advertiserId);
    const requestContext = buildRunContext({
      ...currentRunContext,
      advertiserId: advId,
    });
    const requestKey = buildSherlocRequestOwnershipKey(advId, requestContext);

    try {
      const persisted = localStorage.getItem(getLatestUnsupportedKey(requestKey));
      if (persisted === "1") return;
    } catch (_e) {
      // ignore
    }

    const prev = latestAttemptedRef.current.get(requestKey) || {
      attempted: false,
      disabled: false,
    };

    if (prev.attempted || prev.disabled) return;
    latestAttemptedRef.current.set(requestKey, { ...prev, attempted: true });

    const disablePersistently = () => {
      try {
        localStorage.setItem(getLatestUnsupportedKey(requestKey), "1");
      } catch (_e) {
        // ignore
      }
      latestAttemptedRef.current.set(requestKey, { attempted: true, disabled: true });
    };

    if (latestInFlightRef.current.get(requestKey)) return;

    const latestQuery = new URLSearchParams({
      advertiserId: advId,
    });
    if (requestContext?.date_start) latestQuery.set("startDate", String(requestContext.date_start));
    if (requestContext?.date_end) latestQuery.set("endDate", String(requestContext.date_end));
    if (requestContext?.campaignId && requestContext.campaignId !== "auto") {
      latestQuery.set("campaignId", String(requestContext.campaignId));
      latestQuery.set("campaign_id", String(requestContext.campaignId));
    }
    if (selectedCampaignNameForContext) {
      latestQuery.set("campaignName", String(selectedCampaignNameForContext));
      latestQuery.set("campaign_name", String(selectedCampaignNameForContext));
    }

    const endpoints = [
      `${API_BASE_URL}/api/ai/latest?${latestQuery.toString()}`,
      `${API_BASE_URL}/api/ai/latest/${encodeURIComponent(advId)}`,
      `${API_BASE_URL}/api/ai/${encodeURIComponent(advId)}`,
    ];

    const master = new AbortController();
    latestInFlightRef.current.set(requestKey, master);

    try {
      for (const url of endpoints) {
        const child = new AbortController();
        const onAbort = () => {
          try {
            child.abort();
          } catch (_e) {
            // ignore
          }
        };

        try {
          master.signal.addEventListener("abort", onAbort, { once: true });
        } catch (_e) {
          // ignore
        }

        const timeout = setTimeout(() => {
          try {
            child.abort();
          } catch (_e) {
            // ignore
          }
        }, 12000);

        try {
          const res = await fetch(url, {
            method: "GET",
            headers: { "Content-Type": "application/json", ...getAuthHeader() },
            signal: child.signal,
            cache: "no-store",
          });

          if (res.status === 404) {
            clearTimeout(timeout);
            continue;
          }

          const data = await res.json().catch(() => ({}));

          if (res.status === 400 || data?.error || !res.ok) {
            clearTimeout(timeout);
            disablePersistently();
            return;
          }

          const extractedRun = pickFirst(
            data?.run,
            data?.result?.run,
            data?.data?.run,
            data?.aiRun,
            data?.runRecord,
            data?.latest
          );
          const extractedAggregate = findAggregateDeep(data);

          let extractedSummary = normalizeSummary({
            ...data,
            run: extractedRun || data?.run || data?.result?.run || data?.data?.run || null,
          });

          const extractedRecs = normalizeRecommendations({
            ...data,
            run: extractedRun || data?.run || data?.result?.run || data?.data?.run || null,
          });
          if (!hasRenderableSummary(extractedSummary) && extractedRecs.length) {
            extractedSummary = buildFallbackAiSummaryForPartialNarrative(
              data?.diagnostics?.fallbackReason || "latest_bootstrap_partial_narrative",
              extractedAggregate?.totals || extractedAggregate?.totals_for_ai || null
            );
            publishSherlocTrace("client", "frontend_summary_repaired", {
              source: "latest_bootstrap",
              context: currentRunContext,
              reason: "latest_recommendations_present_summary_missing",
              summaryTitle: extractedSummary.title,
              summaryBulletCount: extractedSummary.bullets.length,
            });
          }

          const extractedAiMeta = normalizeAiMeta({
            ...data,
            run: extractedRun || data?.run || data?.result?.run || data?.data?.run || null,
          });
          const playbackValidation = validatePersistedNarrativePlayback({
            payload: data,
            aggregate: extractedAggregate || null,
            resolved: data?.resolved || data?.result?.resolved || data?.data?.resolved || null,
            summary: extractedSummary,
            recommendations: extractedRecs,
            source: "client_latest_bootstrap",
          });
          const untrustedNarrative = playbackValidation.trusted
            ? null
            : buildUntrustedHistoricalNarrative(playbackValidation.message);
          const safeExtractedSummary = playbackValidation.trusted
            ? extractedSummary
            : untrustedNarrative.summary;
          const safeExtractedRecs = playbackValidation.trusted
            ? extractedRecs
            : untrustedNarrative.recommendations;

          const extractedContext = extractRunContextFromPayload(
            {
              ...data,
              run: extractedRun || data?.run || data?.result?.run || data?.data?.run || null,
              aggregate: extractedAggregate || null,
            },
            {
              advertiserId: advId,
            }
          );

          if (!extractedRun && !extractedAggregate) {
            clearTimeout(timeout);
            disablePersistently();
            return;
          }

          if (!isActiveRunContext(advId, requestContext)) {
            clearTimeout(timeout);
            return;
          }

          const hasMatchingContext = contextsMatch(extractedContext, requestContext);
          publishSherlocTrace("client", "latest_bootstrap", {
            matched: hasMatchingContext,
            currentRunContext: requestContext,
            extractedContext,
            advertiserId: advId,
          });
          const shouldSuppressFailedLatestRun =
            hasMatchingContext &&
            isFailedRunRecord(extractedRun) &&
            hasPersistedReportMetrics({
              aggregate: extractedAggregate,
              resolved: displayScopedResolved,
            });
          const latestRunForState = shouldSuppressFailedLatestRun ? null : extractedRun || null;
          if (shouldSuppressFailedLatestRun) {
            publishSherlocTrace("client", "failed_run_restore_suppressed", {
              source: "latest_bootstrap",
              advertiserId: advId,
              context: extractedContext,
              currentRunContext: requestContext,
              hasMetrics: true,
            });
          }

      if (hasMatchingContext) {
        const latestTimestamp = getSherlocReportDataTimestamp(
          data,
          extractedRun,
          extractedAggregate
        );
        if (
          canApplyReportDataSource({
            source: "latest_bootstrap",
            timestamp: latestTimestamp,
            context: requestContext,
          })
        ) {
          markReportDataSourceApplied({
            source: "latest_bootstrap",
            timestamp: latestTimestamp,
            context: requestContext,
          });
          setAggregate(extractedAggregate || null);
          setOutcomeSemantics(
            extractSherlocOutcomeSemantics(data) ||
              extractSherlocOutcomeSemantics(extractedAggregate)
          );
        } else {
          publishReportDataSkip({
            source: "latest_bootstrap",
            timestamp: latestTimestamp,
            context: requestContext,
            traceSource: "latest_bootstrap",
          });
        }
      }

          if (!playbackValidation.trusted) {
            console.warn("[SherlocReports] blocked persisted narrative playback", {
              source: "client_latest_bootstrap",
              reason: playbackValidation.reason,
              advertiserId: advId,
            });
          }

          const normalizedNarrative = normalizeSherlocTextForRender((safeExtractedRecs || []).join("\n"));
          const narrativeWithPlacements = upsertPlacementLinesIntoPlatformAnalysis(
            normalizedNarrative,
            displayScopedResolved?.byChannel ||
              displayScopedAggregate?.byChannel ||
              extractedAggregate?.byChannel ||
              {}
          );
          const latestNarrativeRecommendations = narrativeWithPlacements
            ? [narrativeWithPlacements]
            : [];

          if (hasMatchingContext) {
            commitVisibleNarrativeSelection({
              source: "latest_run",
              run: latestRunForState || extractedRun || null,
              payload: data,
              aggregate: extractedAggregate || null,
              resolved: data?.resolved || data?.result?.resolved || data?.data?.resolved || null,
              context: extractedContext,
              summary: safeExtractedSummary,
              recommendations: latestNarrativeRecommendations,
              trusted: playbackValidation.trusted,
              blockedReason: playbackValidation.reason,
              apply: () => {
                setLastRun(latestRunForState);
                setAiSummary(safeExtractedSummary);
                setAiRecommendations(latestNarrativeRecommendations);
                setAnalystPanel(extractAnalystPanelFromRunPayload(data));
                setAiRunContext(extractedContext);
                setAiMeta(extractedAiMeta);
                setAiNarrativeMetricSignature(
                  buildNarrativeMetricSignature({
                    aggregate: extractedAggregate || null,
                  })
                );
              },
            });
          }

          try {
            const matchingContext = contextsMatch(extractedContext, requestContext);
            const trustedMatchingNarrative = matchingContext && playbackValidation.trusted;
            const cacheContext = matchingContext ? extractedContext : null;
            const payload = {
              run: latestRunForState,
              aggregate: extractedAggregate || null,
              sources: sources || null,
              resolved: displayScopedResolved || null,
              aiSummary: trustedMatchingNarrative ? safeExtractedSummary : buildEmptyAiSummary(),
              aiRecommendations: trustedMatchingNarrative ? latestNarrativeRecommendations : [],
              narrativeIntegrity:
                data?.narrativeIntegrity ||
                data?.narrative_integrity ||
                extractedRun?.narrative_integrity ||
                null,
              aiRunContext: cacheContext,
              aiMeta: matchingContext ? extractedAiMeta : buildEmptyAiMeta(),
              aiProviderKey: normalizeAiProviderForUi(selectedAiProvider),
              storageVersion: SHERLOC_REPORT_CACHE_VERSION,
              updatedAt: new Date().toISOString(),
            };
            const legacyKey = getLastRunStorageKey(advId);
            localStorage.setItem(legacyKey, JSON.stringify(payload));
            if (cacheContext) {
              const scopedKey = getScopedReportStorageKey({
                advertiserId: advId,
                context: cacheContext,
                aiProvider: selectedAiProvider,
              });
              localStorage.setItem(scopedKey, JSON.stringify(payload));
              publishSherlocTrace("client", "report_save", {
                source: "latest_bootstrap",
                scopedKey,
                legacyKey,
                context: cacheContext,
                selectedAiProvider,
                hasNarrative: Boolean(payload.aiRecommendations?.length),
                hasResolved: Boolean(payload.resolved?.byChannel),
              });
            }
          } catch (_e) {
            // ignore
          }

          clearTimeout(timeout);
          return;
        } catch (_e) {
          clearTimeout(timeout);
          disablePersistently();
          return;
        } finally {
          try {
            master.signal.removeEventListener("abort", onAbort);
          } catch (_e) {
            // ignore
          }
        }
      }

      disablePersistently();
    } finally {
      latestInFlightRef.current.delete(requestKey);
    }
  };

  useEffect(() => {
    if (!resolvedAdvertiserId) return;
    tryFetchLatestFromServerOnce(resolvedAdvertiserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRunContextKey, resolvedAdvertiserId]);

  useEffect(() => {
    const advId = String(resolvedAdvertiserId || "").trim();
    // Rolling-preset contexts hydrate canonical KPI truth too — see
    // refreshLatestAggregateSnapshot for the reporting-fallback rationale.
    if (!advId) return undefined;

    const controller = new AbortController();
    const pageEntryRefresh = refreshLatestAggregateSnapshotRef.current?.(advId, {
      signal: controller.signal,
      traceSource: "page_entry",
    });
    // This effect owns the detached task. The refresh function still rejects
    // cancellation for awaited callers; cleanup cancellation settles here.
    pageEntryRefresh?.catch((error) => {
      if (controller.signal.aborted && error?.name === "AbortError") return;
      publishSherlocTrace("client", "latest_aggregate_refresh_error", {
        traceSource: "page_entry",
        advertiserId: advId,
        message: error?.message || String(error),
      });
    });

    const refreshIfVisible = (traceSource) => {
      if (document.visibilityState === "hidden") return;
      refreshLatestAggregateSnapshotRef.current?.(advId, { traceSource });
    };

    const handleFocus = () => refreshIfVisible("window_focus");
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshIfVisible("visibility_return");
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const intervalId = window.setInterval(() => {
      refreshIfVisible("interval_refresh");
    }, SHERLOC_REPORTS_REFRESH_INTERVAL_MS);

    return () => {
      controller.abort();
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [
    currentRunContextKey,
    resolvedAdvertiserId,
  ]);

  useEffect(() => {
    if (!resolvedAdvertiserId) return;

    let cancelled = false;
    const requestId = ++liveStatusRequestSeqRef.current;
    setGaStatus(buildPendingGoogleAnalyticsStatus());
    const t = setTimeout(async () => {
      try {
        const focusedCampaignId = selectedLiveMetaCampaignId;

        const date_start =
          defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_start || null : null;
        const date_end =
          defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_end || null : null;

        setLiveStatus({
          state: "loading",
          message: "Refreshing LIVE Meta…",
          contextConnected: false,
        });
        setGaStatus(buildPendingGoogleAnalyticsStatus("Refreshing GA4…"));

        const [livePayload, gaPayload, stackAdaptPayload, gaConnectionStatus] = await Promise.all([
          readProviderForSelectedCampaign(() =>
            fetchMetaInsights({
              advertiserId: resolvedAdvertiserId,
              date_start,
              date_end,
              breakdowns: "publisher_platform",
              campaignId: focusedCampaignId,
              campaign_id: focusedCampaignId,
              level: "campaign",
            })
          ),
          fetchGoogleAnalyticsSummary({
            advertiserId: resolvedAdvertiserId,
            date_start,
            date_end,
            campaignId: focusedCampaignId,
            campaign_id: focusedCampaignId,
          }),
          readProviderForSelectedCampaign(() =>
            fetchStackAdaptLiveReporting({
              advertiserId: resolvedAdvertiserId,
              date_start,
              date_end,
              date_preset:
                defaultTimeframe?.mode === "preset" ? defaultTimeframe?.date_preset || "last_30d" : "",
              datePreset:
                defaultTimeframe?.mode === "preset" ? defaultTimeframe?.date_preset || "last_30d" : "",
            })
          ),
          fetchGoogleAnalyticsConnectionStatus({
            advertiserId: resolvedAdvertiserId,
            date_start,
            date_end,
          }),
        ]);

        if (cancelled || requestId !== liveStatusRequestSeqRef.current) return;

        let metaByPlat = {};
        let gaLiveChannel = null;
        let stackAdaptLiveByChannel = {};

        if (!livePayload) {
          setLiveStatus({
            state: "warn",
            message: "LIVE Meta unavailable (no payload).",
            contextConnected: false,
          });
        } else {
          metaByPlat = buildMetaPublisherPlatformAgg(livePayload, {
            campaignId: focusedCampaignId || null,
          });
          const hasMetaContextAssociation = focusedCampaignId
            ? metaCampaignsRef.current.some(
                (campaign) => String(campaign?.id || "") === String(focusedCampaignId)
              )
            : metaCampaignsRef.current.length > 0;
          const metaContextConnected =
            hasMetaContextAssociation && Object.keys(metaByPlat || {}).length > 0;

          setLiveStatus({
            state: "ok",
            message: `LIVE Meta refreshed${focusedCampaignId ? " (campaign filtered)" : ""}.`,
            contextConnected: metaContextConnected,
          });
        }

        {
          const gaDescriptor = getGoogleAnalyticsStatusDescriptor(
            gaPayload,
            gaConnectionStatus,
            {
              focusedCampaignId,
              hasFutureApprovedWindow: approvedWindowState.hasFutureApprovedWindow,
              approvedWindowStartLabel: approvedWindowState.approvedWindowStartLabel,
            }
          );
          gaLiveChannel = gaDescriptor.channel;
          setGaStatus(gaDescriptor);
        }

        if (stackAdaptPayload?.ok) {
          stackAdaptLiveByChannel = buildStackAdaptLiveByChannel(stackAdaptPayload);
        }

        rebuildResolvedView({
          aggregateInput: contextScopedAggregateRef.current || {},
          metaLiveByPlat: metaByPlat || {},
          gaLiveChannel,
          stackAdaptLiveByChannel,
          previousResolvedByChannel: contextScopedResolvedRef.current?.byChannel || {},
          traceSource: "live_refresh_effect",
        });
      } catch (_e) {
        if (cancelled || requestId !== liveStatusRequestSeqRef.current) return;
        setLiveStatus({
          state: "warn",
          message: "LIVE Meta refresh failed.",
          contextConnected: false,
        });
        setGaStatus({
          state: "warn",
          message: "GA4 refresh failed.",
          contextConnected: false,
          classification: "unavailable",
          connectionState: "unavailable",
        });
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    resolvedAdvertiserId,
    selectedCampaignId,
    selectedLiveMetaCampaignId,
    approvedWindowState.approvedWindowStartLabel,
    approvedWindowState.hasFutureApprovedWindow,
    fetchGoogleAnalyticsConnectionStatus,
    fetchGoogleAnalyticsSummary,
    fetchMetaInsights,
    fetchStackAdaptLiveReporting,
    readProviderForSelectedCampaign,
    rebuildResolvedView,
    defaultTimeframe?.mode,
    defaultTimeframe?.date_start,
    defaultTimeframe?.date_end,
    defaultTimeframe?.date_preset,
    statusRefreshTick,
  ]);

  const handleCampaignChange = (e) => {
    const v = e.target.value;
    liveStatusRequestSeqRef.current += 1;
    for (const [, controller] of latestInFlightRef.current.entries()) {
      try {
        controller.abort();
      } catch (_err) {
        // ignore
      }
    }
    latestInFlightRef.current = new Map();
    resetReportDataSourceGuard();
    clearRunSnapshotLock();
    setRunStatusPreservation(null);
    setSelectedCampaignId(v);
    setError("");
    const nextCampaign =
      metaCampaigns.find((campaign) => String(campaign?.id || "") === String(v || "")) || null;
    const nextCampaignContextId = resolveCampaignAnalysisContextCampaignId({
      selectedCampaignId: v,
      selectedCampaign: nextCampaign,
    });
    const nextContext = buildRunContext({
      ...currentRunContext,
      campaignId: nextCampaignContextId,
    });
    clearVisibleNarrativeSelection({
      source: "campaign_change",
      reason: "campaign_selection_changed",
      context: nextContext,
      clearLastRun: true,
      detail: {
        previousCampaignId: currentRunContext?.campaignId || "auto",
        nextCampaignId: nextCampaignContextId,
      },
    });
    setNarrativeAlignmentFailure(null);
    setSources({ live: { byChannel: {} }, ingest: { byChannel: {} } });
    setSourcesContext(null);
    setResolved({ byChannel: {}, totals: {} });
    contextScopedResolvedRef.current = buildEmptyResolved();
    contextScopedAggregateRef.current = null;
    setResolvedContext(null);
    setResolvedOrigin(null);
    setAggregate(null);
    setLiveStatus({ state: "idle", message: "", contextConnected: false });
    setGaStatus(buildPendingGoogleAnalyticsStatus());

    try {
      if (resolvedAdvertiserId) {
        const ck = getCampaignSelectionKey(resolvedAdvertiserId);
        localStorage.setItem(ck, v);
      }
    } catch (_err) {
      // ignore
    }
  };

  const handleAiProviderChange = (e) => {
    const next = normalizeAiProviderForUi(e.target.value);
    setSelectedAiProvider(next);
    setError("");
    setNarrativeAlignmentFailure(null);
    clearRunSnapshotLock();
  };

  const runSherloc = async ({ trigger = "manual", attachOperationId = null } = {}) => {
    if (!resolvedAdvertiserId) {
      setError("Please choose an advertiser before running Sherloc AI.");
      return;
    }

    if (runAbortRef.current) runAbortRef.current.abort();
    const runController = new AbortController();
    runAbortRef.current = runController;
    const isRunActive = () =>
      mountedRef.current &&
      !runController.signal.aborted &&
      runAbortRef.current === runController;

    let prevResolved = displayScopedResolved;
    let focusedCampaignId = null;
    let requestAdvertiserId = String(resolvedAdvertiserId);
    let clientDateStart = null;
    let clientDateEnd = null;
    let clientDatePreset = null;
    let expectedContext = null;
    let metaByPlatForAI = null;
    let gaSummaryForAI = null;
    let stackAdaptLiveByChannelForAI = {};
    let preRunResolvedView = null;
    let lockedSnapshotForRun = null;
    let snapshotBaseAggregate = contextScopedAggregateRef.current || {};
    let snapshotBaseResolved = contextScopedResolvedRef.current || buildEmptyResolved();
    let snapshotBaseTimestamp = null;

    // Hydration replays an analysis that already finished; it must not put the
    // button into "Running Sherloc AI" or clear an error the operator has not
    // dismissed. Everything after this point is the SAME render contract a
    // fresh generation uses — one path, so a reloaded report and a just-
    // generated one cannot diverge.
    const isHydration = trigger === PERSISTED_HYDRATION_TRIGGER;

    try {
      if (!isHydration) {
        setRunning(true);
        setError("");
        setVoiceError("");
        setNarrativeAlignmentFailure(null);
      }

      prevResolved = displayScopedResolved;

      focusedCampaignId = selectedLiveMetaCampaignId;
      requestAdvertiserId = String(resolvedAdvertiserId);

      clientDateStart =
        defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_start || null : null;
      clientDateEnd =
        defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_end || null : null;
      clientDatePreset =
        defaultTimeframe?.mode === "preset"
          ? String(defaultTimeframe?.date_preset || "last_30d")
          : null;

      expectedContext = buildRunContext({
        advertiserId: requestAdvertiserId,
        campaignId: currentRunContext.campaignId,
        date_start: clientDateStart,
        date_end: clientDateEnd,
        date_preset: clientDatePreset,
      });
      publishSherlocTrace("client", "run_analysis_click", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        selectedAiProvider,
      });
      publishSherlocTrace("client", "run_error_cleared", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
      });

      const runStartPlatformStatuses = buildPlatformStatusTrace(displayLivePlatformsForUi);
      const runStartStatusPreservation = {
        context: expectedContext,
        startedAt: new Date().toISOString(),
        platforms: displayLivePlatformsForUi,
        gaChannel:
          visibleGoogleAnalyticsChannel ||
          (confirmedGoogleAnalyticsChannelRef.current?.contextKey === currentRunContextKey
            ? confirmedGoogleAnalyticsChannelRef.current?.channel || null
            : null),
        ga: {
          label: gaConnectionPill,
          className: gaConnectionPillClass,
        },
      };
      setRunStatusPreservation(runStartStatusPreservation);
      publishSherlocTrace("client", "run_analysis_visible_platform_statuses", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        platforms: runStartPlatformStatuses,
        ga: runStartStatusPreservation.ga,
        hasPreservedGaChannel: hasSharedUsableMetrics(runStartStatusPreservation.gaChannel),
      });
      const preserveGaLiveDuringRun = runStartStatusPreservation.ga.label === "LIVE";

      const visibleSource =
        reportDataSourceRef.current?.context &&
        contextsMatch(reportDataSourceRef.current.context, expectedContext)
          ? reportDataSourceRef.current
          : null;
      const currentVisibleSnapshot = buildSherlocRunSnapshotDescriptor({
        source: visibleSource?.source || resolvedOrigin?.source || "current_visible_kpi_truth",
        timestamp:
          visibleSource?.timestamp ||
          getSherlocReportDataTimestamp(displayScopedAggregate, displayScopedResolved),
        aggregate: displayScopedAggregate || null,
        resolved: displayScopedResolved || null,
        sources: displayScopedSources || null,
      });
      publishSherlocTrace("client", "run_analysis_current_visible_snapshot", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        source: currentVisibleSnapshot.source,
        timestamp: currentVisibleSnapshot.timestamp,
        hasMetrics: currentVisibleSnapshot.hasMetrics,
        metrics: currentVisibleSnapshot.metricSignature?.values || null,
      });

      const latestForRun = await refreshLatestAggregateSnapshot(requestAdvertiserId, {
        traceSource: "run_analysis_snapshot_lock",
        applyState: false,
      });
      if (!isActiveRunContext(requestAdvertiserId, expectedContext)) return;

      const candidateRunSnapshot = latestForRun
        ? buildSherlocRunSnapshotDescriptor({
            source: latestForRun.source || "run_analysis_snapshot_lock",
            payload: latestForRun.payload || null,
            aggregate: latestForRun.aggregate || null,
            resolved: latestForRun.resolved || null,
            sources: null,
          })
        : null;
      publishSherlocTrace("client", "run_analysis_candidate_snapshot", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        source: candidateRunSnapshot?.source || null,
        timestamp: candidateRunSnapshot?.timestamp || null,
        hasMetrics: Boolean(candidateRunSnapshot?.hasMetrics),
        metrics: candidateRunSnapshot?.metricSignature?.values || null,
      });

      let selectedRunSnapshot = currentVisibleSnapshot;
      let selectedSnapshotReason = currentVisibleSnapshot.hasMetrics
        ? "current_visible_kpi_truth"
        : "current_visible_empty";

      if (candidateRunSnapshot?.hasMetrics) {
        const candidateTimestamp = Number(candidateRunSnapshot.timestamp || 0);
        const visibleTimestamp = Number(currentVisibleSnapshot.timestamp || 0);
        const candidatePriority = getSherlocReportSourcePriority(candidateRunSnapshot.source);
        const visiblePriority = getSherlocReportSourcePriority(currentVisibleSnapshot.source);
        const candidateIsStrictlyNewer = candidateTimestamp > visibleTimestamp;
        const candidateIsHigherPriority = candidatePriority > visiblePriority;
        const candidateWouldDowngradeVisible = hasSherlocSnapshotMetricDowngrade(
          currentVisibleSnapshot,
          candidateRunSnapshot
        );
        const candidateDiffersFromVisible = hasMaterialNarrativeMetricDiff(
          currentVisibleSnapshot.metricSignature,
          candidateRunSnapshot.metricSignature
        );

        if (!currentVisibleSnapshot.hasMetrics) {
          selectedRunSnapshot = candidateRunSnapshot;
          selectedSnapshotReason = "visible_empty_candidate_has_metrics";
        } else if (candidateIsHigherPriority) {
          selectedRunSnapshot = candidateRunSnapshot;
          selectedSnapshotReason = candidateWouldDowngradeVisible
            ? "candidate_higher_priority_authority_wins"
            : "candidate_higher_priority_than_visible";
        } else if (candidateIsStrictlyNewer && !candidateWouldDowngradeVisible) {
          selectedRunSnapshot = candidateRunSnapshot;
          selectedSnapshotReason = "candidate_strictly_newer_than_visible";
        } else {
          selectedRunSnapshot = currentVisibleSnapshot;
          selectedSnapshotReason = candidateWouldDowngradeVisible
            ? "candidate_metric_downgrade_visible_wins"
            : "candidate_not_newer_visible_wins";
          if (
            candidateWouldDowngradeVisible ||
            candidateDiffersFromVisible ||
            candidateTimestamp < visibleTimestamp
          ) {
            publishSherlocTrace("client", "run_analysis_stale_snapshot_rejected", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              reason: candidateWouldDowngradeVisible
                ? "candidate_metrics_lower_than_current_visible"
                : candidateDiffersFromVisible
                ? "candidate_not_newer_and_metric_signature_differs"
                : "candidate_older_than_visible_snapshot",
              candidate: {
                source: candidateRunSnapshot.source,
                timestamp: candidateRunSnapshot.timestamp,
                metrics: candidateRunSnapshot.metricSignature?.values || null,
              },
              currentVisible: {
                source: currentVisibleSnapshot.source,
                timestamp: currentVisibleSnapshot.timestamp,
                metrics: currentVisibleSnapshot.metricSignature?.values || null,
              },
            });
          }
        }
      } else if (!currentVisibleSnapshot.hasMetrics && candidateRunSnapshot) {
        selectedRunSnapshot = candidateRunSnapshot;
        selectedSnapshotReason = "candidate_without_metrics_fallback";
      }

      snapshotBaseAggregate = selectedRunSnapshot?.aggregate || {};
      snapshotBaseResolved = selectedRunSnapshot?.resolved || buildEmptyResolved();
      snapshotBaseTimestamp = selectedRunSnapshot?.timestamp || snapshotBaseTimestamp;
      prevResolved = snapshotBaseResolved;
      publishSherlocTrace("client", "run_analysis_locked_snapshot_selected", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        reason: selectedSnapshotReason,
        snapshotTimestamp: snapshotBaseTimestamp,
        source: selectedRunSnapshot?.source || "current_visible_kpi_truth",
        selectedMetrics: selectedRunSnapshot?.metricSignature?.values || null,
        currentVisibleMetrics: currentVisibleSnapshot.metricSignature?.values || null,
        candidateMetrics: candidateRunSnapshot?.metricSignature?.values || null,
        hasAggregate: Boolean(snapshotBaseAggregate?.totals),
        hasResolved: Boolean(snapshotBaseResolved?.totals),
      });

      const gaStatusAtRunStart = gaStatus;
      const gaVisualStateAtRunStart = getGoogleAnalyticsPillPresentation(gaStatusAtRunStart);
      const canReuseLiveGa4ForRun =
        isGoogleAnalyticsStatusVisuallyLive(gaStatusAtRunStart) &&
        hasSharedUsableMetrics(gaStatusAtRunStart?.channel);
      if (canReuseLiveGa4ForRun) {
        gaSummaryForAI = gaStatusAtRunStart.channel;
      }
      const ga4RunRecheckReason = canReuseLiveGa4ForRun
        ? "existing_live_status_reused_for_run_analysis"
        : "run_analysis_requires_ga4_context";
      publishSherlocTrace("client", "ga4_status_recheck_reason", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        reason: ga4RunRecheckReason,
        visibleState: gaVisualStateAtRunStart.label,
        silent: canReuseLiveGa4ForRun,
      });
      publishSherlocTrace("client", "ga4_status_recheck_started", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        reason: ga4RunRecheckReason,
        skipped: canReuseLiveGa4ForRun,
        silent: canReuseLiveGa4ForRun,
      });

      try {
        setLiveStatus({
          state: "loading",
          message: "Refreshing LIVE Meta for AI…",
          contextConnected: false,
        });
        if (!canReuseLiveGa4ForRun) {
          const pendingGaStatus = buildPendingGoogleAnalyticsStatus("Refreshing GA4 for AI…");
          const pendingVisualState = getGoogleAnalyticsPillPresentation(pendingGaStatus);
          if (pendingVisualState.label !== gaVisualStateAtRunStart.label) {
            publishSherlocTrace("client", "ga4_status_visual_state_changed", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              reason: ga4RunRecheckReason,
              from: gaVisualStateAtRunStart.label,
              to: pendingVisualState.label,
            });
          }
          if (preserveGaLiveDuringRun) {
            publishSherlocTrace("client", "run_analysis_meta_status_preserved", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              surface: "ga4",
              reason: "preflight_loading_suppressed",
              current: runStartStatusPreservation.ga,
              candidate: pendingVisualState,
            });
          } else {
            setGaStatus(pendingGaStatus);
          }
        }

        const [
          livePayloadForAI,
          gaPayloadForAI,
          stackAdaptPayloadForAI,
          gaConnectionStatus,
        ] = await Promise.all([
          readProviderForSelectedCampaign(() =>
            fetchMetaInsights({
              advertiserId: requestAdvertiserId,
              date_start: clientDateStart,
              date_end: clientDateEnd,
              breakdowns: "publisher_platform",
              campaignId: focusedCampaignId,
              campaign_id: focusedCampaignId,
              level: "campaign",
            })
          ),
          canReuseLiveGa4ForRun
            ? Promise.resolve(null)
            : fetchGoogleAnalyticsSummary({
                advertiserId: requestAdvertiserId,
                date_start: clientDateStart,
                date_end: clientDateEnd,
                campaignId: focusedCampaignId,
                campaign_id: focusedCampaignId,
              }),
          readProviderForSelectedCampaign(() =>
            fetchStackAdaptLiveReporting({
              advertiserId: requestAdvertiserId,
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
            })
          ),
          canReuseLiveGa4ForRun
            ? Promise.resolve(null)
            : fetchGoogleAnalyticsConnectionStatus({
                advertiserId: requestAdvertiserId,
                date_start: clientDateStart,
                date_end: clientDateEnd,
              }),
        ]);

        if (!isActiveRunContext(requestAdvertiserId, expectedContext)) return;

        if (livePayloadForAI) {
          metaByPlatForAI = buildMetaPublisherPlatformAgg(livePayloadForAI, {
            campaignId: focusedCampaignId || null,
          });
          const hasMetaContextAssociation = focusedCampaignId
            ? metaCampaigns.some(
                (campaign) => String(campaign?.id || "") === String(focusedCampaignId)
              )
            : metaCampaigns.length > 0;
          const metaContextConnected =
            hasMetaContextAssociation && Object.keys(metaByPlatForAI || {}).length > 0;
          setLiveStatus({
            state: "ok",
            message: `LIVE Meta refreshed${focusedCampaignId ? " (campaign filtered)" : ""}.`,
            contextConnected: metaContextConnected,
          });
        } else {
          setLiveStatus({
            state: "warn",
            message: "LIVE Meta unavailable for AI (no payload).",
            contextConnected: false,
          });
        }

        if (canReuseLiveGa4ForRun) {
          publishSherlocTrace("client", "ga4_status_recheck_completed", {
            trigger,
            advertiserId: requestAdvertiserId,
            context: expectedContext,
            reason: ga4RunRecheckReason,
            skipped: true,
            visualState: gaVisualStateAtRunStart.label,
            hasGoogleAnalytics: hasSharedUsableMetrics(gaSummaryForAI),
          });
        } else {
          const gaDescriptor = getGoogleAnalyticsStatusDescriptor(
            gaPayloadForAI,
            gaConnectionStatus,
            {
              focusedCampaignId,
              forAi: true,
              hasFutureApprovedWindow: approvedWindowState.hasFutureApprovedWindow,
              approvedWindowStartLabel: approvedWindowState.approvedWindowStartLabel,
            }
          );
          gaSummaryForAI = gaDescriptor.channel;
          const nextGaVisualState = getGoogleAnalyticsPillPresentation(gaDescriptor);
          if (nextGaVisualState.label !== "CHECKING") {
            publishSherlocTrace("client", "ga4_status_visual_state_changed", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              reason: "run_analysis_ga4_recheck_completed",
              from: "CHECKING",
              to: nextGaVisualState.label,
            });
          }
          publishSherlocTrace("client", "ga4_status_recheck_completed", {
            trigger,
            advertiserId: requestAdvertiserId,
            context: expectedContext,
            reason: ga4RunRecheckReason,
            skipped: false,
            visualState: nextGaVisualState.label,
            connectionState: gaDescriptor.connectionState,
            hasGoogleAnalytics: hasSharedUsableMetrics(gaSummaryForAI),
          });
          if (nextGaVisualState.label === "LIVE" || !preserveGaLiveDuringRun) {
            setGaStatus(gaDescriptor);
          } else {
            publishSherlocTrace("client", "run_analysis_meta_status_preserved", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              surface: "ga4",
              reason: "preflight_non_live_result_suppressed",
              current: runStartStatusPreservation.ga,
              candidate: nextGaVisualState,
            });
          }
        }

        if (stackAdaptPayloadForAI?.ok) {
          stackAdaptLiveByChannelForAI = buildStackAdaptLiveByChannel(stackAdaptPayloadForAI);
        }
      } catch (e) {
        if (!isActiveRunContext(requestAdvertiserId, expectedContext)) return;
        console.warn("[SherlocReports][AI Live Grounding] prefetch failed:", e?.message || e);
        publishSherlocTrace("client", "ga4_status_recheck_completed", {
          trigger,
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          reason: ga4RunRecheckReason,
          skipped: canReuseLiveGa4ForRun,
          failed: true,
          visualState: gaVisualStateAtRunStart.label,
        });
        setLiveStatus({
          state: "warn",
          message: "LIVE Meta refresh failed for AI.",
          contextConnected: false,
        });
        if (!canReuseLiveGa4ForRun) {
          const failedGaStatus = {
            state: "warn",
            message: "GA4 refresh failed for AI.",
            contextConnected: false,
            classification: "unavailable",
            connectionState: "unavailable",
          };
          const failedVisualState = getGoogleAnalyticsPillPresentation(failedGaStatus);
          if (failedVisualState.label !== gaVisualStateAtRunStart.label) {
            publishSherlocTrace("client", "ga4_status_visual_state_changed", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              reason: "run_analysis_ga4_recheck_failed",
              from: gaVisualStateAtRunStart.label,
              to: failedVisualState.label,
            });
          }
          if (preserveGaLiveDuringRun) {
            publishSherlocTrace("client", "run_analysis_meta_status_preserved", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              surface: "ga4",
              reason: "preflight_error_suppressed",
              current: runStartStatusPreservation.ga,
              candidate: failedVisualState,
            });
          } else {
            setGaStatus(failedGaStatus);
          }
        }
      }

      const candidateRunLiveByChannel = {
        ...(metaByPlatForAI || {}),
        ...(stackAdaptLiveByChannelForAI || {}),
      };
      const candidateRunPlatforms = getLivePlatformsForUi({
        liveByChannel: candidateRunLiveByChannel,
        reportingByChannel: getSherlocSnapshotReportingByChannel(selectedRunSnapshot),
      });
      const candidateRunPlatformStatuses = buildPlatformStatusTrace(candidateRunPlatforms);
      publishSherlocTrace("client", "run_analysis_candidate_platform_statuses", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        platforms: candidateRunPlatformStatuses,
      });
      const downgradedPlatformKeys = Object.entries(runStartPlatformStatuses)
        .filter(([key, status]) => status === "LIVE" && candidateRunPlatformStatuses[key] !== "LIVE")
        .map(([key]) => key);
      if (downgradedPlatformKeys.length) {
        publishSherlocTrace("client", "run_analysis_platform_downgrade_rejected", {
          trigger,
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          rejectedKeys: downgradedPlatformKeys,
          currentVisible: runStartPlatformStatuses,
          candidate: candidateRunPlatformStatuses,
        });
      }

      const snapshotBaseIsCanonical = isCanonicalSherlocAggregate(snapshotBaseAggregate);
      preRunResolvedView = snapshotBaseIsCanonical
        ? {
            nextSources: buildEmptySources(),
            nextResolved: buildEmptyResolved(),
            applied: false,
          }
        : rebuildResolvedView({
            aggregateInput: snapshotBaseAggregate || {},
            metaLiveByPlat: metaByPlatForAI || {},
            gaLiveChannel: gaSummaryForAI,
            stackAdaptLiveByChannel: stackAdaptLiveByChannelForAI || {},
            previousResolvedByChannel:
              snapshotBaseResolved?.byChannel ||
              prevResolved?.byChannel ||
              contextScopedResolvedRef.current?.byChannel ||
              {},
            traceSource: "run_sherloc_preflight",
            applyState: false,
          });
      const preflightSnapshot = buildSherlocRunSnapshotDescriptor({
        source: "run_sherloc_preflight",
        aggregate: snapshotBaseAggregate || null,
        resolved: preRunResolvedView?.nextResolved || null,
        sources: preRunResolvedView?.nextSources || null,
      });
      if (hasSherlocSnapshotMetricDowngrade(selectedRunSnapshot, preflightSnapshot)) {
        publishSherlocTrace("client", "run_analysis_stale_snapshot_rejected", {
          trigger,
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          reason: "preflight_resolution_downgraded_visible_snapshot",
          candidate: {
            source: preflightSnapshot.source,
            timestamp: preflightSnapshot.timestamp,
            metrics: preflightSnapshot.metricSignature?.values || null,
          },
          currentVisible: {
            source: selectedRunSnapshot?.source || "current_visible_kpi_truth",
            timestamp: selectedRunSnapshot?.timestamp || null,
            metrics: selectedRunSnapshot?.metricSignature?.values || null,
          },
        });
        preRunResolvedView = {
          nextSources:
            selectedRunSnapshot?.sources ||
            preRunResolvedView?.nextSources ||
            buildEmptySources(),
          nextResolved: selectedRunSnapshot?.resolved || buildEmptyResolved(),
          applied: false,
        };
      }
      const canonicalNarrativeTotals =
        preRunResolvedView?.nextResolved?.totals ||
        snapshotBaseResolved?.totals ||
        snapshotBaseAggregate?.totals ||
        {};
      const canonicalNarrativeMetrics = computeReportingMetrics(canonicalNarrativeTotals);
      const canonicalNarrativeSignature = buildNarrativeMetricSignature({
        metrics: canonicalNarrativeMetrics,
      });

      const resolvedReportingForAI = {
        source: snapshotBaseIsCanonical
          ? "canonical_performance_summary"
          : "sherloc_frontend_resolved_reporting_truth",
        generatedAt: new Date().toISOString(),
        context: expectedContext,
        timeframe: {
          date_start: clientDateStart,
          date_end: clientDateEnd,
          date_preset: clientDatePreset,
          label: defaultTimeframeLabel,
        },
        canonicalMetrics: canonicalNarrativeMetrics,
        metricSignature: canonicalNarrativeSignature,
        sourceContract: snapshotBaseAggregate?.sourceContract || null,
        canonicalReportingContract:
          snapshotBaseAggregate?.canonical_reporting_contract ||
          snapshotBaseAggregate?.sourceContract?.contract ||
          null,
        sources: preRunResolvedView?.nextSources || buildEmptySources(),
        resolved: preRunResolvedView?.nextResolved || buildEmptyResolved(),
      };

      lockedSnapshotForRun = {
        context: expectedContext,
        lockedAt: resolvedReportingForAI.generatedAt,
        sourceTimestamp: snapshotBaseTimestamp,
        aggregate: snapshotBaseAggregate || null,
        sources: resolvedReportingForAI.sources,
        resolved: resolvedReportingForAI.resolved,
        metrics: canonicalNarrativeMetrics,
        metricSignature: canonicalNarrativeSignature,
      };
      applyRunSnapshotLock(lockedSnapshotForRun);
      markReportDataSourceApplied({
        source: "run_analysis_locked_snapshot",
        timestamp: lockedSnapshotForRun.lockedAt,
        context: expectedContext,
      });
      if (snapshotBaseAggregate) {
        setAggregate(snapshotBaseAggregate);
      }
      setSources(lockedSnapshotForRun.sources);
      setSourcesContext(expectedContext);
      setResolved(lockedSnapshotForRun.resolved);
      setResolvedContext(expectedContext);
      setResolvedOrigin({
        source: "run_analysis_locked_snapshot",
        context: expectedContext,
        lockedAt: lockedSnapshotForRun.lockedAt,
      });
      publishSherlocTrace("client", "run_snapshot_locked", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        snapshotTimestamp: snapshotBaseTimestamp,
        lockedAt: lockedSnapshotForRun.lockedAt,
        metricSignature: canonicalNarrativeSignature?.values || null,
      });

      const aiRunPayload = {
        run_type: trigger === "manual" ? "manual" : "canonical_aggregate_alignment",
        trigger,
        aiProvider: selectedAiProvider,
        provider: selectedAiProvider,
        campaignId: expectedContext.campaignId,
        campaign_id: expectedContext.campaignId,
        reportingCampaignId: selectedReportingCampaignId,
        reporting_campaign_id: selectedReportingCampaignId,
        /*
         * The governed campaign identity, carried explicitly and independently
         * of any provider's ability to name the campaign. `metaCampaignId`
         * below is null for a campaign no ad platform minted an id for; that
         * says nothing about which campaign this run is FOR, and this field is
         * what keeps the answer immutable in the request itself.
         */
        governedCampaignId: authoritativeCampaignId,
        governed_campaign_id: authoritativeCampaignId,
        metaCampaignId: focusedCampaignId,
        meta_campaign_id: focusedCampaignId,
        campaignName: selectedCampaignNameForContext,
        campaign_name: selectedCampaignNameForContext,
        date_start: clientDateStart,
        date_end: clientDateEnd,
        date_preset: clientDatePreset,
        datePreset: clientDatePreset,
        timeframe_label: defaultTimeframeLabel,
        timeframeLabel: defaultTimeframeLabel,
        resolved_reporting: resolvedReportingForAI,
        resolvedReporting: resolvedReportingForAI,
        reporting_truth: resolvedReportingForAI,

        meta_live: metaByPlatForAI
          ? {
              breakdown: "publisher_platform",
              by_platform: metaByPlatForAI,
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
            }
          : null,

        googleAnalytics: gaSummaryForAI
          ? {
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
              summary: gaSummaryForAI,
            }
          : null,
        google_analytics: gaSummaryForAI
          ? {
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
              summary: gaSummaryForAI,
            }
          : null,
        google_analytics_live: gaSummaryForAI
          ? {
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
              summary: gaSummaryForAI,
            }
          : null,
        ga_live: gaSummaryForAI
          ? {
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
              summary: gaSummaryForAI,
            }
          : null,
        stackAdapt: Object.keys(stackAdaptLiveByChannelForAI || {}).length
          ? {
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
              by_channel: stackAdaptLiveByChannelForAI,
              byChannel: stackAdaptLiveByChannelForAI,
            }
          : null,
        stackadapt_live: Object.keys(stackAdaptLiveByChannelForAI || {}).length
          ? {
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
              by_channel: stackAdaptLiveByChannelForAI,
              byChannel: stackAdaptLiveByChannelForAI,
            }
          : null,
        programmatic_live: Object.keys(stackAdaptLiveByChannelForAI || {}).length
          ? {
              campaign_context: focusedCampaignId || "auto",
              date_start: clientDateStart,
              date_end: clientDateEnd,
              date_preset: clientDatePreset,
              datePreset: clientDatePreset,
              by_channel: stackAdaptLiveByChannelForAI,
              byChannel: stackAdaptLiveByChannelForAI,
            }
          : null,
      };
      publishSherlocTrace("client", "run_analysis_request", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        selectedAiProvider,
        hasResolvedReporting: hasSharedUsableMetrics(canonicalNarrativeMetrics),
        hasMetaLive: Boolean(metaByPlatForAI && Object.keys(metaByPlatForAI).length),
        hasGoogleAnalytics: hasSharedUsableMetrics(gaSummaryForAI),
        hasStackAdaptLive: Boolean(Object.keys(stackAdaptLiveByChannelForAI || {}).length),
        metricSignature: canonicalNarrativeSignature?.values || null,
      });

      // Durable operation lifecycle: one click = one durable operation
      // (repeated clicks reuse it server-side), delivery by polling instead
      // of one multi-minute HTTP request, reload recovery via
      // attachOperationId. The resolved `analysis` envelope is shaped
      // exactly like the legacy synchronous response body.
      const durable = await runSherlocAnalysisViaDurableOperation({
        apiBase: API_BASE_URL,
        getAuthHeader,
        advertiserId: requestAdvertiserId,
        payload: aiRunPayload,
        expectedContext: {
          advertiserId: requestAdvertiserId,
          campaignId: expectedContext.campaignId,
          date_start: clientDateStart,
          date_end: clientDateEnd,
          date_preset: clientDatePreset,
        },
        ...(attachOperationId ? { attachOperationId } : {}),
        signal: runController.signal,
        onOperation: (operation) => {
          if (isRunActive()) setRunAnalysisOperation(operation);
        },
      });
      if (!isRunActive()) return;

      const data = durable.analysis || {};
      publishSherlocTrace("client", "run_analysis_response", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        status: 200,
        ok: true,
        operationId: durable.operation?.id || null,
        supportReference: getRunAnalysisSupportReference(durable.operation),
        operationReplay: Boolean(durable.replay),
        operationRehydrated: Boolean(durable.rehydrated),
        hasError: Boolean(data?.error),
        responseClass: data?.error ? "error" : "candidate_success",
        runStatus: data?.run?.status || data?.result?.run?.status || data?.data?.run?.status || null,
        hasAggregate: Boolean(findAggregateDeep(data)),
        hasNarrativeIntegrity: Boolean(data?.narrativeIntegrity || data?.narrative_integrity),
        fallbackReason: data?.diagnostics?.fallbackReason || null,
        modelReturnedText: data?.diagnostics?.modelReturnedText ?? null,
        summaryBulletsEmpty: data?.diagnostics?.summaryBulletsEmpty ?? null,
        recommendationsEmpty: data?.diagnostics?.recommendationsEmpty ?? null,
      });
      if (data?.error)
        throw new Error(data?.error || data?.hint || "Sherloc AI run failed.");

      const extractedRun = pickFirst(
        data?.run,
        data?.result?.run,
        data?.data?.run,
        data?.aiRun,
        data?.runRecord
      );
      const extractedAggregate = findAggregateDeep(data);

      let extractedSummary = normalizeSummary({
        ...data,
        run: extractedRun || data?.run || data?.result?.run || data?.data?.run || null,
      });

      let extractedRecs = normalizeRecommendations({
        ...data,
        run: extractedRun || data?.run || data?.result?.run || data?.data?.run || null,
      });
      const extractedSummaryHasText = hasRenderableSummary(extractedSummary);
      let extractedRecsHaveText = extractedRecs.some((line) => String(line || "").trim());
      const narrativeCompletion = normalizeSherlocNarrativeCompletion(data);
      publishSherlocTrace("client", "run_analysis_narrative_response_shape", {
        trigger,
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        runId: extractedRun?.id || data?.id || null,
        runStatus:
          extractedRun?.status ||
          data?.status ||
          data?.run?.status ||
          data?.result?.run?.status ||
          data?.data?.run?.status ||
          null,
        summaryTitlePresent: Boolean(String(extractedSummary?.title || "").trim()),
        summaryBulletCount: Array.isArray(extractedSummary?.bullets)
          ? extractedSummary.bullets.filter((line) => String(line || "").trim()).length
          : 0,
        recommendationsCount: extractedRecs.length,
        recommendationsHaveText: extractedRecsHaveText,
        fallbackReason: data?.diagnostics?.fallbackReason || null,
        modelReturnedText: data?.diagnostics?.modelReturnedText ?? null,
      });
      if (!extractedSummaryHasText && extractedRecsHaveText) {
        publishSherlocTrace("client", "summary_rejected_frontend", {
          trigger,
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          reason: "partial_narrative_output",
          fallbackReason: data?.diagnostics?.fallbackReason || null,
          summaryGenerated: data?.diagnostics?.summaryGenerated ?? null,
          recommendationsGenerated: data?.diagnostics?.recommendationsGenerated ?? null,
        });
        extractedSummary = buildFallbackAiSummaryForPartialNarrative(
          data?.diagnostics?.fallbackReason || "partial_narrative_output",
          extractedAggregate?.totals || extractedAggregate?.totals_for_ai || null
        );
        publishSherlocTrace("client", "frontend_summary_repaired", {
          trigger,
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          reason: "recommendations_present_summary_missing",
          fallbackReason: data?.diagnostics?.fallbackReason || null,
          summaryTitle: extractedSummary.title,
          summaryBulletCount: extractedSummary.bullets.length,
        });
      } else if (!extractedSummaryHasText && !extractedRecsHaveText) {
        const fallbackTotals =
          lockedSnapshotForRun?.resolved?.totals ||
          preRunResolvedView?.nextResolved?.totals ||
          extractedAggregate?.totals ||
          extractedAggregate?.totals_for_ai ||
          contextScopedResolvedRef.current?.totals ||
          contextScopedAggregateRef.current?.totals ||
          null;
        extractedSummary = buildFallbackAiSummaryForPartialNarrative(
          data?.diagnostics?.fallbackReason || "empty_narrative_output",
          fallbackTotals
        );
        extractedRecs = [
          normalizeSherlocTextForRender(buildSherlocNarrativeGapNotice(narrativeCompletion)),
        ];
        extractedRecsHaveText = true;
        publishSherlocTrace("client", "frontend_summary_repaired", {
          trigger,
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          reason: "empty_narrative_output_fallback_injected",
          fallbackReason: data?.diagnostics?.fallbackReason || null,
          narrativeCompletionState: narrativeCompletion?.state || null,
          summaryTitle: extractedSummary.title,
          summaryBulletCount: extractedSummary.bullets.length,
        });
      } else if (extractedSummaryHasText && !extractedRecsHaveText) {
        // Summary present, cross-platform narrative missing. Without this branch the run
        // fell through unrepaired and the report printed developer next-steps ("verify the
        // /api/ai/run response payload fields") where an operator expects analysis.
        extractedRecs = [
          normalizeSherlocTextForRender(buildSherlocNarrativeGapNotice(narrativeCompletion)),
        ];
        extractedRecsHaveText = true;
        publishSherlocTrace("client", "frontend_summary_repaired", {
          trigger,
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          reason: "summary_present_cross_platform_narrative_missing",
          fallbackReason: data?.diagnostics?.fallbackReason || null,
          narrativeCompletionState: narrativeCompletion?.state || null,
        });
      }

      const extractedAiMeta = normalizeAiMeta({
        ...data,
        run: extractedRun || data?.run || data?.result?.run || data?.data?.run || null,
      });

      const safeRun =
        extractedRun ||
        (isPlainObject(data)
          ? { id: data?.id || `run_${Date.now()}`, created_at: new Date().toISOString() }
          : null);

      const safeAgg = extractedAggregate || null;

      const ds = pickFirst(
        safeAgg?.date_start,
        safeAgg?.dateStart,
        safeRun?.date_start,
        safeRun?.dateStart,
        data?.date_start,
        data?.dateStart,
        clientDateStart
      );
      const de = pickFirst(
        safeAgg?.date_end,
        safeAgg?.dateEnd,
        safeRun?.date_end,
        safeRun?.dateEnd,
        data?.date_end,
        data?.dateEnd,
        clientDateEnd
      );

      let metaByPlat = metaByPlatForAI;
      let gaLiveChannel = gaSummaryForAI;
      let stackAdaptLiveByChannel = stackAdaptLiveByChannelForAI;
      if (
        !gaLiveChannel &&
        isGoogleAnalyticsStatusVisuallyLive(gaStatus) &&
        hasSharedUsableMetrics(gaStatus?.channel)
      ) {
        gaLiveChannel = gaStatus.channel;
      }
      const needsGa4OverlayRefresh = !gaLiveChannel;

      if (
        !metaByPlat ||
        !gaLiveChannel ||
        !Object.keys(stackAdaptLiveByChannel || {}).length
      ) {
        try {
          setLiveStatus({
            state: "loading",
            message: "Refreshing LIVE Meta…",
            contextConnected: false,
          });
          if (needsGa4OverlayRefresh) {
            const beforeGaVisualState = getGoogleAnalyticsPillPresentation(gaStatus);
            const pendingGaStatus = buildPendingGoogleAnalyticsStatus("Refreshing GA4…");
            const pendingGaVisualState = getGoogleAnalyticsPillPresentation(pendingGaStatus);
            publishSherlocTrace("client", "ga4_status_recheck_reason", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              reason: "run_analysis_missing_ga4_after_ai_response",
              visibleState: beforeGaVisualState.label,
              silent: false,
            });
            publishSherlocTrace("client", "ga4_status_recheck_started", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              reason: "run_analysis_missing_ga4_after_ai_response",
              silent: false,
            });
            if (pendingGaVisualState.label !== beforeGaVisualState.label) {
              publishSherlocTrace("client", "ga4_status_visual_state_changed", {
                trigger,
                advertiserId: requestAdvertiserId,
                context: expectedContext,
                reason: "run_analysis_missing_ga4_after_ai_response",
                from: beforeGaVisualState.label,
                to: pendingGaVisualState.label,
              });
            }
            if (preserveGaLiveDuringRun) {
              publishSherlocTrace("client", "run_analysis_meta_status_preserved", {
                trigger,
                advertiserId: requestAdvertiserId,
                context: expectedContext,
                surface: "ga4",
                reason: "overlay_loading_suppressed",
                current: runStartStatusPreservation.ga,
                candidate: pendingGaVisualState,
              });
            } else {
              setGaStatus(pendingGaStatus);
            }
          }

          const [livePayload, gaPayload, stackAdaptPayload] = await Promise.all([
            !metaByPlat
              ? readProviderForSelectedCampaign(() =>
                  fetchMetaInsights({
                    advertiserId: requestAdvertiserId,
                    date_start: ds,
                    date_end: de,
                    breakdowns: "publisher_platform",
                    campaignId: focusedCampaignId,
                    campaign_id: focusedCampaignId,
                    level: "campaign",
                  })
                )
              : Promise.resolve(null),
            !gaLiveChannel
              ? fetchGoogleAnalyticsSummary({
                  advertiserId: requestAdvertiserId,
                  date_start: ds,
                  date_end: de,
                  campaignId: focusedCampaignId,
                  campaign_id: focusedCampaignId,
                })
              : Promise.resolve(null),
            !Object.keys(stackAdaptLiveByChannel || {}).length
              ? readProviderForSelectedCampaign(() =>
                  fetchStackAdaptLiveReporting({
                    advertiserId: requestAdvertiserId,
                    date_start: ds,
                    date_end: de,
                    date_preset: clientDatePreset,
                    datePreset: clientDatePreset,
                  })
                )
              : Promise.resolve(null),
          ]);

          if (!isActiveRunContext(requestAdvertiserId, expectedContext)) return;

          if (!metaByPlat) {
            if (livePayload) {
              metaByPlat = buildMetaPublisherPlatformAgg(livePayload, {
                campaignId: focusedCampaignId || null,
              });
              const hasMetaContextAssociation = focusedCampaignId
                ? metaCampaigns.some(
                    (campaign) => String(campaign?.id || "") === String(focusedCampaignId)
                  )
                : metaCampaigns.length > 0;
              const metaContextConnected =
                hasMetaContextAssociation && Object.keys(metaByPlat || {}).length > 0;
              setLiveStatus({
                state: "ok",
                message: `LIVE Meta refreshed${focusedCampaignId ? " (campaign filtered)" : ""}.`,
                contextConnected: metaContextConnected,
              });
            } else {
              setLiveStatus({
                state: "warn",
                message: "LIVE Meta unavailable (no payload).",
                contextConnected: false,
              });
            }
          }

          if (!gaLiveChannel) {
            const gaDescriptor = getGoogleAnalyticsStatusDescriptor(
              gaPayload,
              null,
              {
                focusedCampaignId,
              }
            );
            gaLiveChannel = gaDescriptor.channel;
            const nextGaVisualState = getGoogleAnalyticsPillPresentation(gaDescriptor);
            if (nextGaVisualState.label !== "CHECKING") {
              publishSherlocTrace("client", "ga4_status_visual_state_changed", {
                trigger,
                advertiserId: requestAdvertiserId,
                context: expectedContext,
                reason: "run_analysis_ga4_recheck_completed",
                from: "CHECKING",
                to: nextGaVisualState.label,
              });
            }
            publishSherlocTrace("client", "ga4_status_recheck_completed", {
              trigger,
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              reason: "run_analysis_missing_ga4_after_ai_response",
              visualState: nextGaVisualState.label,
              connectionState: gaDescriptor.connectionState,
              hasGoogleAnalytics: hasSharedUsableMetrics(gaLiveChannel),
            });
            if (nextGaVisualState.label === "LIVE" || !preserveGaLiveDuringRun) {
              setGaStatus(gaDescriptor);
            } else {
              publishSherlocTrace("client", "run_analysis_meta_status_preserved", {
                trigger,
                advertiserId: requestAdvertiserId,
                context: expectedContext,
                surface: "ga4",
                reason: "overlay_non_live_result_suppressed",
                current: runStartStatusPreservation.ga,
                candidate: nextGaVisualState,
              });
            }
          }

          if (!Object.keys(stackAdaptLiveByChannel || {}).length && stackAdaptPayload?.ok) {
            stackAdaptLiveByChannel = buildStackAdaptLiveByChannel(stackAdaptPayload);
          }
        } catch (e) {
          if (!isActiveRunContext(requestAdvertiserId, expectedContext)) return;
          console.warn("[SherlocReports] live overlay skipped:", e?.message || e);
          setLiveStatus({
            state: "warn",
            message: "LIVE Meta refresh failed.",
            contextConnected: false,
          });
          if (needsGa4OverlayRefresh) {
            const failedGaStatus = {
              state: "warn",
              message: "GA4 refresh failed.",
              contextConnected: false,
              classification: "unavailable",
              connectionState: "unavailable",
            };
            if (preserveGaLiveDuringRun) {
              publishSherlocTrace("client", "run_analysis_meta_status_preserved", {
                trigger,
                advertiserId: requestAdvertiserId,
                context: expectedContext,
                surface: "ga4",
                reason: "overlay_error_suppressed",
                current: runStartStatusPreservation.ga,
                candidate: getGoogleAnalyticsPillPresentation(failedGaStatus),
              });
            } else {
              setGaStatus(failedGaStatus);
            }
          }
        }
      }

      if (!isActiveRunContext(requestAdvertiserId, expectedContext)) return;

      const responseContext = extractRunContextFromPayload(
        {
          ...data,
          run: safeRun,
          aggregate: safeAgg,
          date_start: ds,
          date_end: de,
          campaignId: focusedCampaignId || "auto",
        },
        expectedContext
      );

      const finalContext = contextsMatch(responseContext, expectedContext)
        ? responseContext
        : expectedContext;

      refreshMetaCampaigns({ advertiserId: requestAdvertiserId, force: true });

      const { nextSources, nextResolved } = rebuildResolvedView({
        aggregateInput: safeAgg || {},
        metaLiveByPlat: metaByPlat || {},
        gaLiveChannel,
        stackAdaptLiveByChannel,
        previousResolvedByChannel: prevResolved?.byChannel || {},
        traceSource: "run_sherloc",
      });
      const activeResponseLock =
        lockedSnapshotForRun ||
        (runSnapshotLockRef.current?.context &&
        contextsMatch(runSnapshotLockRef.current.context, finalContext)
          ? runSnapshotLockRef.current
          : null);
      const validationResolved = activeResponseLock?.resolved || nextResolved;
      const validationSources = activeResponseLock?.sources || nextSources;
      const validationAggregate = activeResponseLock?.aggregate || safeAgg;

      const canonicalNarrativeSource =
        extractedRecs && extractedRecs.length ? extractedRecs.join("\n") : "";

      const normalizedNarrative = normalizeSherlocTextForRender(
        String(canonicalNarrativeSource || "")
      );

      const narrativeWithPlacements = upsertPlacementLinesIntoPlatformAnalysis(
        normalizedNarrative,
        validationResolved?.byChannel || validationAggregate?.byChannel || {}
      );

      const rawResponseNarrativeSignature =
        buildNarrativeMetricSignature({
          aggregate: safeAgg || null,
          resolved: nextResolved || null,
        }) || buildCurrentDisplayNarrativeSignature(nextResolved?.totals || safeAgg?.totals || null);
      const responseNarrativeSignature =
        activeResponseLock?.metricSignature || rawResponseNarrativeSignature;
      const acceptedNarrativeSignature =
        activeResponseLock?.metricSignature ||
        canonicalNarrativeSignature ||
        buildCurrentDisplayNarrativeSignature(
          validationResolved?.totals || validationAggregate?.totals || null
        ) ||
        responseNarrativeSignature;
      const narrativeSignatureMismatch = hasMaterialNarrativeMetricDiff(
        acceptedNarrativeSignature,
        responseNarrativeSignature
      );

      if (narrativeSignatureMismatch) {
        const mismatchDelta = buildNarrativeMetricDelta(
          acceptedNarrativeSignature,
          responseNarrativeSignature
        );
        const retryKey = `${getSherlocReportContextKey(finalContext)}:${
          acceptedNarrativeSignature?.key || "no_signature"
        }`;
        const canSilentRetry = !narrativeIntegrityRerunAttemptedRef.current.has(retryKey);
        if (canSilentRetry) {
          narrativeIntegrityRerunAttemptedRef.current.add(retryKey);
          publishSherlocTrace("client", "narrative_integrity_mismatch_silent_rerun", {
            advertiserId: requestAdvertiserId,
            context: finalContext,
            snapshotTimestamp: activeResponseLock?.sourceTimestamp || snapshotBaseTimestamp || null,
            snapshotLockedAt: activeResponseLock?.lockedAt || null,
            expected: acceptedNarrativeSignature?.values || null,
            response: responseNarrativeSignature?.values || null,
            rawResponse: rawResponseNarrativeSignature?.values || null,
            delta: mismatchDelta,
          });
          setTimeout(() => {
            runSherlocRef.current?.({ trigger: "canonical_aggregate_alignment" });
          }, 0);
          return;
        }

        const message =
          "Sherloc blocked a narrative whose source metrics no longer match the KPI cards. KPI cards remain current; rerun analysis to regenerate the narrative from the latest resolved reporting truth.";
        console.warn("[SherlocReports] narrative metric integrity mismatch", {
          expected: acceptedNarrativeSignature?.values,
          response: responseNarrativeSignature?.values,
          rawResponse: rawResponseNarrativeSignature?.values,
          delta: mismatchDelta,
          context: finalContext,
        });
        publishSherlocTrace("client", "narrative_integrity_rejected", {
          advertiserId: requestAdvertiserId,
          context: finalContext,
          expected: acceptedNarrativeSignature?.values || null,
          response: responseNarrativeSignature?.values || null,
          rawResponse: rawResponseNarrativeSignature?.values || null,
          delta: mismatchDelta,
        });
        clearVisibleNarrativeSelection({
          source: "narrative_integrity_rejected",
          reason: "metric_signature_mismatch",
          context: finalContext,
          detail: {
            expected: acceptedNarrativeSignature?.values || null,
            response: responseNarrativeSignature?.values || null,
            rawResponse: rawResponseNarrativeSignature?.values || null,
            delta: mismatchDelta,
          },
        });
        setNarrativeAlignmentFailure({
          contextKey: currentRunContextKey,
          signatureKey: aggregateMetricSignature?.key || "",
          message,
        });
        setReportViewerState(null);
        setViewerStatus("idle");
        setViewerError("");
        return;
      }

      setAiNarrativeMetricSignature(acceptedNarrativeSignature);
      setNarrativeAlignmentFailure(null);

      const directNarrativeRecommendations =
        !narrativeWithPlacements || !String(narrativeWithPlacements).trim()
          ? [
              normalizeSherlocTextForRender(
                buildSherlocNarrativeGapNotice(narrativeCompletion)
              ),
            ]
          : [narrativeWithPlacements];

      if (!narrativeWithPlacements || !String(narrativeWithPlacements).trim()) {
        publishSherlocTrace("client", "frontend_rejected_payload", {
          advertiserId: requestAdvertiserId,
          context: finalContext,
          reason: "empty_narrative_output",
          fallbackReason: data?.diagnostics?.fallbackReason || null,
          summaryBulletsEmpty: data?.diagnostics?.summaryBulletsEmpty ?? null,
          recommendationsEmpty: data?.diagnostics?.recommendationsEmpty ?? null,
        });
      }

      publishSherlocTrace("client", "direct_run_response_received", {
        trigger,
        advertiserId: requestAdvertiserId,
        campaignId: finalContext?.campaignId || "",
        timeframeKey: getSherlocReportContextKey(finalContext),
        context: finalContext,
        currentContextKey: getSherlocReportContextKey(currentRunContext),
        candidateContextKey: getSherlocReportContextKey(finalContext),
        runId: safeRun?.id || safeRun?.runId || safeRun?.run_id || null,
        runStatus: safeRun?.status || data?.status || null,
        summaryTitle: extractedSummary?.title || "",
        summaryPresent: hasRenderableSummary(extractedSummary),
        recommendationsPresent:
          Array.isArray(directNarrativeRecommendations) &&
          directNarrativeRecommendations.some((line) => String(line || "").trim()),
        recommendationsCount: directNarrativeRecommendations.length,
        selectedVisibleRunId: visibleNarrativeSelectionRef.current?.runId || null,
        selectedVisibleSource: visibleNarrativeSelectionRef.current?.source || null,
      });

      commitVisibleNarrativeSelection({
        source: "direct_run_response",
        run: safeRun,
        payload: data,
        aggregate: validationAggregate,
        resolved: validationResolved,
        context: finalContext,
        summary: extractedSummary,
        recommendations: directNarrativeRecommendations,
        trusted: true,
        apply: () => {
          setLastRun(safeRun);
          setAiSummary(extractedSummary);
          setAiRecommendations(directNarrativeRecommendations);
          setAnalystPanel(extractAnalystPanelFromRunPayload(data));
          setAiRunContext(finalContext);
          setAiMeta(extractedAiMeta);
          setAiNarrativeMetricSignature(acceptedNarrativeSignature);
        },
      });

      try {
        if (!isActiveRunContext(requestAdvertiserId, expectedContext)) return;
        const cachedRecommendations = directNarrativeRecommendations;
        const cachePayload = {
          run: safeRun,
          aggregate: validationAggregate,
          sources: validationSources,
          resolved: validationResolved,
          aiSummary: extractedSummary,
          aiRecommendations: cachedRecommendations,
          narrativeIntegrity:
            data?.narrativeIntegrity ||
            data?.narrative_integrity ||
            safeRun?.narrative_integrity ||
            {
              version: "sherloc_narrative_integrity_v1",
              metricSignature: acceptedNarrativeSignature,
              playbackTrusted: true,
            },
          aiRunContext: finalContext,
          aiMeta: extractedAiMeta,
          aiProviderKey: normalizeAiProviderForUi(selectedAiProvider),
          storageVersion: SHERLOC_REPORT_CACHE_VERSION,
          updatedAt: new Date().toISOString(),
        };
        const legacyKey = getLastRunStorageKey(requestAdvertiserId);
        const scopedKey = getScopedReportStorageKey({
          advertiserId: requestAdvertiserId,
          context: finalContext,
          aiProvider: selectedAiProvider,
        });
        localStorage.setItem(legacyKey, JSON.stringify(cachePayload));
        localStorage.setItem(scopedKey, JSON.stringify(cachePayload));
        publishSherlocTrace("client", "report_save", {
          source: "run_sherloc",
          scopedKey,
          legacyKey,
          context: finalContext,
          selectedCampaignId,
          selectedAiProvider,
          hasNarrative: Boolean(cachedRecommendations.length),
          hasResolved: Boolean(validationResolved?.byChannel),
        });
      } catch (err) {
        console.error("SherlocReports (client): failed to cache run:", err);
      }
      setError("");
      publishSherlocTrace("client", "run_analysis_accepted", {
        advertiserId: requestAdvertiserId,
        context: finalContext,
        selectedAiProvider,
        hasNarrative: Boolean(narrativeWithPlacements),
        metricSignature: acceptedNarrativeSignature?.values || null,
      });
      setReportViewerState(null);
      setViewerStatus("idle");
      setViewerError("");
    } catch (err) {
      if (isAbortLikeSherlocError(err) || runController.signal.aborted) return;
      console.error("SherlocReports (client): run error:", err);
      const rawMessage = err?.message || "Sherloc AI run failed.";
      const narrativeMessage = getClientFacingRunErrorMessage(rawMessage) || rawMessage;

      if (isHydration) {
        // A saved analysis that will not load leaves the page exactly as it
        // was. Hydration is additive — its absence is never the reason a
        // report disappears.
        publishSherlocTrace("client", "persisted_analysis_hydration_failed", {
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          message: narrativeMessage,
        });
        return;
      }

      if (trigger !== "manual") {
        setNarrativeAlignmentFailure({
          contextKey: currentRunContextKey,
          signatureKey: aggregateMetricSignature?.key || "",
          message:
            "Sherloc could not refresh the narrative with the latest KPI context. KPI cards remain current; retry Run Analysis to regenerate the narrative.",
          detail: narrativeMessage,
        });
        publishSherlocTrace("client", "narrative_alignment_refresh_failed", {
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          message: narrativeMessage,
        });
        return;
      }

      const hasLiveFallbackInput =
        Object.values(metaByPlatForAI || {}).some((value) => hasSharedUsableMetrics(value)) ||
        hasSharedUsableMetrics(gaSummaryForAI) ||
        Object.values(stackAdaptLiveByChannelForAI || {}).some((value) =>
          hasSharedUsableMetrics(value)
        );
      const hasCurrentKpiData =
        hasSharedUsableMetrics(lockedSnapshotForRun?.resolved?.totals) ||
        hasSharedUsableMetrics(lockedSnapshotForRun?.aggregate?.totals) ||
        hasSharedUsableMetrics(contextScopedResolvedRef.current?.totals) ||
        hasSharedUsableMetrics(contextScopedAggregateRef.current?.totals) ||
        hasSharedUsableMetrics(preRunResolvedView?.nextResolved?.totals);

      if (
        hasLiveFallbackInput &&
        requestAdvertiserId &&
        expectedContext &&
        isActiveRunContext(requestAdvertiserId, expectedContext)
      ) {
        const fallbackRun = {
          id: `narrative_failed_${Date.now()}`,
          status: "completed_with_fallback",
          narrative_status: "completed_with_fallback",
          narrative_error_message: rawMessage,
          error_message: "",
          advertiser_id: requestAdvertiserId,
          campaign_id: focusedCampaignId || "auto",
          date_start: clientDateStart,
          date_end: clientDateEnd,
          date_preset: clientDatePreset,
          created_at: new Date().toISOString(),
        };
        const { nextSources, nextResolved } = rebuildResolvedView({
          aggregateInput: {},
          metaLiveByPlat: metaByPlatForAI || {},
          gaLiveChannel: gaSummaryForAI,
          stackAdaptLiveByChannel: stackAdaptLiveByChannelForAI || {},
          previousResolvedByChannel: prevResolved?.byChannel || {},
          traceSource: "run_sherloc_narrative_failed_live_fallback",
          applyState: false,
        });
        const hasResolvedFallbackData = hasSharedUsableMetrics(nextResolved?.totals);

        if (hasResolvedFallbackData) {
          const fallbackSummary = buildFallbackAiSummaryForPartialNarrative(
            narrativeMessage,
            nextResolved?.totals || null
          );
          const fallbackNarrative = normalizeSherlocTextForRender(
            `CROSS PLATFORM ANALYSIS:\n- ${narrativeMessage}\n- Live delivery was found for this timeframe, so KPI cards and platform performance remain available.\n- Retry Run Analysis when narrative generation is available.`
          );

          const fallbackSignature =
            buildCurrentDisplayNarrativeSignature(nextResolved?.totals || null) ||
            buildNarrativeMetricSignature({
              resolved: nextResolved || null,
            });
          commitVisibleNarrativeSelection({
            source: "aggregate_only_fallback",
            run: fallbackRun,
            payload: { run: fallbackRun, resolved: nextResolved },
            resolved: nextResolved,
            context: expectedContext,
            summary: fallbackSummary,
            recommendations: [fallbackNarrative],
            trusted: true,
            apply: () => {
              setLastRun(fallbackRun);
              setAiSummary(fallbackSummary);
              setAiRecommendations([fallbackNarrative]);
              setAnalystPanel(null);
              setAiRunContext(expectedContext);
              setAiMeta(buildEmptyAiMeta());
              setAiNarrativeMetricSignature(fallbackSignature);
            },
          });
          setNarrativeAlignmentFailure(null);
          setReportViewerState(null);
          setViewerStatus("idle");
          setViewerError("");
          setError("");
          publishSherlocTrace("client", "aggregate_only_fallback_accepted", {
            advertiserId: requestAdvertiserId,
            context: expectedContext,
            message: narrativeMessage,
            hasResolvedFallbackData,
            summaryFallbackInjected: true,
            summaryTitle: fallbackSummary.title,
          });

          try {
            const cachePayload = {
              run: fallbackRun,
              aggregate: null,
              sources: nextSources,
              resolved: nextResolved,
              aiSummary: fallbackSummary,
              aiRecommendations: [fallbackNarrative],
              aiRunContext: expectedContext,
              aiMeta: buildEmptyAiMeta(),
              aiProviderKey: normalizeAiProviderForUi(selectedAiProvider),
              storageVersion: SHERLOC_REPORT_CACHE_VERSION,
              updatedAt: new Date().toISOString(),
            };
            const legacyKey = getLastRunStorageKey(requestAdvertiserId);
            const scopedKey = getScopedReportStorageKey({
              advertiserId: requestAdvertiserId,
              context: expectedContext,
              aiProvider: selectedAiProvider,
            });
            localStorage.setItem(legacyKey, JSON.stringify(cachePayload));
            localStorage.setItem(scopedKey, JSON.stringify(cachePayload));
            publishSherlocTrace("client", "report_save", {
              source: "run_sherloc_narrative_failed_live_fallback",
              scopedKey,
              legacyKey,
              context: expectedContext,
              selectedCampaignId,
              selectedAiProvider,
              hasNarrative: true,
              hasResolved: Boolean(nextResolved?.byChannel),
            });
          } catch (_cacheErr) {
            // ignore cache failures; the live resolved state is already on screen
          }
          return;
        }
      }

      if (hasCurrentKpiData) {
        const partialRun = {
          id: `narrative_partial_${Date.now()}`,
          status: "completed_with_fallback",
          narrative_status: "completed_with_fallback",
          narrative_error_message: rawMessage,
          error_message: "",
          advertiser_id: requestAdvertiserId,
          campaign_id: focusedCampaignId || "auto",
          date_start: clientDateStart,
          date_end: clientDateEnd,
          date_preset: clientDatePreset,
          created_at: new Date().toISOString(),
        };
        const fallbackNarrative = normalizeSherlocTextForRender(
          `CROSS PLATFORM ANALYSIS:\n- ${AI_NARRATIVE_FAILED_WITH_METRICS_MESSAGE}\n- Backend detail: ${narrativeMessage}\n- Retry Run Analysis when narrative generation is available.`
        );
        const fallbackSummary = buildFallbackAiSummaryForPartialNarrative(
          narrativeMessage,
          lockedSnapshotForRun?.resolved?.totals ||
            lockedSnapshotForRun?.aggregate?.totals ||
            preRunResolvedView?.nextResolved?.totals ||
            contextScopedResolvedRef.current?.totals ||
            contextScopedAggregateRef.current?.totals ||
            null
        );
        const partialSignature = buildCurrentDisplayNarrativeSignature(
          lockedSnapshotForRun?.resolved?.totals ||
            lockedSnapshotForRun?.aggregate?.totals ||
            preRunResolvedView?.nextResolved?.totals ||
            contextScopedResolvedRef.current?.totals ||
            contextScopedAggregateRef.current?.totals ||
            null
        );
        commitVisibleNarrativeSelection({
          source: "aggregate_only_fallback",
          run: partialRun,
          payload: { run: partialRun, resolved: lockedSnapshotForRun?.resolved || null },
          resolved: lockedSnapshotForRun?.resolved || null,
          aggregate: lockedSnapshotForRun?.aggregate || null,
          context: expectedContext,
          summary: fallbackSummary,
          recommendations: [fallbackNarrative],
          trusted: true,
          apply: () => {
            setLastRun(partialRun);
            setAiSummary(fallbackSummary);
            setAiRecommendations([fallbackNarrative]);
            setAnalystPanel(null);
            setAiRunContext(expectedContext);
            setAiMeta(buildEmptyAiMeta());
            setAiNarrativeMetricSignature(partialSignature);
          },
        });
        setNarrativeAlignmentFailure(null);
        setError("");
        publishSherlocTrace("client", "aggregate_only_fallback_accepted", {
          advertiserId: requestAdvertiserId,
          context: expectedContext,
          message: narrativeMessage,
          hasResolvedFallbackData: false,
          hasCurrentKpiData: true,
          summaryFallbackInjected: true,
          summaryTitle: fallbackSummary.title,
        });
        return;
      }

      publishSherlocTrace("client", "run_error_set", {
        advertiserId: requestAdvertiserId,
        context: expectedContext,
        message: narrativeMessage,
      });
      setError(narrativeMessage);
    } finally {
      const shouldClearRunning =
        !isHydration &&
        mountedRef.current &&
        !runController.signal.aborted &&
        runAbortRef.current === runController;
      if (runAbortRef.current === runController) {
        runAbortRef.current = null;
      }
      if (shouldClearRunning) {
        setRunning(false);
      }
    }
  };

  runSherlocRef.current = runSherloc;

  // Canonical hydration — the one read that makes a Sherloc analysis survive
  // leaving the page.
  //
  // Run Analysis has been durable since the 2026-08-16 incident, but only the
  // OPERATION was: rows were written with no campaign binding and the only
  // read answered "is something running right now?", which is null the moment
  // a run finishes. So a completed analysis lived on in the database while the
  // page's sole way back to it was this browser's localStorage — which is why
  // it vanished on another machine, after a sign-out, and whenever any other
  // campaign of the same advertiser ran next.
  //
  // GET /run-operations/:advertiserId/latest answers both halves at once for
  // THIS advertiser and THIS campaign: the last analysis that succeeded, and
  // the operation running now, if any. Both are attached through runSherloc,
  // so a hydrated report and a freshly generated one are assembled by the same
  // code and can never disagree.
  //
  // Attaching is a read. It fetches an existing operation row by id; it never
  // enqueues one, so opening the page cannot spend a model run.
  useEffect(() => {
    if (!resolvedAdvertiserId) {
      setRunAnalysisOperation(null);
      return undefined;
    }

    // Wait for the run context to SETTLE before reading.
    //
    // This is what made a completed analysis look lost in production. The
    // effect used to fire as soon as the advertiser was known — before
    // approvedPlanCampaigns resolved, so the flight window was still empty.
    // runSherloc then built an expectedContext with null dates, the operation's
    // own context guard compared those against the persisted
    // 2026-08-10 - 2026-08-23 and refused the result as belonging to a
    // different selection, and hydration — deliberately silent — swallowed it.
    // Nothing rendered and nothing retried, because the deps did not include
    // the timeframe. The analysis was in the database the whole time.
    //
    // canAttemptPersistedReportRestore is the page's existing readiness gate
    // (campaign selection + approved plans hydrated, rolling presets excluded);
    // the local-cache and latest-bootstrap restores already wait on it.
    if (!canAttemptPersistedReportRestore) return undefined;

    // Identity is the WHOLE context — advertiser, campaign AND timeframe —
    // so settling the flight window re-reads with a context that can match.
    if (persistedAnalysisHydratedContextRef.current === currentRunContextKey) {
      return undefined;
    }
    persistedAnalysisHydratedContextRef.current = currentRunContextKey;

    const scopeKey = buildRunAnalysisScopeKey({
      advertiserId: resolvedAdvertiserId,
      campaignId: currentRunContext?.campaignId,
    });
    // Campaign-switch race: the scope this effect asked about is compared with
    // the scope the SERVER says it answered. A late reply for the campaign the
    // operator has already left is dropped on identity, never on arrival
    // order — so analysis A cannot land on campaign B.
    persistedAnalysisScopeRef.current = scopeKey;
    setRunAnalysisOperation(null);
    let cancelled = false;

    (async () => {
      try {
        const answer = await getLatestPersistedRunAnalysis({
          apiBase: API_BASE_URL,
          getAuthHeader,
          advertiserId: resolvedAdvertiserId,
          campaignId: currentRunContext?.campaignId,
        });
        if (cancelled || persistedAnalysisScopeRef.current !== scopeKey) return;
        if (answer.scopeKey && answer.scopeKey !== scopeKey) {
          publishSherlocTrace("client", "persisted_analysis_scope_rejected", {
            requestedScope: scopeKey,
            answeredScope: answer.scopeKey,
          });
          return;
        }

        // An operation in flight wins the operator card: it is the newer
        // truth about what Sherloc is doing. The saved analysis stays on
        // screen underneath it until the new one actually succeeds.
        if (answer.active) {
          publishSherlocTrace("client", "run_analysis_operation_recovered", {
            advertiserId: String(resolvedAdvertiserId),
            operationId: answer.active.id,
            supportReference: getRunAnalysisSupportReference(answer.active),
            status: answer.active.status,
          });
          setRunAnalysisOperation(answer.active);
        }

        // The saved analysis is rendered FIRST and awaited to completion.
        // runSherloc aborts any run already in flight, so starting the
        // reconnect alongside hydration would cancel it — and a hard refresh
        // during a re-run would show an empty report while the new one
        // generated, which is exactly the erasure this work exists to stop.
        if (answer.latest) {
          publishSherlocTrace("client", "persisted_analysis_hydrated", {
            advertiserId: String(resolvedAdvertiserId),
            scope: scopeKey,
            operationId: answer.latest.id,
            supportReference: getRunAnalysisSupportReference(answer.latest),
            provenance: describeRunAnalysisProvenance(answer.latest),
          });
          try {
            await runSherlocRef.current?.({
              trigger: PERSISTED_HYDRATION_TRIGGER,
              attachOperationId: answer.latest.id,
            });
          } catch (_renderError) {
            // Already traced inside runSherloc; hydration never blocks the
            // reconnect below.
          }
          if (cancelled || persistedAnalysisScopeRef.current !== scopeKey) return;
        } else {
          publishSherlocTrace("client", "persisted_analysis_absent", {
            advertiserId: String(resolvedAdvertiserId),
            scope: scopeKey,
            supported: answer.supported,
          });
        }

        // Only now reconnect to the operation still running. It polls with the
        // saved analysis on screen and replaces it only when it succeeds.
        if (answer.active) {
          runSherlocRef.current?.({
            trigger: "manual",
            attachOperationId: answer.active.id,
          });
        }
      } catch (_hydrationError) {
        // Hydration is advisory: a failed lookup leaves whatever is on the
        // page alone. It never manufactures an empty state. Release the
        // dedupe so a transient failure can be retried when the context
        // next settles, rather than poisoning it for the session.
        if (persistedAnalysisHydratedContextRef.current === currentRunContextKey) {
          persistedAnalysisHydratedContextRef.current = "";
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAdvertiserId, currentRunContextKey, canAttemptPersistedReportRestore]);

  // Governed Safe Retry: only a FAILED_RETRYABLE operation within its
  // attempt budget and past any provider backoff offers it. The retried
  // operation is re-attached so the same lifecycle delivers the result.
  const retryRunAnalysis = async () => {
    const operation = runAnalysisOperation;
    if (!isRunAnalysisRetryAvailable(operation)) return;
    try {
      const { operation: retried } = await retryRunAnalysisOperation({
        apiBase: API_BASE_URL,
        getAuthHeader,
        operationId: operation.id,
      });
      publishSherlocTrace("client", "run_analysis_operation_retry", {
        operationId: operation.id,
        supportReference: getRunAnalysisSupportReference(operation),
      });
      if (retried) setRunAnalysisOperation(retried);
      runSherlocRef.current?.({
        trigger: "manual",
        attachOperationId: operation.id,
      });
    } catch (retryError) {
      setError(
        retryError?.message || "The Sherloc analysis retry could not be started."
      );
    }
  };

  const playVoiceSummary = async () => {
    // Report audio has no governed playback-admission contract yet. Never
    // probe unbound legacy aliases or play a cached URL as current reporting.
    setVoiceLoading(false);
    setVoiceError("Report-derived audio is withheld until governed playback admission is available. The report remains available on screen.");
  };

  const computeChannelMetrics = (channelKey, ch = {}) => {
    const isCinema = channelKey === "cinema";
    const isGA = channelKey === "google_analytics";
    const nestedMetrics = isPlainObject(ch?.metrics) ? ch.metrics : {};
    const metricSource = { ...nestedMetrics, ...(ch || {}) };

    const metricInput = (...values) => pickFirst(...values);
    const metricNumber = (...values) => {
      const raw = metricInput(...values);
      if (raw === null || raw === undefined) return null;
      const numeric = toNumber(raw);
      return Number.isFinite(numeric) ? numeric : null;
    };
    const metricValue = (...keys) =>
      metricInput(
        ...keys.flatMap((key) => [ch?.[key], nestedMetrics?.[key]])
      );
    const metricField = (...keys) => metricNumber(metricValue(...keys));

    const impressions = metricField("impressions");
    const clicks = metricField("clicks");
    const engagements = metricField("engagements");
    const videoViews = metricField("video_views", "videoViews");
    const rawSpend = metricField("spend", "spend_usd");
    const spend = normalizeProviderSurfaceKey(channelKey) ? governedProviderSpend(ch) : resolveClientFacingSpend(metricSource, rawSpend);
    const conversions = metricField("conversions");

    const videoStarted = metricNumber(
      metricValue("video_started", "videoStarts", "video_start", "video_starts")
    );
    const videoCompleted = metricNumber(
      metricValue("video_completed", "videoCompletes", "video_complete", "completed_views")
    );
    const providedVideoCompletionRate = metricNumber(
      metricValue("video_completion_rate", "videoCompletionRate", "completionRate")
    );
    const videoCompletionRate =
      Number.isFinite(providedVideoCompletionRate) && providedVideoCompletionRate >= 0
        ? providedVideoCompletionRate
        : computeRatePercent(videoCompleted, videoStarted);

    const providedCtr = metricField("ctr", "click_through_rate", "clickThroughRate");
    const ctr =
      Number.isFinite(providedCtr) && providedCtr >= 0
        ? providedCtr
        : computeRatePercent(clicks, impressions);
    const providedEngagementRate = metricField("er", "engagement_rate", "engagementRate");
    const er =
      Number.isFinite(providedEngagementRate) && providedEngagementRate >= 0
        ? providedEngagementRate
        : computeRatePercent(engagements, impressions);
    const providedVideoViewRate = metricField("vvr", "video_view_rate", "videoViewRate");
    const vvr =
      Number.isFinite(providedVideoViewRate) && providedVideoViewRate >= 0
        ? providedVideoViewRate
        : computeRatePercent(videoViews, impressions);

    const fallbackCpc =
      clicks > 0 && spend > 0 && Number.isFinite(spend / clicks) ? spend / clicks : null;
    const fallbackCpm =
      impressions > 0 && spend > 0 && Number.isFinite((spend / impressions) * 1000)
        ? (spend / impressions) * 1000
        : null;
    const providedCpc = metricField("cpc", "clientFacingCpc", "client_facing_cpc");
    const providedCpm = metricField("cpm", "clientFacingCpm", "client_facing_cpm");
    const cpc =
      Number.isFinite(providedCpc) && providedCpc >= 0
        ? providedCpc
        : resolveClientFacingCpc(metricSource, clicks, spend, fallbackCpc);
    const cpm =
      Number.isFinite(providedCpm) && providedCpm >= 0
        ? providedCpm
        : resolveClientFacingCpm(metricSource, impressions, spend, fallbackCpm);

    const conversionValueRaw =
      metricValue(
        "conversion_value_usd",
        "conversionValueUsd",
        "conversion_value",
        "conversionValue"
      );

    const conversionValue =
      conversionValueRaw == null || conversionValueRaw === "" ? null : toNumber(conversionValueRaw);
    const conversionValueFinal =
      conversionValue != null && Number.isFinite(conversionValue) ? conversionValue : null;

    const totalBookedTheatersRaw = metricValue("total_booked_theaters", "totalBookedTheaters");
    const totalCircuitsRaw = metricValue("total_circuits", "totalCircuits");
    const totalCitiesRaw = metricValue("total_cities", "totalCities");
    const totalStatesRaw = metricValue("total_states", "totalStates");

    const ticketSalesRaw =
      metricValue(
        "ticket_sales_usd",
        "ticketSalesUsd",
        "ticket_sales",
        "ticketSales",
        "ticket_sales_total",
        "ticketSalesTotal",
        "ticketSalesUSD"
      );

    const ticketSalesNum =
      ticketSalesRaw == null || ticketSalesRaw === "" ? null : toNumber(ticketSalesRaw);
    const ticketSales =
      ticketSalesNum != null && Number.isFinite(ticketSalesNum) ? ticketSalesNum : null;

    const totalBookedTheatersNum =
      totalBookedTheatersRaw == null || totalBookedTheatersRaw === ""
        ? null
        : toNumber(totalBookedTheatersRaw);
    const totalCircuitsNum =
      totalCircuitsRaw == null || totalCircuitsRaw === "" ? null : toNumber(totalCircuitsRaw);
    const totalCitiesNum =
      totalCitiesRaw == null || totalCitiesRaw === "" ? null : toNumber(totalCitiesRaw);
    const totalStatesNum =
      totalStatesRaw == null || totalStatesRaw === "" ? null : toNumber(totalStatesRaw);

    const users = toNumber(metricValue("users", "active_users", "activeUsers") ?? 0);
    const newUsers = toNumber(metricValue("new_users", "newUsers") ?? 0);
    const sessions = toNumber(metricValue("sessions") ?? 0);
    const engagedSessions = toNumber(metricValue("engaged_sessions", "engagedSessions") ?? 0);
    const pageViews = toNumber(
      metricValue("page_views", "pageViews", "screen_page_views", "screenPageViews") ?? 0
    );
    const eventCount = toNumber(metricValue("event_count", "eventCount") ?? 0);
    const keyEvents = toNumber(metricValue("key_events", "keyEvents") ?? 0);
    const avgSessionDuration = toNumber(
      metricValue("avg_session_duration", "avgSessionDuration", "averageSessionDuration") ?? 0
    );
    const engagementRateRaw = toNumber(metricValue("engagement_rate", "engagementRate"));
    const engagementRate =
      Number.isFinite(engagementRateRaw) && engagementRateRaw >= 0
        ? engagementRateRaw
        : computeRatePercent(engagedSessions, sessions);

    return {
      currency: ch.currency || ch.spendCurrency || nestedMetrics.currency || null,
      spendBasis: ch.spendBasis || ch.spend_basis || nestedMetrics.spendBasis || null,
      provider: normalizeProviderSurfaceKey(channelKey) || null,
      impressions: isCinema || isGA ? null : Number.isFinite(impressions) ? impressions : null,
      clicks: isCinema || isGA ? null : Number.isFinite(clicks) ? clicks : null,
      engagements: isCinema || isGA ? null : Number.isFinite(engagements) ? engagements : null,
      videoViews: isCinema || isGA ? null : Number.isFinite(videoViews) ? videoViews : null,
      spend: isCinema || isGA ? null : Number.isFinite(spend) ? spend : null,
      clientFacingSpend: isCinema || isGA ? null : Number.isFinite(spend) ? spend : null,
      rawSpend: isCinema || isGA ? null : Number.isFinite(rawSpend) ? rawSpend : null,
      conversions: isCinema ? null : Number.isFinite(conversions) ? conversions : null,
      ctr: isCinema || isGA ? null : ctr,
      er: isCinema || isGA ? null : er,
      vvr: isCinema || isGA ? null : vvr,
      cpc: isCinema || isGA ? null : cpc,
      cpm: isCinema || isGA ? null : cpm,
      conversionValue: isCinema || isGA ? null : conversionValueFinal,
      videoStarted: isCinema || isGA ? null : Number.isFinite(videoStarted) ? videoStarted : null,
      videoCompleted: isCinema || isGA ? null : Number.isFinite(videoCompleted) ? videoCompleted : null,
      videoCompletionRate: isCinema || isGA ? null : videoCompletionRate,

      totalBookedTheaters:
        totalBookedTheatersNum != null && Number.isFinite(totalBookedTheatersNum)
          ? totalBookedTheatersNum
          : null,
      totalCircuits:
        totalCircuitsNum != null && Number.isFinite(totalCircuitsNum) ? totalCircuitsNum : null,
      totalCities:
        totalCitiesNum != null && Number.isFinite(totalCitiesNum) ? totalCitiesNum : null,
      totalStates:
        totalStatesNum != null && Number.isFinite(totalStatesNum) ? totalStatesNum : null,
      ticketSales,

      users: Number.isFinite(users) ? users : 0,
      newUsers: Number.isFinite(newUsers) ? newUsers : 0,
      sessions: Number.isFinite(sessions) ? sessions : 0,
      engagedSessions: Number.isFinite(engagedSessions) ? engagedSessions : 0,
      pageViews: Number.isFinite(pageViews) ? pageViews : 0,
      eventCount: Number.isFinite(eventCount) ? eventCount : 0,
      keyEvents: Number.isFinite(keyEvents) ? keyEvents : 0,
      avgSessionDuration: Number.isFinite(avgSessionDuration) ? avgSessionDuration : 0,
      engagementRate,
    };
  };

  const appliedReportDataSourceIsCurrent = useMemo(
    () =>
      Boolean(appliedReportDataSource?.context) &&
      contextsMatch(appliedReportDataSource.context, currentRunContext),
    [appliedReportDataSource, currentRunContext]
  );
  const appliedReportDataSourcePriority = appliedReportDataSourceIsCurrent
    ? appliedReportDataSource?.priority || 0
    : 0;
  const isKpiHydrationSettledForContext =
    kpiHydrationGate.contextKey === currentRunContextKey &&
    kpiHydrationGate.status === "settled";
  const canRenderKpiMetrics =
    appliedReportDataSourcePriority >= 2 ||
    isKpiHydrationSettledForContext ||
    isRollingPresetContext;

  const displayScopedCanonicalChannelMap = useMemo(() => {
    const contextAggregateMap = buildSherlocCanonicalChannelMap(contextScopedAggregate);
    const contextResolvedMap = buildSherlocCanonicalChannelMap(contextScopedResolved);
    const stateAggregateMap = buildSherlocCanonicalChannelMap(aggregate);
    const stateResolvedMap = buildSherlocCanonicalChannelMap(resolved);
    const aggregateMap = buildSherlocCanonicalChannelMap(displayScopedAggregate);
    const resolvedIsCanonical =
      resolvedOrigin?.source === "canonical_performance_summary" ||
      displayScopedResolved?.contract === "canonical_reporting_v1" ||
      displayScopedResolved?.canonical_reporting_contract === "canonical_reporting_v1" ||
      displayScopedResolved?.sourceContract?.contract === "canonical_reporting_v1";

    const resolvedMap = {
      ...buildSherlocCanonicalChannelMap(displayScopedResolved),
      ...buildSherlocCanonicalChannelMap({
        contract: resolvedIsCanonical ? "canonical_reporting_v1" : "",
        byChannel: displayScopedResolved?.byChannel || {},
        platforms: displayScopedResolved?.platforms || {},
      }),
    };

    const mergedMap = resolvedIsCanonical
      ? {
          ...contextAggregateMap,
          ...stateAggregateMap,
          ...aggregateMap,
          ...contextResolvedMap,
          ...stateResolvedMap,
          ...resolvedMap,
        }
      : {
          ...resolvedMap,
          ...stateResolvedMap,
          ...contextResolvedMap,
          ...aggregateMap,
          ...stateAggregateMap,
          ...contextAggregateMap,
        };

    // An authoritative missing provider is not permission to resurrect an old
    // saved channel after revocation or an account/mapping change.
    const authoritativeProviderMap = isCanonicalSherlocAggregate(displayScopedAggregate)
      ? aggregateMap : resolvedIsCanonical ? resolvedMap : null;
    if (authoritativeProviderMap) {
      Object.keys(mergedMap).forEach((key) => {
        if (!normalizeProviderSurfaceKey(key)) return;
        if (Object.prototype.hasOwnProperty.call(authoritativeProviderMap, key)) mergedMap[key] = authoritativeProviderMap[key];
        else delete mergedMap[key];
      });
    }
    return rollupSherlocUiPlatformChannelMap(mergedMap);
  }, [
    aggregate,
    contextScopedAggregate,
    contextScopedResolved,
    displayScopedAggregate,
    displayScopedResolved,
    resolved,
    resolvedOrigin?.source,
  ]);

  const displayScopedPlatformMetricEnvelope = useMemo(
    () => ({
      contract: "canonical_reporting_v1",
      byChannel: displayScopedCanonicalChannelMap,
      platformPerformanceByChannel: displayScopedCanonicalChannelMap,
    }),
    [displayScopedCanonicalChannelMap]
  );

  const hasCanonicalPaidPlatformMetricEvidence = useMemo(
    () => hasSherlocPaidPlatformMetricEvidence(displayScopedCanonicalChannelMap),
    [displayScopedCanonicalChannelMap]
  );

  const platformMetricResolutionSources = useMemo(
    () => ({
      current: [
        { label: "contextScopedAggregate", value: contextScopedAggregate },
        { label: "contextScopedResolved", value: contextScopedResolved },
        { label: "aggregateState", value: aggregate },
        { label: "resolvedState", value: resolved },
        { label: "displayScopedCanonicalChannelMap", value: { byChannel: displayScopedCanonicalChannelMap } },
      ],
      fallback: [
        { label: "displayScopedAggregate", value: displayScopedAggregate },
        { label: "displayScopedResolved", value: displayScopedResolved },
        { label: "activeRunSnapshotLock.aggregate", value: activeRunSnapshotLock?.aggregate },
        { label: "activeRunSnapshotLock.resolved", value: activeRunSnapshotLock?.resolved },
        { label: "contextScopedLastRun.aggregate", value: contextScopedLastRun?.aggregate },
        { label: "contextScopedLastRun.snapshot", value: contextScopedLastRun?.snapshot },
      ],
    }),
    [
      activeRunSnapshotLock,
      aggregate,
      contextScopedAggregate,
      contextScopedLastRun,
      contextScopedResolved,
      displayScopedAggregate,
      displayScopedCanonicalChannelMap,
      displayScopedResolved,
      resolved,
    ]
  );

  const kpiSnapshotPlatformChannelMap = useMemo(() => {
    const keys = new Set();
    const add = (rawKey) => {
      const key = canonicalizeSherlocMetricChannelKey(rawKey);
      if (key && key !== "overall") keys.add(key);
    };

    CHANNEL_ORDER.forEach(add);
    Object.keys(displayScopedCanonicalChannelMap || {}).forEach(add);
    Object.keys(displayScopedSources?.live?.byChannel || {}).forEach(add);
    Object.keys(displayScopedResolved?.byChannel || {}).forEach(add);
    Object.keys(displayScopedAggregate?.byChannel || {}).forEach(add);

    const rawMap = Array.from(keys).reduce((acc, key) => {
      if (SHERLOC_PAID_MEDIA_AGGREGATE_EXCLUDED_KEYS.has(key)) return acc;
      const resolvedForCard = resolvePlatformMetricsForCard(key, platformMetricResolutionSources);
      const channel =
        displayScopedCanonicalChannelMap[key] ||
        resolvedForCard.metrics ||
        resolveCanonicalPlatformMetrics(displayScopedResolved, key) ||
        resolveCanonicalPlatformMetrics(displayScopedAggregate, key) ||
        null;
      const metrics = extractSherlocCanonicalPlatformMetrics(channel || {});
      const deliveryEvidence = buildSherlocDeliveryEvidence(metrics);

      if (
        !deliveryEvidence.hasDelivery &&
        !hasSherlocCanonicalMetricPayload(metrics) &&
        !hasSharedUsableMetrics(metrics)
      ) {
        return acc;
      }

      acc[key] = {
        ...metrics,
        resolvedCanonicalKey: key,
        rawCanonicalKey: metrics.rawCanonicalKey || channel?.rawCanonicalKey || key,
        rawChannelObject: channel?.rawChannelObject || channel || metrics,
        platformMetricSource:
          metrics.platformMetricSource ||
          channel?.platformMetricSource ||
          resolvedForCard.debug?.matchedPath ||
          "kpi_snapshot_platform_fallback",
      };
      return acc;
    }, {});

    return rollupSherlocUiPlatformChannelMap(rawMap);
  }, [
    displayScopedAggregate,
    displayScopedCanonicalChannelMap,
    displayScopedResolved,
    displayScopedSources?.live?.byChannel,
    platformMetricResolutionSources,
  ]);

  const hasCurrentPaidPlatformMetricEvidence = useMemo(
    () =>
      hasCanonicalPaidPlatformMetricEvidence ||
      hasSherlocPaidPlatformMetricEvidence(kpiSnapshotPlatformChannelMap),
    [hasCanonicalPaidPlatformMetricEvidence, kpiSnapshotPlatformChannelMap]
  );

  const aggregateMetrics = useMemo(() => {
    if (!canRenderKpiMetrics && !hasCurrentPaidPlatformMetricEvidence) return null;
    const platformFallbackEnvelope = {
      contract: "canonical_reporting_v1",
      byChannel: kpiSnapshotPlatformChannelMap,
      platformPerformanceByChannel: kpiSnapshotPlatformChannelMap,
    };
    const canonicalMetrics = resolveCanonicalAggregateMetrics(
      displayScopedResolved,
      displayScopedAggregate
    );
    if (canonicalMetrics && hasSharedUsableMetrics(canonicalMetrics)) return canonicalMetrics;
    const canonicalPlatformMetrics = resolveCanonicalAggregateMetrics(
      displayScopedPlatformMetricEnvelope
    );
    if (canonicalPlatformMetrics && hasSharedUsableMetrics(canonicalPlatformMetrics)) {
      return canonicalPlatformMetrics;
    }
    const derivedPlatformMetrics = deriveSherlocAggregateMetricsFromPlatformChannels(
      displayScopedResolved,
      displayScopedAggregate
    );
    if (derivedPlatformMetrics && hasSharedUsableMetrics(derivedPlatformMetrics)) {
      return derivedPlatformMetrics;
    }
    const derivedFallbackPlatformMetrics = deriveSherlocAggregateMetricsFromPlatformChannels(
      platformFallbackEnvelope,
      displayScopedPlatformMetricEnvelope,
      displayScopedResolved,
      displayScopedAggregate
    );
    if (derivedFallbackPlatformMetrics && hasSharedUsableMetrics(derivedFallbackPlatformMetrics)) {
      return derivedFallbackPlatformMetrics;
    }
    if (derivedPlatformMetrics) return derivedPlatformMetrics;
    if (derivedFallbackPlatformMetrics) return derivedFallbackPlatformMetrics;
    if (canonicalMetrics) return canonicalMetrics;
    if (canonicalPlatformMetrics) return canonicalPlatformMetrics;
    const usesResolvedTotals = Boolean(
      displayScopedResolved?.totals && Object.keys(displayScopedResolved.totals).length
    );
    const totals =
      (usesResolvedTotals ? displayScopedResolved.totals : displayScopedAggregate?.totals) ||
      null;
    if (!hasSherlocCanonicalMetricPayload(totals)) return null;
    // The matching channel map rides along so blended CTR/CPC/ER/VVR fold
    // only metric-eligible channels, never the whole impression pool.
    const totalsByChannel =
      (usesResolvedTotals
        ? displayScopedResolved?.byChannel
        : displayScopedAggregate?.byChannel) || null;
    return computeReportingMetrics(totals, totalsByChannel);
  }, [
    canRenderKpiMetrics,
    displayScopedAggregate,
    displayScopedPlatformMetricEnvelope,
    displayScopedResolved,
    hasCurrentPaidPlatformMetricEvidence,
    kpiSnapshotPlatformChannelMap,
  ]);

  /*
   * The campaign's qualified video, from the canonical basis the backend
   * already publishes — never re-derived here.
   *
   * Sherloc's KPI strip stated "Video Views" over a whole-scope division of
   * one platform's view count by every impression the campaign served. On a
   * CTV+YouTube flight that headline is YouTube's share presented as the
   * campaign's video delivery, beside a rate whose denominator includes CTV
   * inventory that cannot report a view at all. The governed basis replaces
   * both, and carries the platforms that produced it.
   */
  /*
   * The governed video basis re-folded from channel evidence, as a floor.
   *
   * The strip was seen stating "Qualified Video Views 204.6K" and then
   * REVERTING to the legacy "Video Views" pair a moment later. Nothing about
   * the campaign changed in that moment — the object the rungs below read did.
   * Before a run, the canonical aggregate from the backend wins and carries
   * `qualifiedVideo`. When a run completes, `activeRunSnapshotLock` takes
   * precedence, and its `resolved` is `buildEmptyResolved()` -> `{byChannel,
   * totals}` while its `aggregate` is a REBUILT object — neither carries the
   * key. Every rung went undefined and the slot fell to the generic pair.
   *
   * The third rung was never going to save it: `displayScopedPlatformMetric-
   * Envelope` is `{contract, byChannel, platformPerformanceByChannel}` and has
   * no `qualifiedVideo` property in any code path. It has been dead code.
   *
   * So the floor is EVIDENCE, not another object that might carry a summary.
   * The canonical channel map survives every one of these transitions, and
   * folding it through the same engine the resolver uses gives the same answer
   * the aggregate would have carried. A qualified ruling is a property of what
   * the platforms reported, not of which snapshot happened to win the render.
   *
   * It sits under `qualifiedVideoBasis` deliberately, not under the tiles:
   * `videoKpiFields` reads the same basis, so the shared decision about WHICH
   * video cards exist is steadied by it too. A floor beneath only the tiles
   * would have left the field set still flipping.
   */
  const channelEvidenceVideoBasis = useMemo(() => {
    const channelMap = displayScopedCanonicalChannelMap;
    if (!isPlainObject(channelMap) || !Object.keys(channelMap).length) return null;
    return computeReportingMetrics({}, channelMap);
  }, [displayScopedCanonicalChannelMap]);

  const qualifiedVideoBasis = useMemo(
    () =>
      displayScopedResolved?.qualifiedVideo ||
      displayScopedAggregate?.qualifiedVideo ||
      displayScopedPlatformMetricEnvelope?.qualifiedVideo ||
      channelEvidenceVideoBasis?.qualifiedVideo ||
      null,
    [
      channelEvidenceVideoBasis,
      displayScopedAggregate,
      displayScopedPlatformMetricEnvelope,
      displayScopedResolved,
    ]
  );

  /**
   * The outcome's earned name, and whether a monetary value was ever configured.
   *
   * Both are read from the canonical payload the KPI strip is already drawing
   * from, so the label and the count can never describe different scopes.
   */
  const outcomeConversionsLabel = useMemo(
    () =>
      resolveOutcomeConversionsPresentation(
        outcomeSemantics || displayScopedResolved || displayScopedAggregate || null
      ).label,
    [displayScopedAggregate, displayScopedResolved, outcomeSemantics]
  );

  const hasConfiguredConversionValue = useMemo(() => {
    const value = aggregateMetrics?.conversionValue;
    if (value === null || value === undefined || value === "") return false;
    const numeric = Number(value);
    // A configured value that genuinely measured zero revenue still reports a
    // zero; what must never render as money is the ABSENCE of configuration.
    return Number.isFinite(numeric) && numeric > 0;
  }, [aggregateMetrics]);

  const qualifiedVideoTiles = useMemo(
    () => buildQualifiedVideoKpiTiles(qualifiedVideoBasis),
    [qualifiedVideoBasis]
  );

  /*
   * CPQV rides with the qualified total, priced on the same population.
   *
   * Sherloc is where the whole-scope-division defect keeps reappearing — a
   * page that folds its own totals from per-channel rows can always divide two
   * of them — so this reads the basis the resolver already folded and never
   * touches `aggregateMetrics.spend`. Same evidence floor, same reason: the
   * cost must not blink out from under a total that is still on screen.
   */
  const costPerQualifiedVideoViewTile = useMemo(
    () => {
      const contract = [
        displayScopedResolved,
        displayScopedAggregate,
        displayScopedPlatformMetricEnvelope,
      ]
        .map((candidate) => resolveSpendEfficiencyContract(candidate))
        .find(Boolean);
      return buildCostPerQualifiedVideoViewTile(
        resolveSpendEfficiencyMetric(contract, { metricKey: "cpqv" })
      );
    },
    [
      displayScopedAggregate,
      displayScopedPlatformMetricEnvelope,
      displayScopedResolved,
    ]
  );

  /*
   * Which video cards this strip may render, decided by the SHARED contract
   * rather than here.
   *
   * The rule — canonical wins when present, the legacy pair survives only for
   * a payload that carries no qualified basis — is the same rule Campaign
   * Insights applies, and it lived in `buildAiOptimizationKpiFields` while
   * this page kept its own local ternary. Two implementations of one decision
   * is the shape of this whole defect class: the canonical data arrives and a
   * hand-rolled branch decides differently about it. There is now one
   * decision, and both surfaces read it.
   *
   * The legacy fallback is deliberately NOT deleted. A Meta or TikTok flight
   * carries no qualified ruling, and dropping its video pair would claim the
   * campaign delivered no video at all.
   */
  const videoKpiFields = useMemo(
    () => new Set(buildAiOptimizationKpiFields(qualifiedVideoBasis).map(([field]) => field)),
    [qualifiedVideoBasis]
  );

  /*
   * The completion count and the rate beside it come from ONE governed pair,
   * so they are always about the same platforms. Reading them from separate
   * fields is how a count scoped to CTV+YouTube came to sit beside a rate
   * measured on CTV alone.
   */
  const videoCompletionPair = useMemo(
    () =>
      selectGovernedCountRatePair(displayScopedAggregate, "videoCompletions") ||
      selectGovernedCountRatePair(displayScopedResolved, "videoCompletions") ||
      selectGovernedCountRatePair(displayScopedPlatformMetricEnvelope, "videoCompletions"),
    [displayScopedAggregate, displayScopedPlatformMetricEnvelope, displayScopedResolved]
  );

  const engagementPair = useMemo(
    () =>
      selectGovernedCountRatePair(displayScopedAggregate, "engagements") ||
      selectGovernedCountRatePair(displayScopedResolved, "engagements") ||
      selectGovernedCountRatePair(displayScopedPlatformMetricEnvelope, "engagements"),
    [displayScopedAggregate, displayScopedPlatformMetricEnvelope, displayScopedResolved]
  );

  const aggregateKpiWarning = useMemo(() => {
    if (
      !canRenderKpiMetrics ||
      aggregateMetrics ||
      !resolvedAdvertiserId ||
      hasCurrentPaidPlatformMetricEvidence
    ) {
      return "";
    }
    return "Current KPI metrics are not available in the canonical Sherloc reporting payload for this selected context.";
  }, [
    aggregateMetrics,
    canRenderKpiMetrics,
    hasCurrentPaidPlatformMetricEvidence,
    resolvedAdvertiserId,
  ]);
  const isPlatformPerformanceLoading = Boolean(
    resolvedAdvertiserId &&
      (running ||
        campaignsLoading ||
        !canRenderKpiMetrics ||
        liveStatus?.state === "loading" ||
        gaStatus?.state === "loading")
  );
  const platformMetricFallbackLabel = isPlatformPerformanceLoading ? "Loading..." : "No Data";

  const buildChannelMetricRows = useCallback((key, m) => {
    if (normalizeProviderSurfaceKey(key)) {
      return [
        { k: "Client billed spend", v: formatProviderCurrency(m.spend, m.currency), copper: true },
        { k: "Impressions", v: m.impressions === null ? "Unavailable" : formatNumber(m.impressions) },
        { k: "Clicks", v: m.clicks === null ? "Unavailable" : formatNumber(m.clicks) },
      ];
    }
    if (key === "google_analytics") {
      const rows = [];
      const gaValue = (...keys) => pickFirst(...keys.map((metricKey) => m?.[metricKey]));
      const formatOptionalGaNumber = (value) =>
        value != null && Number.isFinite(toNumber(value))
          ? formatNumber(value)
          : platformMetricFallbackLabel;
      const users = gaValue("users", "activeUsers", "active_users", "totalUsers", "total_users");
      const newUsers = gaValue("newUsers", "new_users");
      const sessions = gaValue("sessions");
      const engagedSessions = gaValue("engagedSessions", "engaged_sessions");
      const engagementRate = gaValue("engagementRate", "engagement_rate");
      const pageViews = gaValue("pageViews", "page_views", "screenPageViews", "screen_page_views");
      const eventCount = gaValue("eventCount", "event_count");
      const keyEvents = gaValue("keyEvents", "key_events", "conversions");
      const avgSessionDuration = gaValue(
        "avgSessionDuration",
        "averageSessionDuration",
        "average_session_duration",
        "avg_session_duration"
      );

      rows.push({ k: "Users", v: formatOptionalGaNumber(users) });
      rows.push({ k: "New Users", v: formatOptionalGaNumber(newUsers) });
      rows.push({ k: "Sessions", v: formatOptionalGaNumber(sessions) });
      rows.push({ k: "Engaged Sessions", v: formatOptionalGaNumber(engagedSessions) });
      rows.push({
        k: "Engagement Rate",
        v: engagementRate != null ? formatPercent(engagementRate) : platformMetricFallbackLabel,
      });

      const extras = [];
      if (pageViews != null && pageViews > 0) {
        extras.push({ k: "Page Views", v: formatNumber(pageViews) });
      }
      if (eventCount != null && eventCount > 0) {
        extras.push({ k: "Event Count", v: formatNumber(eventCount) });
      }
      if (keyEvents != null && keyEvents > 0) {
        extras.push({ k: "Key Events", v: formatNumber(keyEvents) });
      }
      if (avgSessionDuration != null && avgSessionDuration > 0) {
        extras.push({ k: "Avg Session Duration", v: formatDuration(avgSessionDuration) });
      }

      const maxRows = 8;
      const room = Math.max(0, maxRows - rows.length);
      return [...rows, ...extras.slice(0, room)];
    }

    const displayedMetrics = deriveSherlocPaidPlatformDisplayMetrics(m);
    const hasMetricValue = (value) => {
      if (value === null || value === undefined || value === "") return false;
      return Number.isFinite(toNumber(value));
    };
    const hasPositive = (value) => hasMetricValue(value) && Number(value) > 0;
    const formatOptionalMoney = (value) =>
      hasMetricValue(value) ? formatMoney(value) : platformMetricFallbackLabel;
    const formatOptionalNumber = (value) =>
      hasMetricValue(value) ? formatNumber(value) : platformMetricFallbackLabel;
    const metricRows = {
      spend: () => ({ k: "Spend", v: formatOptionalMoney(displayedMetrics.spend), copper: true }),
      impressions: () => ({ k: "Impressions", v: formatOptionalNumber(displayedMetrics.impressions) }),
      clicks: () => ({ k: "Clicks", v: formatOptionalNumber(displayedMetrics.clicks) }),
      ctr: () => ({
        k: "CTR",
        v:
          hasPositive(displayedMetrics.clicks) && hasPositive(displayedMetrics.impressions) && displayedMetrics.ctr != null
            ? formatPercent(displayedMetrics.ctr)
            : platformMetricFallbackLabel,
      }),
      cpm: () => (hasPositive(displayedMetrics.cpm) ? { k: "CPM", v: formatMoney(displayedMetrics.cpm) } : null),
      cpc: () =>
        hasPositive(displayedMetrics.clicks) && hasPositive(displayedMetrics.cpc)
          ? { k: "CPC", v: formatMoney(displayedMetrics.cpc) }
          : null,
      conversions: () =>
        hasPositive(displayedMetrics.conversions) ? { k: "Conversions", v: formatNumber(displayedMetrics.conversions) } : null,
      conversionValue: () =>
        hasPositive(displayedMetrics.conversionValue)
          ? { k: "Conversion Value", v: formatMoney(displayedMetrics.conversionValue) }
          : null,
      engagements: () =>
        hasPositive(displayedMetrics.engagements) ? { k: "Engagements", v: formatNumber(displayedMetrics.engagements) } : null,
      engagementRate: () =>
        hasPositive(displayedMetrics.engagements)
          ? { k: "Engagement Rate", v: displayedMetrics.er != null ? formatPercent(displayedMetrics.er) : platformMetricFallbackLabel }
          : null,
      videoViews: () =>
        hasPositive(displayedMetrics.videoViews) ? { k: "Video Views", v: formatNumber(displayedMetrics.videoViews) } : null,
      videoViewRate: () =>
        hasPositive(displayedMetrics.videoViews)
          ? { k: "Video View Rate", v: displayedMetrics.vvr != null ? formatPercent(displayedMetrics.vvr) : platformMetricFallbackLabel }
          : null,
      videoStarted: () =>
        hasPositive(displayedMetrics.videoStarted)
          ? { k: "Video Started", v: formatNumber(displayedMetrics.videoStarted) }
          : null,
      videoCompleted: () =>
        hasPositive(displayedMetrics.videoCompleted)
          ? { k: "Video Completed", v: formatNumber(displayedMetrics.videoCompleted) }
          : null,
      completionRate: () =>
        hasPositive(displayedMetrics.videoStarted) || hasPositive(displayedMetrics.videoCompleted)
          ? {
              k: "Completion Rate",
              v: displayedMetrics.videoCompletionRate != null ? formatPercent(displayedMetrics.videoCompletionRate) : platformMetricFallbackLabel,
            }
          : null,
    };

    let metricKeys;
    if (SHERLOC_DISPLAY_METRIC_CHANNELS.has(key)) {
      metricKeys = ["spend", "impressions", "clicks", "ctr", "cpm", "cpc", "conversions"];
    } else if (key === "youtube") {
      metricKeys = [
        "spend",
        "impressions",
        "clicks",
        "ctr",
        "videoViews",
        "videoViewRate",
        "cpc",
        "cpm",
        "conversions",
      ];
    } else if (key === "ctv") {
      /*
       * CTV's vocabulary matches its KPI profile, and drops the two rows that
       * were saying the same thing twice.
       *
       * The canonical layer aliases CTV's provider START count into
       * `video_views` — the channel-grain delivery query never asks for a
       * views field at all — so "Video Views" and "Video Started" printed the
       * IDENTICAL number under two names. Beside them sat "Video View Rate"
       * (views over impressions) and "Completion Rate" (completions over
       * starts): on NSC, 98.20% and 98.19%, which round to the same 98.2%.
       * Four rows, two facts, and a reader with no way to tell which rate they
       * were being handed.
       *
       * So CTV states starts, completions and the completion rate — the same
       * metrics, under the same names, as the KPI strip a reader of this
       * narrative is looking at. Both fields remain available to every other
       * channel that reports genuine views distinct from starts.
       */
      metricKeys = [
        "spend",
        "impressions",
        "videoStarted",
        "videoCompleted",
        "completionRate",
        "cpm",
      ];
    } else if (SHERLOC_VIDEO_METRIC_CHANNELS.has(key)) {
      metricKeys = [
        "spend",
        "impressions",
        "videoViews",
        "videoViewRate",
        "videoStarted",
        "videoCompleted",
        "completionRate",
        "cpm",
      ];
    } else if (SHERLOC_SOCIAL_METRIC_CHANNELS.has(key)) {
      metricKeys = [
        "spend",
        "impressions",
        "clicks",
        "ctr",
        "engagements",
        "engagementRate",
        "cpc",
        "cpm",
      ];
    } else {
      metricKeys = ["spend", "impressions", "clicks", "ctr", "cpm", "cpc", "conversions"];
    }

    const maxMetricRows = key === "youtube" ? 9 : 8;

    return metricKeys
      .map((metricKey) => metricRows[metricKey]?.())
      .filter(Boolean)
      .slice(0, maxMetricRows);
  }, [platformMetricFallbackLabel]);

  const aggregateMetricSignature = useMemo(
    () => buildNarrativeMetricSignature({ metrics: aggregateMetrics }),
    [aggregateMetrics]
  );
  const isCanonicalKpiDisplaySource =
    appliedReportDataSourceIsCurrent && appliedReportDataSourcePriority >= 3;
  const isNarrativeOutOfSyncWithKpis = useMemo(
    () =>
      Boolean(
        shouldRenderNarrative &&
          !hasSuccessfulDirectRunNarrative &&
          isCanonicalKpiDisplaySource &&
          aggregateMetricSignature &&
          hasMaterialNarrativeMetricDiff(aggregateMetricSignature, aiNarrativeMetricSignature)
      ),
    [
      aggregateMetricSignature,
      aiNarrativeMetricSignature,
      hasSuccessfulDirectRunNarrative,
      isCanonicalKpiDisplaySource,
      shouldRenderNarrative,
    ]
  );
  const shouldRenderAlignedNarrative =
    shouldRenderNarrative && !isNarrativeOutOfSyncWithKpis;
  const narrativeAlignmentFailureForCurrentSignature = useMemo(() => {
    if (hasSuccessfulVisibleNarrative) return null;
    if (!narrativeAlignmentFailure || !isNarrativeOutOfSyncWithKpis) return null;
    if (narrativeAlignmentFailure.contextKey !== currentRunContextKey) return null;
    if (
      narrativeAlignmentFailure.signatureKey &&
      narrativeAlignmentFailure.signatureKey !== (aggregateMetricSignature?.key || "")
    ) {
      return null;
    }
    return narrativeAlignmentFailure;
  }, [
    aggregateMetricSignature,
    currentRunContextKey,
    hasSuccessfulVisibleNarrative,
    isNarrativeOutOfSyncWithKpis,
    narrativeAlignmentFailure,
  ]);
  const shouldShowNarrativeAlignmentFailure = Boolean(
    narrativeAlignmentFailureForCurrentSignature
  );
  const narrativeAlignmentFailureMessage =
    narrativeAlignmentFailureForCurrentSignature?.message ||
    "Sherloc could not refresh the narrative with the latest KPI context. KPI cards remain current; retry Run Analysis to regenerate the narrative.";

  useEffect(() => {
    publishSherlocTrace("client", "visible_narrative_renderability_evaluated", {
      advertiserId: currentRunContext?.advertiserId || "",
      campaignId: currentRunContext?.campaignId || "",
      timeframeKey: currentRunContextKey,
      visibleRunId: visibleNarrativeSelectionRef.current?.runId || null,
      visibleSource: visibleNarrativeSelectionRef.current?.source || null,
      summaryPresent: hasRenderableSummary(aiSummary),
      recommendationsPresent:
        Array.isArray(aiRecommendations) &&
        aiRecommendations.some((line) => String(line || "").trim()),
      shouldRenderNarrative,
      shouldRenderAlignedNarrative,
      hasSuccessfulVisibleNarrative,
      isNarrativeOutOfSyncWithKpis,
      shouldShowNarrativeAlignmentFailure,
    });
  }, [
    aiRecommendations,
    aiSummary,
    currentRunContext,
    currentRunContextKey,
    hasSuccessfulVisibleNarrative,
    isNarrativeOutOfSyncWithKpis,
    shouldRenderAlignedNarrative,
    shouldRenderNarrative,
    shouldShowNarrativeAlignmentFailure,
  ]);

  useEffect(() => {
    if (!hasSuccessfulVisibleNarrative || !narrativeAlignmentFailure) return;
    publishSherlocTrace("client", "waiting_placeholder_suppressed_for_successful_run", {
      advertiserId: currentRunContext?.advertiserId || "",
      campaignId: currentRunContext?.campaignId || "",
      timeframeKey: currentRunContextKey,
      visibleRunId: visibleNarrativeSelectionRef.current?.runId || null,
      visibleSource: visibleNarrativeSelectionRef.current?.source || null,
      summaryPresent: hasRenderableSummary(aiSummary),
      recommendationsPresent:
        Array.isArray(aiRecommendations) &&
        aiRecommendations.some((line) => String(line || "").trim()),
      reason: "successful_visible_narrative_takes_precedence",
      suppressedMessage: narrativeAlignmentFailure?.message || "",
    });
    setNarrativeAlignmentFailure(null);
  }, [
    aiRecommendations,
    aiSummary,
    currentRunContext,
    currentRunContextKey,
    hasSuccessfulVisibleNarrative,
    narrativeAlignmentFailure,
  ]);

  useEffect(() => {
    if (!shouldShowNarrativeAlignmentFailure) return;
    publishSherlocTrace("client", "waiting_placeholder_rendered", {
      advertiserId: currentRunContext?.advertiserId || "",
      campaignId: currentRunContext?.campaignId || "",
      timeframeKey: currentRunContextKey,
      visibleRunId: visibleNarrativeSelectionRef.current?.runId || null,
      visibleSource: visibleNarrativeSelectionRef.current?.source || null,
      summaryPresent: hasRenderableSummary(aiSummary),
      recommendationsPresent:
        Array.isArray(aiRecommendations) &&
        aiRecommendations.some((line) => String(line || "").trim()),
      reason: narrativeAlignmentFailureForCurrentSignature?.message || "",
      hasSuccessfulVisibleNarrative,
      isNarrativeOutOfSyncWithKpis,
    });
  }, [
    aiRecommendations,
    aiSummary,
    currentRunContext,
    currentRunContextKey,
    hasSuccessfulVisibleNarrative,
    isNarrativeOutOfSyncWithKpis,
    narrativeAlignmentFailureForCurrentSignature,
    shouldShowNarrativeAlignmentFailure,
  ]);

  useEffect(() => {
    if (
      !isNarrativeOutOfSyncWithKpis ||
      running ||
      hasSuccessfulVisibleNarrative ||
      !resolvedAdvertiserId ||
      isRollingPresetContext
    ) {
      return;
    }

    const attemptKey = `${currentRunContextKey}:${aggregateMetricSignature?.key || "no_signature"}`;
    if (narrativeAlignmentAttemptedRef.current.has(attemptKey)) return;
    narrativeAlignmentAttemptedRef.current.add(attemptKey);

    publishSherlocTrace("client", "narrative_alignment_refresh_requested", {
      context: currentRunContext,
      currentSignature: aggregateMetricSignature,
      narrativeSignature: aiNarrativeMetricSignature,
    });

    setNarrativeAlignmentFailure(null);
    runSherlocRef.current?.({ trigger: "canonical_aggregate_alignment" });
  }, [
    aggregateMetricSignature,
    aiNarrativeMetricSignature,
    currentRunContext,
    currentRunContextKey,
    hasSuccessfulVisibleNarrative,
    isNarrativeOutOfSyncWithKpis,
    isRollingPresetContext,
    resolvedAdvertiserId,
    running,
  ]);

  useEffect(() => {
    if (
      !isNarrativeOutOfSyncWithKpis ||
      running ||
      hasSuccessfulVisibleNarrative ||
      shouldShowNarrativeAlignmentFailure
    ) {
      return undefined;
    }

    const attemptKey = `${currentRunContextKey}:${aggregateMetricSignature?.key || "no_signature"}`;
    if (!narrativeAlignmentAttemptedRef.current.has(attemptKey)) return undefined;

    const timeout = setTimeout(() => {
      setNarrativeAlignmentFailure((current) => {
        if (
          current?.contextKey === currentRunContextKey &&
          current?.signatureKey === (aggregateMetricSignature?.key || "")
        ) {
          return current;
        }
        return {
          contextKey: currentRunContextKey,
          signatureKey: aggregateMetricSignature?.key || "",
          message:
            "Sherloc is still waiting on the refreshed narrative. KPI cards are current; retry Run Analysis if the narrative does not update.",
        };
      });
    }, 45000);

    return () => clearTimeout(timeout);
  }, [
    aggregateMetricSignature,
    currentRunContextKey,
    hasSuccessfulVisibleNarrative,
    isNarrativeOutOfSyncWithKpis,
    running,
    shouldShowNarrativeAlignmentFailure,
  ]);

  const livePlatformsForUi = useMemo(() => {
    const approvedPlatforms = getApprovedPlanPlatformsForUi({ campaignPlatformScope });
    const resolvedReportingByChannel = displayScopedResolved?.byChannel || {};
    const reportingByChannel = {
      ...resolvedReportingByChannel,
      ...displayScopedCanonicalChannelMap,
    };
    const livePlatforms = getLivePlatformsForUi({
      liveByChannel: displayScopedSources?.live?.byChannel || {},
      reportingByChannel,
    });

    return mergePlatformSignalsForUi({
      approvedPlatforms,
      livePlatforms,
      campaignPlatformScope,
    }).filter((platform) => isSherlocPaidPerformanceChannelKey(platform?.key || platform?.label));
  }, [
    campaignPlatformScope,
    displayScopedCanonicalChannelMap,
    displayScopedResolved?.byChannel,
    displayScopedSources?.live?.byChannel,
  ]);

  const googleAnalyticsMeasurementChannel = useMemo(() => {
    const preservedGoogleAnalyticsChannel =
      running && confirmedGoogleAnalyticsChannelRef.current?.contextKey === currentRunContextKey
        ? confirmedGoogleAnalyticsChannelRef.current?.channel || null
        : null;
    const runPreservedGoogleAnalyticsChannel =
      running && isGoogleAnalyticsPillReadyLabel(activeRunStatusPreservation?.ga?.label)
        ? activeRunStatusPreservation?.gaChannel || null
        : null;
    const channel = [
      visibleGoogleAnalyticsChannel,
      runPreservedGoogleAnalyticsChannel,
      preservedGoogleAnalyticsChannel,
      displayScopedCanonicalChannelMap?.google_analytics,
      displayScopedSources?.live?.byChannel?.google_analytics,
      displayScopedResolved?.byChannel?.google_analytics,
      displayScopedAggregate?.byChannel?.google_analytics,
      gaStatus?.channel,
    ].find((candidate) => hasUsableMetrics(candidate));

    return channel
      ? {
          ...channel,
          data_source:
            channel.data_source ||
            (runPreservedGoogleAnalyticsChannel || preservedGoogleAnalyticsChannel
              ? "google_analytics_live_preserved_during_run"
              : "google_analytics_live"),
        }
      : null;
  }, [
    activeRunStatusPreservation,
    currentRunContextKey,
    displayScopedAggregate,
    displayScopedCanonicalChannelMap,
    displayScopedResolved,
    displayScopedSources,
    gaStatus,
    running,
    visibleGoogleAnalyticsChannel,
  ]);

  const orderedChannelEntries = useMemo(() => {
    const canonicalMap = { ...displayScopedCanonicalChannelMap };
    const liveByChannel = displayScopedSources?.live?.byChannel || {};
    const inventoryKeys = buildSherlocCardInventoryKeys({
      aggregate: displayScopedAggregate,
      approvedChannelKeys: approvedSherlocChannelKeys,
      liveByChannel,
      platformInventory: livePlatformsForUi,
      resolvedAdvertiser,
      googleAnalyticsChannel: googleAnalyticsMeasurementChannel,
      campaignPlatformScope,
    });

    if (
      googleAnalyticsMeasurementChannel &&
      !hasUsableMetrics(canonicalMap.google_analytics)
    ) {
      canonicalMap.google_analytics = {
        ...googleAnalyticsMeasurementChannel,
        data_source:
          googleAnalyticsMeasurementChannel.data_source || "google_analytics_live",
      };
    }

    const contextInventoryKeys = new Set(inventoryKeys);

    Object.keys(canonicalMap).forEach((key) => inventoryKeys.add(key));

    const map = Array.from(inventoryKeys).reduce((acc, key) => {
      const resolvedForCard = resolvePlatformMetricsForCard(key, platformMetricResolutionSources);
      const canonicalChannel = normalizeProviderSurfaceKey(key) ? canonicalMap[key] || {} :
        canonicalMap[key] ||
        resolvedForCard.metrics ||
        resolveCanonicalPlatformMetrics(displayScopedResolved, key) ||
        resolveCanonicalPlatformMetrics(displayScopedAggregate, key) ||
        {};
      acc[key] = {
        ...canonicalChannel,
        __sherlocCardResolution: resolvedForCard.debug,
      };
      return acc;
    }, {});

    const ordered = [];
    CHANNEL_ORDER.forEach((k) => {
      if (
        isSherlocPaidPerformanceChannelKey(k) &&
        Object.prototype.hasOwnProperty.call(map, k) &&
        isSherlocPerformanceCardVisible({
          key: k,
          channel: map[k],
          inventoryKeys: contextInventoryKeys,
          approvedChannelKeys: approvedSherlocChannelKeys,
          liveByChannel,
          campaignPlatformScope,
        })
      ) {
        ordered.push([k, map[k]]);
      }
    });

    const remaining = Object.entries(map)
      .filter(
        ([k, val]) =>
          isSherlocPaidPerformanceChannelKey(k) &&
          !CHANNEL_ORDER.includes(k) &&
          isSherlocPerformanceCardVisible({
            key: k,
            channel: val,
            inventoryKeys: contextInventoryKeys,
            approvedChannelKeys: approvedSherlocChannelKeys,
            liveByChannel,
            campaignPlatformScope,
          })
      )
      .sort((a, b) => a[0].localeCompare(b[0]));

    return filterSherlocPerformancePlatformEntries([...ordered, ...remaining]);
  }, [
    approvedSherlocChannelKeys,
    campaignPlatformScope,
    displayScopedAggregate,
    displayScopedCanonicalChannelMap,
    displayScopedResolved,
    displayScopedSources,
    googleAnalyticsMeasurementChannel,
    livePlatformsForUi,
    platformMetricResolutionSources,
    resolvedAdvertiser,
  ]);

  useEffect(() => {
    const ctvEntry = orderedChannelEntries.find(([key]) => key === "ctv");
    const displayEntry = orderedChannelEntries.find(([key]) => key === "display");
    if (!ctvEntry && !displayEntry && !aggregateMetrics) return;

    if (process.env.NODE_ENV === "production") return;

    orderedChannelEntries
      .filter(([key]) => ["facebook", "instagram", "youtube", "ctv", "display"].includes(key))
      .forEach(([key, channel]) => {
        const resolvedMetrics = computeChannelMetrics(key, channel || {});
        const resolution = channel?.__sherlocCardResolution || {};
        console.debug("[SherlocReports][platform-card-resolution]", {
          surface: "client",
          selectedAdvertiser: {
            id: resolvedAdvertiserId || null,
            name: resolvedAdvertiser?.name || resolvedAdvertiser?.advertiser || "",
          },
          platformUiKey: key,
          approvedPlatformKey: approvedSherlocChannelKeys.has(key) ? key : null,
          candidateCanonicalKeysChecked: resolution.candidateKeys || [],
          rawCanonicalByChannelObjectKeys: resolution.rawByChannelObjectKeys || [],
          matchedPath: resolution.matchedPath || channel?.platformMetricSource || null,
          matchedRawKey: resolution.matchedRawKey || channel?.rawCanonicalKey || null,
          matchedChannelObject:
            resolution.matchedChannelObject || channel?.rawChannelObject || channel || null,
          extractedMetrics: {
            spend: resolvedMetrics.spend,
            impressions: resolvedMetrics.impressions,
            clicks: resolvedMetrics.clicks,
            ctr: resolvedMetrics.ctr,
          },
          finalDisplayedMetricValues: buildChannelMetricRows(key, resolvedMetrics),
        });
      });

    console.debug("[SherlocReports][hydration]", {
      surface: "client",
      selectedAdvertiser: {
        id: resolvedAdvertiserId || null,
        name: resolvedAdvertiser?.name || resolvedAdvertiser?.advertiser || "",
      },
      aggregateTopLevelKeys: displayScopedAggregate
        ? Object.keys(displayScopedAggregate).slice(0, 40)
        : [],
      resolvedTopLevelKeys: displayScopedResolved
        ? Object.keys(displayScopedResolved).slice(0, 40)
        : [],
      canonicalContract:
        displayScopedResolved?.contract ||
        displayScopedResolved?.canonicalReporting?.contract ||
        displayScopedResolved?.canonicalMetrics?.summary?.contract ||
        displayScopedAggregate?.contract ||
        displayScopedAggregate?.canonicalReporting?.contract ||
        displayScopedAggregate?.canonicalMetrics?.summary?.contract ||
        null,
      aggregateMetrics,
      aggregateTotals: displayScopedResolved?.totals || displayScopedAggregate?.totals || null,
      canonicalByChannelKeys: Object.keys(displayScopedCanonicalChannelMap || {}),
      platformCardSources: orderedChannelEntries.map(([key, channel]) => {
        const resolvedMetrics = computeChannelMetrics(key, channel || {});
        return {
          platform: CHANNEL_LABELS[key] || key,
          key,
          resolvedCanonicalKey: channel?.resolvedCanonicalKey || key,
          rawCanonicalKey: channel?.rawCanonicalKey || null,
          source: channel?.data_source || channel?.source || null,
          sourceLabel: channel?.sourceLabel || channel?.source_label || null,
          sourceTier: channel?.sourceTier || channel?.source_tier || null,
          spendBasis: channel?.spendBasis || channel?.spend_basis || null,
          hasCanonicalMetrics: channel?.hasCanonicalMetrics === true,
          resolution: channel?.__sherlocCardResolution || null,
          resolvedSpend: resolvedMetrics.spend,
          resolvedImpressions: resolvedMetrics.impressions,
          rawChannelMetrics: channel,
        };
      }),
      ctv: ctvEntry ? ctvEntry[1] : null,
      display: displayEntry ? displayEntry[1] : null,
    });
  }, [
    aggregateMetrics,
    approvedSherlocChannelKeys,
    buildChannelMetricRows,
    displayScopedAggregate,
    displayScopedCanonicalChannelMap,
    displayScopedResolved,
    orderedChannelEntries,
    resolvedAdvertiser,
    resolvedAdvertiserId,
  ]);

  useEffect(() => {
    const livePlatforms = (livePlatformsForUi || []).filter(
      (platform) => platform?.status === "LIVE"
    );
    if (!livePlatforms.length) return;

    const current = confirmedLiveStatusRef.current || {};
    const contextMatches = current.contextKey === currentRunContextKey;
    const existing = contextMatches && Array.isArray(current.platforms) ? current.platforms : [];
    const merged = mergePlatformSignalsPreservingLive({
      currentPlatforms: livePlatforms,
      preservedPlatforms: existing,
    });

    confirmedLiveStatusRef.current = {
      ...(contextMatches
        ? current
        : { contextKey: currentRunContextKey, ga: null, platforms: [] }),
      contextKey: currentRunContextKey,
      platforms: merged,
    };
  }, [currentRunContextKey, livePlatformsForUi]);

  const displayLivePlatformsForUi = useMemo(
    () => {
      const preserved = confirmedLiveStatusRef.current;
      const preservedPlatforms =
        preserved?.contextKey === currentRunContextKey ? preserved.platforms || [] : [];

      return mergePlatformSignalsPreservingLive({
        currentPlatforms: livePlatformsForUi,
        preservedPlatforms: [
          ...preservedPlatforms,
          ...(activeRunStatusPreservation?.platforms || []),
        ],
      });
    },
    [activeRunStatusPreservation, currentRunContextKey, livePlatformsForUi]
  );
  useEffect(() => {
    const rawStatuses = buildPlatformStatusTrace(livePlatformsForUi);
    const displayedStatuses = buildPlatformStatusTrace(displayLivePlatformsForUi);
    const preservedKeys = Object.entries(displayedStatuses)
      .filter(([key, status]) => status === "LIVE" && rawStatuses[key] !== "LIVE")
      .map(([key]) => key);

    if (!preservedKeys.length) return;

    publishSherlocTrace("client", "live_platform_status_preserved", {
      context: currentRunContext,
      contextKey: currentRunContextKey,
      preservedKeys,
      rawStatuses,
      displayedStatuses,
    });
  }, [
    currentRunContext,
    currentRunContextKey,
    displayLivePlatformsForUi,
    livePlatformsForUi,
  ]);
  const eligiblePlatformAnalysisByChannel = useMemo(
    () =>
      filterSherlocClientEligibleByChannel({
        byChannel: displayScopedResolved?.byChannel || displayScopedAggregate?.byChannel || {},
        approvedChannelKeys: approvedSherlocChannelKeys,
        liveByChannel: displayScopedSources?.live?.byChannel || {},
      }),
    [
      approvedSherlocChannelKeys,
      displayScopedAggregate,
      displayScopedResolved,
      displayScopedSources,
    ]
  );
  const platformAnalysisByChannelForNarrative = useMemo(() => {
    const merged = { ...(eligiblePlatformAnalysisByChannel || {}) };
    orderedChannelEntries.forEach(([rawKey, metrics]) => {
      const key = canonicalizeChannelKey(rawKey);
      if (!key || hasSharedUsableMetrics(merged[key]) || !hasSharedUsableMetrics(metrics)) {
        return;
      }
      merged[key] = metrics;
    });
    if (hasUsableMetrics(googleAnalyticsMeasurementChannel)) {
      merged.google_analytics = googleAnalyticsMeasurementChannel;
    }
    return merged;
  }, [
    eligiblePlatformAnalysisByChannel,
    googleAnalyticsMeasurementChannel,
    orderedChannelEntries,
  ]);
  const renderedPlatformAnalysisRecommendations = useMemo(
    () =>
      applyChannelAwarePlatformAnalysis(
        aiRecommendations || [],
        platformAnalysisByChannelForNarrative,
        resolvedAdvertiserVertical
      ),
    [aiRecommendations, platformAnalysisByChannelForNarrative, resolvedAdvertiserVertical]
  );
  useEffect(() => {
    const committedPlatforms = getRecommendationPlatformKeys(aiRecommendations || []);
    const renderedPlatforms = getRecommendationPlatformKeys(
      renderedPlatformAnalysisRecommendations || []
    );
    const eligiblePlatforms = Object.keys(platformAnalysisByChannelForNarrative || {})
      .map(canonicalizeChannelKey)
      .filter(Boolean);
    const droppedPlatforms = committedPlatforms.filter(
      (key) => !renderedPlatforms.includes(key)
    );

    publishSherlocTrace("client", "platform_analysis_recommendation_coverage", {
      context: currentRunContext,
      contextKey: currentRunContextKey,
      visibleRunId: visibleNarrativeSelectionRef.current?.runId || null,
      visibleSource: visibleNarrativeSelectionRef.current?.source || null,
      recommendationPlatformsReturned: committedPlatforms,
      recommendationPlatformsCommitted: committedPlatforms,
      recommendationPlatformsRendered: renderedPlatforms,
      eligiblePlatforms,
      droppedPlatforms,
      recommendationCount: Array.isArray(aiRecommendations) ? aiRecommendations.length : 0,
      renderedRecommendationCount: renderedPlatformAnalysisRecommendations.length,
    });
  }, [
    aiRecommendations,
    currentRunContext,
    currentRunContextKey,
    platformAnalysisByChannelForNarrative,
    renderedPlatformAnalysisRecommendations,
  ]);
  const livePlatformLiveCount = useMemo(
    () => displayLivePlatformsForUi.filter((platform) => platform?.status === "LIVE").length,
    [displayLivePlatformsForUi]
  );
  const livePlatformStatusByKey = useMemo(
    () =>
      (displayLivePlatformsForUi || []).reduce((acc, platform) => {
        const key = normalizeLivePlatformKey(platform?.key);
        if (!key) return acc;
        acc[key] = platform?.status || "";
        return acc;
      }, {}),
    [displayLivePlatformsForUi]
  );
  const livePlatformsPillLabel = displayLivePlatformsForUi.length
    ? livePlatformLiveCount
      ? `${livePlatformLiveCount} Live`
      : `${displayLivePlatformsForUi.length} Ready`
    : "No Signals";

  useEffect(() => {
    const winner =
      displayScopedResolved?.totals && Object.keys(displayScopedResolved.totals).length
        ? "resolved"
        : displayScopedAggregate?.totals && Object.keys(displayScopedAggregate.totals).length
        ? "aggregate"
        : "none";
    publishSherlocTrace("client", "cards_winner", {
      winner,
      snapshotLocked: Boolean(activeRunSnapshotLock),
      snapshotLockedAt: activeRunSnapshotLock?.lockedAt || null,
      currentRunContext,
      aggregateRunContext,
      sourcesContext,
      resolvedContext,
      resolvedOrigin,
      totals:
        winner === "resolved"
          ? displayScopedResolved?.totals || null
          : displayScopedAggregate?.totals || null,
    });
  }, [
    activeRunSnapshotLock,
    aggregateRunContext,
    currentRunContext,
    displayScopedAggregate,
    displayScopedResolved,
    sourcesContext,
    resolvedContext,
    resolvedOrigin,
  ]);

  useEffect(() => {
    publishSherlocTrace("client", "status_panel_state", {
      currentRunContext,
      liveStatus,
      gaStatus,
      gaPill: gaConnectionPill,
    });
  }, [currentRunContext, gaConnectionPill, gaStatus, liveStatus]);

  useEffect(() => {
    publishSherlocTrace("client", "live_platforms_state", {
      currentRunContext,
      livePlatforms: livePlatformsForUi,
      displayedLivePlatforms: displayLivePlatformsForUi,
      liveByChannel: displayScopedSources?.live?.byChannel || {},
    });

    if (process.env.NODE_ENV !== "production") {
      console.debug("[SherlocReports][live-platform-status]", {
        surface: "client",
        selectedAdvertiser: {
          id: resolvedAdvertiserId || null,
          name: resolvedAdvertiser?.name || resolvedAdvertiser?.advertiser || "",
        },
        platforms: (displayLivePlatformsForUi || []).map((platform) => ({
          platform: platform.key,
          label: platform.label,
          approvedStatus: platform.approvalStatus || "",
          currentMetricEvidence: platform.deliveryEvidence?.metrics || {},
          finalDeliveryStatus: platform.status || "",
          reason: platform.statusReason || "",
        })),
      });
    }
  }, [
    currentRunContext,
    displayLivePlatformsForUi,
    displayScopedSources,
    livePlatformsForUi,
    resolvedAdvertiser,
    resolvedAdvertiserId,
  ]);

  const getLivePlatformRoute = useCallback((platform) => {
    return resolveLivePlatformAnalyticsRoute(platform?.key, platform?.label);
  }, []);

  const handleLivePlatformNavigate = useCallback(
    (platform) => {
      const route = getLivePlatformRoute(platform);
      if (!route) return;
      if (normalizeProviderSurfaceKey(platform?.key)) {
        const detail = document.getElementById(`provider-${normalizeProviderSurfaceKey(platform.key)}`);
        detail?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        detail?.focus();
        return;
      }
      navigate(route);
    },
    [getLivePlatformRoute, navigate]
  );

  const handleLivePlatformKeyDown = useCallback(
    (event, platform) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleLivePlatformNavigate(platform);
      }
    },
    [handleLivePlatformNavigate]
  );

  const selectedCampaignForUi = useMemo(() => {
    if (!selectedCampaignId || selectedCampaignId === "auto") return null;
    return metaCampaigns.find((c) => String(c.id) === String(selectedCampaignId)) || null;
  }, [metaCampaigns, selectedCampaignId]);

  const selectedCampaignDisplayName = useMemo(() => {
    return selectedCampaignForUi?.name || selectedCampaignNameForContext || "";
  }, [selectedCampaignForUi, selectedCampaignNameForContext]);

  const sherlocPageAwareness = useMemo(() => {
    const toplineMetrics = [
      resolvedAdvertiserId
        ? {
            id: "advertiser",
            label: "Advertiser",
            value: resolvedAdvertiser?.name || selectedAdvertiserName || String(resolvedAdvertiserId),
            meta: selectedCampaignDisplayName
              ? `Campaign: ${selectedCampaignDisplayName}`
              : "Current advertiser scope",
          }
        : null,
      {
        id: "timeframe",
        label: "Timeframe",
        value: defaultTimeframeLabel,
        meta: "Current Sherloc report window",
      },
      {
        id: "engine",
        label: "AI Engine",
        value: prettyAiProvider(selectedAiProvider),
        meta: getAiModelDisplay(aiMeta?.used || aiMeta?.requested || selectedAiProvider, selectedAiProvider) || "Current analysis engine",
      },
      livePlatformsForUi.length
        ? {
            id: "platforms",
            label: "Live Platforms",
            value: String(livePlatformsForUi.length),
            meta: livePlatformsForUi.map((item) => item.label).slice(0, 3).join(", "),
          }
        : null,
      aggregateMetrics?.spend > 0
        ? {
            id: "spend",
            label: "Spend",
            value: formatMoney(aggregateMetrics.spend),
            meta: "Selected report window",
          }
        : null,
      aggregateMetrics?.impressions > 0
        ? {
            id: "impressions",
            label: "Impressions",
            value: formatNumber(aggregateMetrics.impressions),
            meta: "Selected report window",
          }
        : null,
      aggregateMetrics?.ctr != null
        ? {
            id: "ctr",
            label: "CTR",
            value: formatPercent(aggregateMetrics.ctr),
            meta: "Resolved performance signal",
          }
        : null,
      resolvedAdvertiserVertical === "film" && aggregateMetrics?.ticketSales > 0
        ? {
            id: "ticket_sales",
            label: "Ticket Sales",
            value: formatMoney(aggregateMetrics.ticketSales),
            meta: "Cinema-linked signal",
          }
        : null,
    ].filter(Boolean).slice(0, 6);

    return {
      routeKey: "/sherloc-reports",
      pageTitle: "Sherloc Reports",
      pageType: "sherloc",
      toplineAuthority: "shared_fetch",
      advertiserId: resolvedAdvertiserId || null,
      advertiserName: resolvedAdvertiser?.name || selectedAdvertiserName || "",
      advertiserVertical: resolvedAdvertiserVertical,
      campaignId: selectedCampaignPublicCampaignId,
      campaignName: selectedCampaignDisplayName,
      timeframe: {
        label: defaultTimeframeLabel,
        start: defaultTimeframe?.date_start || null,
        end: defaultTimeframe?.date_end || null,
        datePreset: defaultTimeframe?.date_preset || null,
      },
      platformKey: "overall",
      platformLabel: "Sherloc",
      pageCapabilities: [
        "campaign analysis",
        "platform comparison",
        "AI summary",
        "creative insights",
      ],
      toplineMetrics,
      metadata: {
        aiProvider: selectedAiProvider,
        hasNarrative: shouldRenderNarrative,
        livePlatformCount: livePlatformsForUi.length,
      },
    };
  }, [
    aggregateMetrics?.ctr,
    aggregateMetrics?.impressions,
    aggregateMetrics?.spend,
    aggregateMetrics?.ticketSales,
    aiMeta?.requested,
    aiMeta?.used,
    defaultTimeframe,
    defaultTimeframeLabel,
    livePlatformsForUi,
    resolvedAdvertiser,
    resolvedAdvertiserVertical,
    resolvedAdvertiserId,
    selectedAdvertiserName,
    selectedAiProvider,
    selectedCampaignDisplayName,
    selectedCampaignPublicCampaignId,
    shouldRenderNarrative,
  ]);

  usePageAwareness(sherlocPageAwareness);

  const buildChannelViewerMetricRows = (channelKey, metrics) => {
    if (channelKey === "cinema") {
      return [
        {
          k: "Ticket Sales",
          v: metrics.ticketSales != null ? formatMoney(metrics.ticketSales) : "No Data",
          copper: true,
        },
        {
          k: "Booked Theaters",
          v:
            metrics.totalBookedTheaters != null
              ? formatNumber(metrics.totalBookedTheaters)
              : "No Data",
        },
        {
          k: "Circuits",
          v: metrics.totalCircuits != null ? formatNumber(metrics.totalCircuits) : "No Data",
        },
        {
          k: "Cities",
          v: metrics.totalCities != null ? formatNumber(metrics.totalCities) : "No Data",
        },
        {
          k: "States",
          v: metrics.totalStates != null ? formatNumber(metrics.totalStates) : "No Data",
        },
      ];
    }

    return buildChannelMetricRows(channelKey, metrics);
  };

  const hasReportMetricValue = (value) => {
    const text = String(value ?? "").trim();
    return Boolean(text) && !/^no data$/i.test(text);
  };

  const buildChannelViewerSummary = (channelLabel, rows) => {
    const primaryRows = rows.filter((row) => hasReportMetricValue(row.v)).slice(0, 3);
    if (!primaryRows.length) {
      return `${channelLabel} is active in the current report scope, but detailed metrics are not available for this channel yet.`;
    }

    const summaryMetrics = primaryRows.map((row) => `${row.k.toLowerCase()} ${row.v}`).join(", ");
    return `${channelLabel} detail is grounded in the same scoped performance data used by the channel card. Current ${summaryMetrics}.`;
  };

  const isStackAdaptInsightEligibleChannel = (channelKey, channelData = {}) => {
    const key = canonicalizeChannelKey(channelKey);
    const sourceText = [
      channelData?.provider,
      channelData?.data_source,
      channelData?.source,
      channelData?.sourceLabel,
      channelData?.source_label,
      channelData?.platform,
      channelData?.channel_family,
    ]
      .map((value) => String(value || "").toLowerCase())
      .join(" ");

    return (
      /stackadapt|programmatic/.test(sourceText) ||
      ["stackadapt", "programmatic"].includes(key)
    );
  };

  const buildInlineChannelReport = (channelKey, channelData) => {
    const channelLabel = CHANNEL_LABELS[channelKey] || channelKey.replace(/_/g, " ").toUpperCase();
    const metrics = computeChannelMetrics(channelKey, channelData || {});
    const rows = buildChannelViewerMetricRows(channelKey, metrics);
    const hasMetrics = rows.some((row) => hasReportMetricValue(row.v));
    const isLive =
      channelData?.data_source === "meta_live" ||
      channelData?.data_source === "google_analytics_live";
    const googleAnalyticsAudience =
      channelKey === "google_analytics"
      ? buildGoogleAnalyticsAudienceDashboard(channelData || {})
      : null;
    const stackAdaptInsightsEligible = isStackAdaptInsightEligibleChannel(channelKey, channelData || {});
    const focusedCampaignId = selectedLiveMetaCampaignId;
    const stackAdaptInsightKey = stackAdaptInsightsEligible
      ? buildStackAdaptInsightKey({
          advertiserId: resolvedAdvertiserId,
          channelKey,
          campaignId: focusedCampaignId,
          date_start: defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_start || null : null,
          date_end: defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_end || null : null,
          datePreset:
            defaultTimeframe?.mode === "preset" ? defaultTimeframe?.date_preset || "last_30d" : "",
        })
      : "";
    const summary =
      channelKey === "google_analytics"
        ? getGoogleAnalyticsDetailSummary(googleAnalyticsAudience)
        : buildChannelViewerSummary(channelLabel, rows);

    return {
      channelKey,
      channelLabel,
      icon: CHANNEL_ICONS[channelKey] || null,
      statusLabel: isLive ? "Live" : "Current Report Window",
      rows,
      hasMetrics,
      summary,
      googleAnalyticsAudience,
      googleAnalyticsDemographicsStatus: googleAnalyticsAudience?.demographicsStatus || null,
      stackAdaptInsightsEligible,
      stackAdaptInsightKey,
    };
  };

  const handleChannelCardClick = (channelKey) => {
    /*
     * Client-facing analytics is Cphere-native. When a platform has its own
     * governed Analytics page, that page is the destination — not the external
     * Looker/Data Studio surface this card used to embed. The decision is made
     * from platform identity through the canonical route resolver rather than
     * from whatever URL happens to sit on the advertiser record, so a stale or
     * missing dashboard link can no longer decide where a client lands.
     *
     * The embedded-report path is now the fall-through for platforms with no
     * native page yet, and nothing else. The opt-out that used to skip the
     * native route existed for ONE caller — the campaign-level action, which
     * used it to force a platform's embedded dashboard on a campaign-level
     * question — and left with it.
     */
    const internalRoute = resolveLivePlatformAnalyticsRoute(
      channelKey,
      CHANNEL_LABELS[channelKey]
    );
    if (internalRoute) {
      navigate(internalRoute);
      return;
    }

    const buildReportSurface = (adv) => {
      const channelLabel = CHANNEL_LABELS[channelKey] || channelKey;
      const unavailableMessage = `No ${channelLabel} report link is configured for this advertiser.`;

      if (!adv) {
        return {
          channelKey,
          channelLabel,
          embedUrl: "",
          openUrl: "",
          unavailableMessage: "Select an advertiser first to view this report.",
        };
      }

      if (channelKey === "google_analytics") {
        const { openUrl, embedUrl, embedMode } = normalizeSherlocReportSurfaceUrl(
          getGoogleAnalyticsAdvertiserUrl(adv) || ""
        );
        return {
          channelKey,
          channelLabel,
          embedUrl,
          embedMode,
          openUrl,
          unavailableMessage,
        };
      }

      const fieldKey = CHANNEL_FIELD_KEYS[channelKey];
      if (!fieldKey) {
        return {
          channelKey,
          channelLabel,
          embedUrl: "",
          openUrl: "",
          unavailableMessage,
        };
      }

      const { openUrl, embedUrl, embedMode } = normalizeSherlocReportSurfaceUrl(adv[fieldKey]);
      return {
        channelKey,
        channelLabel,
        embedUrl,
        embedMode,
        openUrl,
        unavailableMessage,
      };
    };

    const nextSurface = buildReportSurface(resolvedAdvertiser || null);
    const selectedEntry =
      channelKey === "google_analytics" && googleAnalyticsMeasurementChannel
        ? ["google_analytics", googleAnalyticsMeasurementChannel]
        : orderedChannelEntries.find(([key]) => key === channelKey);
    const inlineReport = buildInlineChannelReport(channelKey, selectedEntry?.[1] || {});

    if (nextSurface.embedUrl) {
      setReportViewerState({
        ...nextSurface,
        type: "embed",
        inlineReport,
      });
      setViewerStatus("loading");
      setViewerError("");
      return;
    }

    setReportViewerState({
      ...nextSurface,
      type: "channel",
      embedUrl: "",
      inlineReport,
      unavailableMessage: nextSurface.unavailableMessage,
    });
    setViewerStatus("fallback");
    setViewerError(nextSurface.unavailableMessage);
  };

  const handleChannelKeyDown = (e, channelKey) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleChannelCardClick(channelKey);
    }
  };

  const runErrorMessage = useMemo(
    () => getRunErrorMessage(contextScopedLastRun),
    [contextScopedLastRun]
  );
  const clientFacingRunErrorMessage = useMemo(
    () => getClientFacingRunErrorMessage(runErrorMessage),
    [runErrorMessage]
  );
  const hasResolvedToplineData = useMemo(
    () => hasSharedUsableMetrics(aggregateMetrics),
    [aggregateMetrics]
  );
  const lastRunNarrativeCompletion = useMemo(
    () => normalizeSherlocNarrativeCompletion({ run: contextScopedLastRun }),
    [contextScopedLastRun]
  );
  // Delivery availability and narrative completion are separate facts. When the run states
  // why the narrative is missing, say that instead of collapsing every cause into one line.
  const runIssueMessage = hasResolvedToplineData
    ? resolveSherlocNarrativeIssueMessage({
        completion: lastRunNarrativeCompletion,
        hasDelivery: true,
        runErrorMessage,
      })
    : clientFacingRunErrorMessage;
  // A run whose completion contract says the narrative completed never shows a failure
  // banner, even if a stale error_message survived on the row.
  const hasRunError =
    !!runErrorMessage && !isSherlocNarrativeCompleted(lastRunNarrativeCompletion);
  const hasBlockingRunError = hasRunError && !hasResolvedToplineData;
  const hasNonBlockingRunError = hasRunError && hasResolvedToplineData;
  const hasGroundedFallbackSuccess =
    !hasRunError &&
    Boolean(aiMeta?.fallbackUsed) &&
    hasRenderableSummary(aiSummary) &&
    Array.isArray(aiRecommendations) &&
    aiRecommendations.some((line) => String(line || "").trim());
  const errorMessageForDisplay =
    error && hasResolvedToplineData ? AI_NARRATIVE_FAILED_WITH_METRICS_MESSAGE : error;
  const shouldShowRunIssueMessage =
    (hasBlockingRunError || hasNonBlockingRunError) && !errorMessageForDisplay;
  const googleAnalyticsMeasurementRows = googleAnalyticsMeasurementChannel
    ? buildChannelMetricRows(
        "google_analytics",
        computeChannelMetrics("google_analytics", googleAnalyticsMeasurementChannel)
      )
    : [];
  const hasGoogleAnalyticsMeasurementRows = googleAnalyticsMeasurementRows.some((row) =>
    hasReportMetricValue(row.v)
  );
  const isGoogleAnalyticsMeasurementReady = isGoogleAnalyticsMeasurementReadyStatus(
    gaStatus,
    gaConnectionPill
  );
  const googleAnalyticsMeasurementPill =
    isGoogleAnalyticsMeasurementReady &&
    ["UNAVAILABLE", "SETUP", "AUTH"].includes(String(gaConnectionPill || "").toUpperCase())
      ? {
          label: "READY",
          className: "sherlocreports-source-pill sherlocreports-source-pill-ready",
        }
      : {
          label: gaConnectionPill,
          className: gaConnectionPillClass,
        };
  const shouldShowGoogleAnalyticsMeasurement = Boolean(
    googleAnalyticsMeasurementChannel || isGoogleAnalyticsMeasurementReady
  );
  const googleAnalyticsMeasurementEmptyMessage =
    googleAnalyticsMeasurementPill.label === "CHECKING" || gaStatus?.state === "loading"
      ? "GA4 is connected; site activity is still loading for this reporting window."
      : isGoogleAnalyticsMeasurementReady
      ? "Connection verified. Campaign-window measurement signals pending."
      : "Google Analytics measurement data is not available for this advertiser yet.";
  const orderedPerformanceCardEntries = useMemo(() => {
    const entriesByKey = new Map(orderedChannelEntries.filter(([key]) =>
      !normalizeProviderSurfaceKey(key) || isSherlocCampaignScopedPlatformKey(campaignPlatformScope, key)
    ));
    // Approved providers retain a scoped detail even when delivery has not arrived.
    for (const key of campaignPlatformScope?.keys || []) {
      const providerKey = normalizeProviderSurfaceKey(key);
      if (providerKey && !entriesByKey.has(providerKey)) entriesByKey.set(providerKey, {});
    }

    if (shouldShowGoogleAnalyticsMeasurement) {
      entriesByKey.set("google_analytics", googleAnalyticsMeasurementChannel || {});
    }

    const ordered = [];
    CHANNEL_ORDER.forEach((key) => {
      if (entriesByKey.has(key)) {
        ordered.push([key, entriesByKey.get(key)]);
        entriesByKey.delete(key);
      }
    });

    const remaining = Array.from(entriesByKey.entries()).sort((a, b) =>
      String(a[0]).localeCompare(String(b[0]))
    );

    return [...ordered, ...remaining];
  }, [
    googleAnalyticsMeasurementChannel,
    orderedChannelEntries,
    campaignPlatformScope,
    shouldShowGoogleAnalyticsMeasurement,
  ]);
  const shouldShowNarrativeSections = Boolean(
    contextScopedLastRun || hasCurrentContextRenderableNarrative || hasSuccessfulVisibleNarrative
  );
  const selectedAiPresentation = useMemo(
    () => getAiProviderPresentation(selectedAiProvider),
    [selectedAiProvider]
  );


  const reportViewerAnalyticsRoute = useMemo(() => {
    if (!reportViewerState) return "";
    return resolveLivePlatformAnalyticsRoute(
      reportViewerState.channelKey,
      reportViewerState.inlineReport?.channelKey,
      reportViewerState.channelLabel,
      reportViewerState.inlineReport?.channelLabel
    );
  }, [reportViewerState]);

  const handleCloseReportViewer = useCallback(() => {
    setReportViewerState(null);
    setViewerStatus("idle");
    setViewerError("");
  }, []);

  const handleOpenFullReport = useCallback(() => {
    if (!reportViewerAnalyticsRoute || !reportViewerState) return;

    const platformKey =
      reportViewerState.channelKey || reportViewerState.inlineReport?.channelKey || "";
    const platformLabel =
      reportViewerState.channelLabel || reportViewerState.inlineReport?.channelLabel || "";

    handleCloseReportViewer();
    navigate(reportViewerAnalyticsRoute, {
      state: {
        from: "/sherloc-reports",
        advertiserId: resolvedAdvertiserId || null,
        advertiserName: resolvedAdvertiser?.name || selectedAdvertiserName || "",
        campaignId: selectedCampaignPublicCampaignId,
        campaignName: selectedCampaignDisplayName,
        timeframe: {
          label: defaultTimeframeLabel,
          start: defaultTimeframe?.date_start || null,
          end: defaultTimeframe?.date_end || null,
          datePreset: defaultTimeframe?.date_preset || null,
        },
        platformKey,
        platformLabel,
      },
    });
  }, [
    defaultTimeframe,
    defaultTimeframeLabel,
    handleCloseReportViewer,
    navigate,
    reportViewerAnalyticsRoute,
    reportViewerState,
    resolvedAdvertiser,
    resolvedAdvertiserId,
    selectedAdvertiserName,
    selectedCampaignDisplayName,
    selectedCampaignPublicCampaignId,
  ]);

  const handleEmbeddedReportUnavailable = useCallback((message) => {
    const fallbackMessage = message || "Embedded dashboard could not be displayed here.";

    setReportViewerState((current) => {
      if (!current?.inlineReport) return current;

      const detailMessage = current.inlineReport.hasMetrics
        ? `${fallbackMessage} Showing current platform detail from Sherloc metrics instead.`
        : `${fallbackMessage} No inline platform metrics are available for this report window.`;

      return {
        ...current,
        type: "channel",
        embedUrl: "",
        unavailableMessage: detailMessage,
      };
    });

    setViewerStatus("fallback");
    setViewerError(fallbackMessage);
  }, []);

  useEffect(() => {
    if (!reportViewerState?.embedUrl || viewerStatus !== "loading") return undefined;

    const timeoutId = window.setTimeout(() => {
      handleEmbeddedReportUnavailable("Embedded dashboard did not finish loading in time.");
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [handleEmbeddedReportUnavailable, reportViewerState, viewerStatus]);

  useEffect(() => {
    if (!reportViewerState) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseReportViewer();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleCloseReportViewer, reportViewerState]);

  useEffect(() => {
    if (!reportViewerState) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [reportViewerState]);

  useEffect(() => {
    const inlineReport = reportViewerState?.inlineReport;
    const insightKey = inlineReport?.stackAdaptInsightKey || "";
    if (!reportViewerState || !inlineReport?.stackAdaptInsightsEligible || !insightKey) {
      return undefined;
    }

    let cancelled = false;
    const focusedCampaignId = selectedLiveMetaCampaignId;
    const date_start =
      defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_start || null : null;
    const date_end =
      defaultTimeframe?.mode === "flight" ? defaultTimeframe?.date_end || null : null;
    const datePreset =
      defaultTimeframe?.mode === "preset" ? defaultTimeframe?.date_preset || "last_30d" : "";

    setStackAdaptInsightState((current) => {
      const existing = current[insightKey];
      if (existing?.status === "ready" || existing?.status === "loading") return current;
      return {
        ...current,
        [insightKey]: {
          status: "loading",
          data: null,
          message: "Loading audience insights...",
        },
      };
    });

    readProviderForSelectedCampaign(() =>
      fetchStackAdaptInsightBreakdowns({
        advertiserId: resolvedAdvertiserId,
        channelKey: inlineReport.channelKey,
        campaignId: focusedCampaignId,
        date_start,
        date_end,
        datePreset,
      })
    ).then((data) => {
      if (cancelled) return;
      setStackAdaptInsightState((current) => ({
        ...current,
        [insightKey]: {
          status: data ? "ready" : "unavailable",
          data: data || null,
          message: toClientSafeAudienceInsightCopy(data?.reason),
        },
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [
    defaultTimeframe,
    fetchStackAdaptInsightBreakdowns,
    readProviderForSelectedCampaign,
    reportViewerState,
    resolvedAdvertiserId,
    selectedCampaignId,
    selectedLiveMetaCampaignId,
  ]);

  useEffect(() => {
    handleCloseReportViewer();
  }, [handleCloseReportViewer, resolvedAdvertiserId]);

  const handleDashboardBack = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      navigate("/dashboard");
    },
    [navigate]
  );

  const handlePreviousPageBack = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const hasUsableHistory =
        typeof window !== "undefined" &&
        window.history.length > 1 &&
        location.key &&
        location.key !== "default";

      if (hasUsableHistory) {
        navigate(-1);
        return;
      }

      navigate("/dashboard");
    },
    [location.key, navigate]
  );

  const triggerSherlocReportsFloatingControl = useCallback((selector) => {
    if (typeof document === "undefined") return;
    const target = document.querySelector(selector);
    if (target && typeof target.click === "function") {
      target.click();
    }
  }, []);

  const handleAskDockClick = useCallback(() => {
    triggerSherlocReportsFloatingControl(SHERLOCREPORTS_ASK_SELECTOR);
  }, [triggerSherlocReportsFloatingControl]);

  const handleEdenDockClick = useCallback(() => {
    triggerSherlocReportsFloatingControl(SHERLOCREPORTS_EDEN_SELECTOR);
  }, [triggerSherlocReportsFloatingControl]);

  /*
   * The campaign-level action's destination is the campaign-level page.
   *
   * Platform Analytics reports the campaign ACROSS platforms, which is what a
   * campaign-level question asks. It is reached by ordinary in-app navigation
   * — no modal, no embedded surface, no platform chosen for it — carrying the
   * campaign Campaign Analysis is actually reporting on so the destination
   * opens on the same campaign rather than the advertiser's first one. The
   * governed advertiser needs no carrying: both surfaces read it from the one
   * governed advertiser catalogue.
   *
   * The window travels with the campaign rather than beside it: Platform
   * Analytics defaults to Campaign Flight and resolves that flight from the
   * campaign it is scoped to, so the scoped page lands on the same approved
   * flight this page reports, resolved by the destination's own contract.
   */
  const platformAnalyticsHandoffCampaign = useMemo(
    () =>
      resolvePlatformAnalyticsHandoffCampaign({
        selectedCampaign: selectedCampaignForUi,
        autoDetectedCampaign: autoDetectedMetaCampaign,
        fallbackCampaignName: selectedCampaignDisplayName,
      }),
    [autoDetectedMetaCampaign, selectedCampaignDisplayName, selectedCampaignForUi]
  );

  const platformAnalyticsPath = useMemo(
    () =>
      buildPlatformAnalyticsScopePath(
        PLATFORM_ANALYTICS_ROUTE,
        platformAnalyticsHandoffCampaign
      ),
    [platformAnalyticsHandoffCampaign]
  );

  const handleViewPlatformAnalytics = useCallback(() => {
    navigate(platformAnalyticsPath);
  }, [navigate, platformAnalyticsPath]);


  const buildStackAdaptInsightDashboard = (state) => {
    const payload = state?.data || null;
    const chartSpecs = Array.isArray(payload?.chartSpecs)
      ? payload.chartSpecs
      : Object.values(payload?.breakdowns || {})
          .map((entry) => entry?.chartSpec)
          .filter(Boolean);

    return {
      status: state?.status || "idle",
      message:
        toClientSafeAudienceInsightCopy(state?.message || payload?.reason),
      chartSpecs: chartSpecs.filter(
        (spec) => spec?.available && Array.isArray(spec.rows) && spec.rows.length
      ),
      hasAnyBreakdown: chartSpecs.some(
        (spec) => spec?.available && Array.isArray(spec.rows) && spec.rows.length
      ),
    };
  };

  const renderStackAdaptInsightCard = (spec) => {
    const rows = (Array.isArray(spec?.rows) ? spec.rows : [])
      .filter((row) => toNumSafe(row?.spend) > 0 || toNumSafe(row?.impressions) > 0)
      .slice(0, 8);
    if (!rows.length) return null;
    const maxValue = Math.max(...rows.map((row) => Math.max(toNumSafe(row.spend), toNumSafe(row.impressions))), 0);
    if (maxValue <= 0) return null;

    return (
      <div className="sherlocreports-ga-chart-card sherlocreports-stackadapt-insight-card" key={spec.key}>
        <h4>{toClientSafeAudienceInsightCopy(spec.title || STACKADAPT_INSIGHT_FALLBACK_TITLES[spec.key] || "Audience Insight")}</h4>
        <div className="sherlocreports-ga-bar-list">
          {rows.map((row, index) => {
            const barValue = Math.max(toNumSafe(row.spend), toNumSafe(row.impressions));
            return (
              <div key={`${spec.key}-${row.key || row.label}`} className="sherlocreports-stackadapt-insight-row">
                <div className="sherlocreports-stackadapt-insight-main">
                  <span>{row.label}</span>
                  <strong>{formatMoney(row.spend || row.cost || 0)}</strong>
                </div>
                <div className="sherlocreports-ga-bar-track">
                  <div
                    className="sherlocreports-ga-bar-fill"
                    style={{
                      width: `${Math.max(4, (barValue / maxValue) * 100)}%`,
                      backgroundColor: GA_DIMENSION_COLORS[index % GA_DIMENSION_COLORS.length],
                    }}
                  />
                </div>
                <div className="sherlocreports-stackadapt-insight-metrics">
                  <span>{formatNumber(row.impressions)} impressions</span>
                  <span>{formatNumber(row.clicks)} clicks</span>
                  <span>{row.ctr != null ? formatPercent(row.ctr) : "CTR —"}</span>
                  <span>{row.cpc != null ? `${formatMoney(row.cpc)} CPC` : "CPC —"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStackAdaptInsightsDashboard = (state) => {
    const dashboard = buildStackAdaptInsightDashboard(state);
    if (!dashboard || dashboard.status === "idle") return null;

    return (
      <div className="sherlocreports-ga-detail-dashboard sherlocreports-stackadapt-detail-dashboard">
        <section className="sherlocreports-ga-dashboard-section">
          <h3>Audience Insights</h3>
          {!dashboard.hasAnyBreakdown ? (
            <p className="sherlocreports-ga-demographic-helper">
              {dashboard.status === "loading"
                ? "Loading audience insight rows..."
                : dashboard.message}
            </p>
          ) : (
            <div className="sherlocreports-ga-chart-grid">
              {dashboard.chartSpecs.map((spec) => renderStackAdaptInsightCard(spec))}
            </div>
          )}
        </section>
      </div>
    );
  };

  const renderChannelDetailPanel = ({
    inlineReport,
    showClose = false,
    compact = false,
    showFallbackNotice = false,
  } = {}) => {
    if (!inlineReport) return null;

    return (
      <div
        className={`sherlocreports-channel-viewer-report ${
          compact ? "sherlocreports-channel-viewer-report-compact" : ""
        }`}
      >
        <div className="sherlocreports-channel-viewer-header">
          <div className="sherlocreports-card-header-row">
            {inlineReport.icon ? (
              <SherlocHeroFrame
                src={inlineReport.icon}
                alt={`${inlineReport.channelLabel} logo`}
                outerClassName="sherlocreports-card-icon-wrapper sherlocreports-report-hero-frame"
                mediaClassName="sherlocreports-hero-frame-media-cover"
                imageClassName="sherlocreports-card-icon sherlocreports-hero-frame-image-cover"
              />
            ) : null}

            <div className="sherlocreports-channel-card-titlecol">
              <span className="sherlocreports-section-chip sherlocreports-section-chip-ice">
                {compact ? "Platform Context" : "Platform Detail"}
              </span>
              <h2 className="sherlocreports-channel-viewer-title">
                {inlineReport.channelLabel}
              </h2>
              <span className="sherlocreports-source-pill sherlocreports-source-pill-ready">
                {inlineReport.statusLabel}
              </span>
            </div>
          </div>

          {showClose ? (
            <button
              type="button"
              className="sherlocreports-report-viewer-close"
              onClick={handleCloseReportViewer}
              aria-label="Close report viewer"
            >
              Close
            </button>
          ) : null}
        </div>

        {showFallbackNotice ? (
          <div className="sherlocreports-channel-viewer-fallback-notice">
            <div className="sherlocreports-report-viewer-status-title">
              Embedded Dashboard Unavailable
            </div>
            <div className="sherlocreports-report-viewer-status-copy">
              {reportViewerState?.unavailableMessage || viewerError}
            </div>
          </div>
        ) : null}

        <div className="sherlocreports-channel-viewer-summary">{inlineReport.summary}</div>

        {inlineReport.hasMetrics ? (
          <div className="sherlocreports-channel-viewer-metric-grid">
            {inlineReport.rows.map((row, idx) => (
              <div
                key={`${inlineReport.channelKey}-viewer-row-${idx}`}
                className="sherlocreports-channel-viewer-metric-card"
              >
                <span className="sherlocreports-metric-k">{row.k}</span>
                <span
                  className={
                    row.copper
                      ? "sherlocreports-metric-v sherlocreports-metric-v-copper"
                      : "sherlocreports-metric-v"
                  }
                >
                  {row.v}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="sherlocreports-report-viewer-error sherlocreports-report-viewer-error-inline">
            <div className="sherlocreports-report-viewer-status-title">Detail Unavailable</div>
            <div className="sherlocreports-report-viewer-status-copy">
              {reportViewerState?.unavailableMessage ||
                `No detailed ${inlineReport.channelLabel} metrics are available for this report window yet.`}
            </div>
          </div>
        )}

        {/* GA4 KPIs above -> Traffic Over Time -> Audience Breakdown below. */}
        {inlineReport.channelKey === "google_analytics" ? (
          <GaTrafficOverTimeChart
            series={gaDailyState.scopeKey === gaDailyScopeKey ? gaDailyState.series : null}
            metricKey={gaDailyMetric}
            onMetricChange={setGaDailyMetric}
            loading={gaDailyState.status === "loading"}
            error={gaDailyState.status === "error" ? gaDailyState.error : ""}
          />
        ) : null}

        {inlineReport.channelKey === "google_analytics"
          ? renderGoogleAnalyticsAudienceDashboard(inlineReport.googleAnalyticsAudience)
          : null}

        {inlineReport.stackAdaptInsightKey
          ? renderStackAdaptInsightsDashboard(stackAdaptInsightState[inlineReport.stackAdaptInsightKey])
          : null}
      </div>
    );
  };

  return (
    <div className="sherlocreports-page-container sherloc-reports-page analytics-dashboard">
      <div className="sherlocreports-page-content">
        <div className="sherlocreports-mobile-nav">
          <button
            type="button"
            className="sherlocreports-mobile-back-button"
            onClick={handlePreviousPageBack}
            aria-label="Go to previous page"
          />
        </div>

        <div
          className="sherlocreports-mobile-assistant-dock"
          data-sherlocreports-mobile-assistant-dock="true"
          role="group"
          aria-label="Sherloc Reports assistant controls"
        >
          <button
            type="button"
            className="sherlocreports-mobile-ask-button"
            onClick={handleAskDockClick}
            aria-label="Ask Eden"
          >
            Ask
          </button>

          <button
            type="button"
            className="sherlocreports-mobile-eden-button"
            onClick={handleEdenDockClick}
            aria-label="Open Eden assistant"
          >
            <span className="sherlocreports-mobile-eden-label">Eden</span>
          </button>
        </div>

        <button
          type="button"
          className="sherlocreports-poweredby-logo-button"
          onClick={handleDashboardBack}
          aria-label="Back to dashboard"
        >
          <PoweredByLogo
            alt="Powered by Logo"
            className="sherlocreports-poweredby-logo poweredby-logo"
            draggable={false}
          />
        </button>

        <div className="cphere-governed-hero-selector-stack">
          <div className="sherlocreports-hero-container">
            <div className="sherlocreports-hero-pill">
              <span className="sherlocreports-hero-pill-label cphere-hero-pill-wordmark">Campaign</span>
              <span className="sherlocreports-hero-pill-tag cphere-hero-pill-tag">Analysis</span>
            </div>
            <div className="sherlocreports-hero-shimmer-bar" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={governedAdvertiserName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={governedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <div className="sherlocreports-controls-panel">
          <div className="sherlocreports-controls-grid">
            <div className="sherlocreports-control-block">
              <div className="sherlocreports-control-title-row">
                <div className="sherlocreports-control-title">Campaign</div>
                <div className="sherlocreports-control-pill sherlocreports-control-pill-iris">
                  {campaignsLoading ? "Loading…" : activeCampaignLabel}
                </div>
              </div>

              <div className="sherlocreports-control-sub">
                Timeframe: <strong>{defaultTimeframeLabel}</strong>
              </div>

              <div className="sherlocreports-campaign-row">
                <div className="sherlocreports-select-shell sherlocreports-select-shell-wide">
                  <select
                    className="sherlocreports-advertiser-select"
                    value={selectedCampaignId}
                    onChange={handleCampaignChange}
                    disabled={campaignsLoading || !resolvedAdvertiserId}
                    title={
                      !resolvedAdvertiserId
                        ? "Select an advertiser first"
                        : "Choose a campaign context"
                    }
                  >
                    {renderAutoCampaignOption ? (
                      <option value="auto" aria-label={autoCampaignOptionAriaLabel}>
                        {autoCampaignOptionLabel}
                      </option>
                    ) : null}
                    {campaignSelectorRows.map((row) => (
                      <option key={row.identity} value={row.value}>
                        {row.label}
                        {/active/i.test(row.status) ? " (active)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  className="sherlocreports-mini-button"
                  onClick={() => {
                    liveStatusRequestSeqRef.current += 1;
                    metaInsightsCacheRef.current = new Map();
                    metaInsightsInFlightRef.current = new Map();
                    gaSummaryCacheRef.current = new Map();
                    gaSummaryInFlightRef.current = new Map();
                    stackAdaptLiveCacheRef.current = new Map();
                    stackAdaptLiveInFlightRef.current = new Map();
                    refreshMetaCampaigns({
                      advertiserId: resolvedAdvertiserId,
                      force: true,
                    });
                    refreshLatestAggregateSnapshotRef.current?.(
                      resolvedAdvertiserId,
                      { traceSource: "manual_refresh" }
                    );
                    setStatusRefreshTick((tick) => tick + 1);
                  }}
                  disabled={campaignsLoading || !resolvedAdvertiserId}
                  title={
                    !resolvedAdvertiserId
                      ? "Select an advertiser first"
                      : "Refresh Meta campaigns"
                  }
                  type="button"
                >
                  Refresh
                </button>
              </div>

              {campaignsError && (
                <div className="sherlocreports-inline-error">{campaignsError}</div>
              )}

              {resolvedAdvertiserId ? (
                <div className="sherlocreports-live-status-stack">
                  <div className="sherlocreports-status-hero-block sherlocreports-ga4-status-block">
                    <div className="sherlocreports-status-hero-header sherlocreports-ga4-status-header">
                      <SherlocHeroFrame
                        src={googleAnalyticsImage}
                        alt="Google Analytics"
                        title="Google Analytics"
                        outerClassName="sherlocreports-status-hero-frame"
                        mediaClassName="sherlocreports-hero-frame-media-cover"
                        imageClassName="sherlocreports-status-hero-logo sherlocreports-ga4-status-logo sherlocreports-hero-frame-image-cover"
                      />
                      <span className="sherlocreports-status-hero-title sherlocreports-ga4-status-title">
                        GA4 Live Status Check
                      </span>
                      <span
                        className={`${gaConnectionPillClass} sherlocreports-status-hero-pill`}
                      >
                        {gaConnectionPill}
                      </span>
                    </div>
                    {gaStatus?.detail ? (
                      <div className="sherlocreports-status-hero-subcopy">
                        {gaStatus.detail}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="sherlocreports-control-block">
              <div className="sherlocreports-control-title-row">
                <div className="sherlocreports-control-title">AI Engine</div>
                <div className="sherlocreports-control-pill sherlocreports-control-pill-copper">
                  <AiProviderInline
                    provider={selectedAiPresentation.normalized || selectedAiProvider}
                  />
                </div>
              </div>

              <div className="sherlocreports-control-sub">
                Choose which model powers Sherloc for this run.
              </div>

              <div className="sherlocreports-select-shell">
                <select
                  className="sherlocreports-advertiser-select sherlocreports-advertiser-select-engine"
                  value={selectedAiProvider}
                  onChange={handleAiProviderChange}
                >
                  <option value="auto">Auto</option>
                  <option value="chatgpt">Sherloc</option>
                  <option value="eden">Eden</option>
                </select>
              </div>

              <div className="sherlocreports-cta-stack sherlocreports-cta-stack-top">
                <button
                  className="sherlocreports-run-button"
                  onClick={runSherloc}
                  disabled={running || !resolvedAdvertiserId}
                  type="button"
                >
                  {/* The button face (FuturisticArmor) cannot set digits, so
                      the live stage + cycle counter render in the operator
                      card below in a numeric-capable face. */}
                  {running ? "Running Sherloc AI — reviewing for accuracy…" : "Run Analysis"}
                </button>

                <button
                  className="sherlocreports-run-button sherlocreports-run-button-secondary"
                  onClick={handleViewPlatformAnalytics}
                  disabled={!resolvedAdvertiserId}
                  type="button"
                  aria-label="View Platform Analytics"
                  data-platform-analytics-path={platformAnalyticsPath}
                >
                  View Platform Analytics
                </button>
              </div>

              {isRunAnalysisOperationActive(runAnalysisOperation) ? (
                <div
                  className="sherlocreports-run-operation-card"
                  data-run-operation-state="active"
                >
                  <div className="sherlocreports-run-operation-row">
                    <span className="sherlocreports-run-operation-stage">
                      {getRunAnalysisStageLabel(runAnalysisOperation)}
                    </span>
                    {getRunAnalysisCycleLabel(runAnalysisOperation) ? (
                      <span className="sherlocreports-run-operation-cycle">
                        {getRunAnalysisCycleLabel(runAnalysisOperation)}
                      </span>
                    ) : null}
                  </div>
                  <div className="sherlocreports-run-operation-row sherlocreports-run-operation-meta">
                    <span>
                      Ref {getRunAnalysisSupportReference(runAnalysisOperation)}
                    </span>
                    <span>
                      Elapsed{" "}
                      {formatRunAnalysisElapsed(
                        getRunAnalysisElapsedMs(runAnalysisOperation)
                      )}
                    </span>
                    {formatRunAnalysisHeartbeat(runAnalysisOperation) ? (
                      <span>{formatRunAnalysisHeartbeat(runAnalysisOperation)}</span>
                    ) : null}
                  </div>
                  <div className="sherlocreports-run-operation-continuity">
                    {SHERLOC_RUN_ANALYSIS_CONTINUITY_COPY}
                  </div>
                </div>
              ) : null}

              {!running &&
              isRunAnalysisOperationTerminal(runAnalysisOperation) &&
              runAnalysisOperation?.status !==
                SHERLOC_RUN_ANALYSIS_STATUSES.COMPLETED ? (
                <div
                  className="sherlocreports-run-operation-card sherlocreports-run-operation-card-failed"
                  data-run-operation-state="failed"
                  data-run-operation-disposition={
                    resolveRunAnalysisDisposition(runAnalysisOperation) || ""
                  }
                >
                  <div className="sherlocreports-run-operation-row">
                    <span className="sherlocreports-run-operation-stage">
                      {getRunAnalysisStageLabel(runAnalysisOperation)}
                    </span>
                  </div>
                  <div className="sherlocreports-run-operation-detail">
                    {describeRunAnalysisTerminalFailure(runAnalysisOperation)}
                  </div>
                  <div className="sherlocreports-run-operation-row sherlocreports-run-operation-meta">
                    <span>
                      Support reference{" "}
                      {getRunAnalysisSupportReference(runAnalysisOperation)}
                    </span>
                  </div>
                  {isRunAnalysisRetryAvailable(runAnalysisOperation) ? (
                    <button
                      className="sherlocreports-run-button sherlocreports-run-operation-retry"
                      onClick={retryRunAnalysis}
                      type="button"
                    >
                      Safe Retry
                    </button>
                  ) : null}
                </div>
              ) : null}

            </div>

            <div className="sherlocreports-control-block sherlocreports-control-block-full">
              <div className="sherlocreports-control-title-row">
                <div className="sherlocreports-control-title">Live Platforms</div>
                <div className="sherlocreports-control-pill sherlocreports-control-pill-ice">
                  {livePlatformsPillLabel}
                </div>
              </div>

              <div className="sherlocreports-control-sub">
                Platforms approved for this report context, elevated to LIVE when current data, campaign selection, and analysis inputs confirm activity.
              </div>

              {displayLivePlatformsForUi.length ? (
                <div className="sherlocreports-live-platforms-grid">
                  {displayLivePlatformsForUi.map((platform) => {
                    const route = getLivePlatformRoute(platform);
                    const isClickable = Boolean(route);

                    return (
                      <div
                        key={platform.key}
                        className={`sherlocreports-live-platform-chip ${
                          isClickable ? "sherlocreports-live-platform-chip-clickable" : ""
                        }`}
                        onClick={
                          isClickable ? () => handleLivePlatformNavigate(platform) : undefined
                        }
                        onKeyDown={
                          isClickable
                            ? (event) => handleLivePlatformKeyDown(event, platform)
                            : undefined
                        }
                        role={isClickable ? "button" : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        aria-label={
                          isClickable ? `Open ${platform.label} analytics` : undefined
                        }
                      >
                        {platform.icon ? (
                          <SherlocHeroFrame
                            src={platform.icon}
                            alt={platform.label}
                            title={platform.label}
                            outerClassName="sherlocreports-live-platform-hero-frame"
                            mediaClassName="sherlocreports-hero-frame-media-cover"
                            imageClassName="sherlocreports-live-platform-logo sherlocreports-hero-frame-image-cover"
                          />
                        ) : (
                          <span className="sherlocreports-live-platform-hero-frame" aria-hidden="true">
                            <span className="sherlocreports-hero-frame-media sherlocreports-hero-frame-media-cover">
                              <span className="sherlocreports-live-platform-fallback">
                                {platform.label.slice(0, 2).toUpperCase()}
                              </span>
                            </span>
                          </span>
                        )}
                        <div className="sherlocreports-live-platform-text">
                          <div className="sherlocreports-live-platform-title-row">
                            <span className="sherlocreports-live-platform-name">
                              {platform.label}
                            </span>
                            <span className="sherlocreports-live-platform-status">
                              {platform.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="sherlocreports-live-platform-empty">
                  No approved media-plan platforms are available for this report context yet.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sherlocreports-form-container">
          {errorMessageForDisplay && (
            <div className="sherlocreports-inline-error sherlocreports-inline-error-center">
              {errorMessageForDisplay}
            </div>
          )}

          {shouldShowRunIssueMessage && (
            <div
              className="sherlocreports-inline-error sherlocreports-inline-error-center"
              style={{ marginTop: 10 }}
            >
              {runIssueMessage}
            </div>
          )}

          {hasGroundedFallbackSuccess && (
            <div
              className="sherlocreports-inline-status sherlocreports-inline-error-center"
              style={{ marginTop: 10 }}
            >
              Sherloc generated a grounded fallback analysis from current campaign delivery data.
            </div>
          )}

          {aggregateKpiWarning && (
            <div
              className="sherlocreports-inline-status sherlocreports-inline-error-center"
              style={{ marginTop: 10 }}
            >
              {aggregateKpiWarning}
            </div>
          )}

          {aggregateMetrics && (
            <div
              className="sherlocreports-kpis sherlocreports-kpis-top"
              style={{ marginTop: 10, marginBottom: 10 }}
            >
              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">Total Spend</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {formatOptionalPreciseMoney(aggregateMetrics.spend)}
                </div>
              </div>

              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">Impressions</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {formatOptionalNumber(aggregateMetrics.impressions)}
                </div>
              </div>

              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">Clicks</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {formatOptionalNumber(aggregateMetrics.clicks)}
                </div>
              </div>

              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">CTR</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {aggregateMetrics.ctr != null ? formatPercent(aggregateMetrics.ctr) : "No Data"}
                </div>
              </div>

              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">CPC</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {aggregateMetrics.cpc != null ? formatMoney(aggregateMetrics.cpc) : "No Data"}
                </div>
              </div>

              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">Engagements</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {formatOptionalNumber(aggregateMetrics.engagements)}
                </div>
              </div>

              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">Engagement Rate</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {engagementPair?.rate != null
                    ? formatPercent(engagementPair.rate)
                    : aggregateMetrics.er != null
                    ? formatPercent(aggregateMetrics.er)
                    : "No Data"}
                </div>
              </div>

              {/*
                One video SLOT, resolved by the governed basis.

                Where a platform carries a qualified signal the strip states
                the campaign total and the native rates that produced it —
                CTV's completion rate on CTV's starts, YouTube's view rate on
                YouTube's impressions, each labelled with the platform that
                measured it and never averaged together. Where no platform has
                a qualified ruling the generic governed pair stays, because
                dropping it would claim a Meta or TikTok flight delivered no
                video at all.
              */}
              {videoKpiFields.has("qualifiedVideoViews") && qualifiedVideoTiles ? (
                <>
                  <div className="sherlocreports-kpi-card">
                    <h2 className="sherlocreports-kpi-label">
                      {qualifiedVideoTiles.count.label}
                    </h2>
                    <div
                      className="sherlocreports-kpi-input sherlocreports-kpi-input-center"
                      title={qualifiedVideoTiles.count.tooltip}
                    >
                      {qualifiedVideoTiles.count.value}
                    </div>
                    <div className="sherlocreports-kpi-detail">
                      {qualifiedVideoTiles.count.detail}
                    </div>
                  </div>

                  {/* The shared field decision, not a local truthiness test:
                      the tile is deliberately built even when unavailable, so
                      only `videoKpiFields` can say whether this slot exists. */}
                  {videoKpiFields.has("costPerQualifiedVideoView") ? (
                    <div className="sherlocreports-kpi-card">
                      <h2 className="sherlocreports-kpi-label">
                        {costPerQualifiedVideoViewTile.label}
                      </h2>
                      <div
                        className="sherlocreports-kpi-input sherlocreports-kpi-input-center"
                        title={costPerQualifiedVideoViewTile.tooltip}
                      >
                        {costPerQualifiedVideoViewTile.value}
                      </div>
                      <div className="sherlocreports-kpi-detail">
                        {costPerQualifiedVideoViewTile.detail}
                      </div>
                    </div>
                  ) : null}

                  {qualifiedVideoTiles.rates.map((rate) => (
                    <div className="sherlocreports-kpi-card" key={rate.platformKey}>
                      <h2 className="sherlocreports-kpi-label">{rate.label}</h2>
                      <div
                        className="sherlocreports-kpi-input sherlocreports-kpi-input-center"
                        title={rate.title}
                      >
                        {rate.value}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {videoKpiFields.has("videoViews") ? (
                    <div className="sherlocreports-kpi-card">
                      <h2 className="sherlocreports-kpi-label">Video Views</h2>
                      <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                        {formatOptionalNumber(aggregateMetrics.videoViews)}
                      </div>
                    </div>
                  ) : null}

                  {videoKpiFields.has("videoViewRate") ? (
                    <div className="sherlocreports-kpi-card">
                      <h2 className="sherlocreports-kpi-label">Video View Rate</h2>
                      <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                        {aggregateMetrics.vvr != null
                          ? formatPercent(aggregateMetrics.vvr)
                          : "No Data"}
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              {/*
                The completion RATE, from the governed pair that also carries
                its count. Rendered whenever that pair exists — its absence is
                a real "not measured here", not a zero.

                The count card that used to sit beside it has left this strip,
                as it has left Campaign Insights and Optimus. Where a qualified
                total renders above, the count is already inside it: CTV's
                completed views ARE the larger share of that total, so the card
                restated a component of its own neighbour while the cost of
                that video went unstated. CPQV holds the slot now.

                The rate still answers to the count — same `videoCompletionPair`
                object, so the population behind the percentage is the one that
                produced it, and the two can never be scoped apart again. That
                co-scoping is the whole point of reading one pair rather than
                two fields, and removing the count card does not weaken it.
              */}
              {videoCompletionPair ? (
                <div className="sherlocreports-kpi-card">
                  <h2 className="sherlocreports-kpi-label">Video Completion Rate</h2>
                  <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                    {videoCompletionPair.rate != null
                      ? formatPercent(videoCompletionPair.rate)
                      : "No Data"}
                  </div>
                </div>
              ) : null}

              <div className="sherlocreports-kpi-card">
                {/* The outcome carries the name it EARNED. The shared resolver
                    reads the campaign's verified tracker verdict and fails
                    closed to "Conversions", so this reads Ad-Attributed Site
                    Visits only where the trackers prove it. */}
                <h2 className="sherlocreports-kpi-label">{outcomeConversionsLabel}</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {formatOptionalNumber(aggregateMetrics.conversions)}
                </div>
              </div>

              <div className="sherlocreports-kpi-card">
                <h2 className="sherlocreports-kpi-label">Conversion Value</h2>
                <div className="sherlocreports-kpi-input sherlocreports-kpi-input-center">
                  {/* $0.00 is a measured result — "no revenue was recorded".
                      An advertiser with no per-outcome value configured has
                      measured nothing, and must not be shown a zero that reads
                      as one. */}
                  {hasConfiguredConversionValue
                    ? formatMoney(aggregateMetrics.conversionValue)
                    : "Not Configured"}
                </div>
              </div>
            </div>
          )}

          {shouldShowNarrativeSections && (
            <>
              {!analystPanel?.analysisV2 && (
                <>
                  <div className="sherlocreports-notes-container">
                <div className="sherlocreports-section-header-row">
                  <label className="sherlocreports-section-label">Campaign Summary</label>
                  <div className="sherlocreports-section-chip sherlocreports-section-chip-copper">
                    Synapse
                  </div>
                </div>

                <div className="sherlocreports-section-box sherlocreports-section-box-glass">
                  <div className="sherlocreports-summary-toprow">
                    <SherlocHeroFrame
                      src={aiOptimizationsImage}
                      alt="Sherloc Cross Platform Snapshot"
                      outerClassName="sherlocreports-card-icon-wrapper"
                      mediaClassName="sherlocreports-hero-frame-media-cover"
                      imageClassName="sherlocreports-card-icon sherlocreports-hero-frame-image-cover"
                    />

                    <button
                      className="eden-voice-summary-button"
                      onClick={playVoiceSummary}
                      disabled={voiceLoading || !resolvedAdvertiserId}
                      title="Play Audio Summary"
                      type="button"
                    >
                      <img
                        className="eden-voice-summary-icon"
                        src={playVoiceSummaryImage}
                        alt="Audio Summary"
                      />
                      <span className="eden-voice-summary-text">
                        {voiceLoading ? "Playing Audio…" : "Play Summary"}
                      </span>
                    </button>
                  </div>

                  {voiceError && <div className="eden-voice-summary-error">{voiceError}</div>}

                  <SherlocRichOutputWithHeroes
                    text={
                      shouldRenderAlignedNarrative
                        ? normalizeSherlocTextForRender(
                            buildCanonicalCampaignSummaryForRender({
                              summary: aiSummary,
                              metrics: aggregateMetrics,
                              byChannel: platformAnalysisByChannelForNarrative,
                              timeframeLabel: defaultTimeframeLabel,
                            })
                          )
                        : hasRunError
                        ? normalizeSherlocTextForRender(
                            `OUTPUT:\n- ${runIssueMessage}\n- Live channel KPIs may still appear below when platform delivery is available.\n- Narrative generation can be retried after the reporting window has more persisted snapshots.`
                          )
                        : shouldShowNarrativeAlignmentFailure
                        ? normalizeSherlocTextForRender(
                            `OUTPUT:\n- ${narrativeAlignmentFailureMessage}\n- Spend to Date and KPI cards are still using the latest resolved reporting truth.\n- Run Analysis can be retried to request a fresh Campaign Summary.`
                          )
                        : ""
                    }
                    emptyMessage={
                      hasRunError
                        ? runIssueMessage
                        : shouldShowNarrativeAlignmentFailure
                        ? narrativeAlignmentFailureMessage
                        : isNarrativeOutOfSyncWithKpis
                        ? "Refreshing summary with the latest KPI context."
                        : shouldRenderAlignedNarrative &&
                          aiRecommendations.length > 0 &&
                          !hasRenderableSummary(aiSummary)
                        ? "Campaign Summary was not returned for this run. Platform Analysis remains available."
                        : // A genuine empty state: nothing is saved for this campaign and
                          // nothing came back from a run. It says what to do about it,
                          // because reaching here now means no analysis has ever been
                          // generated — not that this browser forgot one.
                          "No analysis yet. Run Analysis generates the first report for this campaign."
                    }
                    showChannelHeroes={true}
                    forceFirstLineAsH1={true}
                    suppressOverallHero={true}
                  />
                </div>
                  </div>

                  <div className="sherlocreports-strategy-container">
                <div className="sherlocreports-section-header-row">
                  <label className="sherlocreports-section-label">Platform Analysis</label>
                  <div className="sherlocreports-section-chip sherlocreports-section-chip-green">
                    Insights
                  </div>
                </div>

                <div className="sherlocreports-section-box sherlocreports-section-box-glass">
                  <SherlocRichOutputWithHeroes
                    text={
                      shouldRenderAlignedNarrative
                        ? normalizeSherlocTextForRender(
                            renderedPlatformAnalysisRecommendations.join("\n")
                          )
                        : hasRunError
                        ? normalizeSherlocTextForRender(
                            `CROSS PLATFORM ANALYSIS:\n- ${runIssueMessage}\n- No platform narrative was generated for this run.\n- Live platform delivery can still populate Platform Performance when available.`
                          )
                        : shouldShowNarrativeAlignmentFailure
                        ? normalizeSherlocTextForRender(
                            `CROSS PLATFORM ANALYSIS:\n- ${narrativeAlignmentFailureMessage}\n- Platform Performance cards remain available from the latest resolved reporting truth.\n- Run Analysis can be retried to request fresh Platform Analysis.`
                          )
                        : ""
                    }
                    emptyMessage={
                      hasRunError
                        ? runIssueMessage
                        : shouldShowNarrativeAlignmentFailure
                        ? narrativeAlignmentFailureMessage
                        : isNarrativeOutOfSyncWithKpis
                        ? "Refreshing platform analysis with the latest KPI context."
                        : "No recommendations yet."
                    }
                    showChannelHeroes={true}
                    forceFirstLineAsH1={false}
                    suppressOverallHero={false}
                  />
                </div>
                  </div>
                </>
              )}

              {analystPanel && (
                <div className="sherlocreports-strategy-container">
                  <div className="sherlocreports-section-header-row">
                    <label className="sherlocreports-section-label">
                      {analystPanel?.analysisV2 ? "Campaign Intelligence" : "Analyst Insights"}
                    </label>
                    <div className="sherlocreports-section-chip sherlocreports-section-chip-green">
                      {analystPanel?.analysisV2 ? "Governed V2" : "Prioritized"}
                    </div>
                  </div>
                  <div className="sherlocreports-section-box sherlocreports-section-box-glass">
                    <SherlocAnalystInsightsPanel panel={analystPanel} />
                  </div>
                </div>
              )}
            </>
          )}

          {shouldShowNarrativeSections && !shouldRenderAlignedNarrative && !hasRunError && !isNarrativeOutOfSyncWithKpis && (
            <div className="sherlocreports-muted-center" style={{ marginTop: 18 }}>
              Narrative sections are hidden until you run analysis for the currently selected
              campaign/timeframe.
            </div>
          )}

          {orderedPerformanceCardEntries.length > 0 && (
            <div style={{ marginTop: 26 }}>
              <div className="sherlocreports-section-header-row sherlocreports-section-header-row-center">
                <h2
                  className="sherlocreports-section-label"
                  style={{ textAlign: "center", marginBottom: 0 }}
                >
                  Platform Performance
                </h2>
                <div className="sherlocreports-section-chip sherlocreports-section-chip-ice">
                  {PLATFORM_PERFORMANCE_INGEST_PILL_COPY}
                </div>
              </div>

              <div className="sherlocreports-kpis sherlocreports-kpis-channels">
                {orderedPerformanceCardEntries.map(([key, ch]) => {
                  const label = CHANNEL_LABELS[key] || key.replace(/_/g, " ").toUpperCase();
                  const icon = CHANNEL_ICONS[key] || null;
                  const m = computeChannelMetrics(key, ch || {});
                  const isGoogleAnalyticsMeasurement = key === "google_analytics";
                  const platformStatusKey = normalizeLivePlatformKey(key);
                  const unifiedPlatformStatus = platformStatusKey
                    ? livePlatformStatusByKey[platformStatusKey]
                    : "";
                  const legacyLive =
                    isLiveSherlocChannel(ch) ||
                    isLiveSherlocChannel(displayScopedSources?.live?.byChannel?.[key]);
                  const isApproved = approvedSherlocChannelKeys.has(key);
                  const hasDelivery = hasMeaningfulDeliveryMetrics(ch);
                  const isLive = unifiedPlatformStatus
                    ? unifiedPlatformStatus === "LIVE"
                    : legacyLive;
                  const sourcePill = isGoogleAnalyticsMeasurement
                    ? {
                        ...googleAnalyticsMeasurementPill,
                        title: "GA4 measurement status for this advertiser",
                      }
                    : isLive
                    ? {
                        label: "LIVE",
                        className: "sherlocreports-source-pill sherlocreports-source-pill-live",
                        title: "Live in the current SherlocReports context",
                      }
                    : unifiedPlatformStatus === "READY" || unifiedPlatformStatus === "APPROVED"
                    ? buildSherlocReadyPill()
                    : unifiedPlatformStatus === "AVAILABLE"
                    ? {
                        label: "AVAILABLE",
                        className: "sherlocreports-source-pill sherlocreports-source-pill-ready",
                        title: "Available in the current SherlocReports context",
                      }
                    : hasDelivery
                    ? {
                        label: "INGEST",
                        className: "sherlocreports-source-pill sherlocreports-source-pill-ingest",
                        title: "Pipeline source metadata: current scoped delivery is from ingested/reporting data",
                      }
                    : isApproved
                    ? buildSherlocReadyPill()
                    : {
                        label: "NOT ACTIVE",
                        className: "sherlocreports-source-pill sherlocreports-source-pill-setup",
                        title: "Not active in the current SherlocReports scope",
                      };
                  const isCinema = key === "cinema";

                  return (
                    <div
                      key={key}
                      id={normalizeProviderSurfaceKey(key) ? `provider-${key}` : undefined}
                      className="sherlocreports-kpi-card sherlocreports-channel-card"
                      data-testid={
                        isGoogleAnalyticsMeasurement
                          ? "sherloc-ga4-measurement-card"
                          : undefined
                      }
                      onClick={() => handleChannelCardClick(key)}
                      onKeyDown={(e) => handleChannelKeyDown(e, key)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="sherlocreports-channel-card-top">
                        <div className="sherlocreports-card-header-row">
                          {icon && (
                            <SherlocHeroFrame
                              src={icon}
                              alt={`${label} logo`}
                              outerClassName="sherlocreports-card-icon-wrapper sherlocreports-report-hero-frame"
                              mediaClassName="sherlocreports-hero-frame-media-cover"
                              imageClassName="sherlocreports-card-icon sherlocreports-hero-frame-image-cover"
                            />
                          )}

                          <div className="sherlocreports-channel-card-titlecol">
                            <h2 className="sherlocreports-kpi-label sherlocreports-channel-title">
                              {label}
                            </h2>

                            <span className={sourcePill.className} title={sourcePill.title}>
                              {sourcePill.label}
                            </span>
                          </div>
                        </div>

                        <div className="sherlocreports-channel-chevron" aria-hidden="true">
                          ▸
                        </div>
                      </div>

                      {isGoogleAnalyticsMeasurement ? (
                        <>
                          {hasGoogleAnalyticsMeasurementRows ? (
                            <div className="sherlocreports-channel-metrics">
                              {googleAnalyticsMeasurementRows.map((row, idx) => (
                                <div
                                  key={`google-analytics-row-${idx}`}
                                  className="sherlocreports-metric-row"
                                >
                                  <span className="sherlocreports-metric-k">{row.k}</span>
                                  <span className="sherlocreports-metric-v">{row.v}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div
                              className="sherlocreports-muted-center"
                              style={{ marginTop: 18, textAlign: "left" }}
                            >
                              {googleAnalyticsMeasurementEmptyMessage}
                            </div>
                          )}
                        </>
                      ) : isCinema ? (
                        <div className="sherlocreports-channel-metrics">
                          <div className="sherlocreports-metric-row">
                            <span className="sherlocreports-metric-k">Ticket Sales</span>
                            <span className="sherlocreports-metric-v sherlocreports-metric-v-copper">
                              {m.ticketSales != null ? formatMoney(m.ticketSales) : platformMetricFallbackLabel}
                            </span>
                          </div>

                          <div className="sherlocreports-metric-row">
                            <span className="sherlocreports-metric-k">Booked Theaters</span>
                            <span className="sherlocreports-metric-v">
                              {m.totalBookedTheaters != null
                                ? formatNumber(m.totalBookedTheaters)
                                : platformMetricFallbackLabel}
                            </span>
                          </div>

                          <div className="sherlocreports-metric-row">
                            <span className="sherlocreports-metric-k">Circuits</span>
                            <span className="sherlocreports-metric-v">
                              {m.totalCircuits != null ? formatNumber(m.totalCircuits) : platformMetricFallbackLabel}
                            </span>
                          </div>

                          <div className="sherlocreports-metric-row">
                            <span className="sherlocreports-metric-k">Cities</span>
                            <span className="sherlocreports-metric-v">
                              {m.totalCities != null ? formatNumber(m.totalCities) : platformMetricFallbackLabel}
                            </span>
                          </div>

                          <div className="sherlocreports-metric-row">
                            <span className="sherlocreports-metric-k">States</span>
                            <span className="sherlocreports-metric-v">
                              {m.totalStates != null ? formatNumber(m.totalStates) : platformMetricFallbackLabel}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="sherlocreports-channel-metrics">
                          {buildChannelMetricRows(key, m).map((row, idx) => (
                            <div key={`${key}-row-${idx}`} className="sherlocreports-metric-row">
                              <span className="sherlocreports-metric-k">{row.k}</span>
                              <span
                                className={
                                  row.copper
                                    ? "sherlocreports-metric-v sherlocreports-metric-v-copper"
                                    : "sherlocreports-metric-v"
                                }
                              >
                                {row.v}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>

              {resolvedAdvertiserId && !orderedPerformanceCardEntries.length && !running && (
                <div className="sherlocreports-muted-center" style={{ marginTop: 30 }}>
                  No unified channel cards available yet for this advertiser (no ingest snapshot, no
                  usable live Meta breakdown, and no GA4 summary detected for the current context).
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {reportViewerState ? (
        <div
          className="sherlocreports-report-viewer-overlay"
          onClick={handleCloseReportViewer}
          role="presentation"
        >
          <div
            className="sherlocreports-report-viewer-shell"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`${reportViewerState.channelLabel} report viewer`}
          >
            <div className="sherlocreports-report-viewer-body">
              {reportViewerState.type === "channel" && reportViewerState.inlineReport ? (
                renderChannelDetailPanel({
                  inlineReport: reportViewerState.inlineReport,
                  showClose: true,
                  showFallbackNotice:
                    reportViewerState.channelKey !== "google_analytics" &&
                    (viewerStatus === "fallback" || Boolean(viewerError)),
                })
              ) : viewerStatus === "error" ? (
                <div className="sherlocreports-report-viewer-error">
                  <div className="sherlocreports-report-viewer-status-title">Embed Unavailable</div>
                  <div className="sherlocreports-report-viewer-status-copy">
                    {viewerError || reportViewerState.unavailableMessage}
                  </div>
                </div>
              ) : null}

              {reportViewerState.type !== "channel" && reportViewerState.embedUrl ? (
                <div className="sherlocreports-report-viewer-embed-mode">
                  <div className="sherlocreports-report-viewer-embed-toolbar">
                    <div>
                      <div className="sherlocreports-report-viewer-status-title">
                        {reportViewerState.channelLabel} Dashboard
                      </div>
                    </div>

                    <div className="sherlocreports-report-viewer-embed-actions">
                      <button
                        type="button"
                        className="sherlocreports-run-button sherlocreports-report-viewer-fallback-button"
                        onClick={handleOpenFullReport}
                        disabled={!reportViewerAnalyticsRoute}
                        title={
                          reportViewerAnalyticsRoute
                            ? `Open ${reportViewerState.channelLabel} Analytics`
                            : "No in-app analytics page is available for this channel."
                        }
                      >
                        Open Full Report
                      </button>
                      <button
                        type="button"
                        className="sherlocreports-report-viewer-close"
                        onClick={handleCloseReportViewer}
                        aria-label="Close report viewer"
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  <div className="sherlocreports-report-viewer-embed-grid">
                    <div
                      className={`sherlocreports-report-viewer-frame-shell ${
                        viewerStatus === "error"
                          ? "sherlocreports-report-viewer-frame-shell-hidden"
                          : ""
                      }`}
                    >
                      <ReportEmbedFrame
                        src={reportViewerState.embedUrl}
                        title={`${reportViewerState.channelLabel} Report`}
                        loadingImage={reportLoadingIcon}
                        onLoad={() => {
                          setViewerStatus("ready");
                          setViewerError("");
                        }}
                        onError={() => {
                          handleEmbeddedReportUnavailable(
                            "Embedded dashboard could not be displayed here."
                          );
                        }}
                      />
                    </div>

                    {renderChannelDetailPanel({
                      inlineReport: reportViewerState.inlineReport,
                      compact: true,
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SherlocReports;
